/**
 * 小说编辑器组件
 * 已解决的初始化顺序问题：
 * 1. "Cannot access 'handleSaveSettings' before initialization"：已将函数移动到它被引用之前
 * 2. "Cannot access 'saveNovelToServer' before initialization"：已将函数移动到它被引用之前
 * 3. "Cannot access 'saveCurrentChapter' before initialization"：已将函数移动到它被引用之前
 * 4. 修复"handleChapterSelect is not defined"错误：移除对未定义函数的引用，内联实现替代
 * 5. 修复"handleTabChange is not defined"错误：直接使用内联函数替代未定义的函数
 * 6. 添加全局fetch拦截器，修复硬编码的localhost URL
 */

import React, { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useTheme, useMediaQuery } from '@mui/material';
import { io } from 'socket.io-client';
import apiService from '../services/api';
import api from '../utils/apiInterceptor';
// 不再使用静态AI提示词，改为使用后端动态生成
import * as taskService from '../services/taskService';
// 导入自定义UI组件
import WordCountBadge from './WordCountBadge';
import GeneratingButton from './GeneratingButton';
import StoryStructureProgressPanel from './StoryStructureProgressPanel';
// 🔥🔥🔥 新增：智能分析和规划组件
import RelationshipPlanViewer from './RelationshipPlanViewer';
import EmotionalArcViewer from './EmotionalArcViewer';
import MobileBottomNav from './MobileBottomNav';

// 🔥 导入角色显示配置，避免硬代码
import { 
  getGenderColor, 
  getGenderDisplay, 
  getRoleColor, 
  getRoleDisplay, 
  CHARACTER_BADGE_STYLE, 
  CHARACTER_BADGE_TEXT_STYLE 
} from '../config/characterDisplay.config';
import { exportToWord, exportToTxt } from '../utils/exportUtils';
import { handleStreamGeneration } from '../utils/streamCompletionHandler';
import '../styles/NexusToast.css';
import '../styles/theme.css';
import {
  Container,
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Tab,
  Tabs,
  Grid,
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemSecondaryAction,
  FormControlLabel,
  Divider,
  Checkbox,
  Slider,
  Stack,
  LinearProgress,
  CircularProgress,
  Select,
  ListItemIcon,
  FormControl,
  InputLabel,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextareaAutosize,
  Snackbar,
  Alert,
  Switch,
  AlertTitle,
  Radio,
  RadioGroup,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormGroup,
  FormLabel,
  FormHelperText,
  Collapse,
  Badge,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Drawer,
  Fab,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CreateIcon from '@mui/icons-material/Create';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SettingsIcon from '@mui/icons-material/Settings';
import CircleIcon from '@mui/icons-material/Circle';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningIcon from '@mui/icons-material/Warning';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import ErrorIcon from '@mui/icons-material/Error';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import CachedIcon from '@mui/icons-material/Cached';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoIcon from '@mui/icons-material/Info';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import SortIcon from '@mui/icons-material/Sort';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt';
import ViewComfyIcon from '@mui/icons-material/ViewComfy';
import ViewCompactIcon from '@mui/icons-material/ViewCompact';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ViewDayIcon from '@mui/icons-material/ViewDay';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import ViewAgendaIcon from '@mui/icons-material/ViewAgenda';
import ViewCarouselIcon from '@mui/icons-material/ViewCarousel';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline';
import ViewSidebarIcon from '@mui/icons-material/ViewSidebar';
import ViewTimelineIcon from '@mui/icons-material/ViewTimeline';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import ViewQuiltOutlinedIcon from '@mui/icons-material/ViewQuiltOutlined';
import ViewComfyOutlinedIcon from '@mui/icons-material/ViewComfyOutlined';
import ViewCompactOutlinedIcon from '@mui/icons-material/ViewCompactOutlined';
import ViewStreamOutlinedIcon from '@mui/icons-material/ViewStreamOutlined';
import ViewDayOutlinedIcon from '@mui/icons-material/ViewDayOutlined';
import ViewWeekOutlinedIcon from '@mui/icons-material/ViewWeekOutlined';
import ViewAgendaOutlinedIcon from '@mui/icons-material/ViewAgendaOutlined';
import ViewCarouselOutlinedIcon from '@mui/icons-material/ViewCarouselOutlined';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import ViewHeadlineOutlinedIcon from '@mui/icons-material/ViewHeadlineOutlined';
import ViewSidebarOutlinedIcon from '@mui/icons-material/ViewSidebarOutlined';
import ViewTimelineOutlinedIcon from '@mui/icons-material/ViewTimelineOutlined';
import ViewKanbanOutlinedIcon from '@mui/icons-material/ViewKanbanOutlined';
import ListAltIcon from '@mui/icons-material/ListAlt';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import HubIcon from '@mui/icons-material/Hub';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BugReportIcon from '@mui/icons-material/BugReport';
import '../EditorStyles.css';
import BackgroundGenerateButton from './BackgroundGenerateButton';
// 直接在章节列表中显示状态，移除任务列表
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AlertBanner from './AlertBanner';

// 全局修复：在window对象上添加一个修复函数，用于替换所有fetch调用中的localhost URL
if (typeof window !== 'undefined') {
  const originalFetch = window.fetch;
  window.fetch = function(url, options) {
    if (typeof url === 'string') {
      // 处理localhost:5000 - 保持原样，这是正确的API端点
      if (url.includes('localhost:5000')) {
        // 不修改URL，使用原始的localhost:5000
        console.log('使用原始API URL:', url);
      }
      // 处理localhost:3000 - 这是错误的API端点，需要修改为localhost:5000
      else if (url.includes('localhost:3000/api')) {
        url = url.replace('http://localhost:3000/api', 'http://localhost:5000/api');
        console.log('修复的URL (3000->5000):', url);
      }
    }
    return originalFetch.call(this, url, options);
  };
  console.log('已安装全局fetch修复 - 将localhost:3000/api重定向到localhost:5000/api');
}

// API基础URL配置
const isDevelopment = process.env.NODE_ENV === 'development';
const currentHost = window.location.hostname;

// 根据环境和主机名决定API基础URL
const getApiBaseUrl = () => {
  // 如果通过环境变量明确设置了API URL，则使用它
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // 开发环境或本地访问，跨域调用本地后端
  if (isDevelopment || currentHost === 'localhost') {
    return 'http://localhost:5000/api';
  }

  // 生产环境：同源相对路径，自动适配任何域名/IP/协议（nginx 统一代理 /api 到后端）
  return '/api';
};

const API_BASE_URL = getApiBaseUrl();

// 防抖函数
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// 多层次上下文提取函数
const getPreviousChaptersContext = (chapters, selectedChapter, isForOutline = false) => {
  if (!chapters || chapters.length === 0 || !selectedChapter) {
    return { 
      recentChapters: '', 
      midRangeChapters: '', 
      allPreviousSummary: '',
      usedInteractions: [],
      usedScenarios: [],
      usedPlotDevices: [],
      usedDialoguePatterns: []
    };
  }

  // 上下文层次
  // 大纲生成时，我们需要更多的上下文，所以调整范围
  const recentRange = isForOutline ? 3 : 2;
  const midRange = isForOutline ? 7 : 5;

  // 获取前N章的详细摘要
  const recentChapters = chapters
    .filter(ch => ch.index < selectedChapter && ch.index >= selectedChapter - recentRange && ch.content)
    .sort((a, b) => b.index - a.index) // 降序排列，最近的在前
    .map(ch => {
      // 大纲生成时提取更多内容
      const contentLength = isForOutline ? 800 : 500;
      // 提取前N字作为详细摘要
      const contentPreview = ch.content && ch.content.length > contentLength 
        ? ch.content.substring(0, contentLength) + "..." 
        : ch.content || '';
      return `第${ch.index}章 ${ch.title}：\n${contentPreview}`;
    })
    .join('\n\n');

  // 获取前N-M章的简要摘要
  const midRangeChapters = chapters
    .filter(ch => ch.index < selectedChapter - recentRange && ch.index >= selectedChapter - midRange && ch.content)
    .sort((a, b) => b.index - a.index)
    .map(ch => {
      // 大纲生成时提取更多内容
      const contentLength = isForOutline ? 200 : 100;
      // 提取前N字作为简要摘要
      const contentPreview = ch.content && ch.content.length > contentLength 
        ? ch.content.substring(0, contentLength) + "..." 
        : ch.content || '';
      return `第${ch.index}章 ${ch.title}：\n${contentPreview}`;
    })
    .join('\n\n');

  // 获取之前所有章节的超简要摘要
  const allPreviousSummary = chapters
    .filter(ch => ch.index < selectedChapter - midRange && ch.content)
    .sort((a, b) => b.index - a.index)
    .map(ch => `第${ch.index}章 ${ch.title}：${ch.content ? ch.content.substring(0, 50) + "..." : ""}`)
    .join('\n');

  // 提取已使用的元素 - 通过分析现有内容
  const usedInteractions = [];
  const usedScenarios = [];
  const usedPlotDevices = [];
  const usedDialoguePatterns = [];

  // 从所有已有章节提取特征
  chapters
    .filter(ch => ch.index < selectedChapter && ch.content)
    .forEach(ch => {
      const content = ch.content || '';
      
      // 提取场景描述 - 寻找场景设置段落
      const scenarioMatches = content.match(/[^\n]+描述了.{5,100}场景[^\n]+/g) || [];
      scenarioMatches.forEach(match => {
        if (match.length > 20) { // 避免太短的匹配
          usedScenarios.push(`CH${ch.index}: ${match.substring(0, 50)}...`);
        }
      });
      
      // 提取角色互动模式
      const characterInteractionMatches = content.match(/[^\n]+互动.{5,100}[^\n]+/g) || [];
      characterInteractionMatches.forEach(match => {
        if (match.length > 20) {
          usedInteractions.push(`CH${ch.index}: ${match.substring(0, 50)}...`);
        }
      });
      
      // 提取对话模式
      const dialogueMatches = content.match(/[""](.{10,50})[""].*[""](.{10,50})[""][^\n]*/g) || [];
      dialogueMatches.forEach((match, index) => {
        if (index % 3 === 0) { // 只取部分样本，避免过多
          usedDialoguePatterns.push(`CH${ch.index}: ${match.substring(0, 50)}...`);
        }
      });
      
      // 提取情节设计手法
      const plotDeviceMatches = content.match(/([^\n]+转折[^\n]+|[^\n]+伏笔[^\n]+|[^\n]+冲突[^\n]+|[^\n]+高潮[^\n]+)/g) || [];
      plotDeviceMatches.forEach(match => {
        if (match.length > 20) {
          usedPlotDevices.push(`CH${ch.index}: ${match.substring(0, 50)}...`);
        }
      });
    });

  // 限制每种元素的数量，避免过多
  const limitArraySize = (arr, maxSize = 10) => arr.slice(0, maxSize);
  
  return { 
    recentChapters, 
    midRangeChapters, 
    allPreviousSummary,
    usedInteractions: limitArraySize(usedInteractions),
    usedScenarios: limitArraySize(usedScenarios),
    usedPlotDevices: limitArraySize(usedPlotDevices),
    usedDialoguePatterns: limitArraySize(usedDialoguePatterns)
  };
};

// 默认小说设置
const defaultNovelSettings = {
  author: '',
  genre: '',
  style: '',
  background: '',
  platform: '',
  perspective: '',
  length: '',
  wordCount: '',
  chapterCount: '',
  keywords: [],
  characters: [],
  plotPoints: [],
  worldBuilding: {},
  theme: [],
  additionalNotes: '',
  enableThinking: true, // 是否开启推理模式（开启后AI推理过程实时显示，输出时自动隐藏推理内容）
  channel: '',
  aiModel: 'deepseek-v4-flash' // AI 模型选择（默认使用 deepseek-v4-flash，支持 1M 上下文和 384K 输出）
};

// 规范化设置数据
const normalizeSettings = (settings) => {
  const normalized = { ...defaultNovelSettings, ...settings };
  
  // 确保数组类型字段正确
  normalized.keywords = Array.isArray(normalized.keywords) ? normalized.keywords : [];
  normalized.characters = Array.isArray(normalized.characters) ? normalized.characters : [];
  normalized.plotPoints = Array.isArray(normalized.plotPoints) ? normalized.plotPoints : [];
  normalized.theme = Array.isArray(normalized.theme) ? normalized.theme : [];
  
  // 确保对象类型字段正确
  normalized.worldBuilding = typeof normalized.worldBuilding === 'object' && normalized.worldBuilding !== null 
    ? normalized.worldBuilding 
    : {};
  
  // 确保字符串类型字段正确
  const stringFields = ['author', 'genre', 'style', 'background', 'platform', 'perspective', 'length', 'wordCount', 'chapterCount', 'additionalNotes', 'channel', 'aiModel'];
  stringFields.forEach(field => {
    normalized[field] = typeof normalized[field] === 'string' ? normalized[field] : '';
  });
  
  return normalized;
};

// 在文件顶部添加日志控制变量
const DEBUG = process.env.NODE_ENV === 'development';
const logDebug = (...args) => {
  if (DEBUG) {
    console.log(...args);
  }
};

// 统一ID辅助函数 - 用于处理ID不一致的问题
const normalizeId = (obj) => {
  if (!obj) return obj;
  
  // 复制对象，避免修改原对象
  const normalized = { ...obj };
  
  // 如果存在_id，添加id字段
  if (normalized._id && !normalized.id) {
    normalized.id = normalized._id.toString();
  }
  
  // 如果存在id，添加_id字段
  if (normalized.id && !normalized._id) {
    normalized._id = normalized.id;
  }
  
  return normalized;
};

// 创建TabPanel组件
// 模块级滚动存储，供TabPanel和主组件共享
const __novelScrollStore = { refs: {}, setScrollPositions: null };

const TabPanel = memo(function TabPanel(props) {
  const { children, value, index, sx = {}, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      style={{ height: '100%' }}
      data-tab-key={index}
      ref={el => {
        __novelScrollStore.refs[`tab-${index}`] = el;
      }}
      {...other}
    >
      {value === index && (
        <Box sx={{ height: '100%', overflowY: 'auto', ...sx }}>
          {children}
        </Box>
      )}
    </div>
  );
});

// 为了统一处理更新用户余额的逻辑，创建一个辅助函数
const updateUserBalanceImpl = ({ consumedAmount, setUserBalance }) => {
  setUserBalance(prev => Math.max(0, prev - consumedAmount));
};

// 为了统一处理章节状态更新的逻辑，创建一个辅助函数
const updateChapterStatusImpl = (params) => {
  const { chapterIndex, statusUpdate, setChapterGenerationStatus } = params;
  setChapterGenerationStatus(prev => {
    // 检查prev是否为数组
    if (!Array.isArray(prev)) {
      console.warn('updateChapterStatusImpl: prev不是数组，初始化为空数组');
      prev = [];
    }
    
    // 查找当前章节状态对象
    const chapterStatusIndex = prev.findIndex(s => s.chapterIndex === chapterIndex);
    
    // 创建新的状态数组
    const newStatus = [...prev];
    
    // 如果找不到当前章节状态，创建一个新的
    if (chapterStatusIndex === -1) {
      console.log(`章节${chapterIndex}状态不存在，创建新状态`);
      newStatus.push({
        chapterIndex,
        status: '等待中',
        progress: 0,
        ...statusUpdate
      });
    } else {
      // 更新现有章节状态
    // 如果有详细状态，优先使用
    if (statusUpdate.detailedStatus) {
      statusUpdate.status = statusUpdate.detailedStatus;
    }
    
      newStatus[chapterStatusIndex] = { 
        ...newStatus[chapterStatusIndex], 
        ...statusUpdate 
      };
    }
    
    // 记录日志，方便调试
    const updatedChapter = newStatus.find(s => s.chapterIndex === chapterIndex);
    console.log(`章节${chapterIndex}状态更新:`, updatedChapter);
    
    return newStatus;
  });
};

// 格式化章节状态显示文本
const formatChapterStatus = (status, isFirstSubmission, progress) => {
  // 如果是首次提交的章节，显示更明确的状态
  if (isFirstSubmission) {
    if (status === 'processing' || status === 'generating' || status === '生成中') {
      return '优先生成中';
    }
    return status;
  }
  
  // 详细的生成状态，直接显示
  if ([
    '准备大纲提示词...',
    '正在生成大纲...',
    '提取章节标题...',
    '大纲已保存',
    '准备内容提示词...',
    '正在生成内容...',
    '生成大纲中',
    '基于大纲生成内容中',
    '生成内容中'
  ].includes(status)) {
    return status; // 直接返回详细状态
  }
  
  // 普通章节状态处理
  switch (status) {
    case 'pending':
    case '等待中':
    case '等待生成':
      return '等待中';
    case 'processing':
    case 'generating':
    case '生成中':
      return '生成中';
    case 'generated':
    case '已生成':
      return '已生成';
    case 'completed':
    case '已完成':
      return '已完成';
    case 'failed':
    case '生成失败':
      return '生成失败';
    case 'skipped':
    case '已跳过':
      return '已生成'; // 修改：将"已跳过"改为"已生成"
    default:
      return status;
  }
};

// 为了统一处理将章节保存到本地存储的逻辑，创建一个辅助函数
const saveChaptersToLocalStorageImpl = (novelData, chaptersData) => {
  if (!novelData || !novelData.id) {
    console.warn('未提供有效的小说数据，无法保存到本地存储');
    return false;
  }
  
  try {
    // 保存小说基本信息
    localStorage.setItem(`novel_${novelData.id}`, JSON.stringify({
      id: novelData.id,
      title: novelData.title,
      description: novelData.description,
      outline: novelData.outline,
      settings: novelData.settings,
      updatedAt: new Date().toISOString()
    }));
    
    // 保存每个章节
    if (Array.isArray(chaptersData)) {
      chaptersData.forEach(chapter => {
        if (chapter && (chapter.index || chapter.index === 0)) {
          localStorage.setItem(`novel_${novelData.id}_chapter_${chapter.index}`, JSON.stringify({
            index: chapter.index,
            title: chapter.title,
            content: chapter.content,
            outline: chapter.outline,
            updatedAt: new Date().toISOString()
          }));
        }
      });
    }
    
    console.log(`已将小说《${novelData.title}》数据保存到本地存储`);
    return true;
  } catch (error) {
    console.error('保存到本地存储失败:', error);
    return false;
  }
};

// ==================== 章节内容相似度计算工具（前端子啊） ====================
/**
 * 计算两个文本之间的内容相似度（基于Jaccard相似系数）
 * @param {string} text1 - 文本1
 * @param {string} text2 - 文本2
 * @returns {number} 相似度，0-1之间，1表示完全相同
 */
const calculateChapterSimilarity = (text1, text2) => {
  if (!text1 || !text2 || typeof text1 !== 'string' || typeof text2 !== 'string') {
    return 0;
  }
  
  try {
    // 预处理：移除空白字符并转小写
    const cleanText1 = text1.replace(/\s+/g, ' ').trim().toLowerCase();
    const cleanText2 = text2.replace(/\s+/g, ' ').trim().toLowerCase();
    
    // 如果完全相同，返回1
    if (cleanText1 === cleanText2) {
      return 1;
    }
    
    // 基于字符级别的Jaccard相似度（3-gram）
    const charShingles1 = new Set();
    const charShingles2 = new Set();
    const k = 3;
    
    for (let i = 0; i <= cleanText1.length - k; i++) {
      charShingles1.add(cleanText1.substring(i, i + k));
    }
    for (let i = 0; i <= cleanText2.length - k; i++) {
      charShingles2.add(cleanText2.substring(i, i + k));
    }
    
    let intersection = 0;
    for (const shingle of charShingles1) {
      if (charShingles2.has(shingle)) {
        intersection++;
      }
    }
    const union = charShingles1.size + charShingles2.size - intersection;
    const similarity = union === 0 ? 0 : intersection / union;
    
    return Math.min(1, Math.max(0, similarity));
  } catch (error) {
    console.warn('[calculateChapterSimilarity] 计算相似度失败:', error.message);
    return 0;
  }
};

// 创建新小说的实现函数
const createNewNovelImpl = async ({
  navigate,
  setLoading,
  setLoadError,
  setIsCreatingNovel,
  setNovel,
  setTitle,
  setNovelSettings,
  setChapters,
  setSelectedChapter,
  setActiveTab,
  showMessage,
  existingId = null // 添加一个可选参数来接收已有的ID
}) => {
  console.log('开始创建新小说...');
  setLoading(true);
  setIsCreatingNovel(true);
  
  try {
    // 验证令牌
    const token = localStorage.getItem('authToken');
    if (!token) {
      console.error('创建小说失败: 未找到认证令牌');
      navigate('/login');
      throw new Error('未找到认证令牌，请重新登录');
    }
    
    // 检查是否有预先创建的ID
    let novelId = existingId;
    if (!novelId) {
      // 尝试从localStorage获取最近创建的ID
      const lastCreatedId = localStorage.getItem('lastCreatedNovelId');
      if (sessionStorage.getItem('createdFromDashboard') === 'true' && lastCreatedId) {
        console.log(`检测到Dashboard创建的小说ID: ${lastCreatedId}`);
        novelId = lastCreatedId;
      }
    }
    
    // 创建默认小说数据结构 - 不包含章节
    let createdNovel = {
      title: '新小说',
      description: '',
      settings: { ...defaultNovelSettings },
      chapters: [] // 明确初始化为空数组，不创建默认章节
    };
    
    // 如果已有ID，添加到默认数据中
    if (novelId) {
      createdNovel._id = novelId;
      createdNovel.id = novelId;
      console.log(`使用预先创建的小说ID: ${novelId}`);
      
      // 确保章节的novelId也正确设置
      if (createdNovel.chapters && createdNovel.chapters.length > 0) {
        createdNovel.chapters = createdNovel.chapters.map(chapter => ({
          ...chapter,
          novelId: novelId
        }));
      }
    }
    
    // 如果是从Dashboard创建的小说，则不再发送API请求，直接使用本地数据
    // 检查sessionStorage标记和novelId是否都存在
    const isFromDashboard = sessionStorage.getItem('createdFromDashboard') === 'true' && novelId;
    
    if (!isFromDashboard) {
      // 只有不是从Dashboard创建的小说，才发送API请求
      // 使用apiClient或api对象，而不是直接使用fetch
      try {
        const response = await api.post('/novels', createdNovel, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        // 确保我们从响应中获取正确格式的小说数据
        if (response.data.success && response.data.data) {
          createdNovel = response.data.data;
        } else if (response.data.novel) {
          createdNovel = response.data.novel;
        } else if (response.data.id || response.data._id) {
          createdNovel = response.data;
        }
      } catch (apiError) {
        console.error('API请求失败:', apiError);
        // 如果API请求失败，但我们有ID，继续使用本地数据
        if (!novelId) {
          throw new Error(apiError.message || '服务器通信失败');
        }
        console.log('尽管API请求失败，但将继续使用本地数据');
      }
    } else {
      // 从Dashboard创建的小说，优先从服务器拉取真实数据（保留用户填写的标题）
      console.log('检测到从Dashboard创建的小说，尝试从服务器获取最新数据');
      if (novelId) {
        try {
          const getResp = await api.get(`/novels/${novelId}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          const srvData = getResp.data?.data || getResp.data?.novel || getResp.data;
          if (srvData && (srvData.title || srvData._id || srvData.id)) {
            createdNovel = srvData;
            console.log('已从服务器获取到新创建小说的数据，标题为：', createdNovel.title);
          } else {
            console.warn('从服务器获取小说成功但数据格式异常，继续使用本地数据');
          }
        } catch (fetchErr) {
          console.warn('从服务器获取新创建小说失败，使用本地数据：', fetchErr?.message || fetchErr);
        }
      }
      // 清除会话标记，避免重复处理
      sessionStorage.removeItem('createdFromDashboard');
    }
    
    // 规范化小说数据，确保ID字段一致性
    let normalizedNovel = normalizeId(createdNovel);
    
    // 如果有预先创建的ID，确保使用这个ID
    if (novelId) {
      normalizedNovel = {
        ...normalizedNovel,
        _id: novelId,
        id: novelId
      };
    }

    // 确保章节的novelId与小说ID一致
    if (normalizedNovel.chapters && normalizedNovel.chapters.length > 0) {
      normalizedNovel.chapters = normalizedNovel.chapters.map(chapter => ({
        ...chapter,
        novelId: normalizedNovel.id
      }));
    } else {
      // 初始化为空数组，不再添加默认章节
      normalizedNovel.chapters = [];
    }
    
    // 保存小说ID到本地存储
    if (normalizedNovel.id) {
      localStorage.setItem('lastCreatedNovelId', normalizedNovel.id);
      localStorage.setItem('lastLoadedNovelId', normalizedNovel.id);
      
      // 更新URL以反映新小说的ID
      navigate(`/novel-editor/${normalizedNovel.id}`, { replace: true });
    }
    
    // 更新状态
    setNovel(normalizedNovel);
    // 只有当标题确实为空时才使用默认值，避免覆盖用户输入的标题
    if (normalizedNovel.title && normalizedNovel.title.trim().length > 0) {
      setTitle(normalizedNovel.title);
    } else {
      setTitle('新小说');
    }
    setNovelSettings(normalizedNovel.settings || defaultNovelSettings);
    
    // 设置章节数据
    const chaptersData = normalizedNovel.chapters || [];
    setChapters(chaptersData);
    
    // 如果有章节，选择第一章
    if (chaptersData.length > 0) {
      setSelectedChapter(1); // 用户界面使用1开始的索引
    }
    
    // 🔥 根据项目实际进度自动跳转到适当的标签页
    // 优先级：章节内容 > 故事结构（生成中/已完成） > 大纲 > 基础设置
    if (chaptersData.length > 0 && chaptersData.some(ch => ch.hasContent || (ch.content && ch.content.trim().length > 0))) {
      // 优先级1：如果已经有章节内容，跳转到章节内容页
      console.log('✅ 检测到章节内容，自动跳转到章节内容页');
      setActiveTab(3); // 章节内容在第4个标签（索引3）
    } else if (normalizedNovel.storyStructure) {
      // 优先级2：检查故事结构的实际生成状态
      const structureStatus = normalizedNovel.storyStructure.generationStatus;
      const isStructureGenerating = structureStatus === 'generating' || structureStatus === 'retrying';
      const isStructureCompleted = structureStatus === 'completed' || normalizedNovel.storyStructure.isGenerated;
      
      if (isStructureGenerating || isStructureCompleted) {
        // 只有在故事结构正在生成或已完成时才跳转到故事结构页
        console.log(`✅ 检测到故事结构${isStructureCompleted ? '已生成' : '生成中'}，自动跳转到故事结构页`);
        setActiveTab(2); // 故事结构在第3个标签（索引2）
      } else if (normalizedNovel.outline && normalizedNovel.outline.trim().length > 0) {
        // 优先级3：故事结构未生成，但有大纲，跳转到大纲页
        console.log('✅ 检测到大纲内容，自动跳转到大纲页');
        setActiveTab(1);
      } else {
        // 优先级4：默认跳转到基础设置
        console.log('📝 无进度记录，跳转到基础设置页');
        setActiveTab(0);
      }
    } else if (normalizedNovel.outline && normalizedNovel.outline.trim().length > 0) {
      // 优先级3：没有故事结构，但有大纲，跳转到大纲页
      console.log('✅ 检测到大纲内容，自动跳转到大纲页');
      setActiveTab(1);
    } else {
      // 优先级4：什么都没有，跳转到基础设置
      console.log('📝 新项目，跳转到基础设置页');
      setActiveTab(0);
    }
    
    console.log('新小说创建成功:', normalizedNovel.id);
    showMessage('新小说创建成功', 'success');
    
    return normalizedNovel;
  } catch (error) {
    console.error('创建新小说失败:', error);
    setLoadError(error.message);
    showMessage(`创建新小说失败: ${error.message}`, 'error');
    return null;
  } finally {
    setLoading(false);
    setIsCreatingNovel(false);
  }
};

/**
 * 根据预计章节数解析总章节数（与 FULL_OUTLINE 提示词逻辑一致）
 */
function resolveTotalChapters(novelSettings) {
  const userChapterCount = parseInt(novelSettings.chapterCount, 10);
  if (userChapterCount && userChapterCount > 0) return userChapterCount;
  const { length, wordCount } = novelSettings;
  if (length === '中篇') return 50;
  if (length === '长篇') return 125;
  if (length === '超长篇') return (wordCount || 100) >= 100 ? 600 : 250;
  return 100;
}

/**
 * 按章节数计算大纲生成的 max_tokens（分段容量表，与 getOutlineGenerationPlan 的字数目标匹配：
 * 目标字数×1.5 token/字 + 收尾余量，确保不截断也不虚耗）
 */
function computeOutlineMaxTokens(totalChapters, enableThinking = false) {
  const chapters = Math.max(1, totalChapters);
  let base;
  if (chapters <= 15) base = 11000;
  else if (chapters <= 30) base = 13000;
  else if (chapters <= 50) base = 15500;
  else if (chapters <= 100) base = 18000;
  else if (chapters <= 200) base = 21000;
  else if (chapters <= 400) base = 25000;
  else base = 28000;
  return enableThinking ? Math.min(35000, Math.round(base * 1.25)) : base;
}

/**
 * 一次性生成小说总体大纲 —— 按章节数动态规划篇幅、详略与 API 参数
 * 🔍 搜索关键字：OUTLINE_GENERATION_PLAN
 */
function getOutlineGenerationPlan(totalChapters) {
  const chapters = Math.max(1, totalChapters);

  if (chapters <= 15) {
    return {
      tierLabel: '极短篇',
      detailLevel: '精简框架',
      minWords: 5000,
      maxWords: 7000,
      targetWords: 6000,
      maxTokens: 11000,
      charCount: '2个主要角色和1-2个配角',
      supportingCharMinWords: 60,
      villainCount: '1个',
      worldElementCount: 2,
      includeAdvancedSections: false,
      plotPhaseMode: 'compact',
      climaxInterval: Math.max(3, Math.floor(chapters / 3)),
      openingChapters: Math.min(2, chapters),
      systemHint: '这是一部极短篇作品，大纲应精炼聚焦，只保留核心骨架，严禁冗长铺陈。',
      scaleNote: `⚠️ 规模适配（${chapters}章·极短篇）：大纲控制在5000-7000字（目标约6000字），只写核心设定与主线，人物各60-100字，剧情按「开篇→主线→结局」三段概括，不要逐章展开。`,
    };
  }
  if (chapters <= 30) {
    return {
      tierLabel: '短篇',
      detailLevel: '简明扼要',
      minWords: 6000,
      maxWords: 9000,
      targetWords: 7500,
      maxTokens: 13000,
      charCount: '2-3个主要角色和2个配角',
      supportingCharMinWords: 80,
      villainCount: '1-2个',
      worldElementCount: 2,
      includeAdvancedSections: false,
      plotPhaseMode: 'compact',
      climaxInterval: Math.max(5, Math.floor(chapters / 4)),
      openingChapters: 3,
      systemHint: '这是一部短篇作品，大纲应简明清晰，突出主线与关键转折，避免过度展开。',
      scaleNote: `⚠️ 规模适配（${chapters}章·短篇）：大纲6000-9000字（目标约7500字），配角每人80-120字，剧情分开篇/发展/高潮/结局四段即可，七至九节内容融入前文，勿单独成章。`,
    };
  }
  if (chapters <= 50) {
    const targetWords = 9000;
    const maxWords = 10500;
    const minWords = 7500;
    return {
      tierLabel: '中短篇',
      detailLevel: '标准详细',
      minWords,
      maxWords,
      targetWords,
      maxTokens: computeOutlineMaxTokens(chapters),
      charCount: '3-4个主要角色和2-3个重要配角',
      supportingCharMinWords: 100,
      villainCount: '1-2个',
      worldElementCount: 3,
      includeAdvancedSections: true,
      plotPhaseMode: 'standard',
      climaxInterval: 12,
      openingChapters: 3,
      systemHint: '这是一部中短篇作品，大纲需结构完整但控制篇幅，重点章节适当展开。',
      scaleNote: `⚠️ 规模适配（${chapters}章·中短篇）：大纲${minWords}-${maxWords}字（目标约${targetWords}字），结构完整，勿写成超长篇体量。`,
    };
  }
  if (chapters <= 100) {
    const targetWords = 10500;
    const maxWords = 12000;
    const minWords = 9000;
    return {
      tierLabel: '中篇',
      detailLevel: '详细完整',
      minWords,
      maxWords,
      targetWords,
      maxTokens: computeOutlineMaxTokens(chapters),
      charCount: '3-5个主要角色和2-3个重要配角',
      supportingCharMinWords: 120,
      villainCount: '2个',
      worldElementCount: 3,
      includeAdvancedSections: true,
      plotPhaseMode: 'standard',
      climaxInterval: 15,
      openingChapters: 3,
      systemHint: '这是一部中篇作品，大纲必须详尽且写完整，覆盖从开篇到终章的全部故事弧线，禁止在中途截断或省略后期剧情。',
      scaleNote: `⚠️ 规模适配（${chapters}章·中篇）：大纲${minWords}-${maxWords}字（目标约${targetWords}字），必须完整写到终章设计与伏笔回收，后期剧情不可省略。`,
    };
  }
  if (chapters <= 200) {
    const targetWords = 12500;
    const maxWords = 14500;
    const minWords = 10500;
    return {
      tierLabel: '长篇',
      detailLevel: '详尽深入',
      minWords,
      maxWords,
      targetWords,
      maxTokens: computeOutlineMaxTokens(chapters),
      charCount: '4-6个主要角色和3-5个重要配角',
      supportingCharMinWords: 150,
      villainCount: '2-3个',
      worldElementCount: 4,
      includeAdvancedSections: true,
      plotPhaseMode: 'full',
      climaxInterval: 18,
      openingChapters: 3,
      systemHint: '这是一部长篇作品，大纲必须写完整，充分展开世界观、人物弧线与多线剧情，禁止省略后期与终章。',
      scaleNote: `⚠️ 规模适配（${chapters}章·长篇）：大纲${minWords}-${maxWords}字（目标约${targetWords}字），须覆盖多阶段剧情与终章收束，关键节点具体到章节区间。`,
    };
  }
  if (chapters <= 400) {
    const targetWords = 15000;
    const maxWords = 17500;
    const minWords = 12500;
    return {
      tierLabel: '超长篇',
      detailLevel: '极度详细',
      minWords,
      maxWords,
      targetWords,
      maxTokens: computeOutlineMaxTokens(chapters),
      charCount: '5-7个主要角色和多个重要配角',
      supportingCharMinWords: 180,
      villainCount: '2-3个及多股势力',
      worldElementCount: 5,
      includeAdvancedSections: true,
      plotPhaseMode: 'full',
      climaxInterval: 20,
      openingChapters: 5,
      systemHint: '这是一部超长篇作品，大纲应极度详尽且必须写完整，覆盖史诗级叙事框架与终局，禁止中途截断。',
      scaleNote: `⚠️ 规模适配（${chapters}章·超长篇）：大纲${minWords}-${maxWords}字（目标约${targetWords}字），充分展开各阶段、多线伏笔与势力格局，终章不可省略。`,
    };
  }
  // 401章及以上（含 500、600 章等巨作）：目标16500字，上限18000，一个号一次可完整生成
  const targetWords = 16500;
  const maxWords = 18000;
  const minWords = 15000;
  return {
    tierLabel: '巨作',
    detailLevel: '超级详细',
    minWords,
    maxWords,
    targetWords,
    maxTokens: computeOutlineMaxTokens(chapters),
    charCount: '5-8个主要角色和多个重要配角',
    supportingCharMinWords: 200,
    villainCount: '3个以上及复杂势力网',
    worldElementCount: 5,
    includeAdvancedSections: true,
    plotPhaseMode: 'full',
    climaxInterval: 25,
    openingChapters: 5,
    systemHint: '这是一部章节数极多的巨作，大纲必须超级详尽且写完整，构建宏大叙事体系与终局收束，禁止因篇幅省略后期剧情。',
    scaleNote: `⚠️ 规模适配（${chapters}章·巨作）：大纲${minWords}-${maxWords}字（目标约${targetWords}字），覆盖全部叙事阶段、多线伏笔与终局，禁止逐章流水账，禁止中途截断。`,
  };
}

// 主组件
const NovelEditor = () => {
  const { novelId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // 仅体验卡用户章节上限控制
  const [isTrialOnlyUser, setIsTrialOnlyUser] = useState(false);
  const [trialCheckDone, setTrialCheckDone] = useState(false);
  useEffect(() => {
    // ...
    const TRIAL_STATUS_CACHE_KEY = 'trialRechargeStatus';
    const TRIAL_STATUS_TTL = 6 * 60 * 60 * 1000; // 6 小时缓存，仅对 trialOnly 状态生效

    const readJson = (raw) => {
      if (!raw) return null;
      try {
        return JSON.parse(raw);
      } catch (err) {
        console.warn('无法解析本地缓存，忽略', err);
        return null;
      }
    };

    const getCurrentUserId = () => {
      if (typeof window === 'undefined') return null;
      const storedUser = window.localStorage.getItem('user');
      const parsed = readJson(storedUser);
      return parsed?.id || parsed?._id || parsed?.userId || null;
    };

    const readCachedStatus = () => {
      if (typeof window === 'undefined') return null;
      const cachedRaw = window.localStorage.getItem(TRIAL_STATUS_CACHE_KEY);
      return readJson(cachedRaw);
    };

    const writeCachedStatus = (payload) => {
      if (typeof window === 'undefined') return;
      try {
        window.localStorage.setItem(TRIAL_STATUS_CACHE_KEY, JSON.stringify(payload));
      } catch (err) {
        console.warn('写入试用状态缓存失败', err);
      }
    };

    const clearCachedStatus = () => {
      if (typeof window === 'undefined') return;
      window.localStorage.removeItem(TRIAL_STATUS_CACHE_KEY);
    };

    let mounted = true;

    const ensureStatus = async () => {
      const now = Date.now();
      const userId = getCurrentUserId();
      const cached = readCachedStatus();
      
      // 优先从后端读取权威状态，避免本地误判
      try {
        const resp = await apiService.getAccessStatus?.();
        if (resp && resp.success && resp.status) {
          const isTrial = resp.status === 'trialOnly';
          if (mounted) {
            setIsTrialOnlyUser(isTrial);
            setTrialCheckDone(true);
          }
          writeCachedStatus({
            status: resp.status,
            checkedAt: now,
            userId: userId || '__unknown__',
            permanentFullAccess: resp.status === 'fullAccess'
          });
          // 服务端已返回，直接结束
          return;
        }
      } catch {
        // 忽略，继续走本地兜底逻辑
      }
      if (cached && cached.userId === userId) {
        if (cached.permanentFullAccess) {
          if (mounted) {
            setIsTrialOnlyUser(false);
            setTrialCheckDone(true);
          }
          if (typeof window !== 'undefined') {
            window.localStorage.removeItem('trialRechargeObserved');
          }
          return;
        }

        if (cached.status === 'trialOnly' && now - (cached.checkedAt || 0) < TRIAL_STATUS_TTL) {
          if (mounted) {
            setIsTrialOnlyUser(true);
            setTrialCheckDone(true);
          }
          return;
        }

        if (cached.status === 'fullAccess' && now - (cached.checkedAt || 0) < TRIAL_STATUS_TTL) {
          if (mounted) {
            setIsTrialOnlyUser(false);
            setTrialCheckDone(true);
          }
          if (typeof window !== 'undefined') {
            window.localStorage.removeItem('trialRechargeObserved');
          }
          return;
        }
      }

      try {
        const resp = await apiService.getUserRecharges?.();
        const candidateLists = [
          resp?.data,
          resp?.data?.records,
          resp?.data?.list,
          resp?.data?.data,
          resp?.records,
          resp?.list,
          resp
        ];
        const list = candidateLists.find(Array.isArray) || [];
        if (!mounted) return;

        const hasNonTrialRecharge = list.some(tx => {
          const key = tx?.cardKey || tx?.metadata?.cardKey;
          return !key || (typeof key === 'string' && !key.startsWith('KMCS-'));
        });

        const status = hasNonTrialRecharge || list.length === 0 ? 'fullAccess' : 'trialOnly';

        if (status === 'fullAccess') {
          setIsTrialOnlyUser(false);
          if (typeof window !== 'undefined') {
            window.localStorage.removeItem('trialRechargeObserved');
          }
        } else {
          setIsTrialOnlyUser(true);
        }

        writeCachedStatus({
          status,
          checkedAt: now,
          userId: userId || '__unknown__',
          permanentFullAccess: hasNonTrialRecharge
        });
      } catch (e) {
        console.error('检查充值记录失败，默认给予完整权限', e);
        if (mounted) {
          setIsTrialOnlyUser(false);
          clearCachedStatus();
        }
      } finally {
        if (mounted) setTrialCheckDone(true);
      }
    };

    ensureStatus();

    return () => {
      mounted = false;
    };
  }, []);
  
  // 监听充值完成事件，重新判定是否仅试用：仅普通卡解锁；测试卡保持10章
  useEffect(() => {
    const onUserDataUpdated = (evt) => {
      try {
        const detail = evt?.detail || {};
        if (detail.source !== 'recharge') return;

        const getCurrentUserId = () => {
          if (typeof window === 'undefined') return null;
          try {
            const raw = window.localStorage.getItem('user');
            const parsed = raw ? JSON.parse(raw) : null;
            return parsed?._id || parsed?.id || parsed?.userId || null;
          } catch {
            return null;
          }
        };

        const decideByHistory = async () => {
          try {
            const resp = await apiService.getUserRecharges?.();
            const candidateLists = [
              resp?.data,
              resp?.data?.records,
              resp?.data?.list,
              resp?.data?.data,
              resp?.records,
              resp?.list,
              resp
            ];
            const list = candidateLists.find(Array.isArray) || [];

            // 只要存在“非测试卡”充值就完全解锁；仅有测试卡则保持试用
            const hasNonTrialRecharge = list.some(tx => {
              const key = tx?.cardKey || tx?.metadata?.cardKey;
              return !key || (typeof key === 'string' && !key.startsWith('KMCS-'));
            });

            const userId = getCurrentUserId() || '__unknown__';
            if (typeof window !== 'undefined' && window.localStorage) {
              window.localStorage.setItem(
                'trialRechargeStatus',
                JSON.stringify({
                  status: hasNonTrialRecharge ? 'fullAccess' : 'trialOnly',
                  checkedAt: Date.now(),
                  userId,
                  permanentFullAccess: !!hasNonTrialRecharge
                })
              );
              if (hasNonTrialRecharge) {
                window.localStorage.removeItem('trialRechargeObserved');
              }
            }

            setIsTrialOnlyUser(!hasNonTrialRecharge);
            setTrialCheckDone(true);
          } catch {
            // 如果查询失败，不改变当前状态，避免误放开限制
          }
        };

        // 充值后根据最新记录决定状态
        decideByHistory();
      } catch {
        // 忽略事件处理中的任何异常，避免影响主流程
      }
    };
    
    window.addEventListener('userDataUpdated', onUserDataUpdated);
    return () => window.removeEventListener('userDataUpdated', onUserDataUpdated);
  }, []);
  
  // 用于中断批量生成请求的AbortController引用
  const batchGenAbortControllerRef = useRef(null);
  
  // 基础状态
  // 为了兼容性，保留apiKey变量但设为空字符串
  const apiKey = '';
  const [novel, setNovel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [title, setTitle] = useState('');
  const [userBalance, setUserBalance] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [noAutoSave, setNoAutoSave] = useState(false);
  const [isChapterSwitching, setIsChapterSwitching] = useState(false);
  const [lastSavedContent, setLastSavedContent] = useState('');
  const autoSaveTimerRef = useRef(null);
  const contentTextareaRef = useRef(null);
  const [showProgressPanel, setShowProgressPanel] = useState(false); // 🔥 WebSocket进度面板（嵌入式）
  
  const [chapters, setChapters] = useState([]);
  const [collapsedGroups, setCollapsedGroups] = useState(null); // null=未初始化(全部折叠), { '1-50': true } = 折叠, { '1-50': false } = 展开
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [jumpChapterInput, setJumpChapterInput] = useState('');
  const chapterRowRefs = useRef({});
  // 添加selectedChapterObj状态，确保它总是有定义
  const [selectedChapterObj, setSelectedChapterObj] = useState(null);
  const [currentChapterContent, setCurrentChapterContent] = useState('');
  // 🔥 懒加载等待动画 state（避免用户以为卡住）
  const [isLoadingChapterContent, setIsLoadingChapterContent] = useState(false);
  const [loadingOutlineChapter, setLoadingOutlineChapter] = useState(null);
  const [currentChapterOutline, setCurrentChapterOutline] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  
  // 🎯 新增：保存当前展开的章节ID，用于刷新时保持展开状态
  const [expandedOutlineGroup, setExpandedOutlineGroup] = useState(0); // 当前展开的组（默认展开第0组）
  const [expandedOutlineChapter, setExpandedOutlineChapter] = useState(null); // 当前展开的章节（每次只展开一个）
  
  // 🔥 故事结构折叠框展开状态管理（一次只展开一个）
  const [expandedAccordion, setExpandedAccordion] = useState(null);
  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : null);
  };

  // 🔥 各Tab滚动位置持久化
  const [scrollPositions, setScrollPositions] = useState({});
  __novelScrollStore.setScrollPositions = setScrollPositions;

  // 切换回某Tab时恢复滚动位置
  useEffect(() => {
    const saved = scrollPositions[activeTab];
    if (saved !== undefined && saved !== null) {
      const applyScroll = () => {
        const panelEl = document.getElementById(`tabpanel-${activeTab}`);
        if (panelEl) {
          const scrollBox = panelEl.querySelector('[style*="overflow-y: auto"]') || panelEl.querySelector('[class*="MuiBox-root"]');
          if (scrollBox) scrollBox.scrollTop = saved;
        }
      };
      // 首次可能DOM还未渲染完成
      requestAnimationFrame(applyScroll);
      setTimeout(applyScroll, 100);
    }
  }, [activeTab]);

  // 🔥 切换章节时重置编辑器内容区域滚动位置到顶部
  useEffect(() => {
    if (contentTextareaRef.current) {
      contentTextareaRef.current.scrollTop = 0;
    }
  }, [currentChapterContent]);

  // 🔥 移动端：章节切换后立即将标题栏吸附到顶部导航栏下方，并重触发动画
  useEffect(() => {
    if (!selectedChapter || typeof window === 'undefined') return;
    const isMobile = window.matchMedia('(max-width: 600px)').matches;
    if (!isMobile) return;

    const tryStick = () => {
      const panel = document.querySelector('.chapter-panel');
      if (!panel) return;

      // 找到当前选中章节的 AccordionSummary（标题栏）
      const allSummaries = panel.querySelectorAll('.MuiAccordionSummary-root');
      let targetSummary = null;
      allSummaries.forEach((summary) => {
        const accordion = summary.closest('.MuiAccordion-root');
        if (accordion && accordion.querySelector('.MuiAccordionDetails-root')) {
          // 这个是展开的章节
          targetSummary = summary;
        }
      });

      if (targetSummary) {
        // 重置动画，强制重绘
        targetSummary.style.animation = 'none';
        void targetSummary.getBoundingClientRect();
        targetSummary.style.animation = '';

        // 滚动到面板顶部（top:56px），使 sticky 标题栏被推出并吸附
        targetSummary.scrollIntoView({ block: 'start', behavior: 'instant' });

        // 再微调一次确保 sticky 计算正确触发
        if (panel.scrollHeight > panel.clientHeight) {
          panel.scrollTop -= 1;
          requestAnimationFrame(() => { panel.scrollTop += 1; });
        }
      }
    };

    // 等展开动画完成（Collapse 默认约 300ms）再触发
    const timer = setTimeout(tryStick, 400);
    return () => clearTimeout(timer);
  }, [selectedChapter]);
  
  // 🔥 章节内容分组折叠状态管理（一次只展开一个组）
  const [expandedChapterGroup, setExpandedChapterGroup] = useState(0); // 默认展开第一组
  const handleChapterGroupChange = (groupIndex) => (event, isExpanded) => {
    setExpandedChapterGroup(isExpanded ? groupIndex : null);
  };
  
  const [isCreatingNovel, setIsCreatingNovel] = useState(false);
  const [isOptimizing, setIsOptimizing] = useState(false);
  // 存储正在优化的章节ID和状态
  const [optimizingChapters, setOptimizingChapters] = useState(new Set());
  // 用于强制刷新UI的渲染键
  const [renderKey, setRenderKey] = useState(Date.now());
  
  // 在首次加载和novel改变时，重置优化状态
  useEffect(() => {
    // 重置优化章节集合状态（守卫，保持逻辑位）
    setOptimizingChapters(new Set());
    setRewritingChapters(new Set());
  }, [novel?.id]);
  
  // 🔥 重写章节状态
  const [rewritingChapters, setRewritingChapters] = useState(new Set());
  
  // 🔒 重置按钮冷却时间状态
  const [lastResetTime, setLastResetTime] = useState(0);
  const [resetCooldownRemaining, setResetCooldownRemaining] = useState(0);
  const RESET_COOLDOWN = 5 * 60 * 1000; // 5分钟冷却
  
  // 🔒 更新冷却时间倒计时
  useEffect(() => {
    if (lastResetTime === 0) return;
    
    const updateCooldown = () => {
      const now = Date.now();
      const timeSinceReset = now - lastResetTime;
      const remaining = Math.max(0, RESET_COOLDOWN - timeSinceReset);
      setResetCooldownRemaining(remaining);
      
      if (remaining === 0) {
        clearInterval(intervalId);
      }
    };
    
    updateCooldown();
    const intervalId = setInterval(updateCooldown, 1000);
    
    return () => clearInterval(intervalId);
  }, [lastResetTime, RESET_COOLDOWN]);
  
  // 监控优化中章节状态变化
  useEffect(() => {
    if (optimizingChapters.size > 0) {
      console.log('当前优化中章节:', Array.from(optimizingChapters));
    }
  }, [optimizingChapters]);
  
  // 确保所有章节都有_id属性
  useEffect(() => {
    if (chapters && chapters.length > 0) {
      const updatedChapters = chapters.map(chapter => {
        // 如果没有_id属性，使用id属性
        if (!chapter._id && chapter.id) {
          return { ...chapter, _id: chapter.id };
        }
        // 如果没有id属性，使用_id属性
        if (!chapter.id && chapter._id) {
          return { ...chapter, id: chapter._id };
        }
        return chapter;
      });
      
      // 检查是否有更新
      const needsUpdate = updatedChapters.some(
        (ch, idx) => (!chapters[idx]._id && ch._id) || (!chapters[idx].id && ch.id)
      );
      
      if (needsUpdate) {
        console.log('更新章节ID属性，确保id和_id都存在');
        setChapters(updatedChapters);
      }
    }
  }, [chapters]);
  
  // 检查章节是否正在优化中
const isChapterOptimizing = useCallback((chapter) => {
  // 如果没有章节对象或ID，返回false
  if (!chapter) return false;
  
  // 尝试获取章节ID（支持id或_id属性）
  const chapterId = chapter._id || chapter.id;
  if (!chapterId) return false;
  
  // 确保使用字符串类型进行比较
  const chapterIdStr = chapterId.toString();
  
  // 检查ID是否在优化集合中
  const result = optimizingChapters.has(chapterIdStr);
  
  console.log(`检查章节优化状态 - 索引: ${chapter.index}, ID: ${chapterIdStr}, 正在优化中: ${result}, 已优化: ${chapter.isOptimized}`);
  
  return result;
}, [optimizingChapters]);

  // 🔥 完全取消自动清理/同步重写状态
  // 说明：不再根据服务器状态自动清除本地重写状态，仅保留管理员端的“重置重写状态”入口。
  useEffect(() => {
    // 不做任何自动清理/同步操作，确保重写流程不被误清
    return () => {};
  }, [chapters]);
  
  // 检查章节是否正在重写中
  const isChapterRewriting = useCallback((chapter) => {
    // 如果没有章节对象或ID，返回false
    if (!chapter) return false;
    
    // 尝试获取章节ID（支持id或_id属性）
    const chapterId = chapter._id || chapter.id;
    if (!chapterId) return false;
    
    // 确保使用字符串类型进行比较
    const chapterIdStr = chapterId.toString();
    
    // 检查ID是否在重写集合中
    const result = rewritingChapters.has(chapterIdStr);
    
    console.log(`检查章节重写状态 - 索引: ${chapter.index}, ID: ${chapterIdStr}, 正在重写中: ${result}, 已重写: ${chapter.isRewritten}`);
    
    return result;
  }, [rewritingChapters]);

  const outlineRef = useRef(null);
  const canvasRef = useRef(null);
  const [outline, setOutline] = useState('');
  const [outlineText, setOutlineText] = useState('');
  const [thinkingContent, setThinkingContent] = useState('');

  // Canvas 粒子背景动画
  useEffect(() => {
    const canvas = canvasRef.current || document.getElementById('novelEditorBg');
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;
    let particles = [];
    const colors = [
      { h: 220, s: 90, l: 65 },
      { h: 200, s: 85, l: 60 },
      { h: 240, s: 80, l: 70 },
      { h: 270, s: 75, l: 65 },
      { h: 185, s: 85, l: 60 },
    ];

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    const createParticle = () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.3,
      vy: (Math.random() - 0.5) * 0.3,
      size: Math.random() * 2 + 0.5,
      color: colors[Math.floor(Math.random() * colors.length)],
      alpha: Math.random() * 0.5 + 0.2,
      pulse: Math.random() * Math.PI * 2,
    });

    resize();
    for (let i = 0; i < 50; i++) particles.push(createParticle());
    window.addEventListener('resize', resize);

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        p.x += p.vx;
        p.y += p.vy;
        p.pulse += 0.02;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        const a = p.alpha * (0.6 + 0.4 * Math.sin(p.pulse));
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${p.color.h},${p.color.s}%,${p.color.l}%,${a})`;
        ctx.fill();
        // 连线
        particles.forEach(p2 => {
          const dx = p.x - p2.x, dy = p.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = `hsla(${p.color.h},${p.color.s}%,${p.color.l}%,${0.08 * (1 - dist / 120)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        });
      });
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);
  
  // 添加一个useEffect来清理滚动监听器
  useEffect(() => {
    return () => {
      // 组件卸载时清理滚动监听器
      if (outlineRef.current && outlineRef.current._scrollHandler) {
        console.log('清理大纲滚动监听器');
        outlineRef.current.removeEventListener('scroll', outlineRef.current._scrollHandler);
        outlineRef.current._scrollHandler = null;
      }
    };
  }, []);
  const [novelSettings, setNovelSettings] = useState(defaultNovelSettings);
  const [keywordsMenuOpen, setKeywordsMenuOpen] = useState(false);
  const [tempKeywords, setTempKeywords] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  
  // 🔥 自定义大纲相关状态
  const [useCustomOutline, setUseCustomOutline] = useState(false);
  const [customOutline, setCustomOutline] = useState('');
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // 🔥 章节细纲编辑状态
  const [chapterOutlineEdit, setChapterOutlineEdit] = useState({
    open: false,
    chapterNumber: null,
    originalContent: '',
    editedContent: '',
    saving: false
  });

  // 🔥 关键事件编辑状态
  const [keyEventsEdit, setKeyEventsEdit] = useState({
    open: false,
    chapterNumber: null,
    originalEvents: [],
    editedEvents: [],
    saving: false
  });

  // 🔥 结局编辑状态
  const [endingEdit, setEndingEdit] = useState({
    open: false,
    type: '',
    description: '',
    emotionalTone: '',
    saving: false
  });

  // 🔥 章节标题编辑对话框状态
  const [chapterTitleDialog, setChapterTitleDialog] = useState({
    open: false,
    chapterIndex: null,
    chapterNumber: null,
    prefix: '', // "第x章："
    editedTitle: '', // 冒号后面的内容
    originalTitle: '',
    saving: false
  });

  // 🔥 章节内容编辑对话框状态
  const [chapterContentDialog, setChapterContentDialog] = useState({
    open: false,
    chapterIndex: null,
    chapterNumber: null,
    editedContent: '',
    originalContent: '',
    saving: false
  });

  // 🔥 章节标题编辑状态
  const [chapterTitleEdit, setChapterTitleEdit] = useState({
    isEditing: false,
    editedTitle: '',
    originalTitle: '',
    saving: false
  });

  // 🔥 章节内容编辑状态
  const [chapterContentEdit, setChapterContentEdit] = useState({
    isEditing: false,
    editedContent: '',
    originalContent: '',
    saving: false,
    hasUnsavedChanges: false
  });

  // 🔥 大纲编辑状态管理
  const [outlineEdit, setOutlineEdit] = useState({
    isEditing: false,
    editedContent: '',
    originalContent: '',
    saving: false,
    hasUnsavedChanges: false
  });
  // 查找/替换输入引用（实现放在 showMessage 定义之后）
  const outlineEditRef = useRef(null);

  // 对话框状态管理
  const [dialog, setDialog] = useState({
    open: false,
    title: '',
    content: '',
    confirmText: '确定',
    cancelText: '取消',
    onConfirm: () => {},
    onCancel: () => {}
  });

  // 番茄发文规则详情弹窗状态
  const [tomatoRulesDialogOpen, setTomatoRulesDialogOpen] = useState(false);

  // 添加缺失的状态变量
  const [generatingChapterId, setGeneratingChapterId] = useState(null);
  const [batchGenerating, setBatchGenerating] = useState(false);
  const [batchGenerationTaskId, setBatchGenerationTaskId] = useState(null);
  // 在批量轮询变慢时，用于保持“正在生成第X章”的连续显示
  const [displayGeneratingChapterId, setDisplayGeneratingChapterId] = useState(null);
  // 添加专门的alert消息状态，用于显示在章节列表中
  const [alertMessage, setAlertMessage] = useState({
    open: false,
    message: '',
    type: 'info'
  });
  const [batchProgress, setBatchProgress] = useState({
    current: 0,
    total: 0,
    progress: 0
  });
  // 计算用于展示的当前章节：优先使用服务端返回的 generatingChapterId，若缺失则使用本地推断
  const activeGeneratingChapter = useMemo(() => {
    return generatingChapterId ?? displayGeneratingChapterId ?? null;
  }, [generatingChapterId, displayGeneratingChapterId]);
  const [chapterGenerationStatus, setChapterGenerationStatus] = useState([]);
  const [chaptersGenerated, setChaptersGenerated] = useState([]);
  const [outlineGenerationStatus, setOutlineGenerationStatus] = useState({
    generating: false,
    section: '',
    progress: 0
  });
  const [generatingOutline, setGeneratingOutline] = useState(false);
  const [outlineGenerated, setOutlineGenerated] = useState(false);
  const [batchRange, setBatchRange] = useState({
    startChapter: 1,
    endChapter: 1,
    generateMode: 'complete', // 默认完整生成
    qualityLevel: 1, // 默认中等质量
    skipGenerated: true, // 默认跳过已生成的章节
    dialogueRatio: 30, // 默认对话比例30%
    generateOutlines: true, // 默认生成大纲
    autoSave: true, // 默认自动保存
    continuousMode: true // 默认连续模式
  });
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  // 🔥【弹窗精简】batchConfirmDialogOpen 已移除：二次确认弹窗合并进"后台批量生成"按钮直接执行
  const [batchResults, setBatchResults] = useState([]);
  const [batchResultDialogOpen, setBatchResultDialogOpen] = useState(false);
  const [storyStructConfirmOpen, setStoryStructConfirmOpen] = useState(false);
  const [storyStructConfirmData, setStoryStructConfirmData] = useState({ chapterCount: 30, requiredBalance: 0, userBalance: 0 });
  // 🔥 已移除 skipStoryStructureCheck 状态 - 现在强制要求生成故事结构后才能生成章节内容
  
  // 导出对话框状态
  // 连续显示逻辑：在批量生成期间，每秒根据已知信息推断展示的章节号，避免空窗
  useEffect(() => {
    if (!batchGenerating) {
      setDisplayGeneratingChapterId(null);
      return;
    }
    let cancelled = false;
    const inRange = (idx) => {
      if (!batchRange) return true;
      const normalize = (v) => {
        if (v == null) return undefined;
        const n = typeof v === 'string' ? Number(v) : v;
        return Number.isFinite(n) ? n : undefined;
      };
      const start = normalize(batchRange.startChapter ?? batchRange.start);
      const end = normalize(batchRange.endChapter ?? batchRange.end);
      if (start == null || end == null) return true;
      return idx >= start && idx <= end;
    };
    const computeDisplay = (prevDisplayId) => {
      // 1) 服务端权威信息
      if (typeof generatingChapterId === 'number' && !Number.isNaN(generatingChapterId)) {
        return generatingChapterId;
      }
      // 2) 根据章节状态推断下一个待生成的章节
      if (Array.isArray(chapters) && chapters.length > 0) {
        const sorted = [...chapters].sort((a, b) => a.index - b.index);
        for (const ch of sorted) {
          if (!inRange(ch.index)) continue;
          const st = chapterGenerationStatus?.find(s => s.chapterIndex === ch.index);
          const stVal = st?.status || ch.status || ch.generationStatus || '';
          const isWaitingOrRunning = ['等待中', '等待生成', '生成中', 'pending', 'generating'].includes(stVal);
          const noContent = !ch.content || ch.content.trim().length === 0;
          if (isWaitingOrRunning || noContent) {
            return ch.index;
          }
        }
      }
      // 3) 估算：根据批次进度 current+1
      if (batchProgress && typeof batchProgress.current === 'number') {
        const est = batchProgress.current + 1;
        if (inRange(est)) return est;
      }
      // 4) 回退：保持之前的显示值（仍在范围内）
      if (prevDisplayId && inRange(prevDisplayId)) {
        return prevDisplayId;
      }
      return prevDisplayId || null;
    };
    const tick = () => {
      if (cancelled) return;
      setDisplayGeneratingChapterId(prev => computeDisplay(prev));
    };
    // 立即计算一次，随后定时刷新
    tick();
    const t = setInterval(tick, 1000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [batchGenerating, generatingChapterId, chapters, chapterGenerationStatus, batchRange, batchProgress]);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportOptions, setExportOptions] = useState({
    includeOutline: true,
    includeStats: true
  });
  // 🔥 导出过程状态：防止重复点击 + 持续显示加载进度直到可下载
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState({ current: 0, total: 0 });
  
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('');
  
  // 新章节状态
  const [newChapter, setNewChapter] = useState({
    title: '',
    content: '',
    outline: ''
  });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    content: '',
    confirmText: '确认',
    cancelText: '取消',
    action: null
  });
  const [confirmDialogProps, setConfirmDialogProps] = useState({
    open: false,
    title: '',
    content: '',
    confirmAction: null
  });

  // 在 NovelEditor 组件内添加拖拽状态
  const [batchFloatPos, setBatchFloatPos] = useState({ right: 20, bottom: 20 });
  const [dragging, setDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const batchFloatRef = useRef(null);
  
  // 字数统计相关状态
  const [wordCount, setWordCount] = useState(0);
  const [showWordCount, setShowWordCount] = useState(false);
  
  // 故事结构生成相关状态
  const [storyStructure, setStoryStructure] = useState(null);
  const [generatingStructure, setGeneratingStructure] = useState(false);
  const [structureError, setStructureError] = useState(null);
  const [structureExpanded, setStructureExpanded] = useState(false);
  // 伏笔追踪展开状态（以索引为 key，避免在 map 中调用 useState）
  const [expandedForeshadows, setExpandedForeshadows] = useState({});
  
  // 🔥 移动端优化：故事结构界面折叠栏状态管理
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  // 故事结构各 section 的展开状态（手风琴效果）
  const [expandedSections, setExpandedSections] = useState({});
  // 章节细纲的展开状态（手风琴效果）
  const [expandedChapters, setExpandedChapters] = useState({});

  // 🔥 番茄平台发布信息（AI生成，供 KM发布助手 一键创建番茄书本）
  const [fanqieInfo, setFanqieInfo] = useState(null);
  const [fanqieGenerating, setFanqieGenerating] = useState(false);
  const [fanqieEditing, setFanqieEditing] = useState(false);
  const [fanqieEditDraft, setFanqieEditDraft] = useState(null);
  // 🔥 封面提示词：当前显示索引 + 折叠状态（紧凑展示，省空间）
  const [coverPromptIdx, setCoverPromptIdx] = useState(0);
  const [coverPromptExpanded, setCoverPromptExpanded] = useState(false);
  const [coverPromptGenerating, setCoverPromptGenerating] = useState(false);

  // 🔥【全书体检】全自动审读-修订任务状态（novelAuditService /api/novels/:id/audit/*）
  const [auditActive, setAuditActive] = useState(false);          // 有进行中的体检任务
  const [auditDialogOpen, setAuditDialogOpen] = useState(false);  // 体检面板开关
  const [auditStatus, setAuditStatus] = useState(null);           // /audit/status 返回
  const [auditProblems, setAuditProblems] = useState([]);         // 实时问题流
  const [auditReport, setAuditReport] = useState(null);           // 完成后的只读报告
  // 🔥【体检留痕】历史报告面板（持久可查：历史任务+逐条改前/改后明细）
  const [auditPanelOpen, setAuditPanelOpen] = useState(false);
  const [auditHistory, setAuditHistory] = useState([]);
  const [auditDetail, setAuditDetail] = useState(null);
  const [auditDetailLoading, setAuditDetailLoading] = useState(false);
  const [auditDetailTab, setAuditDetailTab] = useState('fixes');  // fixes修改明细 | problems问题清单
  const [auditFixExpanded, setAuditFixExpanded] = useState({});   // chapterNo → bool 展开的修改卡片
  
  // 移动端检测
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobileDevice(mobile);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 🔥 移动端手风琴处理函数
  const handleSectionToggle = useCallback((sectionKey) => {
    setExpandedSections(prev => {
      if (isMobileDevice) {
        // 如果当前 section 已经是展开状态，则折叠它
        if (prev[sectionKey]) {
          return { ...prev, [sectionKey]: false };
        }
        // 否则展开当前 section，并折叠其他所有 sections（手风琴效果）
        return {
          phase: false,
          plotThreads: false,
          characterProfiles: false,
          endingPlan: false,
          relationshipTimeline: false,
          foreshadowsTracking: false,
          chapterOutline: false,
          [sectionKey]: true
        };
      }

      // 桌面端允许自由展开/折叠每个模块
      return {
        ...prev,
        [sectionKey]: !prev[sectionKey]
      };
    });
  }, [isMobileDevice]);

  const handleChapterToggle = useCallback((chapterIndex) => {
    if (!isMobileDevice) return;
    setExpandedChapters(prev => ({
      ...prev,
      [chapterIndex]: !prev[chapterIndex]
    }));
  }, [isMobileDevice]);

  // 🔥 补全章节大纲相关状态
  const [repairingOutlines, setRepairingOutlines] = useState(false);
  
  // 在 NovelEditor 组件内添加
  const formatChapterTitle = useCallback((title) => {
    if (!title) return '';
    // 去除前后所有*、_、"、'、"、'、【】、[]、()、空格、全角空格等
    return title
      .replace(/^[\s*_"'"【】\[\]()（）]+|[\s*_"'"【】\[\]()（）]+$/g, '')
      .trim();
  }, []);

  // 获取前一章内容，用于生成章节时提供上下文
  const getPreviousChapterContent = useCallback(() => {
    if (!selectedChapter || selectedChapter <= 1 || !chapters) {
      return '';
    }
    
    // 查找前一章
    const previousChapter = chapters.find(ch => ch.index === selectedChapter - 1);
    if (!previousChapter || !previousChapter.content) {
      return '';
    }
    
    return previousChapter.content;
  }, [selectedChapter, chapters]);

  // 拖拽事件处理
  const handleBatchFloatMouseDown = (e) => {
    setDragging(true);
    const rect = batchFloatRef.current.getBoundingClientRect();
    setDragOffset({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    e.preventDefault();
  };
  useEffect(() => {
    if (!dragging) return;
    const handleMouseMove = (e) => {
      setBatchFloatPos(pos => ({
        right: window.innerWidth - (e.clientX - dragOffset.x + batchFloatRef.current.offsetWidth),
        bottom: window.innerHeight - (e.clientY - dragOffset.y + batchFloatRef.current.offsetHeight)
      }));
    };
    const handleMouseUp = () => setDragging(false);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, dragOffset]);

  // 将连续的章节用范围表示，如1-3表示1,2,3章
  const formatChapterRanges = useCallback((chapters) => {
    if (!chapters || chapters.length === 0) return '';
    
    const ranges = [];
    let rangeStart = chapters[0];
    let rangeEnd = chapters[0];
    
    for (let i = 1; i < chapters.length; i++) {
      if (chapters[i] === rangeEnd + 1) {
        // 连续章节
        rangeEnd = chapters[i];
      } else {
        // 不连续，保存当前范围
        ranges.push(rangeStart === rangeEnd ? `${rangeStart}` : `${rangeStart}-${rangeEnd}`);
        rangeStart = rangeEnd = chapters[i];
      }
    }
    
    // 添加最后一个范围
    ranges.push(rangeStart === rangeEnd ? `${rangeStart}` : `${rangeStart}-${rangeEnd}`);
    
    return ranges.join('、');
  }, []);

  // 添加调试日志函数 - 只输出到控制台
  const addDebugLog = useCallback((message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    console.log(`[${type.toUpperCase()}][${timestamp}] ${message}`);
  }, []);

  // 显示消息通知 - 必须放在最前面，因为很多函数都依赖它
  const showMessage = useCallback((message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
    
    // 同时添加到调试日志
    addDebugLog(message, severity);
  }, [addDebugLog]);
  

  // 大纲查找/替换状态与处理函数（依赖 showMessage，放在其后）
  const [findReplace, setFindReplace] = useState({
    findText: '',
    replaceText: '',
    caseSensitive: false,
    lastIndex: 0
  });
  const scrollSelectionIntoView = useCallback((el) => {
    if (!el) return;
    try {
      const totalLength = el.value ? el.value.length : 0;
      const caret = typeof el.selectionStart === 'number' ? el.selectionStart : 0;
      const ratio = totalLength > 0 ? caret / totalLength : 0;
      const targetTop = Math.max(0, (el.scrollHeight * ratio) - (el.clientHeight / 2));
      el.scrollTop = targetTop;
    } catch (_) {}
  }, []);
  const escapeRegExp = useCallback((s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), []);
  const handleFindNext = useCallback(() => {
    const { findText, caseSensitive } = findReplace;
    const source = outlineEdit.editedContent || '';
    if (!findText || !source) return;
    const haystack = caseSensitive ? source : source.toLowerCase();
    const needle = caseSensitive ? findText : findText.toLowerCase();
    let start = findReplace.lastIndex;
    const textarea = outlineEditRef.current;
    if (textarea && textarea.selectionEnd != null) {
      start = textarea.selectionEnd;
    }
    let idx = haystack.indexOf(needle, Math.max(0, start));
    if (idx === -1 && start > 0) {
      idx = haystack.indexOf(needle, 0);
    }
    if (idx !== -1 && outlineEditRef.current) {
      outlineEditRef.current.focus();
      outlineEditRef.current.setSelectionRange(idx, idx + findText.length);
      scrollSelectionIntoView(outlineEditRef.current);
      setFindReplace(prev => ({ ...prev, lastIndex: idx + findText.length }));
    }
  }, [findReplace, outlineEdit.editedContent, scrollSelectionIntoView]);
  const handleReplaceOne = useCallback(() => {
    const { findText, replaceText, caseSensitive } = findReplace;
    const source = outlineEdit.editedContent || '';
    if (!findText || !source) return;
    const textarea = outlineEditRef.current;
    let selectionStart = textarea ? textarea.selectionStart : 0;
    let selectionEnd = textarea ? textarea.selectionEnd : 0;
    const currentSelected = source.slice(selectionStart, selectionEnd);
    const isMatch = (!!findText) && (
      caseSensitive ? currentSelected === findText : currentSelected.toLowerCase() === findText.toLowerCase()
    );
    if (isMatch) {
      const nextContent = source.slice(0, selectionStart) + replaceText + source.slice(selectionEnd);
      setOutlineEdit(prev => ({
        ...prev,
        editedContent: nextContent,
        hasUnsavedChanges: true
      }));
      const newCaret = selectionStart + replaceText.length;
      setTimeout(() => {
        if (outlineEditRef.current) {
          outlineEditRef.current.focus();
          outlineEditRef.current.setSelectionRange(newCaret, newCaret);
          scrollSelectionIntoView(outlineEditRef.current);
        }
        setFindReplace(prev => ({ ...prev, lastIndex: newCaret }));
        handleFindNext();
      }, 0);
    } else {
      handleFindNext();
    }
  }, [findReplace, outlineEdit.editedContent, handleFindNext, scrollSelectionIntoView]);
  const handleReplaceAll = useCallback(() => {
    const { findText, replaceText, caseSensitive } = findReplace;
    const source = outlineEdit.editedContent || '';
    if (!findText || !source) return;
    const flags = caseSensitive ? 'g' : 'gi';
    const pattern = new RegExp(escapeRegExp(findText), flags);
    const nextContent = source.replace(pattern, replaceText);
    if (nextContent !== source) {
      setOutlineEdit(prev => ({
        ...prev,
        editedContent: nextContent,
        hasUnsavedChanges: true
      }));
      setFindReplace(prev => ({ ...prev, lastIndex: 0 }));
      showMessage('已完成全部替换', 'success');
    } else {
      showMessage('未找到需要替换的内容', 'info');
    }
  }, [findReplace, escapeRegExp, outlineEdit.editedContent, showMessage]);
  
  // 处理保存设置 - 移到最前面，确保它在所有依赖它的函数之前定义
  const handleSaveSettings = useCallback(async (updatedSettings) => {
    setIsSaving(true);
    try {
      if (!novel) throw new Error('小说对象未定义');
      
      // 使用normalizeSettings函数确保所有字段都有正确的类型和值
      // 先合并现有设置和更新的设置
      const mergedSettings = normalizeSettings({
        ...novel.settings,
        ...novelSettings,
        ...updatedSettings
      });

      let novelId = novel._id || novel.id;
      if (!novelId) {
        const pathParts = window.location.pathname.split('/');
        const urlNovelId = pathParts[pathParts.length - 1];
        if (urlNovelId && urlNovelId !== 'novel-editor' && urlNovelId !== 'new') {
          novelId = urlNovelId;
        } else {
          const lastCreatedId = localStorage.getItem('lastCreatedNovelId');
          const lastLoadedId = localStorage.getItem('lastLoadedNovelId');
          novelId = lastCreatedId || lastLoadedId;
        }
        if (!novelId) throw new Error('小说ID未定义');
      }

      // Create a clean novel object for saving - only update settings, not title
      const updatedNovel = {
        settings: mergedSettings,
        updatedAt: new Date().toISOString()
      };

      // Update local state first
      setNovelSettings(mergedSettings);
      
      // Save to server
      const response = await api.put(`/novels/${novelId}`, updatedNovel);
      const serverNovel = response.data?.data || response.data;
      if (serverNovel && serverNovel.settings) {
        setNovel(serverNovel);
      } else {
        setNovel({ ...serverNovel, settings: mergedSettings });
      }
      showMessage('设置已保存', 'success');
      return { success: true };
    } catch (error) {
      console.error('保存设置失败:', error);
      showMessage(`保存设置失败: ${error.message}`, 'error');
      return { success: false, error: error.message };
    } finally {
      setIsSaving(false);
    }
  }, [novel, novelSettings, setNovel, setNovelSettings, showMessage]);

  // 获取最新的用户余额 - 必须放在最前面以避免初始化前访问错误
  const fetchAndUpdateUserBalance = useCallback(async () => {
    try {
      // 从服务器获取最新的用户信息
      const token = localStorage.getItem('authToken');
      if (!token) return;
      
      // 保存当前余额以便比较
      const currentBalance = userBalance;
      
      // 使用api.get方法直接获取用户资料
      const response = await api.get('/auth/me');
      const userData = response.data;
      
      // 只有当用户数据存在且余额有变化时才更新
      if (userData && userData.user) {
        // 保存到localStorage
        localStorage.setItem('user', JSON.stringify(userData.user));
      
        // 只有当余额变化时才更新状态
        if (userData.user.balance !== currentBalance) {
          setUserBalance(userData.user.balance);
        }
      } else {
        // 直接从localStorage获取最新值，同样只在变化时更新
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
          const userDataFromStorage = JSON.parse(storedUser);
          if (userDataFromStorage.balance !== currentBalance) {
            setUserBalance(userDataFromStorage.balance);
            console.log('从本地存储获取用户余额:', userDataFromStorage.balance);
          }
        }
      }
    } catch (error) {
      console.error('获取用户余额失败:', error);
    }
  }, [userBalance]);  // 添加userBalance作为依赖项，以便在比较时使用最新值
  
  // 更新用户余额 - 这个函数必须提前定义，在组件中优先位置定义
  const updateUserBalance = useCallback((consumedAmount) => {
    return updateUserBalanceImpl({ consumedAmount, setUserBalance });
  }, []);

  // 将章节保存到本地存储的函数
  const saveChaptersToLocalStorage = useCallback((novelData, chaptersData) => {
    return saveChaptersToLocalStorageImpl(novelData, chaptersData);
  }, []);

  // 保存小说数据到服务器的函数（必须放在所有依赖它的 useCallback 之前）
  const saveNovelToServer = useCallback(async (novelData, showNotification = true, forceChapters = null) => {
    try {
      if (!novelData) {
        throw new Error('无法保存：小说数据为空');
      }

      // 规范化小说数据，确保ID一致性
      const normalizedNovel = normalizeId(novelData);
      let novelId = normalizedNovel.id;
      
      // 如果没有ID，尝试从其他来源获取
      if (!novelId) {
        // 从URL获取
        const pathParts = window.location.pathname.split('/');
        const urlNovelId = pathParts[pathParts.length - 1];
        
        if (urlNovelId && urlNovelId !== 'novel-editor' && urlNovelId !== 'new') {
          novelId = urlNovelId;
          console.log(`从URL获取小说ID: ${novelId}`);
          } else {
          // 从本地存储获取
          const lastCreatedId = localStorage.getItem('lastCreatedNovelId');
          const lastLoadedId = localStorage.getItem('lastLoadedNovelId');
          novelId = lastCreatedId || lastLoadedId;
          console.log(`从本地存储获取小说ID: ${novelId}`);
        }
        
        // 更新规范化的小说对象
        if (novelId) {
          normalizedNovel.id = novelId;
        }
      }

      // 检查是否是简化的小说对象（只包含标题）
      const isSimplifiedNovel = normalizedNovel.hasOwnProperty('title') && 
                               Object.keys(normalizedNovel).length <= 3 && // id, title 和可能的 _id
                               !normalizedNovel.hasOwnProperty('chapters');
      
      if (isSimplifiedNovel) {
        console.log('检测到简化的小说对象（只有标题）:', normalizedNovel);
        
        // 对于简化的小说对象，直接发送PUT请求只更新标题
        if (novelId && novelId !== 'new') {
          console.log(`直接更新小说标题: "${normalizedNovel.title}"`);
          const response = await api.put(`/novels/${novelId}`, { title: normalizedNovel.title });
          return { success: true, novel: response.data };
        } else {
          throw new Error('无法更新标题：无效的小说ID');
        }
      }
      
      // 合并所有设置
      const mergedSettings = {
        ...defaultNovelSettings,
        ...novelSettings,
        ...(normalizedNovel.settings || {})
      };
      
      // 使用传入的章节数据或当前章节状态
      const chaptersToSave = forceChapters || chapters;
      
      // 确保我们保存所有章节，包括本地创建但可能尚未保存到服务器的章节
      console.log(`保存前检查：本地章节数量: ${chaptersToSave.length}`);
      
      // 仅保存小说元数据，避免在PUT中携带庞大的章节内容，防止触发413
      const dataToSave = {
        ...normalizedNovel,
        settings: mergedSettings,
        outline: outline || '', // 保存大纲内容
        updatedAt: new Date().toISOString()
      };
      
      // 🔥 关键修复：明确删除 chapters 字段，避免发送大量章节数据导致413错误
      const removedChapters = dataToSave.chapters ? dataToSave.chapters.length : 0;
      delete dataToSave.chapters;
      
      // 🔥 删除故事结构数据（storyStructure）- 故事结构已在生成时自动保存到后端
      // 包含：relationshipTracking, emotionalArcs, chapterOutlineMap 等，500章时非常大
      if (dataToSave.storyStructure) {
        console.log(`✅ [修复413] 删除故事结构数据（已在后端保存）`);
        delete dataToSave.storyStructure;
      }
      
      // 🔥 如果大纲内容过大（超过1MB），也不发送，避免413错误
      if (dataToSave.outline && dataToSave.outline.length > 1000000) {
        console.warn(`大纲内容过大(${dataToSave.outline.length}字符)，跳过发送`);
        delete dataToSave.outline;
      }
      
      // 计算请求体大小（估算）
      const requestBodySize = JSON.stringify(dataToSave).length;
      console.log(`✅ [修复413] 已删除 ${removedChapters} 个章节 + 故事结构，请求体大小约: ${(requestBodySize / 1024).toFixed(2)} KB`);
      console.log('已从保存请求中移除章节内容和故事结构，按后端要求仅保存基础元数据');

      console.log('准备保存小说:', {
        id: dataToSave.id,
        title: dataToSave.title,
        chaptersCount: dataToSave.chapters?.length,
        hasOutline: Boolean(dataToSave.outline),
        settingsComplete: Boolean(dataToSave.settings)
      });
      
      let response;
      let savedNovel;
      
      if (novelId && novelId !== 'new') {
        // 更新现有小说
        console.log(`更新小说ID: ${novelId}`);
        response = await api.put(`/novels/${novelId}`, dataToSave);
        savedNovel = response.data;
          } else {
        // 创建新小说
        console.log('创建新小说');
        response = await api.post('/novels', dataToSave);
        savedNovel = response.data;
      }

      // 检查响应是否成功
      savedNovel = normalizeId(savedNovel);

      // 提取正确的数据对象（处理可能的不同响应结构）
      if (savedNovel.success && savedNovel.data) {
        savedNovel = savedNovel.data;
      } else if (savedNovel.success && savedNovel.novel) {
        // 🔥 后端返回 { success: true, novel: {...} } 格式
        savedNovel = savedNovel.novel;
        console.log('从 savedNovel.novel 提取数据:', savedNovel);
      }
      
      // 保存ID到localStorage
      if (savedNovel.id) {
        localStorage.setItem('lastCreatedNovelId', savedNovel.id);
        localStorage.setItem('lastLoadedNovelId', savedNovel.id);
        
        // 优化：清除缓存，确保下次加载时获取最新数据
        try {
          const cacheKey = `novel_cache_${savedNovel.id}`;
          sessionStorage.removeItem(cacheKey);
          console.log(`已清除小说缓存: ${cacheKey}`);
        } catch (e) {
          console.warn('清除缓存失败:', e);
        }
        
        // 确保本地小说对象包含正确的ID
        if (novel) {
          setNovel(normalizeId({
            ...novel,
            id: savedNovel.id
          }));
        }
        
        // 确保所有章节都有正确的ID
        if (savedNovel.chapters && Array.isArray(savedNovel.chapters) && savedNovel.chapters.length > 0) {
          console.log(`服务器返回了 ${savedNovel.chapters.length} 个章节，更新本地章节ID`);
          
          // 创建服务器章节的映射，按索引
          const serverChaptersMap = {};
          savedNovel.chapters.forEach(ch => {
            if (ch.index) {
              serverChaptersMap[ch.index] = ch;
            }
          });
          
          // 更新本地章节，确保ID与服务器一致
          const updatedChapters = chapters.map(localCh => {
            const serverCh = serverChaptersMap[localCh.index];
            if (serverCh && serverCh.id) {
              console.log(`更新章节 ${localCh.index} 的ID: ${serverCh.id}`);
              return { ...localCh, id: serverCh.id };
            }
            return localCh;
          });
          
          // 更新本地状态
          setChapters(updatedChapters);
          
          // 保存到本地存储
          saveChaptersToLocalStorage({...novel, id: savedNovel.id}, updatedChapters);
        }
      }
      
      if (showNotification) {
        showMessage('小说保存成功', 'success');
      }
      
      return { success: true, novel: savedNovel };
    } catch (error) {
      console.error('保存小说时出错:', error);
      
      // 用户友好的错误消息
      const errorMessage = error.userMessage || error.message || '未知错误';
      
      if (showNotification) {
        showMessage(`保存失败: ${errorMessage}`, 'error');
      }
      
      return { success: false, error: errorMessage };
    }
  }, [novel, novelSettings, outline, chapters, defaultNovelSettings, showMessage, navigate, setDialog, userBalance]);

  // 保存当前章节结构的函数 (不保存内容)
  const saveCurrentChapter = useCallback(async () => {
    if (!novel || !selectedChapter || generatingChapterId === selectedChapter) {
      return { success: false };
    }
    
    try {
      // 查找当前章节
      const currentChapter = chapters.find(ch => ch.index === selectedChapter);
      
      if (!currentChapter) {
        console.error(`找不到索引为${selectedChapter}的章节`);
        return { success: false, error: `找不到索引为${selectedChapter}的章节` };
      }
      
      // 只更新章节标题等基本信息，不包含内容
      const updatedChapter = {
        ...currentChapter,
        title: currentChapter.title,
        index: currentChapter.index,
        id: currentChapter.id,
        updatedAt: new Date().toISOString()
      };
      
      console.log(`保存章节 #${selectedChapter} 结构信息（不包含内容）...`);
      
      // 更新本地章节数组，但不包含内容
      const updatedChapters = chapters.map(ch =>
        ch.index === selectedChapter ? updatedChapter : ch
      );
      
      // 创建只包含必要信息的小说对象
      const novelBasicInfo = {
        ...novel,
        title: title || novel.title,
        settings: novelSettings,
        outline: outline || novel.outline,
        // 保留章节结构，但不包括内容
        chapters: updatedChapters.map(ch => ({
          id: ch.id,
          index: ch.index,
          title: ch.title
        }))
      };
      
      const result = await saveNovelToServer(novelBasicInfo, false);
      
      if (result.success) {
        console.log(`章节 #${selectedChapter} 结构保存成功`);
        return { success: true };
          } else {
        // 处理认证错误
        if (result.authError) {
          showMessage('认证已过期，请重新登录', 'error');
          setTimeout(() => navigate('/login'), 1500);
        } 
        else {
          showMessage(`保存章节结构失败: ${result.error}`, 'error');
        }
        
        return result;
      }
    } catch (error) {
      console.error('保存章节结构时出错:', error);
      showMessage('保存章节结构失败: ' + error.message, 'error');
      return { success: false, error: error.message };
    }
  }, [novel, selectedChapter, chapters, title, novelSettings, outline, saveNovelToServer, showMessage, navigate]);

  // 保存当前章节结构的函数 (不包含内容)
  const saveChapterToServer = useCallback(async (chapterData) => {
    try {
      // 🔥 修复：如果传入的章节数据包含内容，保留内容字段，避免更新标题时丢失内容
      // 只有在明确不需要保存内容时才移除（例如批量操作时）
      const dataToSave = { 
        ...chapterData
      };
      
      // 如果明确标记为不保存内容，则移除内容字段
      if (chapterData.skipContent === true) {
        dataToSave.content = undefined;
        delete dataToSave.skipContent;
      }
      
      const token = localStorage.getItem('authToken');
      if (!token) {
        showMessage('无法保存章节：未找到认证令牌，请重新登录', 'error');
        return { success: false, authError: true, error: '未找到认证令牌，请重新登录' };
      }
      
      const normalizedNovel = normalizeId(novel);
      let novelId = normalizedNovel?.id;
      if (!novelId) {
        const pathParts = window.location.pathname.split('/');
        const urlNovelId = pathParts[pathParts.length - 1];
        if (urlNovelId && urlNovelId !== 'novel-editor' && urlNovelId !== 'new') {
          novelId = urlNovelId;
      } else {
          const lastCreatedId = localStorage.getItem('lastCreatedNovelId');
          const lastLoadedId = localStorage.getItem('lastLoadedNovelId');
          novelId = lastCreatedId || lastLoadedId;
        }
      }
      
      if (!novelId) {
        showMessage('无法保存章节：无法确定小说ID', 'error');
        return { success: false, error: '无法确定小说ID' };
      }
      
      const chapterIndex = dataToSave.index;
      if (chapterIndex === undefined) {
        return { success: false, error: '章节索引未定义' };
      }
      
      console.log(`保存章节结构 (不包含内容): 小说ID=${novelId}, 章节索引=${chapterIndex}`);
      
      // 规范化章节数据
      const normalizedChapterData = normalizeId({
        ...dataToSave,
        novelId: novelId
      });
      
      // 使用api实例而不是直接用axios
      const response = await api.put(
        `/novels/${novelId}/chapters/${chapterIndex}`,
        normalizedChapterData
      );
      
      if (response.status === 200 || response.status === 201) {
        // 重新拉取小说数据，确保本地和服务器同步
        const updatedNovel = response.data && response.data.data ? response.data.data : response.data;
        setNovel(updatedNovel);
        return { success: true, data: response.data };
      } else {
        return { success: false, error: `服务器返回错误: ${response.status} ${response.statusText}` };
      }
    } catch (error) {
      showMessage(`保存章节失败: ${error.message}`, 'error');
      return { success: false, error: error.message };
    }
  }, [novel, showMessage]);

  // 🔥 保存章节标题
  const saveChapterTitle = useCallback(async (chapterIndex, newTitle) => {
    try {
      if (!novel) {
        showMessage('无法保存：缺少必要信息', 'error');
        return { success: false };
      }

      // 从 chapters 数组中查找章节对象
      const chapterObj = chapters.find(ch => ch.index === chapterIndex);
      if (!chapterObj) {
        showMessage('无法找到章节信息', 'error');
        return { success: false };
      }

      // 🔥 修复：确保传递完整的章节对象，包括内容，避免更新标题时丢失内容
      const result = await saveChapterToServer({
        ...chapterObj,
        title: newTitle,
        index: chapterIndex,
        // 保留所有原有字段，包括content
        content: chapterObj.content || ''
      });

      if (result.success) {
        // 更新本地状态
        setChapters(prev => prev.map(ch => 
          ch.index === chapterIndex 
            ? { ...ch, title: newTitle, updatedAt: new Date().toISOString() }
            : ch
        ));
        
        // 更新selectedChapterObj（如果当前选中的是这个章节）
        if (selectedChapter === chapterIndex) {
          setSelectedChapterObj(prev => prev ? { ...prev, title: newTitle } : null);
        }
        
        showMessage('章节标题已保存', 'success');
        return { success: true };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      console.error('保存章节标题失败:', error);
      showMessage(`保存失败: ${error.message}`, 'error');
      return { success: false, error: error.message };
    }
  }, [novel, chapters, selectedChapter, saveChapterToServer, showMessage]);

  // 🔥 保存章节内容
  const saveChapterContentToServer = useCallback(async (chapterIndex, newContent) => {
    try {
      if (!novel) {
        showMessage('无法保存：缺少必要信息', 'error');
        return { success: false };
      }

      // 从 chapters 数组中查找章节对象
      const chapterObj = chapters.find(ch => ch.index === chapterIndex);
      if (!chapterObj) {
        showMessage('无法找到章节信息', 'error');
        return { success: false };
      }

      const token = localStorage.getItem('authToken');
      if (!token) {
        showMessage('未找到认证令牌，请重新登录', 'error');
        return { success: false };
      }

      const normalizedNovel = normalizeId(novel);
      let novelId = normalizedNovel?.id;
      if (!novelId) {
        const pathParts = window.location.pathname.split('/');
        const urlNovelId = pathParts[pathParts.length - 1];
        if (urlNovelId && urlNovelId !== 'novel-editor' && urlNovelId !== 'new') {
          novelId = urlNovelId;
        } else {
          const lastCreatedId = localStorage.getItem('lastCreatedNovelId');
          const lastLoadedId = localStorage.getItem('lastLoadedNovelId');
          novelId = lastCreatedId || lastLoadedId;
        }
      }

      if (!novelId) {
        showMessage('无法保存：无法确定小说ID', 'error');
        return { success: false };
      }

      // 更新章节内容
      const response = await api.put(
        `/novels/${novelId}/chapters/${chapterIndex}`,
        {
          ...chapterObj,
          content: newContent,
          index: chapterIndex
        }
      );

      if (response.status === 200 || response.status === 201) {
        const updatedNovel = response.data && response.data.data ? response.data.data : response.data;
        setNovel(updatedNovel);
        
        // 更新本地chapters状态
        setChapters(prev => prev.map(ch => 
          ch.index === chapterIndex 
            ? { ...ch, content: newContent, updatedAt: new Date().toISOString() }
            : ch
        ));
        
        // 更新selectedChapterObj
        setSelectedChapterObj(prev => prev ? { ...prev, content: newContent } : null);
        
        // 更新lastSavedContent
        setLastSavedContent(newContent);
        
        showMessage('章节内容已保存', 'success');
        return { success: true, data: response.data };
      } else {
        return { success: false, error: `服务器返回错误: ${response.status}` };
      }
    } catch (error) {
      console.error('保存章节内容失败:', error);
      showMessage(`保存失败: ${error.message}`, 'error');
      return { success: false, error: error.message };
    }
  }, [novel, chapters, showMessage]);

  // 🔥 章节正文懒加载（getNovel 瘦身后 content=null，按需拉取正文回填 state）
  // 不依赖 chapters state（避免闭包陈旧），调用方按 chapter.hasContent && !chapter.content 决定是否调
  const fetchChapterContent = useCallback(async (chapterIndex, opts = {}) => {
    if (!chapterIndex || !novel) return null;
    const nid = novel.id || novel._id;
    try {
      const resp = await api.get(`/novels/${nid}/chapters/${chapterIndex}/content`);
      const d = resp.data?.data || resp.data;
      if (d) {
        // 回填到 chapters state（避免重复拉取）；导出批量拉取时传 skipStateUpdate 跳过逐章渲染，最后统一回填
        if (!opts.skipStateUpdate) {
          setChapters(prev => prev.map(ch => ch.index === chapterIndex
            ? { ...ch, content: d.content || '', outline: d.outline || ch.outline, hasContent: (d.content || '').trim().length > 0, contentLength: d.contentLength }
            : ch));
        }
        return d;
      }
      return null;
    } catch (e) {
      console.warn('懒加载章节正文失败:', e.message);
      return null;
    }
  }, [novel]);

  // 🔥 章纲正文懒加载（章纲摘要瘦身后 outlineContent 为空，展开某章时按需拉取）
  const loadChapterOutline = useCallback(async (chapterNumber) => {
    if (!novel || !chapterNumber) return;
    const nid = novel.id || novel._id;
    setLoadingOutlineChapter(chapterNumber);
    try {
      const resp = await api.get(`/novels/${nid}/story-structure/chapter-outline/${chapterNumber}`);
      const d = resp.data?.data || resp.data;
      if (d) {
        setStoryStructure(prev => {
          if (!prev || !prev.outlineEnforcement || !Array.isArray(prev.outlineEnforcement.chapterOutlineMap)) return prev;
          const map = prev.outlineEnforcement.chapterOutlineMap.map(ch =>
            (Number(ch.chapterNumber) === Number(chapterNumber))
              ? { ...ch, outlineContent: d.outlineContent, plot: d.plot, keyEvents: d.keyEvents, title: d.title || ch.title }
              : ch
          );
          return { ...prev, outlineEnforcement: { ...prev.outlineEnforcement, chapterOutlineMap: map } };
        });
      }
    } catch (e) { console.warn('懒加载章纲正文失败:', e.message); }
    finally { setLoadingOutlineChapter(null); }
  }, [novel]);
  const handleChapterSelect = useCallback(async (chapterIndex) => {
    try {
      // 检查是否有未保存的更改（标题或内容）
      const hasUnsavedTitle = chapterTitleEdit.isEditing && 
        chapterTitleEdit.editedTitle !== chapterTitleEdit.originalTitle;
      const hasUnsavedContent = chapterContentEdit.isEditing && 
        chapterContentEdit.editedContent !== chapterContentEdit.originalContent;
      const hasUnsavedChanges = hasUnsavedTitle || hasUnsavedContent || 
        (chapterContentEdit.hasUnsavedChanges && !chapterContentEdit.isEditing);

      // 如果有未保存的更改，提醒用户
      if (hasUnsavedChanges) {
        const confirmMessage = hasUnsavedTitle && hasUnsavedContent 
          ? '章节标题和内容有未保存的修改，是否保存后再切换？'
          : hasUnsavedTitle 
          ? '章节标题有未保存的修改，是否保存后再切换？'
          : '章节内容有未保存的修改，是否保存后再切换？';
        
        const shouldSave = window.confirm(confirmMessage);
        
        if (shouldSave) {
          // 保存标题
          if (hasUnsavedTitle) {
            setChapterTitleEdit(prev => ({ ...prev, saving: true }));
            const titleResult = await saveChapterTitle(selectedChapter, chapterTitleEdit.editedTitle);
            if (titleResult.success) {
              setChapterTitleEdit(prev => ({ 
                ...prev, 
                isEditing: false, 
                saving: false,
                originalTitle: chapterTitleEdit.editedTitle
              }));
            } else {
              setChapterTitleEdit(prev => ({ ...prev, saving: false }));
              return; // 保存失败，不切换章节
            }
          }
          
          // 保存内容
          if (hasUnsavedContent || chapterContentEdit.hasUnsavedChanges) {
            const contentToSave = chapterContentEdit.isEditing 
              ? chapterContentEdit.editedContent 
              : currentChapterContent;
            setChapterContentEdit(prev => ({ ...prev, saving: true }));
            const contentResult = await saveChapterContentToServer(selectedChapter, contentToSave);
            if (contentResult.success) {
              setChapterContentEdit(prev => ({ 
                ...prev, 
                isEditing: false, 
                saving: false,
                originalContent: contentToSave,
                hasUnsavedChanges: false
              }));
              setCurrentChapterContent(contentToSave);
              setLastSavedContent(contentToSave);
            } else {
              setChapterContentEdit(prev => ({ ...prev, saving: false }));
              return; // 保存失败，不切换章节
            }
          }
        } else {
          // 用户选择不保存，取消编辑模式
          if (hasUnsavedTitle) {
            setChapterTitleEdit(prev => ({ 
              ...prev, 
              isEditing: false, 
              editedTitle: prev.originalTitle 
            }));
          }
          if (hasUnsavedContent || chapterContentEdit.hasUnsavedChanges) {
            setChapterContentEdit(prev => ({ 
              ...prev, 
              isEditing: false, 
              editedContent: prev.originalContent,
              hasUnsavedChanges: false
            }));
          }
        }
      }
      
      // 设置章节切换标志，禁用自动保存
      setIsChapterSwitching(true);
      
      // 如果当前章节未保存（非编辑模式下的自动保存检查）
      if (!isChapterSwitching && currentChapterContent !== lastSavedContent && !chapterContentEdit.isEditing) {
        const currentChapter = chapters.find(ch => ch.index === selectedChapter);
        if (currentChapter) {
          const updatedChapters = chapters.map(ch =>
            ch.index === selectedChapter
              ? { ...ch, content: currentChapterContent, updatedAt: new Date().toISOString() }
              : ch
          );
          
          // 保存到本地存储
          saveChaptersToLocalStorage(novel, updatedChapters);
          
          // 更新服务器
          const updatedNovel = {
            ...novel,
            chapters: updatedChapters,
            updatedAt: new Date().toISOString()
          };
          await saveNovelToServer(updatedNovel, false);
          setLastSavedContent(currentChapterContent);
        }
    }
    
      // 更新选中的章节
    setSelectedChapter(chapterIndex);

      // 🔥 展开目标章节所在的分组（25章/组，与章节列表 CHAPTERS_PER_GROUP 对齐）
      const targetGroupIndex = Math.floor((chapterIndex - 1) / 25);
      setExpandedChapterGroup(targetGroupIndex);

      // 展开目标章节所在的分组，折叠其他分组
      const groupStart = Math.floor((chapterIndex - 1) / 50) * 50 + 1;
      const groupEnd = Math.min(groupStart + 49, chapters.length);
      const targetGroupKey = `${groupStart}-${groupEnd}`;
      const newCollapsed = {};
      for (let i = 0; i < chapters.length; i += 50) {
        const gs = i + 1;
        const ge = Math.min(i + 50, chapters.length);
        const gk = `${gs}-${ge}`;
        newCollapsed[gk] = gk !== targetGroupKey;
      }
      setCollapsedGroups(newCollapsed);
    
      // 获取新章节的内容
      const selectedChapterData = chapters.find(ch => ch.index === chapterIndex);
      // 更新selectedChapterObj状态
      setSelectedChapterObj(selectedChapterData || null);
      
      // 重置编辑状态
      setChapterTitleEdit({
        isEditing: false,
        editedTitle: '',
        originalTitle: '',
        saving: false
      });
      setChapterContentEdit({
        isEditing: false,
        editedContent: '',
        originalContent: '',
        saving: false,
        hasUnsavedChanges: false
      });
      
      if (selectedChapterData) {
        // 🔥 瘦身后懒加载：content 为空但 hasContent 为真时，从后端拉取正文
        let chapterContent = selectedChapterData.content || '';
        let chapterOutline = selectedChapterData.outline || '';
        if (!chapterContent && selectedChapterData.hasContent) {
          setIsLoadingChapterContent(true);
          try {
            const d = await fetchChapterContent(chapterIndex);
            if (d) {
              chapterContent = d.content || '';
              chapterOutline = d.outline || chapterOutline;
            }
          } finally { setIsLoadingChapterContent(false); }
        }
        setCurrentChapterContent(chapterContent);
        setCurrentChapterOutline(chapterOutline);
        setLastSavedContent(chapterContent);
      } else {
        setCurrentChapterContent('');
        setCurrentChapterOutline('');
        setLastSavedContent('');
      }
    } catch (error) {
      console.error('切换章节时发生错误:', error);
      showMessage('切换章节失败: ' + error.message, 'error');
    } finally {
      // 章节切换完成后，重置标志
      setIsChapterSwitching(false);
    }
  }, [
    chapters,
    selectedChapter,
    currentChapterContent,
    lastSavedContent,
    novel,
    saveChaptersToLocalStorage,
    saveNovelToServer,
    showMessage,
    isChapterSwitching,
    chapterTitleEdit,
    chapterContentEdit,
    saveChapterTitle,
    saveChapterContentToServer,
    fetchChapterContent
  ]);

  // 导航到仪表板函数 - 只保存基础设置和大纲内容
  const handleReturnToDashboard = useCallback(() => {
    // 🔥【返回秒回优化】立即跳转，保存改为后台异步（fire-and-forget），不阻塞用户
    //    章节正文在编辑过程中已通过 debouncedSaveChapterContent 实时保存，
    //    这里保存的只是基础设置/大纲，后台静默执行，失败只记日志不弹窗
    const isAssist = novel?.creationType === 'assist';
    const redirectPath = location.state?.from || (isAssist ? '/tools' : '/auto-novel');

    // 1. 立即跳转，让用户感觉秒回
    navigate(redirectPath);

    // 2. 后台静默保存（不 await，不阻塞，不弹窗）
    (async () => {
      try {
        if (novelSettings) {
          await handleSaveSettings(novelSettings);
        }
        const novelBasicInfo = {
          ...novel,
          title: title || novel.title,
          settings: novelSettings,
          outline: outline || novel.outline,
          chapters: novel.chapters ? novel.chapters.map(ch => ({
            id: ch.id,
            index: ch.index,
            title: ch.title
          })) : []
        };
        await saveNovelToServer(novelBasicInfo, true);
        console.log('✅ 返回时后台保存完成');
      } catch (error) {
        // 保存失败只记日志，不打扰用户（章节正文已实时保存，丢失风险低）
        console.warn('⚠️ 返回时后台保存失败（不影响已保存的章节内容）:', error.message);
      }
    })();
  }, [novel, novelSettings, title, outline, saveNovelToServer, handleSaveSettings, navigate, location.state]);

  // 🔥 保存章节大纲（细纲）
  const saveChapterOutline = useCallback(async (chapterNumber, newContent) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        showMessage('未找到认证令牌，请重新登录', 'error');
        return { success: false };
      }

      const response = await fetch(`${API_BASE_URL}/novels/${novel._id}/story-structure/chapter-outline`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          chapterNumber,
          outlineContent: newContent
        })
      });

      if (response.ok) {
        const result = await response.json();
        // 🔥【修复】章纲UI渲染源是 storyStructure state（不是 novel state），
        //    旧代码只更新 novel 导致保存成功后界面仍显示旧内容。
        //    这里同步更新 storyStructure.outlineEnforcement.chapterOutlineMap：
        //    - 内嵌模式：直接改对应章的 outlineContent/plot
        //    - 外置瘦身模式（__blueprintsSlimmed，摘要无 outlineContent）：
        //      补 hasOutline 标记并写入 outlineContent，展开时无需再懒加载旧值
        const syncMap = (oe) => {
          if (!oe || !Array.isArray(oe.chapterOutlineMap)) return;
          const target = oe.chapterOutlineMap.find(ch => Number(ch.chapterNumber) === Number(chapterNumber));
          if (target) {
            target.outlineContent = newContent;
            target.plot = newContent;
            target.hasOutline = true;
          }
        };
        setStoryStructure(prev => {
          if (!prev?.outlineEnforcement) return prev;
          const oe = { ...prev.outlineEnforcement, chapterOutlineMap: [...(prev.outlineEnforcement.chapterOutlineMap || [])] };
          syncMap(oe);
          return { ...prev, outlineEnforcement: oe };
        });
        // 同步 novel state（历史行为保留，避免依赖 novel.storyStructure 的地方读到旧值）
        setNovel(prev => {
          if (!prev?.storyStructure?.outlineEnforcement) return prev;
          const ss = JSON.parse(JSON.stringify(prev.storyStructure));
          syncMap(ss.outlineEnforcement);
          return { ...prev, storyStructure: ss };
        });
        showMessage('章节细纲已保存', 'success');
        return { success: true, data: result };
      } else {
        const error = await response.json();
        showMessage(`保存失败: ${error.message || '未知错误'}`, 'error');
        return { success: false, error: error.message };
      }
    } catch (error) {
      console.error('保存章节细纲失败:', error);
      showMessage(`保存失败: ${error.message}`, 'error');
      return { success: false, error: error.message };
    }
  }, [novel, showMessage]);

  // 🔥 保存关键事件
  const saveKeyEvents = useCallback(async (chapterNumber, newEvents) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        showMessage('未找到认证令牌，请重新登录', 'error');
        return { success: false };
      }

      const response = await fetch(`${API_BASE_URL}/novels/${novel._id}/story-structure/key-events`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          chapterNumber,
          keyEvents: newEvents
        })
      });

      if (response.ok) {
        const result = await response.json();
        // 更新本地novel数据
        setNovel(prev => {
          const updated = { ...prev };
          if (updated.storyStructure?.outlineEnforcement?.chapterOutlineMap) {
            const chapterIndex = updated.storyStructure.outlineEnforcement.chapterOutlineMap.findIndex(
              ch => ch.chapterNumber === chapterNumber
            );
            if (chapterIndex !== -1) {
              updated.storyStructure.outlineEnforcement.chapterOutlineMap[chapterIndex].keyEvents = newEvents;
            }
          }
          return updated;
        });
        showMessage('关键事件已保存', 'success');
        return { success: true, data: result };
      } else {
        const error = await response.json();
        showMessage(`保存失败: ${error.message || '未知错误'}`, 'error');
        return { success: false, error: error.message };
      }
    } catch (error) {
      console.error('保存关键事件失败:', error);
      showMessage(`保存失败: ${error.message}`, 'error');
      return { success: false, error: error.message };
    }
  }, [novel, showMessage]);

  // 🔥 保存结局设计
  const saveEndingPlan = useCallback(async (endingPlan) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        showMessage('未找到认证令牌，请重新登录', 'error');
        return { success: false };
      }

      const response = await fetch(`${API_BASE_URL}/novels/${novel._id}/story-structure/ending-plan`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ endingPlan })
      });

      if (response.ok) {
        const result = await response.json();
        // 更新本地novel数据
        setNovel(prev => ({
          ...prev,
          storyStructure: {
            ...prev.storyStructure,
            endingPlan: endingPlan
          }
        }));
        showMessage('结局设计已保存', 'success');
        return { success: true, data: result };
      } else {
        const error = await response.json();
        showMessage(`保存失败: ${error.message || '未知错误'}`, 'error');
        return { success: false, error: error.message };
      }
    } catch (error) {
      console.error('保存结局设计失败:', error);
      showMessage(`保存失败: ${error.message}`, 'error');
      return { success: false, error: error.message };
    }
  }, [novel, showMessage]);

  // 保存设置按钮点击处理函数 - 依赖 handleSaveSettings
  const handleSave = useCallback(() => {
    setIsSaving(true);
    
    handleSaveSettings(novelSettings)
      .then(result => {
        setIsSaving(false);
        if (result.success) {
          showMessage("设置已保存", "success");
        } else {
          showMessage(`设置保存失败: ${result.error}`, "error");
        }
      })
      .catch(error => {
        setIsSaving(false);
        showMessage(`设置保存出错: ${error.message || '未知错误'}`, "error");
      });
  }, [novelSettings, handleSaveSettings, showMessage, setIsSaving]);

  // 保存并退出 - 只保存基础设置和大纲内容
  const handleSaveAndExit = useCallback(async () => {
    try {
      setIsSaving(true);
      
      // 1. 只保存设置和大纲，不保存章节内容
      console.log("保存基础设置和大纲内容，不包含章节内容");
      
      // 2. 保存设置
      if (novelSettings) {
        await handleSaveSettings(novelSettings);
      }
      
      // 3. 保存小说基础信息和大纲
      // 创建只包含必要信息的小说对象
      const novelBasicInfo = {
        ...novel,
        title: title || novel.title,
        settings: novelSettings,
        outline: outline || novel.outline,
        // 保留章节结构，但不包括内容
        chapters: novel.chapters ? novel.chapters.map(ch => ({
          id: ch.id,
          index: ch.index,
          title: ch.title
        })) : []
      };
      
      const result = await saveNovelToServer(novelBasicInfo, true);
      
      // 根据创作类型决定跳转：优先使用来源页面
      const redirectPath = location.state?.from || (novel?.creationType === 'assist' ? '/tools' : '/dashboard');
      
      if (result && result.success) {
        showMessage('保存成功，正在返回列表...', 'success');
        setTimeout(() => navigate(redirectPath), 800);
      } else {
        throw new Error(result?.error || '保存失败');
      }
    } catch (error) {
      console.error('保存失败:', error);
      showMessage(`保存失败: ${error.message || '未知错误'}`, 'error');
      if (window.confirm('保存失败，是否仍要返回列表？未保存的更改将丢失。')) {
        // 优先使用来源页面，否则根据creationType判断
        navigate(location.state?.from || (novel?.creationType === 'assist' ? '/tools' : '/auto-novel'));
      }
    } finally {
      setIsSaving(false);
    }
  }, [novel, novelSettings, title, outline, saveNovelToServer, handleSaveSettings, navigate, showMessage]);

    // 章节状态更新 - 最好提前定义
    const updateChapterStatusImpl = (params) => {
      const { chapterIndex, statusUpdate, setChapterGenerationStatus } = params;
      setChapterGenerationStatus(prev => {
        // 检查prev是否为数组对象形式
        if (Array.isArray(prev) && prev.length > 0 && prev[0].chapterIndex !== undefined) {
          // 使用对象数组形式 - 查找章节索引
          const chapterStatusIndex = prev.findIndex(s => s.chapterIndex === chapterIndex);
          
          // 创建新的状态数组
          const newStatus = [...prev];
          
          // 如果找不到当前章节状态，创建一个新的
          if (chapterStatusIndex === -1) {
            console.log(`章节${chapterIndex}状态不存在，创建新状态对象`);
            newStatus.push({
              chapterIndex,
              status: statusUpdate.status || '等待中',
              progress: statusUpdate.progress || 0,
              ...statusUpdate
            });
          } else {
            // 更新现有章节状态
            newStatus[chapterStatusIndex] = { 
              ...newStatus[chapterStatusIndex], 
              ...statusUpdate 
            };
          }
          
          console.log(`章节${chapterIndex}状态更新(对象数组形式):`, newStatus.find(s => s.chapterIndex === chapterIndex));
          return newStatus;
        } else {
          // 使用传统数组索引形式
        const newStatus = [...prev];
        if (!newStatus[chapterIndex]) {
            newStatus[chapterIndex] = { status: '等待中', progress: 0 };
        }
        newStatus[chapterIndex] = { ...newStatus[chapterIndex], ...statusUpdate };
          console.log(`章节${chapterIndex}状态更新(索引数组形式):`, newStatus[chapterIndex]);
        return newStatus;
        }
      });
    };

    // 章节内容保存函数（已禁用，章节内容现在由后端直接保存到数据库）
    const autoSaveChapter = useCallback(async (chapterIndex, content) => {
      console.log(`章节内容保存已禁用：章节内容由后端管理，前端不再保存章节内容`);
      console.log(`章节 ${chapterIndex} 内容长度: ${content ? content.length : 0}`);
        
      // 返回成功，但实际上不执行任何保存操作
      return { success: true, message: '章节内容由后端管理，前端不再保存章节内容' };
    }, []);
    
    // 自动保存已禁用，此函数仅用于日志记录
    const debouncedSaveChapterContent = useCallback(
      debounce((content) => {
        if (selectedChapter && content) {
          console.log(`自动保存已禁用: 章节${selectedChapter}, 内容长度=${content ? content.length : 0}，内容变更不会自动保存`);
          // 不再调用autoSaveChapter
        }
      }, 2000), // 2秒防抖延迟
      [selectedChapter]
    );

    // 移除自动保存功能的启用逻辑
    useEffect(() => {
      if (novel) {
        setNoAutoSave(true); // 禁用自动保存，改为手动保存模式
      }
    }, [novel]);

    // 检查认证状态
    const checkAuth = useCallback(() => {
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.log('未找到认证令牌，重定向到登录页');
        navigate('/login');
        return false;
      }
      
      // 认证成功时，刷新用户余额
      fetchAndUpdateUserBalance();
      
      return token;
    }, [navigate, fetchAndUpdateUserBalance]);

    // 加载小说数据的函数 - 优化版本：添加缓存和渐进式加载
    const loadNovel = useCallback(async (id, useCache = true) => {
      // 优化：先显示加载状态，但立即设置基本信息（如果有缓存）
      setLoadError(null);
      
      // 优化：检查缓存
      if (useCache && id && id !== 'new') {
        const cacheKey = `novel_cache_${id}`;
        const cachedData = sessionStorage.getItem(cacheKey);
        if (cachedData) {
          try {
            const cachedNovel = JSON.parse(cachedData);
            const cacheTime = cachedNovel._cacheTime || 0;
            const now = Date.now();
            // 缓存有效期5分钟
            // 🔥【瘦身兼容】旧缓存（瘦身前的全量数据）缺少 hasContent/contentLength 字段，
            //    会导致列表显示"0字"。检测 __chaptersSlimmed 标记，无标记则跳过缓存强制重载
            if (now - cacheTime < 5 * 60 * 1000 && cachedNovel.__chaptersSlimmed === true) {
              console.log(`使用缓存数据加载小说，ID: ${id}`);
              // 先设置基本信息，让UI立即响应
              setNovel(cachedNovel);
              if (cachedNovel.title && cachedNovel.title.trim().length > 0) {
                setTitle(cachedNovel.title);
              } else {
                setTitle('无标题小说');
              }
              setChapters(cachedNovel.chapters || []);
              setCollapsedGroups(null);
              setSelectedChapter(1);
              const normalizedSettings = normalizeSettings(cachedNovel.settings || {});
              if (normalizedSettings) {
                setNovelSettings(normalizedSettings);
              }
              if (cachedNovel.outline) {
                setOutline(cachedNovel.outline);
                setOutlineText(cachedNovel.outline);
                setOutlineGenerated(true);
              }
              if (cachedNovel.storyStructure) {
                setStoryStructure(cachedNovel.storyStructure);
              }
              // 不设置loading=false，继续在后台加载最新数据
            }
          } catch (e) {
            console.warn('读取缓存失败:', e);
          }
        }
      }
      
      setLoading(true);
      
      // 防止在组件卸载后设置状态
      let isMounted = true;
      
      try {
        let loadedNovel;
        if (id && id !== 'new') {
          const response = await api.get(`/novels/${id}`);
          const rawNovel = response.data && response.data.data ? response.data.data : response.data;
          loadedNovel = normalizeId(rawNovel);
          // 规范化后的小说数据就绪
          
          // 优化：保存到缓存
          if (useCache && loadedNovel) {
            try {
              const cacheKey = `novel_cache_${id}`;
              const cacheData = {
                ...loadedNovel,
                _cacheTime: Date.now()
              };
              sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
            } catch (e) {
              console.warn('保存缓存失败:', e);
            }
          }
      } else {
          // 创建新小说 ... 保持原有逻辑
          // ... existing code ...
        }
        if (loadedNovel) {
          // 优化：分批设置状态，先设置关键信息
          setNovel(loadedNovel);
          if (loadedNovel.title && loadedNovel.title.trim().length > 0) {
            setTitle(loadedNovel.title);
          } else {
            setTitle('无标题小说');
          }
          
          // 使用requestIdleCallback或setTimeout分批处理非关键数据
          setTimeout(async () => {
          // 🔥【防闪烁】先同步懒加载第1章正文，再把 content 合并进 chapters 一次性设置
          // 避免编辑框出现"空 → 真实内容"的二次刷新闪烁
          let firstChapterContent = '';
          let firstChapterOutline = '';
          const firstCh = (loadedNovel.chapters || []).find(ch => ch.index === 1);
          if (firstCh && !firstCh.content && firstCh.hasContent) {
            setIsLoadingChapterContent(true);
            try {
              const d = await fetchChapterContent(1);
              if (d) {
                firstChapterContent = d.content || '';
                firstChapterOutline = d.outline || '';
              }
            } catch (e) { console.warn('懒加载第一章正文失败:', e.message); }
            finally { setIsLoadingChapterContent(false); }
          }
          // 把第1章 content 合并进 chapters 数组（避免后续二次 setChapters）
          const chaptersWithFirstContent = firstChapterContent
            ? (loadedNovel.chapters || []).map(ch => ch.index === 1
                ? { ...ch, content: firstChapterContent, outline: firstChapterOutline || ch.outline }
                : ch)
            : (loadedNovel.chapters || []);

          setChapters(chaptersWithFirstContent);
          setCollapsedGroups(null);
          setSelectedChapter(1);
          // 🔥 同步设置编辑框内容（与 chapters 一起批处理，避免闪烁）
          setCurrentChapterContent(firstChapterContent);
          setLastSavedContent(firstChapterContent);
          if (firstChapterOutline) setCurrentChapterOutline(firstChapterOutline);

          const normalizedSettings = normalizeSettings(loadedNovel.settings || {});
          if (normalizedSettings) {
            setNovelSettings(normalizedSettings);
          }

          // 加载大纲内容
          if (loadedNovel.outline) {
            console.log(`加载大纲内容，长度: ${loadedNovel.outline.length}`);
            setOutline(loadedNovel.outline);
            setOutlineText(loadedNovel.outline);
            setOutlineGenerated(true);
      } else {
            console.log('小说没有大纲内容');
            setOutline('');
            setOutlineText('');
            setOutlineGenerated(false);
          }

          // 🎨 加载故事结构（如果存在）
          if (loadedNovel.storyStructure && loadedNovel.storyStructure.isGenerated) {
            console.log('✅ 检测到已生成的故事结构，正在加载...');
            console.log('   - 阶段数:', loadedNovel.storyStructure.phases?.length || 0);
            console.log('   - 剧情线数:', loadedNovel.storyStructure.plotThreads?.length || 0);
            console.log('   - 角色画像数:', loadedNovel.storyStructure.characterProfiles?.length || 0);
            setStoryStructure(loadedNovel.storyStructure);
              setStructureExpanded(true);
          } else {
            console.log('小说暂无故事结构');
            setStoryStructure(null);
          }

          // 🔥 加载番茄平台发布信息（故事结构阶段1完成后自动生成）
          setFanqieInfo(loadedNovel.fanqieInfo || null);
          }, 0);
          
          localStorage.setItem('lastCreatedNovelId', loadedNovel.id);
          localStorage.setItem('lastLoadedNovelId', loadedNovel.id);
          
          // 优化：用户余额在后台异步加载，不阻塞UI
          setTimeout(() => {
          fetchAndUpdateUserBalance();
          }, 100);
          
          // 🔥 根据项目实际进度自动跳转到适当的标签页
          // 优先级：章节内容 > 故事结构（生成中/已完成） > 大纲 > 基础设置
          const chaptersData = loadedNovel.chapters || [];
          if (chaptersData.length > 0 && chaptersData.some(ch => ch.hasContent || (ch.content && ch.content.trim().length > 0))) {
            console.log('✅ 检测到章节内容，自动跳转到章节内容页');
            setActiveTab(3);
          } else if (loadedNovel.storyStructure) {
            const structureStatus = loadedNovel.storyStructure.generationStatus;
            const isStructureGenerating = structureStatus === 'generating' || structureStatus === 'retrying';
            const isStructureCompleted = structureStatus === 'completed' || loadedNovel.storyStructure.isGenerated;
            
            if (isStructureGenerating || isStructureCompleted) {
              console.log(`✅ 检测到故事结构${isStructureCompleted ? '已生成' : '生成中'}，自动跳转到故事结构页`);
              setActiveTab(2);
            } else if (loadedNovel.outline && loadedNovel.outline.trim().length > 0) {
              console.log('✅ 检测到大纲内容，自动跳转到大纲页');
              setActiveTab(1);
            } else {
              console.log('📝 无进度记录，跳转到基础设置页');
              setActiveTab(0);
            }
          } else if (loadedNovel.outline && loadedNovel.outline.trim().length > 0) {
            console.log('✅ 检测到大纲内容，自动跳转到大纲页');
            setActiveTab(1);
          } else {
            console.log('📝 打开已有项目，跳转到基础设置页');
            setActiveTab(0);
          }
        } else {
          throw new Error('加载小说失败: 未返回小说数据');
        }
        setLoading(false);
        return loadedNovel;
      } catch (error) {
        console.error('加载小说时出错:', error);
        setLoading(false);
        setLoadError(error.message);
        showMessage(`加载小说失败: ${error.message}`, 'error');
        throw error;
      }
    }, [setLoading, setLoadError, setNovel, setTitle, setChapters, setSelectedChapter, setNovelSettings, setOutline, setOutlineText, setOutlineGenerated, fetchAndUpdateUserBalance, showMessage, setActiveTab]);
    
    // 加载章节内容的函数 - 先定义不依赖其他函数的
    const loadChapterContent = useCallback(async (novelId, chapterIndex) => {
      try {
        if (!novelId || !chapterIndex) {
          console.error('无法加载章节内容：小说ID或章节索引未定义');
          return { success: false, error: '小说ID或章节索引未定义' };
        }
        
        // 验证令牌
        const token = localStorage.getItem('authToken');
        if (!token) {
          console.error('无法加载章节内容：未找到认证令牌');
          return { success: false, authError: true, error: '未找到认证令牌，请重新登录' };
        }
        
        // 设置自定义头信息
        const customHeaders = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };
        
        console.log(`加载章节内容: 小说ID ${novelId}, 章节 ${chapterIndex}`);
        
        // 发送请求获取章节内容
        const response = await api.get(
          `/novels/${novelId}/chapters/${chapterIndex}`,
          { headers: customHeaders }
        );
        
        if (response.status === 200) {
          console.log('章节内容加载成功:', response.data);
          return { success: true, data: response.data };
                  } else {
          console.error('章节内容加载失败:', response.status, response.statusText);
          return { success: false, error: `服务器返回错误: ${response.status} ${response.statusText}` };
      }
    } catch (error) {
        console.error('加载章节内容时出错:', error);
        
        // 判断是否是404错误（章节不存在）
        if (error.response && error.response.status === 404) {
          return { success: false, error: '小说或章节不存在' };
        }
        
        // 判断是否是认证错误
        if (error.response && error.response.status === 401) {
          return { success: false, authError: true, error: '认证已过期，请重新登录' };
        }
        
        return { 
          success: false, 
          error: error.response ? `${error.response.status} ${error.response.statusText}` : error.message 
        };
      }
    }, []);
    
    // 保存小说标题的函数
    const saveTitle = useCallback(async (newTitle) => {
      console.log(`保存小说标题: "${newTitle}"`);
      
      if (!novel) {
        console.error('无法保存标题：小说对象未定义');
        showMessage('无法保存标题：小说未加载', 'error');
        return false;
      }
      
      if (!newTitle || newTitle.trim() === '') {
        console.error('无法保存空标题');
        showMessage('标题不能为空', 'error');
        return false;
      }
      
      // 如果标题没有变化，不需要保存
      if (novel.title === newTitle) {
        console.log('标题未发生变化，无需保存');
        return true;
      }
      
      // 规范化小说对象，获取统一的ID
      const normalizedNovel = normalizeId(novel);
      let novelId = normalizedNovel.id;
      
      // 如果小说对象中没有ID，尝试其他来源
      if (!novelId) {
        // 从URL获取
        const pathParts = window.location.pathname.split('/');
        const urlNovelId = pathParts[pathParts.length - 1];
        
        if (urlNovelId && urlNovelId !== 'novel-editor' && urlNovelId !== 'new') {
          novelId = urlNovelId;
        } else {
          // 从本地存储获取
          const lastCreatedId = localStorage.getItem('lastCreatedNovelId');
          const lastLoadedId = localStorage.getItem('lastLoadedNovelId');
          novelId = lastCreatedId || lastLoadedId;
        }
      }
      
      // 如果仍然没有ID，无法保存
      if (!novelId) {
        console.error('无法保存标题：无法确定小说ID');
        showMessage('无法保存标题：无法确定小说ID', 'error');
        return false;
      }
      
      try {
        // 更新本地状态
        setTitle(newTitle);
        
        // 准备更新对象并规范化
        const updatedNovel = normalizeId({
          ...normalizedNovel,
          title: newTitle,
          id: novelId
        });
        
        // 显示保存指示器
        showMessage('正在保存标题...', 'info');
        
        // 只更新标题，不更新其他信息，避免数据冲突
        const simplifiedNovel = {
          id: novelId,
          title: newTitle
        };
        
        // 保存到服务器
        const result = await saveNovelToServer(simplifiedNovel, false); // 不显示通知
        
        if (result && result.success) {
          console.log('标题保存成功');
          // 更新小说对象
          setNovel(prev => ({...prev, title: newTitle}));
          showMessage('标题已保存', 'success');
          
          // 验证保存结果
          try {
            // 从服务器重新获取小说信息以确认标题已更新
            const verifyResponse = await api.get(`/novels/${novelId}`);
            const verifiedNovel = verifyResponse.data?.data || verifyResponse.data;
            if (verifiedNovel && verifiedNovel.title === newTitle) {
              console.log('服务器确认标题已更新');
            } else {
              console.warn(`标题验证不匹配：期望="${newTitle}", 服务器="${verifiedNovel?.title || '未知'}"`);
              // 如果不匹配，再尝试一次保存
              await api.put(`/novels/${novelId}`, { title: newTitle });
            }
          } catch (verifyError) {
            console.error('验证标题更新时出错:', verifyError);
          }
          
        return true;
      } else {
          console.error('保存标题失败:', result?.error || '未知错误');
          showMessage(`保存标题失败: ${result?.error || '未知错误'}`, 'error');
        return false;
      }
    } catch (error) {
        console.error('保存标题时出错:', error);
        showMessage(`保存标题时出错: ${error.message || '未知错误'}`, 'error');
      return false;
    }
    }, [novel, saveNovelToServer, showMessage]);
    
    // 创建新小说的函数 - 使用外部实现，避免循环依赖
    const createNewNovel = useCallback(async () => {
      return await createNewNovelImpl({
        navigate,
        setLoading,
        setLoadError,
        setIsCreatingNovel,
        setNovel,
        setTitle,
        setNovelSettings,
        setChapters,
        setSelectedChapter,
        setActiveTab,
        showMessage
      });
    }, [navigate, setLoading, setLoadError, setIsCreatingNovel, setNovel, setTitle, setNovelSettings, setChapters, setSelectedChapter, setActiveTab, showMessage]);
    
    // 初始化逻辑
    useEffect(() => {
      // 编辑器组件初始化（静默）
      
      // 获取令牌
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.log('未找到认证令牌，重定向到登录页');
        navigate('/login');
        return;
      }
      
      // 令牌验证（静默）
      
      // 立即获取最新的用户余额
      fetchAndUpdateUserBalance().catch(error => {
        console.error('初始化时获取用户余额失败:', error);
      });
      
      // 从localStorage获取最近创建的ID信息
      const lastCreatedId = localStorage.getItem('lastCreatedNovelId');
      
      // 判断是否是从Dashboard新创建的小说
      const isFromDashboard = novelId && novelId === lastCreatedId && 
                              sessionStorage.getItem('createdFromDashboard') === 'true';
      
      if (isFromDashboard) {
        console.log('检测到从Dashboard创建的新小说，直接创建本地实例');
        
        // 直接创建本地实例
        createNewNovelImpl({
          navigate,
          setLoading,
          setLoadError,
          setIsCreatingNovel,
          setNovel,
          setTitle,
          setNovelSettings,
          setChapters,
          setSelectedChapter,
          setActiveTab,
          showMessage,
          existingId: novelId // 传入URL中的ID
        });
      }
      // 如果没有小说ID或为'new'，直接创建新小说
      else if (!novelId || novelId === 'new') {
        console.log('未提供有效小说ID，直接调用createNewNovelImpl');
        // 直接使用实现函数而不是通过createNewNovel
        createNewNovelImpl({
          navigate,
          setLoading,
          setLoadError,
          setIsCreatingNovel,
          setNovel,
          setTitle,
          setNovelSettings,
          setChapters,
          setSelectedChapter,
          setActiveTab,
          showMessage
        });
                  } else {
        // 否则加载现有小说，但如果加载失败，尝试使用本地数据
        loadNovel(novelId).catch(error => {
          console.error('加载小说失败，尝试创建本地副本:', error);
          
          // 如果加载失败并且ID与最近创建的匹配，创建本地副本
          if (novelId === lastCreatedId) {
            console.log('尝试为最近创建的小说创建本地副本');
            createNewNovelImpl({
              navigate,
              setLoading,
              setLoadError,
              setIsCreatingNovel,
              setNovel,
              setTitle,
              setNovelSettings,
              setChapters,
              setSelectedChapter,
              setActiveTab,
              showMessage,
              existingId: novelId
            });
          }
        });
      }
    }, [novelId, navigate, loadNovel, showMessage, 
        setLoading, setLoadError, setIsCreatingNovel, 
        setNovel, setTitle, setNovelSettings, setChapters, 
        setSelectedChapter, fetchAndUpdateUserBalance]);
    
    // 创建提示词函数 - 移到组件级别，避免重复定义
    const createPrompt = useCallback((section, existingContent = '') => {
      const { genre, style, background, characters, plotPoints, worldBuilding, theme } = novelSettings;

      // 声明所有需要的变量
      let detailedInstructions = '';
      let chapterRange = '';
      let totalChapters = 0;
      let startChapter = 1;
      let keyChapters = [];
      let narrativeStages = 0;
      let finalChapters = [];
      const contextInfo = { recentChapters: '', midRangeChapters: '', allPreviousSummary: '', usedInteractions: [], usedScenarios: [], usedPlotDevices: [], usedDialoguePatterns: [] };
      
      // 一次性生成完整小说大纲的提示词（按章节数动态规划篇幅与详略）
      if (section === 'FULL_OUTLINE') {
        const { length, wordCount } = novelSettings;
        const totalChapters = resolveTotalChapters(novelSettings);
        const plan = getOutlineGenerationPlan(totalChapters);

        const keyChapters = [];
        if (totalChapters <= 30) {
          keyChapters.push(1, Math.floor(totalChapters * 0.33), Math.floor(totalChapters * 0.66), totalChapters);
        } else if (totalChapters <= 50) {
          keyChapters.push(1, Math.floor(totalChapters * 0.2), Math.floor(totalChapters * 0.4),
            Math.floor(totalChapters * 0.6), Math.floor(totalChapters * 0.8), totalChapters);
        } else if (totalChapters <= 100) {
          keyChapters.push(1, Math.floor(totalChapters * 0.1), Math.floor(totalChapters * 0.25),
            Math.floor(totalChapters * 0.45), Math.floor(totalChapters * 0.65), Math.floor(totalChapters * 0.85), totalChapters);
        } else if (totalChapters <= 250) {
          keyChapters.push(1, Math.floor(totalChapters * 0.1), Math.floor(totalChapters * 0.2),
            Math.floor(totalChapters * 0.35), Math.floor(totalChapters * 0.5), Math.floor(totalChapters * 0.65),
            Math.floor(totalChapters * 0.8), Math.floor(totalChapters * 0.95), totalChapters);
        } else {
          [0.05, 0.1, 0.2, 0.35, 0.5, 0.65, 0.8, 0.92, 0.98, 1].forEach(p => keyChapters.push(Math.floor(totalChapters * p)));
        }

        const finalChapters = [];
        const lastCount = totalChapters <= 30
          ? Math.min(3, totalChapters)
          : Math.min(7, Math.floor(totalChapters * 0.02) + 5);
        for (let i = totalChapters - lastCount + 1; i <= totalChapters; i++) {
          if (i > 0) finalChapters.push(i);
        }

        const uniqueKeyChapters = keyChapters.filter((v, i, a) => a.indexOf(v) === i).sort((a, b) => a - b);
        const charCount = plan.charCount;
        const worldElements = Array.from({ length: plan.worldElementCount }, (_, i) =>
          "元素" + (i + 1) + "：名称、来历、在故事中的作用、对情节的推动"
          ).join("\n- ");

        const plotSection = plan.plotPhaseMode === 'compact'
          ? `1.【开篇设计】（第1-${plan.openingChapters}章）
- 开篇切入点、主角亮相、核心钩子
- 前${plan.openingChapters}章的看点承诺

2.【主线发展】（第${plan.openingChapters + 1}章至第${Math.max(plan.openingChapters + 1, Math.floor(totalChapters * 0.75))}章）
- 主线目标与核心冲突推进
- 关键转折点（对应关键章节：${uniqueKeyChapters.join('、')}）
- 主要爽点分布

3.【高潮与结局】（第${Math.floor(totalChapters * 0.75) + 1}章至第${totalChapters}章）
- 最终对决与主角抉择
- 伏笔回收与情感落点
- 各角色归宿`
          : `1.【开篇设计】（第1-${plan.openingChapters}章，重点中的重点）
- 开篇切入点：故事从哪里开始（不是平淡日常，是有冲突的起点）
- 主角首次亮相的方式：
- 前${plan.openingChapters}章的核心冲突/钩子是什么：
- 前${plan.openingChapters}章需要建立读者期待的"看点承诺"：
- 前${plan.openingChapters}章不可犯的错误（新手常踩的坑）：

2.【前期剧情】（第1章至第${Math.floor(totalChapters * 0.3)}章）
- 前期核心目标：主角要完成什么、建立什么
- 前期主要对手/障碍：
- 前期关键转折点：
- 前期情感线发展（如果有）：
- 前期"爽点"分布：列出${totalChapters <= 50 ? '2-3' : '3-5'}个主要爽点及其对应的情节
- 前期节奏建议：

3.【中期剧情】（第${Math.floor(totalChapters * 0.3) + 1}章至第${Math.floor(totalChapters * 0.7)}章）
- 中期核心目标：主角面临什么新挑战、困境如何升级
- 中期主要对手/障碍升级：
- 中期关键转折点：
- 中期情感线发展（如果有）：
- 中期"虐点"与"爽点"平衡：
- 中期最危险的低谷点（情绪最低落处）：
- 中期高潮点：
- 中期节奏建议：

4.【后期剧情】（第${Math.floor(totalChapters * 0.7) + 1}章至第${Math.floor(totalChapters * 0.95)}章）
- 后期核心目标：如何收束所有线索、推向最终对决
- 后期主要对手/障碍：
- 后期关键转折点：
- 后期高潮铺垫（倒计时感如何营造）：
- 后期情感线结局处理（如果有）：

5.【终章设计】（第${Math.floor(totalChapters * 0.95)}章至第${totalChapters}章）
- 最终对决的具体场景与方式：
- 主角的关键抉择（体现人物成长）：
- 反派的最终命运：
- 每个重要配角的最终归宿（简要）：
- 所有伏笔的最终回收（逐一列举）：
- 结局的情感落点：
- 结局的余韵设计（留白/悬念/彩蛋）`;

        const advancedSections = plan.includeAdvancedSections
          ? `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
【七、人物成长体系】
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

1.【主角成长轨迹】
- 起点状态（第1章时的能力/处境/心境）：
- 成长节点（列出${totalChapters <= 100 ? '3' : '3-5'}个关键成长转折点，对应大致章节）：
- 终态（第${totalChapters}章时的主角）：
- 成长弧线的核心主题（不是变强，而是变强背后的成长）：

2.【配角/反派成长弧线】
- 重要配角A：从第X章到第Y章的变化轨迹
- 重要配角B：从第X章到第Y章的变化轨迹
- 反派的认知转变（如果有）：

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
【八、悬念与节奏设计】
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

1.【悬念设置体系】
- 开篇悬念（留住读者的第一个钩子）：
- 贯穿型悬念（从头埋到尾的核心理谜）：
- 阶段悬念（约每${plan.climaxInterval}章的小高潮悬念）：
- 悬念解密节奏：哪些早揭、哪些晚揭、哪些留给续作

2.【爽点/爆点设计】
- 核心爽点1：具体表现形式、对应章节
- 核心爽点2：具体表现形式、对应章节
${totalChapters > 50 ? '- 核心爽点3：具体表现形式、对应章节\n' : ''}- 情绪节奏：爽→平→虐→爽的波浪式节奏

3.【禁忌/雷区提醒】
- 绝对禁止出现的桥段（本类型读者最讨厌什么）：
- 需要谨慎处理的敏感点：
- 与竞品的差异化底线（不要模仿哪些烂俗套路）：

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
【九、场景与氛围设计】
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

1.【标志性场景】（${Math.min(plan.worldElementCount, totalChapters <= 50 ? 3 : 5)}个，读者一提就能想到的画面）
- 场景1：名称、视觉描写核心、情感功能、对应情节
- 场景2：名称、视觉描写核心、情感功能、对应情节
${totalChapters > 30 ? '- 场景3：名称、视觉描写核心、情感功能、对应情节\n' : ''}
2.【叙事节奏与章节结构】
- 每章理想字数：
- 章末钩子的设置原则：
- 大高潮与小高潮的分布（约每${plan.climaxInterval}章一次大高潮）：
- 日常与高潮的配比（约7:3）：`
          : `
【七至九节说明】本作品仅${totalChapters}章，篇幅较短，人物成长、悬念节奏、场景氛围等要点请融入前述各部分，无需单独成章，避免重复赘述。`;

        const detailStyleHint = totalChapters <= 30
          ? '各模块用概括性描述即可，禁止逐章流水账，禁止过度铺陈世界观细节'
          : totalChapters <= 100
            ? '重点章节适当展开，非重点部分可概括，整体保持框架性'
            : '内容需充分展开，每项要求都需要具体描述，但禁止无节制堆砌';

        return `请以专业小说策划的视角，为一部${plan.tierLabel}（共${totalChapters}章）小说创建${plan.detailLevel}的大纲。本大纲将成为写作的核心基础。

${plan.scaleNote}

【基本信息】
- 平台: ${novelSettings.platform || '通用平台'}
- 视角: ${novelSettings.perspective || '第三人称'}
- 频道: ${novelSettings.channel || '男频'}
- 长度: ${length || '中篇'} (约${wordCount || '10'}万字)
- 预计章节数: ${totalChapters}章（${plan.tierLabel}）
- 文风: ${style || '流畅自然'}
- 类型: ${genre || '未指定'}
- 关键词: ${novelSettings.keywords?.join(', ') || '未指定'}
- 补充要求: ${novelSettings.additionalNotes || '无特殊要求'}

【类型约束 - 最高优先级】
1. 用户选择的类型：${genre || '未指定'}
2. 你必须严格按照"${genre || '未指定'}"类型创作，绝对不允许偏离
3. 严禁添加任何其他类型的元素、设定或情节
4. 每个设定都必须通过"类型一致性测试"：是否符合用户选择的类型？
5. 禁止为了"创新"而混入其他类型内容

【字数要求 - 严格执行】
- 目标字数：约${plan.targetWords}字
- 字数范围：${plan.minWords}-${plan.maxWords}字（尽量接近目标字数，完整性优先于压缩篇幅）
- 详略标准：${plan.detailLevel}，${detailStyleHint}
- ⚠️ 完整性优先：必须写到终章与结局，禁止为控字数而省略后期剧情；可压缩重复描写，不可删减剧情阶段

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
【一原创性声明与市场定位】
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

1.【作品创意来源】
- 阐述本作品在"${genre || '未指定'}"类型中的独特创意来源
- 灵感触发点：具体是什么事件/体验/思考让你想到这个故事
- 与同类作品的本质区别：本文凭什么让读者选择你而非其他作品
- 本作品的差异化竞争力

2.【目标读者画像】
- 主要读者群体：年龄段、阅读偏好、消费习惯
- 读者的核心诉求：读者想从本文获得什么情感体验
- 市场空白点：本作品填补了市场哪些未被满足的需求
- 预期阅读体验：爽感类型、情感节奏、完读率预期

3.【作品商业定位】
- 核心卖点：最吸引读者的1-3个核心亮点
- 对标作品：参考但不模仿的${totalChapters <= 30 ? '1-2' : '3'}部同类作品
- 差异化卖点：本作品独特的不可替代性
- 更新节奏建议：日更/隔日更对情节密度的影响

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
【二、世界观体系设计】${totalChapters <= 30 ? '（精简，控制篇幅）' : '（重点展开部分）'}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

1.【时空背景】
- 时代特征：具体年代、社会风貌、政治经济文化背景
- 地理环境：主要发生地的自然与人文地理
- 社会结构：阶层划分、势力分布、权力格局
- 科技/修炼/能力体系（如有）：等级划分、资源稀缺性、社会影响

2.【世界运行法则】
- 本世界最核心的运行规律
- 普通人与顶层存在的差距如何设定
- 世界观对情节发展的约束与推动
- 世界观中最有"爽感"的设定点

3.【标志性世界元素】（${plan.worldElementCount}个，越具体越好）
- ${worldElements}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
【三、人物塑造系统】（本部分最为关键）
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

【角色命名与性别规范 - 强制要求】
- 每个角色名字后面必须标注性别：(男) 或 (女)
- 名字必须与性别一致：男性角色只能用男性化名字，女性角色只能用女性化名字
- 禁止：男角用"若曦""雨薇"，女角用"浩然""志远"
- 优先使用单字姓+两字名，如"林凡""苏瑶"
- 格式：角色名(性别)，年龄，身份
- 角色数量：${charCount}

1.【主角】
- 姓名(性别)：具体名字
- 年龄：
- 身份：
- 成长背景：具体的成长经历，造就了什么样的性格
- 性格特征：核心性格+外在表现，矛盾点在哪里
- 行为准则：他/她做什么事有什么底线
- 金手指/独特优势：主角凭什么赢（要具体，不能空洞）
- 初期状态：故事开始时主角的处境、困境

2.【重要配角】（每个至少${plan.supportingCharMinWords}字，详述角色弧线）
- 角色名(性别)，年龄，身份
- 与主角关系：
- 性格特点：
- 在故事中的作用（不是工具人）：
- 角色弧线：从故事开始到结局，这个角色的成长/变化/收获是什么
- 与主角的情感/利益纠葛：

3.【反派/对手】（${plan.villainCount}，层次分明）
- 主要反派角色名(性别)，年龄，身份
- 与主角的核心冲突来源：
- 反派的能力与优势：
- 反派的弱点与最终败因：
- 次要对手/势力：
- 各方势力的利益关系图谱：

4.【势力格局】${totalChapters <= 30 ? '（如适用，简要说明）' : ''}
- 各主要势力/门派/组织的名称与定位
- 主角与各势力的关系变化路线
- 势力博弈对主线剧情的影响

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
【四、核心矛盾体系】
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

1.【主要矛盾】
- 矛盾的本质：核心冲突是什么（不是打斗，是利益/情感/命运的冲突）
- 在"${genre || '未指定'}"类型中的典型呈现方式
- 矛盾的不可调和性：为什么双方必须分出胜负
- 矛盾升级路径：从小到大如何一步步推向高潮

2.【次要矛盾】（${totalChapters <= 30 ? '1-2' : '2-3'}条）
- 矛盾A：涉及角色、核心冲突点、与主线的关系
- 矛盾B：涉及角色、核心冲突点、与主线的关系
${totalChapters > 30 ? '- 矛盾C：涉及角色、核心冲突点、与主线的关系\n' : ''}
3.【暗线/伏笔系统】
- 伏笔1：埋设位置（对应情节章节）、揭晓位置、作用
${totalChapters > 15 ? '- 伏笔2：埋设位置（对应情节章节）、揭晓位置、作用\n' : ''}${totalChapters > 50 ? '- 伏笔3：埋设位置（对应情节章节）、揭晓位置、作用\n' : ''}- 伏笔回收保证：所有埋设必须对应回收，不能有悬而未决

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
【五、主题升华设计】
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

1.【核心主题】
- 本作品最想表达的思想/价值观是什么
- 在"${genre || '未指定'}"类型中的独特表达方式
- 如何通过情节让读者自然感受到而非说教

2.【辅线主题】（${totalChapters <= 30 ? '0-1' : '1-2'}个）
- 主题A：通过哪条情节线体现
${totalChapters > 30 ? '- 主题B：通过哪条情节线体现\n' : ''}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
【六、剧情发展系统】（覆盖第1章到第${totalChapters}章）
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

关键章节位置：${uniqueKeyChapters.join('、')}章
终章群：${finalChapters.join('、')}章

${plotSection}
${advancedSections}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
【输出格式要求 - 必须遵守】
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

1. 【小说标题】放在大纲最顶部，用【小说标题】：《{标题}》格式，标题不超过15字
2. 【结构纪律·最高优先级】严格按【剧情发展系统】的阶段划分（开篇/前期/中期/后期/终章）用概括性段落描述故事走向；全篇严禁逐章罗列、严禁输出逐章细纲、章纲、每章计划或"第X章：标题+内容"式的清单——大纲是全书总纲，每章的具体内容属于后续"章纲生成"阶段的任务，此处只允许写到"阶段划分+关键转折+伏笔布设"粒度
3. 关键章节（第${uniqueKeyChapters.join('、')}章）至多各用一小段话（80字内）点明该章承担的关键转折作用，严禁逐场逐幕展开
4. 禁止使用"三步并作两步"、"如临大敌"、"浑身一震"等固定表达
5. 角色描写要具体到姓名、性格、行为模式，不允许"万能配角"
6. 本大纲总字数控制在${plan.minWords}-${plan.maxWords}字之间（目标约${plan.targetWords}字）；若内容接近上限，优先压缩细节描述，绝不通过逐章展开来增加篇幅
7. 必须覆盖第1章到第${totalChapters}章的完整故事线，且必须写完整：含【终章设计】、全部伏笔回收、各角色归宿，禁止在中途停止或省略后期/结局部分（"覆盖"指阶段与主线连续完整，不是逐章各写一段）
8. 若篇幅紧张，可压缩世界观细节，但剧情六段（含终章）与人物弧线不可删减
9. 【结尾格式·强制】全部内容写完后，最后一行必须原样输出下面这行结尾说明（其中字数直接使用给定数字，禁止自行统计或估算字数）：
*本大纲共约${plan.targetWords}字，涵盖《{标题}》第1章至第${totalChapters}章全部叙事阶段，含所有伏笔回收、角色归宿与终章设计。*`;
      }

      // 以下为旧的「三部分分别生成」代码段，已废弃（代码保留但不再被调用）
      /* if (false) {
        【基本信息】
        
        【基本信息】
        - 平台: ${novelSettings.platform || '通用平台'}
        - 视角: ${novelSettings.perspective || '第三人称'}
        - 频道: ${novelSettings.channel || '男频'}
        - 长度: ${novelSettings.length || '中篇'} (约${novelSettings.wordCount || '10'}万字)
        - 预计章节数: ${novelSettings.chapterCount || '20-30'}章
        - 文风: ${style || '流畅自然'}
        - 类型: ${genre || '未指定'}
        - 关键词: ${novelSettings.keywords?.join(', ') || '未指定'}
        - 补充要求: ${novelSettings.additionalNotes || '无特殊要求'}
        `;
        
        // 声明全局变量
          let detailedInstructions = '';
        let chapterRange = '';
        let totalChapters = 0;
        let startChapter = 1;
        let keyChapters = [];
        let narrativeStages = 0;
        let finalChapters = [];
        
        if (existingContent) {
          // 根据不同部分提供详细的指导，而不仅是通用提示
          if (section === '第二部分') {
            // 🔥 修复：使用用户实际设置的章节数，而不是硬编码
            // 优先使用用户设置的chapterCount，如果没有则根据篇幅类型估算
            const userChapterCount = parseInt(novelSettings.chapterCount);
            
            if (userChapterCount && userChapterCount > 0) {
              // 用户明确设置了章节数，使用用户设置
              totalChapters = userChapterCount;
            } else {
              // 用户未设置，根据篇幅类型估算
              if (novelSettings.length === '中篇') {
                totalChapters = 50;
              } else if (novelSettings.length === '长篇') {
                totalChapters = 125;
              } else if (novelSettings.length === '超长篇') {
                const wordCount = novelSettings.wordCount || 100;
                if (wordCount >= 100) {
                  totalChapters = 600;
                } else {
                  totalChapters = 250;
                }
              } else {
                totalChapters = 50;
              }
            }
            
            // 🔥 根据实际章节数动态生成关键章节
            const chapterCount = totalChapters;
            
            // 动态计算关键章节的位置
            if (chapterCount <= 50) {
              // 中短篇：均匀分布5个关键章节
              keyChapters = [
                Math.floor(chapterCount * 0.2),   // 20%位置
                Math.floor(chapterCount * 0.4),   // 40%位置
                Math.floor(chapterCount * 0.6),   // 60%位置
                Math.floor(chapterCount * 0.8),   // 80%位置
                chapterCount                      // 结尾
              ];
              narrativeStages = 3;
            } else if (chapterCount <= 150) {
              // 长篇：分布7个关键章节
              keyChapters = [
                Math.floor(chapterCount * 0.2),
                Math.floor(chapterCount * 0.4),
                Math.floor(chapterCount * 0.6),
                Math.floor(chapterCount * 0.8),
                Math.floor(chapterCount * 0.92),
                Math.floor(chapterCount * 0.96),
                chapterCount
              ];
              narrativeStages = 5;
            } else if (chapterCount <= 300) {
              // 超长篇（中等）：分布7-8个关键章节
              keyChapters = [
                Math.floor(chapterCount * 0.2),
                Math.floor(chapterCount * 0.4),
                Math.floor(chapterCount * 0.6),
                Math.floor(chapterCount * 0.8),
                Math.floor(chapterCount * 0.9),
                Math.floor(chapterCount * 0.96),
                chapterCount
              ];
              narrativeStages = 6;
            } else {
              // 超长篇（极长）：分布8个关键章节
              keyChapters = [
                Math.floor(chapterCount * 0.1),
                Math.floor(chapterCount * 0.2),
                Math.floor(chapterCount * 0.4),
                Math.floor(chapterCount * 0.6),
                Math.floor(chapterCount * 0.8),
                Math.floor(chapterCount * 0.9),
                Math.floor(chapterCount * 0.96),
                chapterCount
              ];
              narrativeStages = 8;
            }
            
            // 生成最后5章的列表
            finalChapters = [];
            for (let i = Math.max(1, chapterCount - 4); i <= chapterCount; i++) {
              finalChapters.push(i);
            }
          
          let chapterRange = `${startChapter}-${totalChapters}`;
            
            // 🔥 动态计算重点章节数量和分布说明
            let keyChapterCount;
            let distribution;
            
            if (chapterCount <= 50) {
              keyChapterCount = Math.max(5, Math.floor(chapterCount * 0.12));
              distribution = `开头(1-${Math.floor(chapterCount*0.2)}章)规划1-2个，发展期规划2-3个，中期规划1-2个，后期规划1个`;
            } else if (chapterCount <= 150) {
              keyChapterCount = Math.max(8, Math.floor(chapterCount * 0.10));
              distribution = `开头(1-${Math.floor(chapterCount*0.1)}章)规划2个，前期(${Math.floor(chapterCount*0.1)+1}-${Math.floor(chapterCount*0.3)}章)规划2-3个，中期(${Math.floor(chapterCount*0.3)+1}-${Math.floor(chapterCount*0.7)}章)规划3-4个，后期(${Math.floor(chapterCount*0.7)+1}-结局)规划2-3个`;
            } else if (chapterCount <= 300) {
              keyChapterCount = Math.max(15, Math.floor(chapterCount * 0.08));
              distribution = `开头(1-${Math.floor(chapterCount*0.08)}章)规划3个，前期(${Math.floor(chapterCount*0.08)+1}-${Math.floor(chapterCount*0.24)}章)规划4-5个，中期(${Math.floor(chapterCount*0.24)+1}-${Math.floor(chapterCount*0.72)}章)规划5-7个，后期(${Math.floor(chapterCount*0.72)+1}-结局)规划3-5个`;
            } else {
              keyChapterCount = Math.max(25, Math.floor(chapterCount * 0.06));
              distribution = `开头(1-${Math.floor(chapterCount*0.06)}章)规划4-5个，前期(${Math.floor(chapterCount*0.06)+1}-${Math.floor(chapterCount*0.24)}章)规划6-8个，中期(${Math.floor(chapterCount*0.24)+1}-${Math.floor(chapterCount*0.76)}章)规划8-12个，后期(${Math.floor(chapterCount*0.76)+1}-结局)规划5-8个`;
            }
            
            detailedInstructions = `
          【第二部分 - 前期剧情设计】
          
          ⚠️ 【字数限制 - 最高优先级】
          - 第二部分总字数：1500-2000字（绝对不超过2500字）
          - 请严格控制每个模块的字数，不要超出规定范围
          
          1. 【剧情发展系统】(600-800字)
          - 主线剧情的逐步展开
          - 2-3条重要支线及其与主线关系
          - 重要情感关系的发展
          
          2. 【人物成长体系】(400-600字)
          - 主角能力与性格的发展轨迹
          - 重要配角的戏份与互动
          - 反派势力的逐步显现
          
          3. 【悬念与伏笔】(300-400字)
          - 核心谜团的设置与解密节奏
          - 重要伏笔的埋设
          
          4. 【场景设计】(200-300字)
          - 标志性场景的设计
          - 冲突场景的独特方式
`;
          } else if (section === '第三部分') {
            // 🔥 修复：使用用户实际设置的章节数，而不是硬编码
            const userChapterCount = parseInt(novelSettings.chapterCount);
            
            if (userChapterCount && userChapterCount > 0) {
              // 用户明确设置了章节数，使用用户设置
              totalChapters = userChapterCount;
            } else {
              // 用户未设置，根据篇幅类型估算
              if (novelSettings.length === '中篇') {
                totalChapters = 100;
              } else if (novelSettings.length === '长篇') {
                totalChapters = 250;
              } else if (novelSettings.length === '超长篇') {
                totalChapters = 600;
              } else {
                totalChapters = 100;
              }
            }
            
            // 🔥 第三部分从中间开始（约50%位置），因为前两部分已经完成
            startChapter = Math.floor(totalChapters * 0.5) + 1;
            
            // 🔥 动态生成关键章节（后半部分）
            const chapterCount = totalChapters;
            
            if (chapterCount <= 50) {
              keyChapters = [
                startChapter,
                Math.floor(chapterCount * 0.6),
                Math.floor(chapterCount * 0.8),
                chapterCount
              ];
              narrativeStages = 2;
            } else if (chapterCount <= 150) {
              keyChapters = [
                startChapter,
                Math.floor(chapterCount * 0.7),
                Math.floor(chapterCount * 0.85),
                Math.floor(chapterCount * 0.95),
                chapterCount
              ];
              narrativeStages = 3;
            } else if (chapterCount <= 300) {
              keyChapters = [
                startChapter,
                Math.floor(chapterCount * 0.6),
                Math.floor(chapterCount * 0.72),
                Math.floor(chapterCount * 0.84),
                Math.floor(chapterCount * 0.92),
                Math.floor(chapterCount * 0.98),
                chapterCount
              ];
              narrativeStages = 5;
            } else {
              keyChapters = [
                startChapter,
                Math.floor(chapterCount * 0.6),
                Math.floor(chapterCount * 0.7),
                Math.floor(chapterCount * 0.8),
                Math.floor(chapterCount * 0.9),
                Math.floor(chapterCount * 0.96),
                Math.floor(chapterCount * 0.99),
                chapterCount
              ];
              narrativeStages = 6;
            }
            
            // 🔥 生成最后5-7章的列表
            finalChapters = [];
            const lastChapterCount = Math.min(7, Math.floor(chapterCount * 0.02) + 5);
            for (let i = Math.max(startChapter, chapterCount - lastChapterCount + 1); i <= chapterCount; i++) {
              finalChapters.push(i);
            }
            
          chapterRange = `${startChapter}-${totalChapters}`;
            
            detailedInstructions = `
          【第三部分 - 终章创作指南】
          
          ⚠️ 【字数限制 - 最高优先级】
          - 第三部分总字数：1200-1600字（绝对不超过2000字）
          - 请严格控制每个模块的字数，不要超出规定范围
          
          1. 【结局设计】(500-800字)
          1.1 结局场景：
          - 结局地点的象征意义
          - 结局方式的独特性
          - 角色成长与蜕变的体现
          
          1.2 关键时刻：
          - 决定性的转折点
          - 角色的最终选择
          
          2. 【人物命运】(400-600字)
          - 主角的最终命运与成就
          - 重要配角的结局安排
          - 反派/对手的最终归宿
          
          【注意】
          - 所有伏笔必须回收
          - 所有人物必须交代
          - 避免套路化结局
`;
          }
          
          return `${basePrompt}
          
          【已生成的内容】
          ${existingContent}
          
          ${detailedInstructions}
          
          【重要提示】
          请基于上述内容继续生成大纲的${section}部分，确保与已有内容保持连贯性和一致性。
          你的输出将直接被添加到最终文档中，因此：
          1. 不要重复已有内容
          2. 不要重新生成第一部分的内容
          3. 只生成${section}的内容
          4. 不要包含"第一部分"、"第二部分"或"第三部分"这样的标题
          5. 不要包含任何额外的解释，直接生成大纲内容
          
          【大纲三部分结构说明】
          - 第一部分：总体构思、世界观和人物设定 - 奠定小说基础
          - 第二部分：故事线索和前半部分章节 - 勾勒剧情发展
          - 第三部分：后半部分章节 - 完成剧情并提供完整收尾
          
          请直接返回${section}的大纲内容。`;
        }
        
        if (section === '第一部分') {
          // 根据篇幅调整人物数量和内容详细程度
          let characterCount = '';
          let detailLevel = '';
          
          // 根据小说篇幅调整第一部分内容
          // 注意：短篇选项已被取消
          if (novelSettings.length === '中篇') {
            characterCount = '3-5个主要角色和2-3个重要配角';
            detailLevel = '适中详细程度，重点突出';
          } else if (novelSettings.length === '长篇') {
            characterCount = '4-6个主要角色和3-5个重要配角';
            detailLevel = '详细且全面的设定';
          } else if (novelSettings.length === '超长篇') {
            characterCount = '5-8个主要角色和多个重要配角';
            detailLevel = '非常详细的设定，包含多条故事线';
          } else {
            // 默认设置
            characterCount = '3-5个主要角色和2-3个重要配角';
            detailLevel = '适中详细程度，重点突出';
          }
          
          return `${basePrompt}
          
          【标题输出要求 - 放在全文最顶部】
          - 标题长度不超过15个字（包含15个字），避免与常见作品重复
          - 标题风格必须严格符合"${novelSettings.genre || '未指定'}"类型与平台调性
          - 标题需具备强记忆点与强点击属性，参考该类型热门网文标题风格（不得抄袭）
          - 禁止出现其他类型元素或误导性设定词
          - 输出格式：
          【小说标题】：《{生成的长标题}》
          
          【类型约束 - 最高优先级】
          1. 用户选择的类型：${novelSettings.genre || '未指定'}
          2. 你必须严格按照"${novelSettings.genre || '未指定'}"类型创作，绝对不允许偏离
          3. 严禁添加任何其他类型的元素、设定或情节
          4. 每个设定都必须通过"类型一致性测试"：是否符合用户选择的类型？
          5. 禁止为了"创新"而混入其他类型内容
          
          【第一部分创作框架】
          
          ⚠️ 【字数限制 - 最高优先级】
          - 第一部分总字数：1800-2200字（绝对不超过2500字）
          - 请严格控制每个模块的字数，不要超出规定范围
          
          1. 【原创性声明与创意来源】(120-150字)
          - 阐述本作品在"${novelSettings.genre || '未指定'}"类型中的独特创意来源
          - 说明与同类作品的本质区别
          
          2. 【作品定位与市场特色】(150-200字)
          - 目标读者群体分析
          - 市场空白点
          - 预期阅读体验
          
          3. 【世界观体系设计】(400-500字)
          - 时空背景与社会制度
          - 世界运行的基本法则
          - 3-5个标志性世界元素及其作用
          
          4. 【人物塑造系统】(600-800字)
          
          🔴 【强制要求 - 角色性别标注与命名一致】
          - 每个角色名字后面必须标注性别：(男) 或 (女)
          - 名字必须与性别一致：男性角色只能使用男性化名字，女性角色只能使用女性化名字，禁止性别反转或中性/模糊名字
          - 检查性别标注与名字气质是否冲突（如男角用“若曦”“雨薇”，女角用“浩然”“志远”都不允许），发现必须立即更正
          - 优先使用单字姓+两字名，避免复姓或过长名字
          - 格式：角色名(性别)，年龄，身份
          
          - 主角：【必须】角色名(性别)，年龄，身份，成长背景，性格特征，行为准则
          - 重要配角(每个150字)：【必须】角色名(性别)，年龄，身份，与主角关系，行为动机
          - 反派/对手：【必须】角色名(性别)，年龄，身份，对抗方式，与主角联系
          
          5. 【核心矛盾设计】(300-400字)
          - 主要矛盾的"${novelSettings.genre || '未指定'}"类型呈现
          - 次要矛盾的层次化设计
          - 矛盾升级路径
          
          6. 【主题升华设计】(150-200字)
          - 核心主题的"${novelSettings.genre || '未指定'}"类型表达
          - 独特的价值观呈现`;
        } else if (section === '第二部分') {
          // 🔥 修复：使用用户实际设置的章节数，而不是硬编码
          totalChapters = parseInt(novelSettings.chapterCount) || 100;
          startChapter = 1;
          keyChapters = [];
          narrativeStages = 0;
          finalChapters = [];
          
          // 🔥 根据实际章节数动态计算关键章节分布
          if (totalChapters <= 50) {
            narrativeStages = 3;
            keyChapters = [
              1,
              Math.floor(totalChapters * 0.3),
              Math.floor(totalChapters * 0.6),
              Math.floor(totalChapters * 0.9),
              totalChapters
            ];
          } else if (totalChapters <= 100) {
            narrativeStages = 5;
            keyChapters = [
              1,
              Math.floor(totalChapters * 0.15),
              Math.floor(totalChapters * 0.35),
              Math.floor(totalChapters * 0.6),
              Math.floor(totalChapters * 0.85),
              totalChapters
            ];
          } else if (totalChapters <= 250) {
            narrativeStages = 7;
            keyChapters = [
              1,
              Math.floor(totalChapters * 0.1),
              Math.floor(totalChapters * 0.25),
              Math.floor(totalChapters * 0.45),
              Math.floor(totalChapters * 0.65),
              Math.floor(totalChapters * 0.85),
              totalChapters
            ];
          } else {
            narrativeStages = 8;
            keyChapters = [
              1,
              Math.floor(totalChapters * 0.08),
              Math.floor(totalChapters * 0.2),
              Math.floor(totalChapters * 0.35),
              Math.floor(totalChapters * 0.5),
              Math.floor(totalChapters * 0.65),
              Math.floor(totalChapters * 0.8),
              Math.floor(totalChapters * 0.95),
              totalChapters
            ];
          }
          
          return `${basePrompt}
          
          【第二部分 - 前期剧情设计】
          
          ⚠️ 【字数限制 - 最高优先级】
          - 第二部分总字数：2000-2500字（绝对不超过3000字）
          - 请严格控制每个模块的字数，不要超出规定范围
          
          【类型约束重申 - 最高优先级】
          - 用户选择的类型：${novelSettings.genre || '未指定'}
          - 你必须严格按照"${novelSettings.genre || '未指定'}"类型创作剧情
          - 严禁在剧情中添加任何其他类型的元素或设定
          - 剧情必须完全符合"${novelSettings.genre || '未指定'}"类型的基本规则
          
          【篇幅信息】
          - 小说篇幅：${novelSettings.length || '中篇'}
          - 总章节数：${totalChapters}章
          - 重点章节数：${novelSettings.length === '短篇' ? '5-8个' : novelSettings.length === '中篇' ? '8-12个' : novelSettings.length === '长篇' ? '15-20个' : novelSettings.length === '超长篇' ? '25-35个' : '8-12个'}
          - 说明：重点章节是为后期生成具体章节内容提供指导的关键转折点
          
          1. 【剧情发展系统】(600-800字)
          - 主线剧情的逐步展开
          - 2-3条重要支线及其与主线关系
          - 重要情感关系的发展
          
          2. 【人物成长体系】(400-600字)
          - 主角能力与性格的发展轨迹
          - 重要配角的戏份与互动
          - 反派势力的逐步显现
          
          3. 【悬念与伏笔】(300-400字)
          - 核心谜团的设置与解密节奏
          - 重要伏笔的埋设
          
          4. 【场景设计】(200-300字)
          - 标志性场景的设计
          - 冲突场景的独特方式`;
        } else if (section === '第三部分') {
          // 🔥 修复：使用用户实际设置的章节数，而不是硬编码
          totalChapters = parseInt(novelSettings.chapterCount) || 100;
          // 第三部分从中间开始（约50%位置）
          startChapter = Math.floor(totalChapters * 0.5) + 1;
          keyChapters = [];
          narrativeStages = 0;
          finalChapters = [];
          
          // 🔥 根据实际章节数动态计算后半部分的关键章节
          const finalChapterCount = Math.min(10, Math.floor(totalChapters * 0.1)); // 最后10%或最多10章
          finalChapters = [];
          for (let i = 0; i < finalChapterCount; i++) {
            finalChapters.push(totalChapters - finalChapterCount + i + 1);
          }
          
          // 动态计算关键章节分布（后半部分）
          if (totalChapters <= 50) {
            narrativeStages = 2;
            keyChapters = [
              startChapter,
              Math.floor(totalChapters * 0.7),
              Math.floor(totalChapters * 0.9),
              totalChapters
            ];
          } else if (totalChapters <= 100) {
            narrativeStages = 3;
            keyChapters = [
              startChapter,
              Math.floor(totalChapters * 0.65),
              Math.floor(totalChapters * 0.8),
              Math.floor(totalChapters * 0.95),
              totalChapters
            ];
          } else if (totalChapters <= 250) {
            narrativeStages = 5;
            keyChapters = [
              startChapter,
              Math.floor(totalChapters * 0.6),
              Math.floor(totalChapters * 0.72),
              Math.floor(totalChapters * 0.84),
              Math.floor(totalChapters * 0.92),
              Math.floor(totalChapters * 0.98),
              totalChapters
            ];
          } else {
            narrativeStages = 6;
            keyChapters = [
              startChapter,
              Math.floor(totalChapters * 0.6),
              Math.floor(totalChapters * 0.7),
              Math.floor(totalChapters * 0.8),
              Math.floor(totalChapters * 0.88),
              Math.floor(totalChapters * 0.95),
              Math.floor(totalChapters * 0.98),
              totalChapters
            ];
          }
          
          return `${basePrompt}
          
          【第三部分 - 终章创作指南】
          
          ⚠️ 【字数限制 - 最高优先级】
          - 第三部分总字数：1200-1600字（绝对不超过2000字）
          - 请严格控制每个模块的字数，不要超出规定范围
          
          【类型约束重申 - 最高优先级】
          - 用户选择的类型：${novelSettings.genre || '未指定'}
          - 你必须严格按照"${novelSettings.genre || '未指定'}"类型创作结局
          - 严禁在结局中添加任何其他类型的元素或设定
          - 结局必须完全符合"${novelSettings.genre || '未指定'}"类型的基本规则
          
          1. 【结局设计】(500-800字)
          1.1 结局场景：
          - 结局地点的象征意义
          - 结局方式的独特性
          - 角色成长与蜕变的体现
          
          1.2 关键时刻：
          - 决定性的转折点
          - 角色的最终选择
          
          2. 【人物命运】(400-600字)
          - 主角的最终命运与成就
          - 重要配角的结局安排
          - 反派/对手的最终归宿
          
          【注意】
          - 所有伏笔必须回收
          - 所有人物必须交代
          - 避免套路化结局
          - 确保结局完全符合"${novelSettings.genre || '未指定'}"类型`;
        }
        
        return basePrompt;
      }
      */
      
      // 构建角色描述
      const characterDescriptions = characters?.map?.(char => 
        `${char.name}：${char.description}`
      )?.join('\n') || '';
      
      // 构建世界观描述
      const worldDescription = worldBuilding ? Object.entries(worldBuilding)
        .map(([key, value]) => `${key}：${value}`)
        .join('\n') : '';
      
      // 构建情节要点
      const plotDescription = plotPoints?.map?.(point => 
        `${point.title}：${point.description}`
      )?.join('\n') || '';
      
      // 构建主题描述
      const themeDescription = theme?.map?.(t => 
        `${t.title}：${t.description}`
      )?.join('\n') || '';
      
      // 根据不同的section构建不同的提示词
      let prompt = '';
      switch(section) {
        case 'outline':
          prompt = `请根据以下信息创作一个详细的小说大纲：

类型：${genre || '未指定'}
写作风格：${style || '流畅自然'}
背景设定：${background || ''}

主要人物：
${characterDescriptions}

世界观设定：
${worldDescription}

主要情节：
${plotDescription}

主题元素：
${themeDescription}

请创作一个包含以下内容的大纲：
1. 开篇：介绍主要人物和背景
2. 发展：3-5个关键情节点
3. 高潮：主要冲突和转折
4. 结局：故事收尾和主题升华

要求：
1. 保持情节的连贯性和合理性
2. 突出人物性格特点
3. 体现世界观设定
4. 符合写作风格要求
5. 突出主题元素`;
          break;
          
        case 'chapter':
          // 获取前文章节作为上下文
          const getPreviousChaptersContext = () => {
            if (!chapters || chapters.length === 0 || !selectedChapter) {
              return { recentChapters: '', midRangeChapters: '', allPreviousSummary: '' };
            }

            // 获取前1-2章的详细摘要
            const recentChapters = chapters
              .filter(ch => ch.index < selectedChapter && ch.index >= selectedChapter - 2 && ch.content)
              .sort((a, b) => b.index - a.index) // 降序排列，最近的在前
              .map(ch => {
                // 提取前500字作为详细摘要
                const contentPreview = ch.content && ch.content.length > 500 
                  ? ch.content.substring(0, 500) + "..." 
                  : ch.content || '';
                return `第${ch.index}章 ${ch.title}：\n${contentPreview}`;
              })
              .join('\n\n');

            // 获取前3-5章的简要摘要
            const midRangeChapters = chapters
              .filter(ch => ch.index < selectedChapter - 2 && ch.index >= selectedChapter - 5 && ch.content)
              .sort((a, b) => b.index - a.index)
              .map(ch => {
                // 提取前100字作为简要摘要
                const contentPreview = ch.content && ch.content.length > 100 
                  ? ch.content.substring(0, 100) + "..." 
                  : ch.content || '';
                return `第${ch.index}章 ${ch.title}：\n${contentPreview}`;
              })
              .join('\n\n');

            // 获取之前所有章节的超简要摘要
            const allPreviousSummary = chapters
              .filter(ch => ch.index < selectedChapter - 5 && ch.content)
              .sort((a, b) => b.index - a.index)
              .map(ch => `第${ch.index}章 ${ch.title}：${ch.content ? ch.content.substring(0, 50) + "..." : ""}`)
              .join('\n');

            return { recentChapters, midRangeChapters, allPreviousSummary };
          };

          prompt = `请根据以下信息创作一个高质量的小说章节：

【基本信息】
类型：${genre || '未指定'}
写作风格：${style || '流畅自然'}
背景设定：${background || ''}
叙事视角：${novelSettings.perspective || '第三人称'}

【章节信息】
当前章节：第${selectedChapter}章
${chapters.length > 0 ? `总章节数：${chapters.length}章` : ''}

【角色信息】
${characterDescriptions}

【世界观设定】
${worldDescription}

【主要情节】
${plotDescription}

【主题元素】
${themeDescription}

【本章大纲】
${existingContent || '未提供本章大纲'}

${contextInfo.recentChapters ? `【近期上下文】(前1-2章)
${contextInfo.recentChapters}` : ''}

${contextInfo.midRangeChapters ? `【中期上下文】(前3-5章)
${contextInfo.midRangeChapters}` : ''}

${contextInfo.allPreviousSummary ? `【全局故事概览】
${contextInfo.allPreviousSummary}` : ''}

【写作指导】
1. 确保本章内容与前文保持严格的连贯性，特别是角色行为、设定和情节发展
2. 避免重复前文已经出现的场景描写手法、对话模式或情节设计
3. 深入角色心理，保持角色行为与已建立的性格一致
4. 必须使用${novelSettings.perspective || '第三人称'}视角，保持${style || '流畅自然'}的文风
5. 字数控制在2000-3000字之间
6. 创作符合当前章节进展的精彩内容，不要重复前文已经发生的情节

【章节标题要求（强制自然化）】
1. 标题风格：自然口语，像给朋友描述本章看点；拒绝诗句/对仗/古风腔
2. 字数：6–18 字为宜，允许自然波动，不要整齐统一
3. 用词：避免模板化高频词与口号式并列句，不要堆砌成语
4. 相关性：紧扣本章核心，不标题党，不过度剧透

请创作出高质量的章节内容，确保故事情节连贯、角色形象一致、内容丰富多彩且符合整体写作风格。`;
        break;
        
      default:
        prompt = `请根据以下信息创作内容：

类型：${genre || '未指定'}
写作风格：${style || '流畅自然'}
背景设定：${background || ''}

主要人物：
${characterDescriptions}

世界观设定：
${worldDescription}

主要情节：
${plotDescription}

主题元素：
${themeDescription}

已有内容：
${existingContent}

请继续创作，要求：
1. 保持情节的连贯性
2. 符合人物性格特点
3. 体现世界观设定
4. 符合写作风格
5. 突出主题元素`;
        break;
    }
    
    return prompt;
      }, [novelSettings, chapters]);
  
  // ============================================================
  // 🔥🔥🔥 全新一次性生成小说大纲函数（DeepSeek-V4-Pro 384K 输出支持）
  // 🔍 搜索关键字：SINGLE_STEP_OUTLINE_GENERATOR
  // 说明：
  // 1. 一次调用生成完整大纲，无需分段，速度更快、质量更连贯
  // 2. DeepSeek-V4-Pro 最大输出 384K tokens；大纲篇幅与 max_tokens 按章节数动态规划（见 getOutlineGenerationPlan）
  // ============================================================
  const generateNovelOutlineFull = useCallback(async () => {
    setShowWordCount(true);

    let shouldAutoScroll = true;
    const outlineElement = outlineRef.current;
    if (outlineElement) {
      const scrollHandler = () => {
        shouldAutoScroll = outlineElement.scrollHeight - outlineElement.scrollTop - outlineElement.clientHeight < 50;
      };
      outlineElement.removeEventListener('scroll', outlineElement._scrollHandler);
      outlineElement._scrollHandler = scrollHandler;
      outlineElement.addEventListener('scroll', scrollHandler);
    }

    const totalChapters = resolveTotalChapters(novelSettings);
    const outlinePlan = getOutlineGenerationPlan(totalChapters);
    const prompt = createPrompt('FULL_OUTLINE', '');

    const outlineMaxTokens = computeOutlineMaxTokens(totalChapters, !!novelSettings.enableThinking);
    console.log(`[大纲生成] ${totalChapters}章 · ${outlinePlan.tierLabel} · 目标${outlinePlan.targetWords}字 · max_tokens=${outlineMaxTokens}${novelSettings.enableThinking ? '（含推理模式加成）' : ''}`);

    // 固定使用 DeepSeek V4 Flash 大模型，max_tokens 随章节数动态调整
    const params = {
      model: 'deepseek-v4-flash',
      messages: [
        {
          role: 'system',
          content: `你是一位获得过茅盾文学奖的资深小说家，擅长构思宏大的叙事框架和引人入胜的情节。我需要你创建一个结构清晰、篇幅适配且必须写完整的小说大纲。${outlinePlan.systemHint} 章节数越多，大纲篇幅与输出上限相应增加；必须写到终章与结局，不得因篇幅在中途截断。`
        },
        { role: 'user', content: prompt }
      ],
      temperature: 0.5,
      max_tokens: outlineMaxTokens,
      top_p: 0.8,
      presence_penalty: 0.3,
      frequency_penalty: 0.5,
      stream: true,
      metadata: {
        novelId: novel._id,
        novelTitle: novel.title || '未命名小说',
        chapterCount: totalChapters,
        outlineTier: outlinePlan.tierLabel,
        targetOutlineWords: outlinePlan.targetWords
      },
      // 🔥 补充扣费描述信息：不传时消费记录只显示默认"流式生成"，无法追溯来源
      billingInfo: {
        novelName: novel.title || '未命名小说',
        chapterNumber: '',
        operationType: 'AI生成小说大纲'
      }
    };

    // 推理模式参数（DeepSeek 支持顶层参数）
    if (novelSettings.enableThinking) {
      params.enable_thinking = true;
      setThinkingContent(''); // 开始前清空推理内容
    }

    // ═══════════════════════════════════════════════════════════════
    // 【小说大纲】提交给AI的完整提示词 → 已转发到后端服务器控制台输出
    // ═══════════════════════════════════════════════════════════════

    let generatedContent = '';
    const result = await handleStreamGeneration(
      API_BASE_URL,
      params,
      'FULL_OUTLINE',
      (chunk, fullContent, currentWordCount) => {
        let cleaned = fullContent;
        if (cleaned.startsWith('正')) cleaned = cleaned.substring(1);
        cleaned = cleaned.replace(/\*\*/g, '');
        cleaned = cleaned.replace(/^#+\s*/gm, '');
        cleaned = cleaned.replace(/([\u4e00-\u9fa5a-zA-Z0-9])\1{2,}/g, m => m[0]);
        cleaned = cleaned.replace(/(.{2})\1{3,}/g, m => m.substring(0, 3));
        setWordCount(currentWordCount);
        setOutlineText(cleaned);
        if (outlineElement && shouldAutoScroll) {
          setTimeout(() => { if (outlineElement) outlineElement.scrollTop = outlineElement.scrollHeight; }, 10);
        }
      },
      (statusMessage) => {
        setOutlineText(prev => prev + `\n\n[${statusMessage}]\n\n`);
      },
      '/ai/proxy-ai', // 🔥 走计费路由：大纲每日前10次免费，超出后每次扣1000字（服务端 quotaService 裁决）
      (thinkingText) => {
        // 推理内容回调：null表示推理阶段结束（即将输出正式内容）
        if (thinkingText === null) {
          setThinkingContent('');
        } else {
          setThinkingContent(prev => prev + thinkingText);
        }
      }
    );

    generatedContent = result.content;
    if (generatedContent.startsWith('正')) generatedContent = generatedContent.substring(1);
    generatedContent = generatedContent.replace(/\*\*/g, '');
    generatedContent = generatedContent.replace(/^#+\s*/gm, '');
    generatedContent = generatedContent.replace(/([\u4e00-\u9fa5a-zA-Z0-9])\1{2,}/g, m => m[0]);
    generatedContent = generatedContent.replace(/(.{2})\1{3,}/g, m => m.substring(0, 3));

    if (!result.isComplete) {
      throw new Error('生成未完成（未收到 [DONE]），请重试');
    }

    if (outlineElement && outlineElement._scrollHandler) {
      outlineElement.removeEventListener('scroll', outlineElement._scrollHandler);
      outlineElement._scrollHandler = null;
    }

    setThinkingContent(''); // 生成完成后清空推理内容
    return { content: generatedContent, finalWordCount: result.wordCount || 0 };
  }, [createPrompt, outlineRef, novel, novelSettings]);
  // 处理章节优化
  const handleOptimizeChapter = useCallback(async (chapterIndex) => {
    // 如果未提供章节索引，使用当前选中的章节
    const targetChapterIndex = chapterIndex || selectedChapter;

    // 查找要优化的章节
    const chapterToOptimize = chapters.find(ch => ch.index === targetChapterIndex);
    if (!chapterToOptimize || !chapterToOptimize.content) {
      showMessage("无法优化：章节不存在或没有内容", "error");
      return;
    }

    // 预检查余额：每章前5次优化免费（后端裁决），仅当余额低于单次扣费标准时提醒
    //    不做硬拦截——免费次数可能未用完，由后端额度服务最终裁决
    if (userBalance < 1000) {
      showMessage(`当前余额${userBalance}字。本章免费优化次数若已用完，将无法执行（每次优化消耗1000字）`, "warning");
    }

    // 获取章节ID
    const chapterId = chapterToOptimize._id || chapterToOptimize.id;
    if (!chapterId) {
      showMessage("无法优化：章节ID不存在", "error");
      return;
    }

    // 确保ID是字符串类型
    const chapterIdStr = chapterId.toString();

    // 检查是否已在优化
    if (optimizingChapters.has(chapterIdStr)) {
      showMessage("该章节正在优化中，请稍候...", "warning");
      return;
    }

    // 定义在try块外部，这样catch块也可以访问
    let optimizeData = null;

    try {
      // 设置优化状态（使用章节ID而非索引）
      setOptimizingChapters(prev => new Set([...prev, chapterIdStr]));
      
      // 更新章节对象中的优化状态
      const updatedChapters = chapters.map(ch => 
        ch.index === targetChapterIndex 
          ? { ...ch, isOptimizing: true } 
          : ch
      );
      setChapters(updatedChapters);
      
      showMessage("正在优化章节内容...", "info");
      
      // 获取前一章和后一章内容作为上下文
      const prevChapterData = targetChapterIndex > 0 ? chapters.find(ch => ch.index === targetChapterIndex - 1) : null;
      const nextChapterData = targetChapterIndex < chapters.length - 1 ? chapters.find(ch => ch.index === targetChapterIndex + 1) : null;

      const prevChapter = prevChapterData?.content || '';
      const nextChapter = nextChapterData?.content || '';

      // 准备优化数据
      optimizeData = {
        content: chapterToOptimize.content,
        novelId: novel?.id || novel?._id,
        chapterId: chapterId,
        chapterTitle: chapterToOptimize.title,
        perspective: novelSettings?.perspective || '第三人称',
        style: novelSettings?.style || '写实风格',
        novelTitle: novel?.title || '',
        genre: novelSettings?.genre || '',
        prevChapter,
        nextChapter,
        novelOutline: novel?.outline || '',
        requestId: `optimize-${chapterId}-${Date.now()}`
      };
      
      // 添加关键参数校验
      if (!optimizeData.novelId) {
        console.error('优化失败: 缺少novelId', { 
          novel, 
          novelId: novel?.id, 
          novelId2: novel?._id,
          optimizeData 
        });
        showMessage('优化失败: 缺少novelId。请刷新页面后重试', 'error');
        return;
      }
      
      if (!optimizeData.chapterId) {
        console.error('优化失败: 缺少chapterId', { 
          chapterToOptimize, 
          optimizeData,
          chapterId 
        });
        showMessage('优化失败: 缺少chapterId。请刷新页面后重试', 'error');
        return;
      }

      // 发送优化请求前记录关键信息
      console.log('准备发送优化请求，关键参数:', {
        novelId: optimizeData.novelId,
        chapterId: optimizeData.chapterId,
        contentLength: optimizeData.content?.length,
        isOptimized: chapterToOptimize.isOptimized,
        isOptimizing: chapterToOptimize.isOptimizing
      });

      // 发送优化请求
      const { promise } = apiService.optimizeChapterContentAsync(optimizeData);
      
      const response = await promise;
      
      if (response && response.success && response.content) {
        try {
          // 后端已扣除余额，前端只需更新UI
          // 更新章节内容
          const updatedChapters = chapters.map(ch =>
            ch.index === targetChapterIndex
              ? {
                  ...ch,
                  content: response.content,
                  updatedAt: new Date().toISOString(),
                  optimizedAt: new Date().toISOString(),
                  isOptimized: true,
                  isOptimizing: false
                }
              : ch
          );

          setChapters(updatedChapters);

          // 如果当前正在查看这个章节，更新显示的内容
          if (selectedChapter === targetChapterIndex) {
            setCurrentChapterContent(response.content);
          }

          // 保存到服务器和本地存储
          const updatedNovel = {
            ...novel,
            chapters: updatedChapters,
            updatedAt: new Date().toISOString()
          };

          // 先保存到本地存储
          saveChaptersToLocalStorage(novel, updatedChapters);

          // 再保存到服务器
          const saveResult = await saveNovelToServer(updatedNovel, false);
          if (saveResult.success) {
            showMessage("章节优化完成并已保存", "success");
          } else {
            showMessage("章节优化完成，但保存失败", "warning");
          }

          // 更新用户余额（使用后端返回的余额）
          if (typeof response.balance === 'number') {
            setUserBalance(response.balance);
          } else {
            fetchAndUpdateUserBalance();
          }
        } catch (error) {
          console.error('保存优化结果失败:', error);
          showMessage(`优化失败：${error.message || '保存结果失败'}`, "error");
          return;
        }
      } else {
        const errorMsg = response?.message || "服务器响应格式错误";
        showMessage(`优化失败：${errorMsg}`, "error");
      }
    } catch (error) {
      console.error('优化章节失败:', error);
      
      // 增强错误日志
      const errorDetails = {
        errorName: error.name,
        errorMessage: error.message,
        errorResponse: error.response,
        errorData: error.response?.data,
        errorStatus: error.response?.status,
        params: {
          novelId: optimizeData.novelId,
          chapterId: optimizeData.chapterId,
          chapterIndex: targetChapterIndex
        }
      };
      console.error('优化章节失败详情:', errorDetails);
      
      // 错误信息提取和显示
      let errorMsg = '未知错误';
      if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      // 特殊错误处理
      if (errorMsg.includes('缺少必要参数: novelId')) {
        errorMsg = `缺少必要参数: novelId (${novel?.id || novel?._id || '未找到'})`;
        console.error('novelId缺失问题:', {
          'novel.id': novel?.id,
          'novel._id': novel?._id,
          'optimizeData.novelId': optimizeData.novelId
        });
      }
      
      showMessage(`优化章节失败: ${errorMsg}`, "error");
      
      // 清除章节的优化中状态
      const updatedChapters = chapters.map(ch =>
        ch.index === targetChapterIndex ? { ...ch, isOptimizing: false } : ch
      );
      setChapters(updatedChapters);
    } finally {
      // 重置优化状态
      setOptimizingChapters(prev => {
        const next = new Set(prev);
        next.delete(chapterIdStr);
        return next;
      });
    }
  }, [chapters, novel, selectedChapter, optimizingChapters, setOptimizingChapters, apiService, saveNovelToServer, showMessage, setUserBalance, fetchAndUpdateUserBalance, saveChaptersToLocalStorage]);

  // 🔥 处理章节重写（同步模式，类似优化章节）
  const handleRewriteChapter = useCallback(async (chapterIndex) => {
    const targetChapterIndex = chapterIndex || selectedChapter;
    
    // 查找要重写的章节
    const chapterToRewrite = chapters.find(ch => ch.index === targetChapterIndex);
    if (!chapterToRewrite || !chapterToRewrite.content) {
      showMessage("无法重写：章节不存在或没有内容", "error");
      return;
    }
    
    // 检查内容长度
    if (chapterToRewrite.content.trim().length < 100) {
      showMessage("章节内容太短，无法重写（至少需要100字）", "error");
      return;
    }
    
    // 预检查余额：每章前5次重写免费（后端裁决），仅当余额低于单次扣费标准时提醒
    //    不做硬拦截——免费次数可能未用完，由后端额度服务最终裁决
    if (userBalance < 1000) {
      showMessage(`当前余额${userBalance}字。本章免费重写次数若已用完，将无法执行（每次重写消耗1000字）`, "warning");
    }
    
    // 获取章节ID
    const chapterId = chapterToRewrite._id || chapterToRewrite.id;
    if (!chapterId) {
      showMessage("无法重写：章节ID不存在", "error");
      return;
    }
    
    const chapterIdStr = chapterId.toString();
    
    // 检查是否已在重写
    if (rewritingChapters.has(chapterIdStr)) {
      showMessage("该章节正在重写中，请稍候...", "warning");
      return;
    }
    
    try {
      // 设置重写状态
      setRewritingChapters(prev => new Set([...prev, chapterIdStr]));
      
      // 更新章节对象中的重写状态
      const updatedChapters = chapters.map(ch => 
        ch.index === targetChapterIndex 
          ? { ...ch, isRewriting: true } 
          : ch
      );
      setChapters(updatedChapters);
      
      showMessage("正在重写章节内容...", "info");
      
      console.log(`准备重写章节 ${targetChapterIndex}`);
      
      // 准备重写数据
      const rewriteData = {
        novelId: novel?.id || novel?._id,
        chapterId: chapterId,
        chapterIndex: targetChapterIndex, // 添加章节序号，后端优先使用这个
        requestId: `rewrite-${chapterId}-${Date.now()}`
      };
      
      // 参数校验
      if (!rewriteData.novelId) {
        showMessage('重写失败: 缺少novelId。请刷新页面后重试', 'error');
        return;
      }
      
      if (!rewriteData.chapterId) {
        showMessage('重写失败: 缺少chapterId。请刷新页面后重试', 'error');
        return;
      }
      
      console.log('🔥🔥🔥 [前端版本: V2024-11-24-23:48] 准备发送重写请求');
      console.log('准备发送重写请求，完整参数:', rewriteData);
      console.log('关键参数检查:', {
        novelId: rewriteData.novelId,
        chapterId: rewriteData.chapterId,
        chapterIndex: rewriteData.chapterIndex,
        targetChapterIndex: targetChapterIndex,
        contentLength: chapters[targetChapterIndex - 1]?.content?.length
      });
      
      // 🔥 发送同步重写请求（等待完成）
      const { promise } = apiService.rewriteChapterContentAsync(rewriteData);
      const response = await promise;
      
      if (response && response.success && response.content) {
        try {
          // 🔥 计费说明：重写费用已由服务端 /ai/rewrite-chapter-sync 扣收
          //    （每章前5次免费，超出每次1000字），前端不再重复扣费
          {
            // 更新章节内容
            const updatedChapters = chapters.map(ch =>
              ch.index === targetChapterIndex
                ? { 
                    ...ch, 
                    content: response.content, 
                    updatedAt: new Date().toISOString(),
                    rewrittenAt: new Date().toISOString(),
                    isRewritten: true,
                    isRewriting: false
                  }
                : ch
            );
            
            setChapters(updatedChapters);
            
            // 如果当前正在查看这个章节，更新显示的内容
            if (selectedChapter === targetChapterIndex) {
              setCurrentChapterContent(response.content);
              // 更新编辑器内容
              setChapterContentEdit(prev => ({
                ...prev,
                editedContent: response.content,
                originalContent: response.content
              }));
            }
            
            // 保存到服务器和本地存储
            const updatedNovel = {
              ...novel,
              chapters: updatedChapters,
              updatedAt: new Date().toISOString()
            };
            
            // 先保存到本地存储
            saveChaptersToLocalStorage(novel, updatedChapters);
            
            // 再保存到服务器
            const saveResult = await saveNovelToServer(updatedNovel, false);
            if (saveResult.success) {
              showMessage("章节重写完成并已保存", "success");
            } else {
              showMessage("章节重写完成，但保存失败", "warning");
            }
            
            // 刷新用户余额（服务端已完成扣费）
            fetchAndUpdateUserBalance();
          }
        } catch (error) {
          console.error('重写结果保存失败:', error);
          showMessage(`重写失败：${error.message || '保存失败'}`, "error");
          return;
        }
      } else {
        const errorMsg = response?.message || "服务器响应格式错误";
        showMessage(`重写失败：${errorMsg}`, "error");
      }
    } catch (error) {
      console.error('重写章节失败:', error);
      
      let errorMsg = '未知错误';
      if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      showMessage(`重写章节失败: ${errorMsg}`, "error");
      
      // 清除章节的重写中状态
      const updatedChapters = chapters.map(ch =>
        ch.index === targetChapterIndex ? { ...ch, isRewriting: false } : ch
      );
      setChapters(updatedChapters);
    } finally {
      // 重置重写状态
      setRewritingChapters(prev => {
        const next = new Set(prev);
        next.delete(chapterIdStr);
        return next;
      });
    }
  }, [chapters, novel, selectedChapter, rewritingChapters, apiService, saveNovelToServer, showMessage, setUserBalance, fetchAndUpdateUserBalance, saveChaptersToLocalStorage, setCurrentChapterContent, setChapterContentEdit]);

  // 兼容移动端的复制文本函数
  const copyTextToClipboard = useCallback((text) => {
    // 创建一个临时的textarea元素
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';  // 避免页面滚动
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);

    try {
      // 创建临时文本区域
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      textArea.style.top = '0';
      document.body.appendChild(textArea);
      
      // 首先尝试使用新的 Clipboard API
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function' && window.isSecureContext) {
        return navigator.clipboard.writeText(text)
          .then(() => {
            document.body.removeChild(textArea);
            return true;
          })
          .catch(err => {
            console.error('Clipboard API 失败，使用回退方法', err);
            // 如果 Clipboard API 失败，使用传统方法
            textArea.select();
            const successful = document.execCommand('copy');
            document.body.removeChild(textArea);
            return successful;
          });
      }

      // 如果不支持 Clipboard API，使用传统方法
      textArea.select();
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      return Promise.resolve(successful);
    } catch (err) {
      const textArea = document.querySelector('textarea[style*="position: fixed"]');
      if (textArea && textArea.parentNode) {
        document.body.removeChild(textArea);
      }
      console.error('复制失败:', err);
      return Promise.resolve(false);
    }
  }, []);

  // 复制章节标题
  const handleCopyChapterTitle = useCallback((chapter) => {
    copyTextToClipboard(chapter.title)
      .then(() => {
        showMessage('已复制章节标题');
      })
      .catch(() => {
        showMessage('复制失败，请手动复制', 'error');
      });
  }, [showMessage, copyTextToClipboard]);

  // 复制章节内容
  const handleCopyChapterContent = useCallback((chapter) => {
    if (!chapter.content) return;
    copyTextToClipboard(chapter.content)
      .then(() => {
        showMessage('已复制章节内容');
      })
      .catch(() => {
        showMessage('复制失败，请手动复制', 'error');
      });
  }, [showMessage, copyTextToClipboard]);
  
  // 从本地存储加载章节数据
  const loadChapterFromLocalStorage = useCallback((novelId, chapterIndex) => {
    if (!novelId || chapterIndex === undefined) {
      return null;
    }
    
    try {
      const chapterData = localStorage.getItem(`novel_${novelId}_chapter_${chapterIndex}`);
      if (!chapterData) return null;
      
      return JSON.parse(chapterData);
    } catch (error) {
      console.error(`从本地存储加载章节数据失败:`, error);
      return null;
    }
  }, []);
  
  // 渲染章节列表项 (优化UI + 虚拟化)
  const renderChaptersListItems = useCallback((chapterItems) => {
    const sortedChapters = chapterItems.sort((a, b) => a.index - b.index);
    
    // 🔥 虚拟化逻辑：当章节数量超过50时，只渲染前100个+选中的章节
    let chaptersToRender = sortedChapters;
    if (sortedChapters.length > 50) {
      // 渲染前100个章节
      const first100 = sortedChapters.slice(0, 100);
      
      // 如果选中的章节不在前100个中，额外添加它
      if (selectedChapter && selectedChapter > 100) {
        const selectedChapterObj = sortedChapters.find(ch => ch.index === selectedChapter);
        if (selectedChapterObj) {
          chaptersToRender = [...first100, selectedChapterObj].sort((a, b) => a.index - b.index);
        } else {
          chaptersToRender = first100;
        }
      } else {
        chaptersToRender = first100;
      }
      
      console.log(`🔥 [虚拟化] 章节总数: ${sortedChapters.length}, 渲染数量: ${chaptersToRender.length}`);
    }
    
    return chaptersToRender.map((chapter) => {
        // 🔥 修复：计算实际字数（去除换行符、空格等格式字符）
        const wordCount = chapter.content 
          ? chapter.content
              .replace(/\n/g, '')      // 去除所有换行符
              .replace(/\r/g, '')      // 去除回车符
              .replace(/\t/g, '')      // 去除制表符
              .replace(/\s+/g, '')     // 去除所有空格（包括全角空格）
              .length 
          : 0;
        const hasOutline = chapter.hasOutline || (chapter.outline && chapter.outline.trim().length > 0);
        const hasContent = chapter.hasContent || (chapter.content && chapter.content.trim().length > 0);
      const isSelected = selectedChapter === chapter.index;
        const isOptimized = chapter.isOptimized;
        
        // 获取章节的生成状态
        const isGenerating = generatingChapterId === chapter.index;
        
        // 使用章节ID(而非索引)检查是否正在优化中
        const chapterId = (chapter._id || chapter.id || '').toString();
        const isOptimizing = chapterId ? optimizingChapters.has(chapterId) : false;
        
        // 使用章节ID检查是否正在重写中
        const isRewriting = chapterId ? rewritingChapters.has(chapterId) : false;
        const isRewritten = chapter.isRewritten;
        
        // 输出章节状态用于调试
        console.log(`章节 ${chapter.index} 状态检查:`, {
          index: chapter.index,
          title: chapter.title,
          status: chapter.status,
          generationStatus: chapter.generationStatus,
        });
        
        // 检查该章节是否在批量生成队列中
        const batchStatus = chapterGenerationStatus?.find(s => s.chapterIndex === chapter.index);
        const isInBatchQueue = !!batchStatus;
        const batchStatusText = batchStatus?.status || '';
        
        // 使用章节自身的生成状态属性（如果有）
        const chapterGenerationStatusText = chapter.generationStatus || '';
        const chapterGenerationProgress = chapter.generationProgress || 0;
        
        // 判断章节状态 - 优先使用服务器返回的status字段
        const isChapterGenerating = chapter.status === 'generating';
        const isChapterPending = chapter.status === 'pending';
        
        // 判断是否为首次提交的章节（首章）
        const isFirstChapter = chapter.index === 1;
        const isFirstSubmission = isFirstChapter && (!hasContent || isGenerating);
        
        // 格式化显示的状态文本 - 优先使用服务器返回的status判断
        let formattedBatchStatus = isChapterGenerating ? '生成中' : 
                                  (isChapterPending ? '等待中' : 
                                  formatChapterStatus(batchStatusText, isFirstSubmission, batchStatus?.progress));
        
        let formattedChapterStatus = isChapterGenerating ? '生成中' : 
                                    (isChapterPending ? '等待中' : 
                                    formatChapterStatus(chapterGenerationStatusText, isFirstSubmission, chapterGenerationProgress));

        // 处理"已跳过"状态 - 将已跳过状态改为"已生成"
        if (batchStatusText === '已跳过') {
          if (isInBatchQueue) {
            batchStatus.status = '已生成';
            formattedBatchStatus = '已生成';
          }
        }
        
        if (chapterGenerationStatusText === '已跳过') {
          chapter.generationStatus = '已生成';
          formattedChapterStatus = '已生成';
        }
      
        // 优化标题格式：第X章：章节标题
        // 如果章节有内容且标题包含冒号，保留完整标题
        // 如果章节有内容但标题不包含冒号，格式化为"第X章：标题内容"
        // 如果章节没有内容，只显示"第X章"
        // 确保标题始终有"第X章："前缀
        let formattedTitle = `第${chapter.index}章`;
        if (chapter.title && chapter.title.trim()) {
          const cleanTitle = chapter.title.replace(/^第\d+章[：:]\s*/, '');
          if (cleanTitle) {
            formattedTitle = `第${chapter.index}章：${cleanTitle}`;
          }
        }
        
      return (
          <ListItemButton 
            key={chapter.index}
            selected={isSelected}
            onClick={() => handleChapterSelect(chapter.index)}
            disabled={isGenerating}
            sx={{ 
              borderRadius: 1,
              mb: 0.5,
              '&.Mui-selected': {
                backgroundColor: 'action.selected',
              },
              position: 'relative'
            }}
          >
            <ListItemText 
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                    {/* 优化/重写状态彩色圆点 */}
                    {(isOptimized || isRewritten || isOptimizing || isRewriting) && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                        {isOptimized && (
                          <Tooltip title="已优化">
                            <Box sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: '#4caf50',
                              mr: isRewritten || isRewriting ? 0.5 : 0
                            }} />
                          </Tooltip>
                        )}
                        {isOptimizing && (
                          <Tooltip title="优化中">
                            <Box sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: '#ff9800',
                              mr: isRewritten || isRewriting ? 0.5 : 0
                            }} />
                          </Tooltip>
                        )}
                        {isRewritten && !isRewriting && (
                          <Tooltip title="已重写">
                            <Box sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: '#2196f3'
                            }} />
                          </Tooltip>
                        )}
                      </Box>
                    )}
                    {/* 状态指示图标 - 排除优化和重写状态 */}
                    {(isGenerating || (isInBatchQueue && batchStatusText && batchStatusText !== '已完成' && batchStatusText !== '已跳过' && batchStatusText !== '已生成') || 
                      (chapterGenerationStatusText && chapterGenerationStatusText !== '已完成' && chapterGenerationStatusText !== '已跳过' && chapterGenerationStatusText !== '已生成')) && 
                      !isOptimizing && !isRewriting && (
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                    )}
                    {(isInBatchQueue && (batchStatusText === '等待中' || batchStatusText === '等待生成')) && (
                      <HourglassEmptyIcon fontSize="small" color="warning" sx={{ mr: 1 }} />
                    )}
                  <Typography variant="body1" component="span">
                    {formattedTitle}
                  </Typography>
                  {/* 添加状态标签在标题右侧 - 只在批量生成过程中显示 */}
                  {(batchGenerating) && (
                    <Box sx={{ ml: 1 }}>
                      {/* 强制显示状态标签 - 根据生成状态和章节索引 */}
                      {(() => {
                        // 调试输出
                        console.log(`渲染章节${chapter.index}的状态标签: status=${chapter.status}, generatingChapterId=${generatingChapterId}`);
                        
                        // 检查是否是当前正在生成的章节
                        const isCurrentlyGenerating = generatingChapterId === chapter.index;
                        
                        // 根据generatingChapterId和chapterGenerationStatus决定显示哪个标签
                        if (isCurrentlyGenerating) {
                          return <Chip size="small" label="生成中" color="primary" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />;
                        }
                        
                        // 检查章节生成状态
                        const chapterStatus = chapterGenerationStatus?.find(s => s.chapterIndex === chapter.index);
                        if (chapterStatus) {
                          if (chapterStatus.status === '生成中') {
                            return <Chip size="small" label="生成中" color="primary" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />;
                          } else if (chapterStatus.status === '等待中' || chapterStatus.status === '等待生成') {
                            return <Chip size="small" label="等待中" color="warning" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />;
                          } 
                          // 移除"已生成"标签
                        }
                        
                        // 回退到使用chapter.status
                        if (chapter.status === 'generating') {
                          return <Chip size="small" label="生成中" color="primary" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />;
                        } else if (chapter.status === 'pending') {
                          return <Chip size="small" label="等待中" color="warning" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />;
                        }
                        
                        // 最后的回退：根据章节索引和当前正在生成的章节ID
                        if (chapter.index === generatingChapterId) {
                          return <Chip size="small" label="生成中" color="primary" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />;
                        } else {
                          return <Chip size="small" label="等待中" color="warning" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />;
                        }
                      })()}
                    </Box>
                  )}
                  {/* 移除重复的状态标签，已在标题右侧显示 */}
                  </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', ml: 1, minWidth: '60px', justifyContent: 'flex-end' }}>
                    {hasOutline && (
                      <Chip 
                        size="small" 
                        label="大纲" 
                        variant="filled" 
                        color="secondary"
                        sx={{ 
                          height: 20, 
                          fontSize: '0.7rem',
                          mr: 0.5,
                          '& .MuiChip-label': { px: 1, py: 0 }
                        }} 
                      />
                    )}
                    <Chip 
                      size="small" 
                      label={wordCount > 0 ? `${wordCount}字` : '0字'} 
                      color={wordCount > 0 ? "primary" : "default"}
                      variant={wordCount > 0 ? "filled" : "outlined"} 
                      sx={{ 
                        height: 20, 
                        fontSize: '0.7rem',
                        fontWeight: 'bold',
                        visibility: 'visible' // 确保始终可见
                      }} 
                    />
                  </Box>
                </Box>
              }
              secondary={
                <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                  {/* 状态标签 */}
                              {isGenerating && (
                    <Chip 
                      size="small" 
                label={isFirstSubmission ? "优先生成中" : "生成中"} 
                color={isFirstSubmission ? "primary" : "warning"}
                      variant="outlined" 
                icon={<CircularProgress size={12} color="inherit" />}
                      sx={{ height: 20, '& .MuiChip-label': { px: 1, py: 0 } }}
                    />
                  )}
                              {/* 强制显示状态 - 只在生成中或等待中时显示 */}
                  {batchGenerating && !isGenerating && (() => {
                    // 检查是否是当前正在生成的章节
                    const isCurrentlyGenerating = generatingChapterId === chapter.index;
                    
                    // 检查章节生成状态
                    const chapterStatus = chapterGenerationStatus?.find(s => s.chapterIndex === chapter.index);
                    
                    // 确定当前状态
                    let currentStatus = '';
                    if (isCurrentlyGenerating) {
                      currentStatus = '生成中';
                    } else if (chapterStatus) {
                      if (chapterStatus.status === '生成中') {
                        currentStatus = '生成中';
                      } else if (chapterStatus.status === '等待中' || chapterStatus.status === '等待生成') {
                        currentStatus = '等待中';
                      }
                    } else if (chapter.status === 'generating') {
                      currentStatus = '生成中';
                    } else if (chapter.status === 'pending') {
                      currentStatus = '等待中';
                    }
                    
                    // 只有在有状态时才显示标签
                    return currentStatus ? (
                    <Chip 
                      size="small" 
                        label={currentStatus}
                        color={currentStatus === '生成中' ? "primary" : "warning"}
                      variant="outlined" 
                        icon={currentStatus === '生成中' ? 
                          <CircularProgress size={12} color="inherit" /> : 
                          <HourglassEmptyIcon fontSize="small" />}
                      sx={{ height: 20, '& .MuiChip-label': { px: 1, py: 0 } }}
                    />
                    ) : null;
                  })()}
                  {isOptimizing && (
                    <Chip 
                      size="small" 
                      label="优化中" 
                      variant="outlined" 
                      color="warning"
                      icon={<CircularProgress size={12} color="inherit" />}
                      sx={{ height: 20, '& .MuiChip-label': { px: 1, py: 0 } }}
                    />
                  )}

                  {hasContent && (
                    <Chip
                      size="small"
                      label="内容"
                      variant="outlined"
                      color="primary"
                      sx={{ height: 20, '& .MuiChip-label': { px: 1, py: 0 } }}
                    />
                  )}
                  {/* 番茄发布状态徽章 */}
                  {(() => {
                    const ps = chapter.publishStatus || 'none';
                    if (ps === 'none') return null;
                    const map = {
                      queued: { label: '待发布', color: 'warning', tooltip: '该章节已在KM发布助手中排队' },
                      publishing: { label: '发布中', color: 'info', tooltip: 'KM发布助手正在发布该章节' },
                      published: { label: '已发布', color: 'success', tooltip: chapter.fanqieUrl || '已成功发布到番茄小说' },
                      failed: { label: '发布失败', color: 'error', tooltip: chapter.publishMessage || '发布失败，请在KM发布助手中重试' }
                    };
                    const cfg = map[ps];
                    if (!cfg) return null;
                    return (
                      <Tooltip title={cfg.tooltip}>
                        <Chip
                          size="small"
                          label={cfg.label}
                          variant="outlined"
                          color={cfg.color}
                          sx={{ height: 20, fontSize: '0.7rem', '& .MuiChip-label': { px: 1, py: 0 } }}
                        />
                      </Tooltip>
                    );
                  })()}
                  {isOptimized && (
                    <Box
                      sx={{
                        minWidth: 16,
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'rgba(76, 175, 80, 0.15)',
                        color: '#66bb6a',
                        border: '1px solid rgba(76, 175, 80, 0.25)',
                        fontSize: '0.6rem',
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      优
                    </Box>
                  )}
                  {isRewriting && (
                    <Chip 
                      size="small" 
                      label="重写中" 
                      variant="outlined" 
                      color="info"
                      icon={<CircularProgress size={12} color="inherit" />}
                      sx={{ 
                        height: 20, 
                        '& .MuiChip-label': { px: 1, py: 0 },
                        borderColor: '#00acc1',
                        color: '#00acc1'
                      }}
                    />
                  )}
                  {isRewritten && !isRewriting && (
                    <Box
                      sx={{
                        minWidth: 16,
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'rgba(255, 152, 0, 0.15)',
                        color: '#ffa726',
                        border: '1px solid rgba(255, 152, 0, 0.25)',
                        fontSize: '0.6rem',
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      重
                    </Box>
                  )}
                  
                              {/* 进度条 - 仅在生成或队列中时显示 */}
            {(isGenerating || (isInBatchQueue && batchStatus?.progress > 0) || chapterGenerationProgress > 0) && (
              <Box sx={{ width: '100%', mt: 0.5 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={isInBatchQueue ? batchStatus?.progress || 0 : chapterGenerationProgress} 
                  sx={{ 
                    height: 4, 
                    borderRadius: 2,
                    backgroundColor: isFirstSubmission ? 'rgba(25, 118, 210, 0.2)' : undefined,
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: isFirstSubmission ? 'primary.main' : undefined
                    }
                  }}
                />
                {/* 首章不显示百分比，只显示"优先生成中" */}
                {!isFirstSubmission && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    <span>{isInBatchQueue ? batchStatus?.progress || 0 : chapterGenerationProgress}%</span>
                    {((isInBatchQueue && batchStatus?.progress > 0 && batchStatus?.progress < 100) || 
                      (!isInBatchQueue && chapterGenerationProgress > 0 && chapterGenerationProgress < 100)) && (
                      <span>预计{Math.ceil((100 - (isInBatchQueue ? batchStatus?.progress || 0 : chapterGenerationProgress)) / 10)}分钟</span>
                    )}
                  </Typography>
                )}
              </Box>
                  )}
              </Box>
              }
              primaryTypographyProps={{ component: 'div' }}
              secondaryTypographyProps={{ component: 'div' }}
            />
          </ListItemButton>
      );
    });
  }, [selectedChapter, handleChapterSelect, generatingChapterId, optimizingChapters, rewritingChapters, chapterGenerationStatus, chapters, batchGenerating, JSON.stringify(chapters)]);
  
  // 渲染章节列表 (优化UI)
  const renderChapterList = useCallback(() => {
    if (chapters.length === 0) {
    return (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Typography color="text.secondary">
            还没有章节，请点"添加章节"按钮创建
          </Typography>
        </Box>
      );
    }
    
    // 输出所有章节的状态，用于调试
    console.log('渲染章节列表，当前章节状态:');
    chapters.forEach(ch => {
      console.log(`章节${ch.index}: 标题="${ch.title}", status=${ch.status}`);
    });
    
    // 临时修复：如果有章节正在生成中，确保其状态正确
    // 根据控制台日志，找出当前正在生成中的章节
    const fixedChapters = [...chapters];
    
    // 检查是否有任何章节已经标记为生成中
    const generatingChapter = fixedChapters.find(ch => ch.status === 'generating');
    
    // 如果没有找到生成中的章节，但有正在生成的章节ID，则修复状态
    if (!generatingChapter && generatingChapterId !== null && !batchGenerating) {
      // 只在非批量生成且有明确的生成章节ID时才修复
      const idx = fixedChapters.findIndex(ch => ch.index === generatingChapterId);
      if (idx >= 0) {
        console.log(`修复章节${generatingChapterId}的状态为"生成中"`);
        fixedChapters[idx].status = 'generating';
        fixedChapters[idx].generationStatus = '生成中';
      }
    }
    
    return renderChaptersListItems(fixedChapters);
  }, [chapters, renderChaptersListItems, JSON.stringify(chapters)]);
  
  // 定义增强版的fetchNovelData函数
  const fetchNovelData = useCallback(async (id, options = {}) => {
    try {
      // 🎯 新增：支持配置选项
      const {
        preserveTab = false,           // 是否保持当前标签页（批量生成时为true）
        preserveExpanded = false,      // 是否保持章节展开状态（批量生成时为true）
        silent = false,                // 是否静默刷新（批量生成时为true）
        clearGenerationStatus = false, // 是否清除生成状态（重置时为true）
        preserveRewritingState = false // 🔥 是否保持重写中状态（重写轮询时为true）
      } = options;
      
      // 🎯 保存当前状态
      const savedTab = preserveTab ? activeTab : null;
      const savedExpanded = preserveExpanded ? expandedOutlineChapter : null;
      
      console.log('🔄 刷新小说数据，配置:', {
        preserveTab,
        preserveExpanded,
        savedTab,
        savedExpanded
      });
      
      // 检查是否需要显示加载状态（只在首次加载时显示）
      const shouldShowLoading = !silent && (!novel || !novel.id); 
      
      if (shouldShowLoading) {
        console.log(`开始从服务器获取小说数据: ${id}`);
        setLoading(true);
      } else {
        console.log(`静默刷新小说数据: ${id}`);
      }
      
      // 保存当前章节状态和生成状态，确保不会丢失
      const currentChapters = [...chapters];
      // 🎯 如果需要清除生成状态（重置时），则不保留旧的生成状态
      const currentGenerationStatus = clearGenerationStatus ? [] : (Array.isArray(chapterGenerationStatus) ? [...chapterGenerationStatus] : []);
      
      console.log(`刷新前保存当前状态: ${currentChapters.length}章, ${currentGenerationStatus.length}个生成状态${clearGenerationStatus ? ' (已清除)' : ''}`);
      
      const response = await api.get(`/novels/${id}`);
      const novelData = response.data?.data || response.data;
      
      if (novelData) {
        console.log('服务器返回小说数据:', {
          title: novelData.title,
          chaptersCount: novelData.chapters?.length || 0,
          fetchedAt: new Date().toISOString()
        });
        
        // 更新小说基本信息
        setNovel(novelData);
        setTitle(novelData.title || '');
        setNovelSettings(novelData.settings || defaultNovelSettings);
        setOutline(novelData.outline || '');
        setOutlineText(novelData.outline || '');
        
        // 获取并处理章节数据
        // 确保先检查本地章节数据
        if (currentChapters.length > 0) {
          console.log(`当前本地章节数量: ${currentChapters.length}`, 
            currentChapters.map(ch => `${ch.index}: ${ch.title?.substring(0, 20)}`));
        }
        
        // 处理服务器返回的章节数据
        if (novelData.chapters && Array.isArray(novelData.chapters)) {
          // 对章节进行排序
          const sortedChapters = [...novelData.chapters].sort((a, b) => a.index - b.index);
          
          console.log(`服务器返回章节数量: ${sortedChapters.length}`, 
            sortedChapters.map(ch => `${ch.index}: ${ch.title?.substring(0, 20)}`));
          
          // 获取本地章节状态，包括批量生成的章节
          // 注意：chapterGenerationStatus 包含了所有正在生成的章节信息
          const generatingChapters = Array.isArray(chapterGenerationStatus) ? chapterGenerationStatus : [];
          
          // 创建新的章节数组，优先保留本地章节数据
          // 使用之前保存的currentChapters而不是chapters，确保不会丢失最新状态
          let updatedChapters = [...currentChapters]; // 从保存的本地章节开始
          
          console.log(`保留本地章节数据: ${updatedChapters.length}章`, 
            updatedChapters.map(ch => `${ch.index}: ${ch.title?.substring(0, 20)} (ID: ${ch.id})`));
          
          // 确保批量生成中的章节也被包含在章节列表中
          if (generatingChapters.length > 0 || currentGenerationStatus.length > 0) {
            // 使用当前保存的生成状态，确保不会丢失
            const allGeneratingChapters = [...generatingChapters, ...currentGenerationStatus];
            console.log(`检查批量生成中的章节: ${allGeneratingChapters.length}个`);
            
            allGeneratingChapters.forEach(genChapter => {
              if (!genChapter || !genChapter.chapterIndex) return; // 跳过无效数据
              
              // 检查这个生成中的章节是否已在本地章节列表中
              const existsInLocal = updatedChapters.some(ch => ch.index === genChapter.chapterIndex);
              
              if (!existsInLocal) {
                console.log(`添加批量生成中的章节到列表: 第${genChapter.chapterIndex}章 (${genChapter.status || '状态未知'})`);
                
                // 添加这个章节到本地列表，即使它还没有内容
                updatedChapters.push({
                  id: uuidv4(), // 临时ID，会在服务器返回后更新
                  index: genChapter.chapterIndex,
                  title: genChapter.title || `第${genChapter.chapterIndex}章`,
                  content: '',
                  outline: '',
                  status: 'generating', // 标记为生成中
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                  generationStatus: genChapter.status || '等待生成',
                  generationProgress: genChapter.progress || 0
                });
              } else {
                // 更新现有章节的生成状态
                const chapterIndex = updatedChapters.findIndex(ch => ch.index === genChapter.chapterIndex);
                if (chapterIndex >= 0) {
                  updatedChapters[chapterIndex].generationStatus = genChapter.status || updatedChapters[chapterIndex].generationStatus || '等待生成';
                  updatedChapters[chapterIndex].generationProgress = genChapter.progress || updatedChapters[chapterIndex].generationProgress || 0;
                }
              }
            });
            
            // 重新排序章节
            updatedChapters.sort((a, b) => a.index - b.index);
          }
          
          // 处理服务器返回的章节数据，仅更新内容而不覆盖本地创建的章节
          sortedChapters.forEach(serverChapter => {
            // 规范化章节数据
            serverChapter = normalizeId(serverChapter);
            
            // 🔥 修复：更智能的章节匹配逻辑，优先使用ID；当本地为临时占位（生成中）且ID不一致时，允许按index合并，避免重复
            const localIndex = updatedChapters.findIndex(ch => {
              const chId = (ch.id || ch._id || '').toString();
              const serverId = (serverChapter.id || serverChapter._id || '').toString();
              const sameIndex = ch.index === serverChapter.index;
              
              // 1) 优先使用ID严格匹配（最可靠）
              if (chId && serverId && chId === serverId) {
                return true;
              }
              
              // 2) 当本地章节是“生成中”占位（一般使用临时UUID），即使ID不同，也允许按index合并
              //    条件：同一index，且本地处于生成中或内容为空（典型占位特征）
              const isTemporaryLocal =
                (ch.status === 'generating' || ch.generationStatus || !ch.content || ch.content.length === 0);
              
              if (sameIndex && isTemporaryLocal) {
                return true;
              }
              
              // 3) 当双方都没有ID（极端情况），退化为index匹配并记录告警
              if (!chId && !serverId && sameIndex) {
                console.warn(`⚠️ 章节匹配警告: 第${ch.index}章缺少ID，使用index匹配（可能不准确）`);
                return true;
              }
              
              return false;
            });
            
            if (localIndex >= 0) {
              // 更新现有章节的内容，但保留本地设置的ID和生成状态
              const localChapter = updatedChapters[localIndex];
              const localId = (localChapter.id || localChapter._id || '').toString();
              const serverId = (serverChapter.id || serverChapter._id || '').toString();
              
              console.log(`更新章节内容: 第${serverChapter.index}章 (本地ID: ${localId || '无'}, 服务器ID: ${serverId || '无'})`);
              
              // 保存原始状态信息
              const originalStatus = localChapter.generationStatus;
              const originalProgress = localChapter.generationProgress;
              const originalIsRewritten = localChapter.isRewritten;
              const originalIsRewriting = localChapter.isRewriting;
              
              // 🔥 修复：选择性合并，避免错误覆盖状态
              // 只更新服务器明确返回的字段，而不是展开所有属性
              updatedChapters[localIndex] = {
                ...localChapter,
                // 更新内容相关字段
                content: serverChapter.content !== undefined ? serverChapter.content : localChapter.content,
                title: serverChapter.title !== undefined ? serverChapter.title : localChapter.title,
                outline: serverChapter.outline !== undefined ? serverChapter.outline : localChapter.outline,
                updatedAt: serverChapter.updatedAt || localChapter.updatedAt,
                // 当本地是临时占位而服务器有真实ID时，用服务器ID覆盖，避免后续重复
                id: serverId || localChapter.id,
                _id: serverId || localChapter._id || serverChapter._id,
                // 保留生成状态
                generationStatus: originalStatus,
                generationProgress: originalProgress,
                // 🔥 关键修复：只有服务器明确返回状态时才更新，避免错误标记
                // 使用严格检查，确保只更新匹配的章节
                isOptimized: serverChapter.isOptimized !== undefined ? serverChapter.isOptimized : localChapter.isOptimized,
                isOptimizing: serverChapter.isOptimizing !== undefined ? serverChapter.isOptimizing : localChapter.isOptimizing,
                // 🔥 重写状态：只有当服务器明确返回且ID匹配时才更新
                isRewritten: (serverChapter.isRewritten !== undefined && (!!serverId ? localId === serverId : true)) 
                  ? serverChapter.isRewritten 
                  : (!!serverId ? (localId === serverId ? originalIsRewritten : localChapter.isRewritten) : originalIsRewritten),
                isRewriting: (serverChapter.isRewriting !== undefined && (!!serverId ? localId === serverId : true))
                  ? serverChapter.isRewriting
                  : (!!serverId ? (localId === serverId ? originalIsRewriting : localChapter.isRewriting) : originalIsRewriting),
                rewrittenAt: serverChapter.rewrittenAt || localChapter.rewrittenAt,
                // 番茄发布状态（KM发布助手回传）
                publishStatus: serverChapter.publishStatus !== undefined ? serverChapter.publishStatus : (localChapter.publishStatus || 'none'),
                publishMessage: serverChapter.publishMessage !== undefined ? serverChapter.publishMessage : (localChapter.publishMessage || ''),
                fanqieUrl: serverChapter.fanqieUrl || localChapter.fanqieUrl || '',
                publishedAt: serverChapter.publishedAt || localChapter.publishedAt || null
              };
            } else {
              // 添加服务器上有但本地没有的章节
              console.log(`添加服务器返回的新章节: 第${serverChapter.index}章 (ID: ${serverChapter.id || '无'})`);
              updatedChapters.push(serverChapter);
            }
          });
          
          // 最终排序
          updatedChapters.sort((a, b) => a.index - b.index);
          
          console.log(`合并后章节数量: ${updatedChapters.length}`, 
            updatedChapters.map(ch => `${ch.index}: ${ch.title?.substring(0, 20)}`));
          
          // 始终更新状态，确保显示最新的章节内容
          // 确保批量生成的章节状态在UI中显示
          // 从chapterGenerationStatus获取生成状态（重置时使用空数组）
          const batchGeneratingChapters = clearGenerationStatus ? [] : (Array.isArray(chapterGenerationStatus) ? [...chapterGenerationStatus] : []);
          
          if (batchGeneratingChapters.length > 0) {
            console.log(`确保批量生成中的章节在UI中显示: ${batchGeneratingChapters.length}个`);
            
            // 确保所有批量生成的章节都在列表中
            let hasNewChapters = false;
            
            batchGeneratingChapters.forEach(genChapter => {
              if (!genChapter || !genChapter.chapterIndex) return; // 跳过无效数据
              
              // 检查这个章节是否在列表中
              const existingIndex = updatedChapters.findIndex(ch => ch.index === genChapter.chapterIndex);
              
              if (existingIndex >= 0) {
                // 更新现有章节的生成状态
                updatedChapters[existingIndex].generationStatus = genChapter.status;
                updatedChapters[existingIndex].generationProgress = genChapter.progress || 0;
              } else {
                // 添加新章节
                console.log(`添加缺失的生成中章节: 第${genChapter.chapterIndex}章 (${genChapter.status})`);
                updatedChapters.push({
                  id: uuidv4(), // 临时ID
                  index: genChapter.chapterIndex,
                  title: genChapter.title || `第${genChapter.chapterIndex}章`,
                  content: '',
                  outline: '',
                  status: 'generating',
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                  generationStatus: genChapter.status,
                  generationProgress: genChapter.progress || 0
                });
                hasNewChapters = true;
              }
            });
            
            // 如果添加了新章节，重新排序
            if (hasNewChapters) {
              updatedChapters.sort((a, b) => a.index - b.index);
            }
          }
          
          // 更新本地章节状态为最新数据
          console.log(`更新本地章节状态: ${updatedChapters.length}章`);
          setChapters(updatedChapters);
          
          // 🔥 从服务器数据恢复优化中和重写中的章节状态
          // 保留已有的状态，并添加服务器返回的状态
          setOptimizingChapters(prev => {
            const newSet = new Set(prev); // 保留已有的状态
            updatedChapters.forEach(chapter => {
              const chapterId = (chapter._id || chapter.id || '').toString();
              if (chapterId && chapter.isOptimizing) {
                newSet.add(chapterId);
                console.log(`🔄 从服务器恢复优化中状态: 第${chapter.index}章 (ID: ${chapterId})`);
              } else if (chapterId && !chapter.isOptimizing && newSet.has(chapterId)) {
                // 如果服务器返回的章节不再是优化中，则从集合中移除
                newSet.delete(chapterId);
                console.log(`✅ 清除已完成的优化状态: 第${chapter.index}章 (ID: ${chapterId})`);
              }
            });
            return newSet;
          });
          
          // 🔥 重写章节已改为同步模式，不再需要复杂的状态同步
          setRewritingChapters(prev => {
            const newSet = new Set();
            
            updatedChapters.forEach(chapter => {
              const chapterId = (chapter._id || chapter.id || '').toString();
              
              // 只有当服务器明确标记为重写中时才添加到集合
              if (chapterId && chapter.isRewriting === true) {
                newSet.add(chapterId);
                console.log(`🔄 从服务器恢复重写中状态: 第${chapter.index}章 (ID: ${chapterId})`);
              } else if (chapterId && chapter.isRewriting === false) {
                // 服务器明确标记为不再重写中，确保从集合中移除
                if (prev.has(chapterId)) {
                  console.log(`✅ 清除已完成的重写状态: 第${chapter.index}章 (ID: ${chapterId})`);
                }
              }
            });
            
            // 清理本地集合中不存在的章节（服务器没有返回这些章节）
            prev.forEach(chapterId => {
              if (!newSet.has(chapterId)) {
                // 检查这个章节是否还在章节列表中
                const chapter = updatedChapters.find(ch => {
                  const chId = (ch.id || ch._id || '').toString();
                  return chId === chapterId;
                });
                
                if (!chapter) {
                  // 章节不存在，清除状态
                  console.log(`✅ 清除不存在的章节的重写状态: ID ${chapterId}`);
                  return; // 不添加到新集合
                }
              }
            });
            
            return newSet;
          });
          
          // 更新本地存储
          saveChaptersToLocalStorage(novelData, updatedChapters);
        }
        
        console.log('小说数据获取和更新成功');
        
        // ========== 智能跳转到最后编辑的界面 ==========
        // 🎯 新增：根据配置决定是否跳转标签页
        if (preserveTab && savedTab !== null) {
          // 如果需要保持标签页，恢复之前保存的标签
          if (savedTab !== activeTab) {
            console.log(`🎯 恢复保存的标签页: ${savedTab} (当前: ${activeTab})`);
            setActiveTab(savedTab);
          } else {
            console.log(`🎯 标签页已是目标值: ${savedTab}，无需恢复`);
          }
        } else if (shouldShowLoading) {
          // 只在首次加载时执行跳转逻辑
          // 判断跳转到哪个标签页
          let targetTab = 0; // 默认跳转到基础设置
          
          // 🔥 使用与loadExistingNovel相同的跳转逻辑，避免重复判断
          // 优先级：章节内容 > 故事结构（生成中/已完成） > 大纲 > 基础设置
          
          // 检查是否有章节内容
          const hasChapterContent = novelData.chapters && novelData.chapters.some(ch => 
            ch.content && ch.content.trim().length > 0
          );
          
          if (hasChapterContent) {
            // 优先级1：如果有章节内容，跳转到章节内容页面
            targetTab = 3;
            console.log('[useEffect跳转] ✅ 检测到章节内容，跳转到章节内容页面');
          } else if (novelData.storyStructure) {
            // 优先级2：检查故事结构的实际生成状态
            const structureStatus = novelData.storyStructure.generationStatus;
            const isStructureGenerating = structureStatus === 'generating' || structureStatus === 'retrying';
            const isStructureCompleted = structureStatus === 'completed' || novelData.storyStructure.isGenerated;
            
            if (isStructureGenerating || isStructureCompleted) {
              // 只有在故事结构正在生成或已完成时才跳转到故事结构页
              targetTab = 2;
              console.log(`[useEffect跳转] ✅ 检测到故事结构${isStructureCompleted ? '已生成' : '生成中'}，跳转到故事结构页面`);
            } else if (novelData.outline && novelData.outline.trim().length > 0) {
              // 优先级3：故事结构未生成，但有大纲，跳转到大纲页
              targetTab = 1;
              console.log('[useEffect跳转] ✅ 检测到大纲内容，跳转到大纲页面');
            } else {
              // 优先级4：默认跳转到基础设置
              targetTab = 0;
              console.log('[useEffect跳转] 📝 无进度记录，停留在基础设置页面');
            }
          } else if (novelData.outline && novelData.outline.trim().length > 0) {
            // 优先级3：没有故事结构，但有大纲，跳转到大纲页
            targetTab = 1;
            console.log('[useEffect跳转] ✅ 检测到大纲内容，跳转到大纲页面');
          } else {
            // 优先级4：什么都没有，跳转到基础设置
            targetTab = 0;
            console.log('[useEffect跳转] 📝 无任何内容，停留在基础设置页面');
          }
          
          // 🔥 只在目标标签页与当前标签页不同时才执行跳转，避免重复渲染
          if (targetTab !== activeTab) {
            console.log(`[useEffect跳转] 从标签页 ${activeTab} 跳转到 ${targetTab}`);
            setActiveTab(targetTab);
          } else {
            console.log(`[useEffect跳转] 已在目标标签页 ${targetTab}，无需跳转`);
          }
        }
        
        // 🎯 恢复章节展开状态（兼容新旧格式：旧版数字章节号，新版 chapter-N 字符串）
        if (preserveExpanded && savedExpanded !== null) {
          const isNew = String(savedExpanded).startsWith('chapter-');
          const chapterNum = isNew ? Number(String(savedExpanded).replace('chapter-', '')) : Number(savedExpanded);
          const GROUP_SIZE = 50;
          const groupIndex = Math.floor((chapterNum - 1) / GROUP_SIZE);
          console.log(`🎯 恢复展开的章节: ${chapterNum}，组: ${groupIndex}`);
          setExpandedOutlineGroup(groupIndex);
          setExpandedOutlineChapter(chapterNum);
        }
      }
    } catch (error) {
      console.error('获取小说数据失败:', error);
      setLoadError(error.message || '加载小说数据失败');
      showMessage(`加载小说数据失败: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [setLoading, setNovel, setTitle, setNovelSettings, setOutline, setOutlineText, setChapters, setLoadError, chapters, showMessage, saveChaptersToLocalStorage, setActiveTab, activeTab, expandedOutlineChapter, setExpandedOutlineChapter, novel, chapterGenerationStatus]);
  
  // 🔥 处理标题编辑 - 移到组件主作用域，以便在章节列表中使用
  // 🔥 处理标题编辑 - 打开对话框
  const handleTitleEditStart = useCallback((chapter = null) => {
    // 如果传入了章节对象，使用它；否则使用当前选中的章节
    const targetChapter = chapter || selectedChapterObj;
    if (!targetChapter) {
      showMessage('请先选择章节', 'warning');
      return;
    }

    const currentTitle = targetChapter.title || '';
    const chapterIndex = targetChapter.index;
    const chapterNumber = chapterIndex;

    // 解析标题格式 "第x章：标题内容"
    const titleMatch = currentTitle.match(/^(第\d+章：)(.*)$/);
    let prefix = '';
    let titleAfterColon = '';

    if (titleMatch) {
      prefix = titleMatch[1]; // "第x章："
      titleAfterColon = titleMatch[2] || ''; // 冒号后面的内容
    } else {
      // 如果没有匹配到格式，尝试提取章节号
      const numberMatch = currentTitle.match(/第(\d+)章/);
      if (numberMatch) {
        prefix = `第${chapterNumber}章：`;
        titleAfterColon = currentTitle.replace(/^第\d+章：?\s*/, '');
      } else {
        // 如果完全没有格式，创建新格式
        prefix = `第${chapterNumber}章：`;
        titleAfterColon = currentTitle;
      }
    }

    setChapterTitleDialog({
      open: true,
      chapterIndex,
      chapterNumber,
      prefix,
      editedTitle: titleAfterColon,
      originalTitle: currentTitle,
      saving: false
    });
  }, [selectedChapterObj, showMessage]);

  // 🔥 处理内容编辑 - 打开对话框
  const handleContentEditStart = useCallback((chapter = null) => {
    // 如果传入了章节对象，使用它；否则使用当前选中的章节
    const targetChapter = chapter || selectedChapterObj;
    if (!targetChapter) {
      showMessage('请先选择章节', 'warning');
      return;
    }

    const currentContent = targetChapter.content || '';
    const chapterIndex = targetChapter.index;
    const chapterNumber = chapterIndex;

    if (!currentContent || currentContent.trim().length === 0) {
      showMessage('该章节还没有内容', 'warning');
      return;
    }

    setChapterContentDialog({
      open: true,
      chapterIndex,
      chapterNumber,
      editedContent: currentContent,
      originalContent: currentContent,
      saving: false
    });
  }, [selectedChapterObj, showMessage]);

  // ============================================================
  // 番茄发布引导（发布由 KM发布助手 桌面端完成，网站只做引导）
  // ============================================================
  const handlePublishGuide = useCallback(() => {
    showMessage('章节发布请在电脑端打开 KM发布助手 完成：登录本账号 → 选择本小说 → 勾选章节一键发布到番茄', 'info');
  }, [showMessage]);

  // 渲染章节内容区域
  const renderChapterContentArea = useCallback(() => {
    // 使用状态中的selectedChapterObj而不是重新查找
    
    if (!selectedChapterObj) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4 }}>
          <Typography variant="body1" align="center" sx={{ mb: 2 }}>
          请选择或添加一个章节
        </Typography>
          <Button 
            variant="outlined" 
            startIcon={<AddIcon />}
            onClick={() => setOpenDialog(true)}
          >
            添加新章节
          </Button>
        </Box>
      );
    }
    
    // 检查是否正在生成或优化内容
    const isGenerating = selectedChapterObj.generating || generatingChapterId === selectedChapter;
    const isOptimizing = optimizingChapters.has(selectedChapter);
    
    // 判断是否为首次提交的章节（首章）
    const isFirstChapter = selectedChapter === 1;
    const hasContent = selectedChapterObj.content && selectedChapterObj.content.trim().length > 0;
    const isFirstSubmission = isFirstChapter && (!hasContent || isGenerating);
    
    // 确保使用最新的章节内容，并移除第一行标题（如果内容以标题行开头）
    const rawContent = selectedChapterObj.content || currentChapterContent || '';
    const displayContent = (() => {
      if (!rawContent) return '';
      const lines = rawContent.split('\n');
      const firstLine = lines[0]?.trim() || '';
      // 如果第一行是标题格式（第X章：标题 或 第X章 标题），则跳过第一行
      const isTitleLine = /^第[一二三四五六七八九十百千万零\d]+章[：:]\s*\S/.test(firstLine) ||
        /^第[一二三四五六七八九十百千万零\d]+章\s+\S/.test(firstLine) ||
        /^#+\s*第[一二三四五六七八九十百千万零\d]+章/.test(firstLine);
      if (isTitleLine && lines.length > 1) {
        return lines.slice(1).join('\n').trim();
      }
      return rawContent;
    })();
    
    // 优化标题格式：第X章：章节标题
    // 如果章节有内容且标题包含冒号，保留完整标题
    // 如果章节有内容但标题不包含冒号，格式化为"第X章：标题内容"
    // 如果章节没有内容，只显示"第X章"
    const formattedTitle = hasContent ? 
      (selectedChapterObj.title.match(/^第\d+章[：:](.+)$/) ? 
        selectedChapterObj.title : 
        selectedChapterObj.title !== `第${selectedChapter}章` ?
          `第${selectedChapter}章：${selectedChapterObj.title.replace(/^第\d+章[：:]?\s*/, '')}` :
          `第${selectedChapter}章`
      ) : `第${selectedChapter}章`;

    // 复制章节内容函数
    const handleCopyContent = () => {
      if (!displayContent) {
        showMessage("没有可复制的内容", "warning");
        return;
      }
      
      // 创建临时textarea
      const textarea = document.createElement('textarea');
      textarea.value = displayContent;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      
      try {
        // 执行复制
        document.execCommand('copy');
          showMessage("章节内容已复制到剪贴板", "success");
      } catch (err) {
          console.error('复制失败:', err);
        showMessage("复制失败", "error");
      } finally {
        // 清理临时元素
        document.body.removeChild(textarea);
      }
    };
    
    // 复制章节标题函数
    const handleCopyTitle = () => {
      // 使用状态中的selectedChapterObj
      if (!selectedChapterObj) {
        showMessage("没有可复制的标题", "warning");
        return;
      }

      // 获取纯文本标题（去除《》等符号）
      const title = selectedChapterObj.title.replace(/[《》]/g, '').trim();
      
      // 创建临时textarea
      const textarea = document.createElement('textarea');
      textarea.value = title;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      
      try {
        // 执行复制
        document.execCommand('copy');
        showMessage("章节标题已复制到剪贴板", "success");
      } catch (err) {
        console.error('复制失败:', err);
        showMessage("复制失败", "error");
      } finally {
        // 清理临时元素
        document.body.removeChild(textarea);
      }
    };

    // 🔥 处理标题编辑 - 已移到组件主作用域，这里不再需要

    const handleTitleEditCancel = () => {
      setChapterTitleEdit(prev => ({
        ...prev,
        isEditing: false,
        editedTitle: prev.originalTitle
      }));
    };

    const handleTitleSave = async () => {
      if (!chapterTitleEdit.editedTitle.trim()) {
        showMessage('章节标题不能为空', 'warning');
        return;
      }
      
      setChapterTitleEdit(prev => ({ ...prev, saving: true }));
      const result = await saveChapterTitle(selectedChapter, chapterTitleEdit.editedTitle);
      
      if (result.success) {
        setChapterTitleEdit(prev => ({
          ...prev,
          isEditing: false,
          saving: false,
          originalTitle: prev.editedTitle
        }));
      } else {
        setChapterTitleEdit(prev => ({ ...prev, saving: false }));
      }
    };

    // 🔥 处理内容编辑 - 已移到组件主作用域，这里不再需要

    const handleContentEditCancel = () => {
      setChapterContentEdit(prev => ({
        ...prev,
        isEditing: false,
        editedContent: prev.originalContent,
        hasUnsavedChanges: false
      }));
    };

    const handleContentSave = async () => {
      setChapterContentEdit(prev => ({ ...prev, saving: true }));
      const result = await saveChapterContentToServer(selectedChapter, chapterContentEdit.editedContent);
      
      if (result.success) {
        setChapterContentEdit(prev => ({
          ...prev,
          isEditing: false,
          saving: false,
          originalContent: prev.editedContent,
          hasUnsavedChanges: false
        }));
        setCurrentChapterContent(chapterContentEdit.editedContent);
        setLastSavedContent(chapterContentEdit.editedContent);
      } else {
        setChapterContentEdit(prev => ({ ...prev, saving: false }));
      }
    };

    const handleContentChange = (e) => {
      const newContent = e.target.value;
      setChapterContentEdit(prev => ({
        ...prev,
        editedContent: newContent,
        hasUnsavedChanges: newContent !== prev.originalContent
      }));
    };
    
    return (
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* 章节标题和状态 */}
        <Accordion 
          defaultExpanded 
          sx={{ 
            mb: 2, 
            borderRadius: '8px',
            '&:before': { display: 'none' },
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            overflow: 'visible',
            '& .MuiAccordionDetails-root': {
              overflow: 'visible'
            }
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ 
              backgroundColor: 'rgba(0,30,60,0.02)',
              borderRadius: '8px 8px 0 0'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
              {chapterTitleEdit.isEditing ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, mr: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    value={chapterTitleEdit.editedTitle}
                    onChange={(e) => setChapterTitleEdit(prev => ({ ...prev, editedTitle: e.target.value }))}
                    disabled={chapterTitleEdit.saving}
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(255, 255, 0, 0.1)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 0, 0.15)',
                        }
                      }
                    }}
                    placeholder="请输入章节标题"
                  />
                  <IconButton
                    size="small"
                    onClick={handleTitleSave}
                    disabled={chapterTitleEdit.saving || !chapterTitleEdit.editedTitle.trim()}
                    color="primary"
                    sx={{ 
                      bgcolor: 'primary.main',
                      color: 'white',
                      '&:hover': { bgcolor: 'primary.dark' },
                      '&:disabled': { bgcolor: 'action.disabledBackground' }
                    }}
                  >
                    {chapterTitleEdit.saving ? <CircularProgress size={16} /> : <SaveIcon />}
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={handleTitleEditCancel}
                    disabled={chapterTitleEdit.saving}
                    color="inherit"
                  >
                    <CancelIcon />
                  </IconButton>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', textAlign: 'left' }}>
                {formattedTitle}
              </Typography>
                </Box>
              )}
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip 
                  size="small" 
                  label={displayContent ? `${displayContent.length}字` : '0字'} 
                  color={displayContent && displayContent.length > 0 ? "primary" : "default"}
                  variant={displayContent && displayContent.length > 0 ? "filled" : "outlined"} 
                  sx={{ 
                    height: 20, 
                    fontSize: '0.7rem',
                    fontWeight: 'bold'
                  }} 
                />
                {selectedChapterObj?.outline && (
                  <Chip 
                    size="small" 
                    label="大纲" 
                    variant="filled" 
                    color="secondary"
                    sx={{ 
                      height: 20, 
                      fontSize: '0.7rem',
                      '& .MuiChip-label': { px: 1, py: 0 }
                    }} 
                  />
                )}
            {(isGenerating || isOptimizing) && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  <Typography variant="caption" color={isFirstSubmission ? "primary" : "text.secondary"} sx={{ fontWeight: isFirstSubmission ? 'bold' : 'normal' }}>
                    {isGenerating ? (isFirstSubmission ? '优先生成中' : '生成中') : '优化中'}
                </Typography>
              </Box>
                  )}
                {selectedChapterObj?.isOptimized && (
                  <Box sx={{ minWidth: 16, width: 16, height: 16, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(76, 175, 80, 0.15)', color: '#66bb6a', border: '1px solid rgba(76, 175, 80, 0.25)', fontSize: '0.6rem', fontWeight: 700, flexShrink: 0 }}>
                    优
                  </Box>
                )}
                {selectedChapterObj?.isRewritten && (
                  <Box sx={{ minWidth: 16, width: 16, height: 16, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255, 152, 0, 0.15)', color: '#ffa726', border: '1px solid rgba(255, 152, 0, 0.25)', fontSize: '0.6rem', fontWeight: 700, flexShrink: 0 }}>
                    重
                  </Box>
                )}
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ 
            p: 2, 
            overflow: 'visible',
            minHeight: 'auto',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* 章节操作按钮 */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 1.5, 
              mb: 2,
              width: '100%',
              overflow: 'visible'
            }}>
              {/* 复制和编辑按钮组 - 2x2布局 */}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 1,
                width: '100%',
                overflow: 'visible'
              }}>
                {/* 第一行：复制标题和复制内容 */}
                <Box sx={{ 
                  display: 'flex', 
                  gap: 1, 
                  flexWrap: 'wrap',
                  width: '100%'
                }}>
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopyTitle}
              disabled={!selectedChapterObj}
                    sx={{ 
                      flex: 1, 
                      borderRadius: '20px', 
                      minWidth: {xs: '45%', sm: 0},
                      fontSize: {xs: '0.75rem', sm: '0.875rem'}
                    }}
            >
              复制标题
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopyContent}
              disabled={!displayContent}
                    sx={{ 
                      flex: 1, 
                      borderRadius: '20px', 
                      minWidth: {xs: '45%', sm: 0},
                      fontSize: {xs: '0.75rem', sm: '0.875rem'}
                    }}
            >
              复制内容
            </Button>
                </Box>
                
                {/* 第二行：手动改标题和手动改内容 */}
                <Box sx={{ 
                  display: 'flex', 
                  gap: 1, 
                  flexWrap: 'wrap',
                  width: '100%'
                }}>
                  {(() => {
                    const chapterId = selectedChapterObj?._id || selectedChapterObj?.id;
                    const isRewriting = chapterId ? rewritingChapters.has(chapterId.toString()) : false;
                    return (
                      <>
                        <Button
                          variant="outlined"
                          startIcon={<EditIcon />}
                          onClick={handleTitleEditStart}
                          disabled={!selectedChapterObj || isGenerating || isOptimizing || isRewriting}
                          sx={{ 
                            flex: 1, 
                            borderRadius: '20px',
                            borderColor: 'primary.main',
                            color: 'primary.main',
                            minWidth: {xs: '45%', sm: 0},
                            fontSize: {xs: '0.75rem', sm: '0.875rem'},
                            '&:hover': {
                              borderColor: 'primary.dark',
                              backgroundColor: 'rgba(25, 118, 210, 0.08)'
                            }
                          }}
                        >
                          手动改标题
                        </Button>
                        
                        <Button
                          variant="outlined"
                          startIcon={<EditIcon />}
                          onClick={handleContentEditStart}
                          disabled={!displayContent || isGenerating || isOptimizing || isRewriting}
                          sx={{ 
                            flex: 1, 
                            borderRadius: '20px',
                            borderColor: 'primary.main',
                            color: 'primary.main',
                            minWidth: {xs: '45%', sm: 0},
                            fontSize: {xs: '0.75rem', sm: '0.875rem'},
                            '&:hover': {
                              borderColor: 'primary.dark',
                              backgroundColor: 'rgba(25, 118, 210, 0.08)'
                            }
                          }}
                        >
                          手动改内容
                        </Button>
                      </>
                    );
                  })()}
                </Box>

                {/* 第三行：发布到番茄（纯引导，发布由KM发布助手桌面端完成） */}
                <Box sx={{
                  display: 'flex',
                  gap: 1,
                  flexWrap: 'wrap',
                  width: '100%'
                }}>
                  <Tooltip title={
                    selectedChapterObj?.publishStatus === 'published'
                      ? (selectedChapterObj.fanqieUrl || '该章节已成功发布到番茄小说')
                      : '发布请在电脑端打开 KM发布助手：登录本账号后选择本小说即可一键发布'
                  }>
                    <Button
                      variant="outlined"
                      startIcon={<CloudUploadIcon />}
                      onClick={handlePublishGuide}
                      disabled={!displayContent}
                      sx={{
                        flex: 1,
                        borderRadius: '20px',
                        minWidth: { xs: '45%', sm: 0 },
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        borderColor: selectedChapterObj?.publishStatus === 'published' ? 'success.main' : 'warning.main',
                        color: selectedChapterObj?.publishStatus === 'published' ? 'success.main' : 'warning.main',
                        '&:hover': {
                          borderColor: selectedChapterObj?.publishStatus === 'published' ? 'success.dark' : 'warning.dark',
                          backgroundColor: 'rgba(255, 152, 0, 0.08)'
                        }
                      }}
                    >
                      {selectedChapterObj?.publishStatus === 'published' ? '已发布番茄' : '发布到番茄'}
                    </Button>
                  </Tooltip>
                </Box>
          </Box>
        </Box>
          </AccordionDetails>
        </Accordion>
        
        {/* 显示生成内容的进度条 */}
        {isGenerating && (
          <LinearProgress 
            sx={{ mb: 2, height: 4, borderRadius: 2 }}
            variant="indeterminate"
          />
        )}
        
        {/* 章节内容文本区域 - 使用Accordion包装 */}
        <Accordion 
          defaultExpanded 
          sx={{ 
            borderRadius: { xs: '6px', sm: '8px' },
            '&:before': { display: 'none' },
            boxShadow: { xs: 'none', sm: '0 2px 8px rgba(0,0,0,0.08)' }
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ 
              backgroundColor: 'rgba(0,30,60,0.02)',
              borderRadius: { xs: '6px 6px 0 0', sm: '8px 8px 0 0' },
              minHeight: { xs: '48px', sm: '56px' },
              px: { xs: 1.5, sm: 2 }
            }}
          >
            <Typography variant="subtitle1" sx={{ 
              fontWeight: 'bold',
              fontSize: { xs: '0.95rem', sm: '1rem' }
            }}>
              章节内容
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: { xs: 1.5, sm: 2 } }}>
            {chapterContentEdit.isEditing ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Alert 
                  severity="info" 
                  icon={<EditIcon />}
                  sx={{ 
                    bgcolor: 'rgba(33, 150, 243, 0.1)',
                    border: '1px solid rgba(33, 150, 243, 0.3)',
                    '& .MuiAlert-icon': { color: 'primary.main' }
                  }}
                >
                  <AlertTitle>编辑模式</AlertTitle>
                  您正在编辑章节内容，修改后请点击保存按钮保存更改。
                  {chapterContentEdit.hasUnsavedChanges && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'warning.main' }}>
                      您有未保存的修改
                    </Typography>
                  )}
                </Alert>
                <TextField
                  fullWidth
                  multiline
                  rows={isMobile ? 12 : 15}
                  variant="outlined"
                  placeholder="在这里编辑章节内容..."
                  value={chapterContentEdit.editedContent}
                  onChange={handleContentChange}
                  disabled={chapterContentEdit.saving || isGenerating || isOptimizing}
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(255, 255, 0, 0.05)',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 0, 0.08)',
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'rgba(255, 255, 0, 0.1)',
                      },
                      '& textarea': {
                        fontSize: { xs: '0.95rem', sm: '1rem' },
                        lineHeight: { xs: 1.8, sm: 1.6 },
                        padding: { xs: '12px 14px', sm: '16px' },
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word'
                      }
                    }
                  }}
                />
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    onClick={handleContentEditCancel}
                    disabled={chapterContentEdit.saving}
                    startIcon={<CancelIcon />}
                  >
                    取消
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleContentSave}
                    disabled={chapterContentEdit.saving || isGenerating || isOptimizing}
                    startIcon={chapterContentEdit.saving ? <CircularProgress size={16} /> : <SaveIcon />}
                    color="primary"
                  >
                    {chapterContentEdit.saving ? '保存中...' : '保存'}
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    章节内容（只读模式）
                  </Typography>
                </Box>
        <TextField
          fullWidth
          multiline
          rows={isMobile ? 12 : 15}
          variant="outlined"
          placeholder="等待内容生成..."
          value={displayContent}
          InputProps={{
            readOnly: true,  // Material-UI 层面只读
          }}
          inputProps={{
            readOnly: true,  // HTML input 层面只读
            style: { cursor: 'default' }  // 改变光标样式
          }}
          onFocus={(e) => e.target.blur()}  // 🔥 防止移动端键盘弹出：聚焦时立即失焦
          onClick={(e) => e.preventDefault()}  // 🔥 防止点击事件
        sx={{ 
            '& .MuiOutlinedInput-root': {
              '& textarea': {
                fontSize: { xs: '0.95rem', sm: '1rem' },
                lineHeight: { xs: 1.8, sm: 1.6 },
                padding: { xs: '12px 14px', sm: '16px' },
                wordBreak: 'break-word',
                overflowWrap: 'break-word'
              }
            }
          }}
              disabled={isGenerating || isOptimizing}
        />
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      </Box>
    );
  }, [
    chapters, 
    selectedChapter, 
    currentChapterContent, 
    isOptimizing, 
    generatingChapterId,
    showMessage,
    novel,
    selectedChapterObj,
    novelSettings,
    getPreviousChapterContent,
    fetchNovelData,
    isGenerating,
    debouncedSaveChapterContent,
    setOpenDialog,
    chapterTitleEdit,
    chapterContentEdit,
    saveChapterTitle,
    saveChapterContentToServer,
    setCurrentChapterContent,
    setLastSavedContent
  ]);

  // 渲染大纲生成状态 - 科技感设计
  const renderOutlineGenerationStatus = useCallback(() => {
    if (!outlineGenerationStatus.generating) return null;
    
    return (
      <Paper
        className="outline-gen-tech-panel"
        sx={{
          p: { xs: 2, sm: 3 },
          mt: 2,
          background: 'linear-gradient(135deg, rgba(0, 15, 30, 0.95) 0%, rgba(0, 10, 25, 0.98) 100%) !important',
          border: '1px solid rgba(0, 212, 255, 0.2)',
          borderRadius: '12px',
          boxShadow: '0 0 30px rgba(0, 212, 255, 0.08), inset 0 1px 0 rgba(0, 212, 255, 0.1)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* 科技网格背景 */}
        <Box sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `
            linear-gradient(rgba(0, 212, 255, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 212, 255, 0.03) 1px, transparent 1px)
          `,
          backgroundSize: '20px 20px',
          pointerEvents: 'none',
        }} />

        {/* 扫描线动画 */}
        <Box sx={{
          position: 'absolute',
          inset: 0,
          background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0, 212, 255, 0.015) 2px, rgba(0, 212, 255, 0.015) 4px)',
          pointerEvents: 'none',
          animation: 'scanMove 4s linear infinite',
          '@keyframes scanMove': {
            '0%': { transform: 'translateY(0)' },
            '100%': { transform: 'translateY(20px)' },
          },
        }} />

        {/* 顶部进度条 - 线条式无百分比 */}
        <Box sx={{ mb: 2.5, position: 'relative', zIndex: 1 }}>
          <Box sx={{
            height: '3px',
            borderRadius: '2px',
            bgcolor: 'rgba(0, 212, 255, 0.08)',
            overflow: 'hidden',
            position: 'relative',
          }}>
            {/* 流动光效 */}
            <Box sx={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(90deg, transparent 0%, rgba(0, 212, 255, 0.6) 50%, rgba(0, 255, 200, 0.8) 70%, rgba(0, 212, 255, 0.6) 50%, transparent 100%)',
              backgroundSize: '200% 100%',
              animation: 'techFlow 2s linear infinite',
              '@keyframes techFlow': {
                '0%': { backgroundPosition: '200% 0' },
                '100%': { backgroundPosition: '-200% 0' },
              },
            }} />
            {/* 发光端点 */}
            <Box sx={{
              position: 'absolute',
              right: 0,
              top: '-2px',
              width: '7px',
              height: '7px',
              borderRadius: '50%',
              background: '#00ff88',
              boxShadow: '0 0 8px #00ff88, 0 0 16px rgba(0, 255, 136, 0.6)',
              animation: 'dotPulse 1s ease-in-out infinite',
              '@keyframes dotPulse': {
                '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                '50%': { opacity: 0.6, transform: 'scale(1.4)' },
              },
            }} />
          </Box>
        </Box>

        {/* 主体内容 */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, position: 'relative', zIndex: 1 }}>
          {/* 标题行 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {/* 旋转数据图标 */}
            <Box sx={{
              width: 36,
              height: 36,
              borderRadius: '10px',
              border: '1px solid rgba(0, 212, 255, 0.4)',
              background: 'rgba(0, 212, 255, 0.06)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 0 12px rgba(0, 212, 255, 0.15)',
              animation: 'iconGlow 2s ease-in-out infinite',
              '@keyframes iconGlow': {
                '0%, 100%': { boxShadow: '0 0 12px rgba(0, 212, 255, 0.15)' },
                '50%': { boxShadow: '0 0 20px rgba(0, 212, 255, 0.3)' },
              },
            }}>
              <CircularProgress size={18} sx={{ color: '#00d4ff', animation: 'spin 1.5s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />
            </Box>
            <Typography variant="body2" sx={{
              color: 'rgba(255,255,255,0.9)',
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              fontWeight: 500,
              letterSpacing: '0.02em',
            }}>
              {outlineGenerationStatus.message || '正在生成大纲，请稍候...'}
            </Typography>
          </Box>
        </Box>
      </Paper>
    );
  }, [outlineGenerationStatus]);

  // 渲染章节生成状态
  const renderGenerationStatus = useCallback(() => {
    // 生成状态标签（仅移动端挂载）：只显示章节数，不带百分比/进度条（桌面端已有其他进度显示）
    const shouldShow = batchGenerating || !!activeGeneratingChapter;
    if (!shouldShow) return null;
    return (
      <Box
        sx={{
          mt: '2px',
          px: 1.25,
          py: 0.75,
          borderRadius: '8px',
          background: 'rgba(25, 118, 210, 0.08)',
          border: '1px solid rgba(25, 118, 210, 0.28)',
          display: 'flex',
          alignItems: 'center',
          animation: 'novelFadeIn 0.3s ease both'
        }}
      >
        <CircularProgress size={12} sx={{ mr: 0.75 }} color="primary" />
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#42a5f5', whiteSpace: 'nowrap' }}>
          {batchGenerating
            ? `批量生成中 ${batchProgress.current}/${batchProgress.total} 章`
            : `正在生成第${activeGeneratingChapter}章`}
        </Typography>
      </Box>
    );
  }, [batchGenerating, activeGeneratingChapter, batchProgress]);

  // 渲染批量生成浮动窗口
  const renderBatchProgressFloat = useCallback(() => {
    // 即使不是批量生成，如果有单个章节在生成，也显示进度窗口
    if (!batchGenerating && !activeGeneratingChapter) return null;
    
    // 获取当前生成中章节的状态
    const currentChapterStatus = activeGeneratingChapter
      ? chapterGenerationStatus.find(s => s.chapterIndex === activeGeneratingChapter) 
      : null;
    
    return (
      <Paper
        ref={batchFloatRef}
        elevation={6}
        sx={{
          position: 'fixed',
          right: batchFloatPos.right,
          // 🔥 移动端：宽度自适应 + 抬到底部导航栏上方（含安全区）；桌面端保持可拖动位置
          bottom: { xs: 'calc(70px + env(safe-area-inset-bottom, 0px))', sm: batchFloatPos.bottom },
          width: { xs: 'calc(100vw - 32px)', sm: 350 },
          maxWidth: 'calc(100vw - 32px)',
          borderRadius: 2,
          overflow: 'hidden',
          zIndex: 1300,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          cursor: dragging ? 'move' : 'default',
          userSelect: 'none',
          border: '1px solid rgba(25, 118, 210, 0.2)',
        }}
      >
        <Box 
          sx={{ 
            p: 1.5, 
            backgroundImage: 'linear-gradient(to right, #1976d2, #2196f3)',
            color: 'white', 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            cursor: 'move' 
          }}
          onMouseDown={handleBatchFloatMouseDown}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AutorenewIcon sx={{ mr: 1, animation: 'spin 2s linear infinite', '@keyframes spin': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' }
            } }} />
            <Typography variant="subtitle2">
              {batchGenerating ? '批量生成进度' : '章节生成进度'}
              <span style={{ fontSize: '0.8rem', opacity: 0.9, marginLeft: '4px' }}>
                (可在后台继续)
              </span>
            </Typography>
          </Box>
          <IconButton 
            size="small" 
            sx={{ color: 'white' }}
            onClick={() => {
              if (window.confirm('确定要停止生成吗？当前进度将丢失。')) {
                // 调用abort()中断所有进行中的API请求
                if (batchGenAbortControllerRef.current) {
                  batchGenAbortControllerRef.current.abort();
                  batchGenAbortControllerRef.current = null;
                }
                
                setBatchGenerating(false);
                setIsGenerating(false);
                setGeneratingChapterId(null);
                showMessage('已停止生成', 'info');
              }
            }}
          >
            <StopIcon fontSize="small" />
          </IconButton>
        </Box>
        
        <Box sx={{ p: 2 }}>
          {batchGenerating && (
            <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" fontWeight="medium">
              总进度: {batchProgress.current}/{batchProgress.total} 章
            </Typography>
            <Typography variant="body2" color="primary.main" fontWeight="bold">
              {Math.round(batchProgress.progress)}%
            </Typography>
          </Box>
          
          <LinearProgress 
            variant="determinate" 
            value={batchProgress.progress}
            sx={{ height: 8, borderRadius: 5, mb: 1.5 }}
          />
            </>
          )}
          
          {activeGeneratingChapter && (
              <Box 
                sx={{ 
                mt: batchGenerating ? 2 : 0, 
                  p: 1.5, 
                  bgcolor: 'rgba(25, 118, 210, 0.08)', 
                  borderRadius: 1,
                  border: '1px solid rgba(25, 118, 210, 0.2)'
                }}
              >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  当前生成：第 {activeGeneratingChapter} 章
                </Typography>
                {currentChapterStatus && (
                  <Typography variant="subtitle2" fontWeight="bold" color="primary.main">
                        {currentChapterStatus.progress}%
                      </Typography>
                )}
                    </Box>
                    
              {currentChapterStatus && (
                <>
                    <LinearProgress 
                      variant="determinate" 
                      value={currentChapterStatus.progress}
                    sx={{ height: 6, borderRadius: 3, mt: 1, mb: 1 }}
                    />
                    
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      gap: 0.5
                    }}>
                      {currentChapterStatus.progress < 100 ? (
                        <>
                          <CircularProgress size={12} thickness={6} />
                          {currentChapterStatus.status || "生成中..."}
                        </>
                      ) : (
                        <>
                          <CheckCircleIcon fontSize="inherit" sx={{ color: 'success.main' }} />
                          已完成
                        </>
                      )}
                    </Typography>
                    
                        <Typography variant="caption" color="text.secondary">
                      预计剩余时间: {Math.max(1, Math.round((100 - currentChapterStatus.progress) / 10))} 分钟
                        </Typography>
                      </Box>
                  </>
                )}
              </Box>
          )}
              
          {/* 任务列表信息 */}
          <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* 已完成章节 */}
              {batchProgress.current > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" color="success.main" sx={{ display: 'flex', alignItems: 'center' }}>
                    <CheckCircleIcon fontSize="inherit" sx={{ mr: 0.5 }} />
                    {`已完成 ${batchProgress.current} 章`}
                  </Typography>
                
                <Typography variant="caption" color="text.secondary">
                  总预计时间: {batchProgress.total * 2} 分钟
                  </Typography>
                </Box>
              )}
            
            {/* 已移除任务列表链接，因为我们直接在章节列表中显示进度 */}
          </Box>
        </Box>
      </Paper>
    );
  }, [batchGenerating, batchProgress, generatingChapterId, chapterGenerationStatus, dragging, batchFloatPos, navigate]);

  // 处理保存章节
  const handleSaveChapter = useCallback(() => {
    // 基本实现
    saveCurrentChapter();
    showMessage("章节已保存", "success");
  }, [saveCurrentChapter]);

  // 添加章节
  const handleAddChapter = useCallback(() => {
    // 基本实现
    const newChapterIndex = chapters.length > 0 
      ? Math.max(...chapters.map(ch => ch.index)) + 1
      : 1;
      
    const newChapter = {
      index: newChapterIndex,
      title: `第${newChapterIndex}章`,
      content: '',
      outline: ''
    };
    
    setChapters([...chapters, newChapter]);
    saveChaptersToLocalStorage(novel, [...chapters, newChapter]);
    setSelectedChapter(newChapterIndex);
    showMessage(`已添加第${newChapterIndex}章`, "success");
  }, [chapters, saveChaptersToLocalStorage, novel]);

  // 选项数据
  const platforms = ['番茄小说网', '七猫小说网', '起点中文网', '晋江文学城', '纵横中文网', '17K小说网', '掌阅文学'];
  const perspectives = ['第一人称', '第三人称', '全知视角', '多重视角'];
  const lengths = ['中篇', '长篇', '超长篇'];
  const styles = [
    '写实风格', '过AI检测风格', '浪漫风格', '诗意风格', '硬核风格', '轻松诙谐', 
    '热血风格', '悬疑风格', '古典风格', '现代风格', '青春风格',
    '商业风格', '学院风格', '江湖风格', '都市风格', '乡土风格',
    '科技风格', '魔幻风格', '奇幻风格', '武侠风格', '军旅风格'
  ];
  const genres = [
    // 🔥 超热门类型（前10位）- 主流网文市场的支柱
    '都市',      // NO.1 最热门，职场、商战、都市生活
    '玄幻',      // NO.2 传统王者，境界修炼、异世界
    '现言',      // NO.3 女频第一大类，现代言情
    '仙侠',      // NO.4 修真类热门，仙侠修炼
    '校园',      // NO.5 青春校园，年轻读者最爱
    '悬疑',      // NO.6 推理破案，烧脑类热门
    '科幻',      // NO.7 未来科技，持续热门
    '古言',      // NO.8 女频古代言情，宫廷权谋
    '武侠',      // NO.9 传统武侠，江湖侠义
    '历史',      // NO.10 历史架空，权谋争霸
    
    // ⭐ 热门类型（11-20位）- 大众喜爱的主流类型
    '修真',      // 修仙升级流
    '穿越',      // 穿越题材，热门机制
    '重生',      // 重生逆袭，爽文必备
    '末世',      // 末日生存，紧张刺激
    '游戏',      // 游戏竞技，虚拟现实
    '职场',      // 职场奋斗，现实题材
    '商战',      // 商业竞争，智斗谋略
    '青春',      // 青春成长，情感细腻
    '言情',      // 通用言情，情感主导
    '架空',      // 架空历史，自由发挥
    
    // 💫 中等热度（21-30位）- 有固定受众的类型
    '奇幻',      // 西方魔法，奇幻世界
    '轻小说',    // 轻松欢快，二次元风
    '同人',      // 原作衍生，粉丝向
    '机甲',      // 机甲战斗，硬核科幻
    '星际',      // 星际探索，太空冒险
    '未来',      // 未来世界，科技发展
    '种田',      // 种田经营，休闲向
    '军事',      // 军事战争，铁血硬汉
    '体育',      // 体育竞技，热血向
    '探险',      // 冒险探索，寻宝猎奇
    
    // 🌙 较冷门（31-40位）- 小众或细分类型
    '灵异',      // 鬼怪灵异，超自然
    '恐怖',      // 惊悚恐怖，氛围向
    '宫斗',      // 宫廷斗争，权谋争宠
    '古代',      // 古代背景，历史向
    '民国',      // 民国风云，特定时期
    '现代',      // 现代背景，生活向
    '都会',      // 都会生活，都市细分
    '乡村',      // 乡村田园，生活向
    '异世',      // 异世界穿越，奇幻向
    '变身'       // 变身题材，小众向
  ];
  // 🔥 关键词列表 - 分类设计，首位为"无关键词"特殊选项
  const keywords = [
    '无关键词', // ⭐ 特殊选项：与其他关键词互斥
    
    // 核心爽点（10个）
    '爽文', '扮猪吃虎', '装逼打脸', '逆袭', '废材崛起', 
    '无敌流', '天才主角', '金手指', '复仇', '热血',
    
    // 剧情机制（12个）
    '穿越', '重生', '系统流', '签到流', '修仙', '武侠', 
    '玄幻', '都市异能', '科幻', '末世', '游戏', '竞技',
    
    // 情感关系（8个）
    '爱情', '单女主', '多女主', '后宫', '无CP', 
    '兄弟情', '师徒情', '亲情',
    
    // 氛围风格（10个）
    '轻松搞笑', '黑暗', '治愈', '悬疑推理', '惊悚', 
    '燃向', '智斗', '谋略', '励志', '虐心',
    
    // 发展路线（8个）
    '成长', '冒险', '种田', '经营', '争霸', 
    '团队', '独行', '商战',
    
    // 世界观（8个）
    '东方玄幻', '西方奇幻', '现代都市', '古代', 
    '未来', '异界', '洪荒', '仙侠',
    
    // 特殊元素（6个）
    '反派视角', '群像', '技术流', '职场', '校园', '娱乐圈'
  ];

  // 🎯 关键词分类结构
  const keywordCategories = [
    {
      name: '特殊选项',
      keywords: ['无关键词'],
      color: '#9c27b0',
      isSpecial: true
    },
    {
      name: '核心爽点',
      keywords: ['爽文', '扮猪吃虎', '装逼打脸', '逆袭', '废材崛起', '无敌流', '天才主角', '金手指', '复仇', '热血'],
      color: '#1976d2'
    },
    {
      name: '剧情机制',
      keywords: ['穿越', '重生', '系统流', '签到流', '修仙', '武侠', '玄幻', '都市异能', '科幻', '末世', '游戏', '竞技'],
      color: '#388e3c'
    },
    {
      name: '情感关系',
      keywords: ['爱情', '单女主', '多女主', '后宫', '无CP', '兄弟情', '师徒情', '亲情'],
      color: '#d32f2f'
    },
    {
      name: '氛围风格',
      keywords: ['轻松搞笑', '黑暗', '治愈', '悬疑推理', '惊悚', '燃向', '智斗', '谋略', '励志', '虐心'],
      color: '#f57c00'
    },
    {
      name: '发展路线',
      keywords: ['成长', '冒险', '种田', '经营', '争霸', '团队', '独行', '商战'],
      color: '#7b1fa2'
    },
    {
      name: '世界观',
      keywords: ['东方玄幻', '西方奇幻', '现代都市', '古代', '未来', '异界', '洪荒', '仙侠'],
      color: '#0288d1'
    },
    {
      name: '特殊元素',
      keywords: ['反派视角', '群像', '技术流', '职场', '校园', '娱乐圈'],
      color: '#c2185b'
    }
  ];

  // 🔧 重置卡住的生成任务（仅用于服务器重启后恢复）
  const handleResetStuckTasks = useCallback(() => {
    // 🔒 检查冷却时间
    const now = Date.now();
    const timeSinceReset = now - lastResetTime;
    
    if (timeSinceReset < RESET_COOLDOWN && lastResetTime !== 0) {
      const remainingMinutes = Math.ceil((RESET_COOLDOWN - timeSinceReset) / 60000);
      showMessage(
        `⏳ 请等待 ${remainingMinutes} 分钟后再次使用此功能（防止频繁误操作）`,
        'warning'
      );
      return;
    }
    
    // 检测是否有生成中状态的章节
    const generatingChapters = chapters.filter(
      ch => ch.generationStatus === '生成中' || ch.generationStatus === 'generating' || ch.status === 'generating'
    );
    
    // 检测是否有待生成状态的章节
    const pendingChapters = chapters.filter(
      ch => ch.generationStatus === 'pending' || ch.status === 'pending'
    );
    
    // 🔥 新增：检测是否有卡住的重写中状态的章节
    const stuckRewritingChapters = chapters.filter(ch => {
      const chapterId = (ch.id || ch._id || '').toString();
      const isRewriting = ch.isRewriting || (chapterId ? rewritingChapters.has(chapterId) : false);
      // 如果重写中状态超过30分钟，认为是卡住的
      if (isRewriting && ch.updatedAt) {
        const updatedTime = new Date(ch.updatedAt).getTime();
        const now = Date.now();
        const timeDiff = now - updatedTime;
        return timeDiff > 30 * 60 * 1000; // 30分钟
      }
      return isRewriting; // 如果没有更新时间，也认为是卡住的
    });
    
    // 检测是否有没有内容的章节（无论状态如何）
    const emptyChapters = chapters.filter(
      ch => !ch.content || ch.content.trim().length === 0
    );
    
    // 如果没有任何需要处理的章节，显示提示并返回
    if (generatingChapters.length === 0 && pendingChapters.length === 0 && emptyChapters.length === 0 && stuckRewritingChapters.length === 0) {
      showMessage('没有发现需要重置的生成任务、重写任务或未生成内容的章节', 'info');
      return;
    }
    
    // 显示严重警告确认对话框
    setConfirmDialog({
      open: true,
      title: '紧急重置功能 - 仅限生成任务卡死，十分钟不生成一章的情况下使用',
      content: (
        <Box>
          {/* 严重警告 */}
          <Box sx={{
            p: 2,
            mb: 2,
            borderRadius: '10px',
            background: 'rgba(245, 158, 11, 0.08)',
            border: '1px solid rgba(245, 158, 11, 0.25)',
            boxShadow: '0 0 16px rgba(245, 158, 11, 0.08)'
          }}>
            <Typography sx={{ fontWeight: 'bold', mb: 1, color: '#fbbf24', fontSize: '0.9rem' }}>
              ⛔ 重要警告：此功能仅用于特殊情况
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.75, color: 'rgba(255,255,255,0.75)' }}>
              ✓ 仅当单章生成超过 <strong>10分钟</strong> 仍未完成时使用
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.75, color: 'rgba(255,255,255,0.75)' }}>
              ✓ 仅当怀疑服务器已重启时使用
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(239, 68, 68, 0.9)' }}>
              ✗ 正常生成时请勿点击（会中断正在进行的任务）
            </Typography>
          </Box>

          {/* 系统检测信息 */}
          <Typography variant="body2" sx={{ fontWeight: 700, mb: 1.5, color: '#06b6d4', fontSize: '0.88rem' }}>
            系统检测到：
          </Typography>
          <Box sx={{
            p: 2,
            mb: 2,
            borderRadius: '10px',
            background: 'rgba(6, 182, 212, 0.04)',
            border: '1px solid rgba(6, 182, 212, 0.12)'
          }}>
            {generatingChapters.length > 0 && (
              <Box sx={{ mb: 0.75 }}>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.82rem' }}>
                  <strong style={{ color: '#f59e0b' }}>{generatingChapters.length}</strong> 个章节处于"生成中"状态
                </Typography>
              </Box>
            )}
            {pendingChapters.length > 0 && (
              <Box sx={{ mb: 0.75 }}>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.82rem' }}>
                  <strong style={{ color: '#06b6d4' }}>{pendingChapters.length}</strong> 个章节处于"待生成"状态
                </Typography>
              </Box>
            )}
            {emptyChapters.length > 0 && (
              <Box sx={{ mb: 0.75 }}>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.82rem' }}>
                  <strong style={{ color: '#10b981' }}>{emptyChapters.length}</strong> 个章节没有内容
                </Typography>
              </Box>
            )}
            {stuckRewritingChapters.length > 0 && (
              <Box>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.82rem' }}>
                  <strong style={{ color: '#f59e0b' }}>{stuckRewritingChapters.length}</strong> 个章节卡在"重写中"状态
                </Typography>
              </Box>
            )}
          </Box>

          {/* 智能提示 */}
          <Box sx={{
            p: 2,
            mb: 2,
            borderRadius: '10px',
            background: 'rgba(168, 85, 247, 0.06)',
            border: '1px solid rgba(168, 85, 247, 0.15)',
            borderLeft: '3px solid rgba(168, 85, 247, 0.4)'
          }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#a855f7', mb: 1, fontSize: '0.88rem' }}>
              🤔 在点击前请确认：
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.82rem', mb: 0.5 }}>
              • 章节已经生成超过10分钟了吗？
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.82rem', mb: 0.5 }}>
              • 进度条是否长时间没有变化？
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.82rem', mb: 1 }}>
              • 服务器是否刚刚重启过？
            </Typography>
            <Typography variant="body2" sx={{ color: '#ef4444', fontWeight: 700, fontSize: '0.82rem' }}>
              如果以上都不是，请点击"取消"，继续等待生成完成
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ fontWeight: 700, mb: 1, color: '#06b6d4', fontSize: '0.88rem' }}>
            重置后将：
          </Typography>
          <Box sx={{
            p: 2,
            borderRadius: '10px',
            background: 'rgba(16, 185, 129, 0.05)',
            border: '1px solid rgba(16, 185, 129, 0.12)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.75, gap: 1 }}>
              <Typography sx={{ color: '#10b981', fontSize: '0.82rem', mt: 0.1 }}>✓</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.82rem' }}>已有内容的章节 → 标记为"已完成"</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.75, gap: 1 }}>
              <Typography sx={{ color: '#10b981', fontSize: '0.82rem', mt: 0.1 }}>✓</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.82rem' }}>无内容的章节 → 重置为"待生成"</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.75, gap: 1 }}>
              <Typography sx={{ color: '#10b981', fontSize: '0.82rem', mt: 0.1 }}>✓</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.82rem' }}>清除所有卡住的"重写中"状态</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Typography sx={{ color: '#10b981', fontSize: '0.82rem', mt: 0.1 }}>✓</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.82rem' }}>自动重启批量生成任务（智能跳过已完成章节）</Typography>
            </Box>
          </Box>
        </Box>
      ),
      confirmText: '我确认需要重置',
      cancelText: '取消（继续等待）',
      action: async () => {
        try {
          showMessage('正在重置任务状态，请稍候...', 'info');
          
          // 🔥 调用后端API重置章节状态
          const nid = novel._id || novel.id || novelId;
          console.log(`调用重置API: /novels/${nid}/reset-generating-status`);
          
          const resetResponse = await api.post(`/novels/${nid}/reset-generating-status`);
          console.log('重置API响应:', resetResponse.data);
          
          // ⚠️ 重要：先清除前端的生成状态和重写状态，再刷新小说数据
          // 这样 fetchNovelData 就不会保留旧的状态
          setBatchGenerating(false);
          setIsGenerating(false);
          setGeneratingChapterId(null);
          setChapterGenerationStatus([]);
          // 🔥 新增：清除所有重写中状态
          setRewritingChapters(new Set());
          console.log('✅ 已清除所有重写中状态');
          
          // 刷新小说数据，获取最新状态（clearGenerationStatus: true 确保不保留旧的生成状态）
          await fetchNovelData(nid, { silent: true, clearGenerationStatus: true });
          
          // 🔒 再次确保清空生成状态，防止异步问题
          setChapterGenerationStatus([]);
          setBatchGenerating(false);
          setIsGenerating(false);
          setGeneratingChapterId(null);
          
          const result = resetResponse.data;
          
          // 构建提示消息
          let message = `重置完成！`;
          const details = [];
          
          if (result.completedCount > 0) details.push(`已完成: ${result.completedCount}章`);
          if (result.pendingCount > 0) details.push(`待生成: ${result.pendingCount}章`);
          if (result.rewritingCleared > 0) details.push(`已清理 ${result.rewritingCleared} 个卡住的重写状态`);
          if (result.tasksCancelled > 0) details.push(`已清理 ${result.tasksCancelled} 个卡住的生成任务`);
          if (result.rewriteTasksCancelled > 0) details.push(`已取消 ${result.rewriteTasksCancelled} 个卡住的重写任务`);
          
          if (details.length > 0) {
            message += ' ' + details.join(' | ');
          }
          
          showMessage(message, 'success');
          console.log('✅ [重置完成] 重置成功，准备自动重启...');
          
          // 🚀 自动重启批量生成任务
          // 等待短暂延迟，确保状态已完全更新
          console.log('⏰ [自动重启] 设置1秒延时任务...');
          const timeoutId = setTimeout(async () => {
            console.log('⏰ [自动重启] 延时任务开始执行，开始分析章节...');
            try {
              console.log('📊 [自动重启] 开始获取最新章节数据...');
              // 重新获取最新的章节数据
              const response = await api.get(`/novels/${nid}`);
              console.log('📊 [自动重启] API 原始响应:', response.data);
              // 兼容包装和非包装的响应格式
              const updatedNovel = response.data?.data || response.data;
              console.log('📊 [自动重启] 解包后的小说数据:', updatedNovel);
              console.log('📊 [自动重启] updatedNovel.chapters:', updatedNovel.chapters);
              const updatedChapters = updatedNovel.chapters || [];
              console.log(`📊 [自动重启] 获取到 ${updatedChapters.length} 个章节`, updatedChapters);
              
              // 找出所有需要生成的章节（没有内容或没有大纲的）
              const chaptersNeedingGeneration = [];
              
              for (const chapter of updatedChapters) {
                // 判断是否需要大纲
                const needsOutline = !chapter.outline || chapter.outline.trim().length === 0;
                
                // 判断是否需要内容 - 无论章节状态如何，只看内容是否存在
                const needsContent = !chapter.content || chapter.content.trim().length === 0;
                
                console.log(`📋 [自动重启] 第${chapter.index}章: 需要大纲=${needsOutline}, 需要内容=${needsContent}, 状态=${chapter.status || '未知'}, 生成状态=${chapter.generationStatus || '未知'}`);
                
                // 只要章节缺少内容或大纲，就添加到需要生成的列表中
                if (needsOutline || needsContent) {
                  chaptersNeedingGeneration.push({
                    index: chapter.index,
                    needsOutline,
                    needsContent
                  });
                }
              }
              
              console.log(`🎯 [自动重启] 发现 ${chaptersNeedingGeneration.length} 个章节需要生成`);
              
              if (chaptersNeedingGeneration.length === 0) {
                console.log('✅ [自动重启] 所有章节都已完成');
                showMessage('所有章节都已完成，无需继续生成', 'success');
                return;
              }
              
              // 计算生成范围
              const minIndex = Math.min(...chaptersNeedingGeneration.map(ch => ch.index));
              const maxIndex = Math.max(...chaptersNeedingGeneration.map(ch => ch.index));
              console.log(`📏 [自动重启] 生成范围: 第${minIndex}章 到 第${maxIndex}章`);
              
              // 判断生成模式：如果所有章节都需要完整生成，使用complete模式；否则智能判断
              const allNeedComplete = chaptersNeedingGeneration.every(ch => ch.needsOutline && ch.needsContent);
              const allNeedOutlineOnly = chaptersNeedingGeneration.every(ch => ch.needsOutline && !ch.needsContent);
              const allNeedContentOnly = chaptersNeedingGeneration.every(ch => !ch.needsOutline && ch.needsContent);
              
              let generateMode = 'complete';
              if (allNeedOutlineOnly) {
                generateMode = 'outlineOnly';
              } else if (allNeedContentOnly) {
                generateMode = 'contentOnly';
              }
              console.log(`🎨 [自动重启] 生成模式: ${generateMode}`);
              
              // 设置批量生成参数
              console.log('⚙️ [自动重启] 设置批量生成参数...');
              setBatchRange({
                startChapter: minIndex,
                endChapter: maxIndex,
                skipGenerated: true, // 跳过已生成的章节
                generateMode: generateMode
              });
              
              console.log(`🚀 [自动重启] 批量生成参数已设置: 第${minIndex}章 到 第${maxIndex}章，模式: ${generateMode}`);
              showMessage(`正在自动重启批量生成：第${minIndex}章 到 第${maxIndex}章...`, 'info');
              
              // 🔥 使用新的后端批量生成流程（直接生成内容，不需要先生成大纲）
              console.log('⏰ [自动重启] 准备调用后端批量生成API...');
              
              // 准备章节ID列表（使用章节索引）
              const chapterIds = chaptersNeedingGeneration.map(ch => ch.index.toString());
              console.log('📋 [自动重启] 章节索引列表:', chapterIds);
              
              // 确保所有章节都在本地章节列表中
              let localChapters = [...updatedChapters];
              for (const chapterId of chapterIds) {
                const chapterIndex = parseInt(chapterId);
                if (!localChapters.find(ch => ch.index === chapterIndex)) {
                  console.log(`📝 [自动重启] 创建第${chapterIndex}章`);
                  localChapters.push({
                    id: uuidv4(),
                    index: chapterIndex,
                    title: `第${chapterIndex}章`,
                    content: '',
                    outline: '',
                    status: 'draft',
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString()
                  });
                }
              }
              localChapters.sort((a, b) => a.index - b.index);
              setChapters(localChapters);
              setCollapsedGroups(null);
              
              // 设置章节状态为等待中
              const initialStatus = chapterIds.map((id, index) => ({
                chapterIndex: parseInt(id),
                title: `第${id}章`,
                status: index === 0 ? '准备中' : '等待中',
                progress: 0
              }));
              setChapterGenerationStatus(initialStatus);
              
              // 调用后端批量生成API
              try {
                const novelIdToUse = updatedNovel._id || updatedNovel.id;
                console.log(`🚀 [自动重启] 调用后端API，novelId=${novelIdToUse}, 章节数=${chapterIds.length}`);
                
                const result = await taskService.createBatchGenerationTask({
                  novelId: novelIdToUse,
                  chapterIds: chapterIds,
                  settings: {
                    ...(novelSettings?.aiSettings || {}),
                    model: 'deepseek-v4-flash'
                  },
                  outline: outline || '',
                  genre: novelSettings?.genre || '',
                  style: novelSettings?.style || '',
                  perspective: novelSettings?.perspective || ''
                });
                
                console.log('✅ [自动重启] 批量生成任务创建成功:', result);
                
                // 保存任务ID用于轮询状态
                if (result && result.data && result.data.taskId) {
                  setBatchGenerationTaskId(result.data.taskId);
                  console.log(`✅ [自动重启] 已保存任务ID: ${result.data.taskId}`);
                }
                
                // 设置批量生成标志
                setBatchGenerating(true);
                
                showMessage(`批量生成任务已启动，共${chapterIds.length}个章节`, 'success');
              } catch (error) {
                console.error('❌ [自动重启] 调用后端API失败:', error);
                showMessage('自动重启失败: ' + (error.message || '未知错误'), 'error');
                // 清除章节状态
                setChapterGenerationStatus([]);
              }
              
            } catch (error) {
              console.error('❌ [自动重启] 批量生成失败:', error);
              showMessage('自动重启失败，请手动启动批量生成: ' + error.message, 'warning');
            }
          }, 1000);
          
          // 🔒 设置冷却时间（5分钟）
          setLastResetTime(Date.now());
          console.log('✅ 重置成功，已启动5分钟冷却时间');
          
        } catch (error) {
          console.error('重置任务失败:', error);
          showMessage('重置失败: ' + error.message, 'error');
        }
      }
    });
  }, [chapters, showMessage, fetchNovelData, novel, novelId, api, setBatchGenerating, setIsGenerating, setGeneratingChapterId, setChapterGenerationStatus, setBatchRange, lastResetTime, RESET_COOLDOWN]);

  // 处理打开批量生成对话框
  const handleOpenBatchDialog = useCallback(() => {
    // ========== 前置条件检查 ==========
    
    // 1. 检查基础设置是否完整
    const missingSettings = [];
    if (!novelSettings.platform) missingSettings.push('发布平台');
    if (!novelSettings.perspective) missingSettings.push('视角选择');
    // description字段不存在，已移除
    if (!novelSettings.wordCount) missingSettings.push('预计字数');
    if (!novelSettings.chapterCount) missingSettings.push('预计章节数');
    if (!novelSettings.style) missingSettings.push('写作风格');
    if (!novelSettings.genre) missingSettings.push('小说类型'); // 修复：使用genre而不是type
    if (!novelSettings.keywords) missingSettings.push('关键词标签');
    // additionalNotes（补充要求）是可选的，不检查
    
    if (missingSettings.length > 0) {
      setDialog({
        open: true,
        title: '请完善基础设置',
        content: `批量生成章节需要先完成以下设置：

━━━━━━━━━━━━━━━━━━━━

${missingSettings.map((item, index) => `${index + 1}. ${item}`).join('\n')}

━━━━━━━━━━━━━━━━━━━━

提示：请先切换到"基础设置"页面完成配置`,
        confirmText: '知道了',
        onConfirm: () => {
          setDialog({ open: false });
          setActiveTab(0); // 切换到基础设置页面
        },
      });
      return;
    }
    
    // 2. 检查大纲是否已生成
    if (!outline || outline.trim().length < 50) {
      setDialog({
        open: true,
        title: '请先生成大纲',
        content: `批量生成章节需要先完成大纲创作

━━━━━━━━━━━━━━━━━━━━

大纲的作用：
• 确定故事主线和发展方向
• 规划章节内容和节奏
• 保证剧情连贯性

━━━━━━━━━━━━━━━━━━━━

提示：请先切换到"大纲编辑"页面生成大纲`,
        confirmText: '知道了',
        onConfirm: () => {
          setDialog({ open: false });
          setActiveTab(1); // 切换到大纲编辑页面
        },
      });
      return;
    }
    
    // 3. 检查大纲是否生成完成
    if (!outlineGenerated) {
      showMessage("大纲尚未生成完成，请等待大纲生成完成后再进行批量生成", "warning");
      return;
    }
    
    // 4. 检查是否有进行中的生成操作
    if (generatingOutline) {
      showMessage("大纲生成中，请等待完成后再进行批量生成", "warning");
      return;
    }
    
    if (generatingStructure) {
      showMessage("故事结构生成中，请等待完成后再进行批量生成", "warning");
      return;
    }
    
    // 5. 检查是否有批量生成任务正在进行
    // 🔥 修复：如果 batchGenerating 为 true 但 chapterGenerationStatus 为空（都已完成），自动清理状态
    const hasActiveChapterStatus = Array.isArray(chapterGenerationStatus) && chapterGenerationStatus.some(s =>
      s.status && (String(s.status).includes('生成中') || String(s.status).includes('等待中'))
    );
    if (batchGenerating || isGenerating) {
      // 🔥 自检：如果 batchGenerating=true 但没有任何活跃的章节状态，说明状态已过期，自动清理
      if (!hasActiveChapterStatus) {
        console.warn('⚠️ [状态自检] batchGenerating=true 但没有活跃章节，自动清理状态');
        setBatchGenerating(false);
        setIsGenerating(false);
        setChapterGenerationStatus([]);
        setGeneratingChapterId(null);
      } else {
        showMessage("已有批量生成任务正在进行，请等待当前任务完成后再试", "warning");
        return;
      }
    }

    // 6. 检查是否有章节正在生成中（只检查生成中状态，不检查等待中）
    // 注意：等待中状态不应该阻止启动新的批量生成，因为等待中可能是因为异常中断
    const generatingChapterIndices = Array.isArray(chapterGenerationStatus) 
      ? chapterGenerationStatus
        .filter(ch => ch.status === '生成中' || ch.status === 'generating')
        .map(ch => ch.chapterIndex)
      : [];
    
    if (generatingChapterIndices.length > 0) {
      console.warn('⚠️ 有章节正在生成中:', generatingChapterIndices);
      showMessage("有章节正在生成中，请等待当前任务完成后再试", "warning");
      return;
    }
    
    // ========== 所有检查通过，继续批量生成流程 ==========

    // 🔥 检查故事结构是否已生成（chapterOutlineMap 必须有数据）
    const storyStructureExists = novel?.storyStructure?.outlineEnforcement?.chapterOutlineMap &&
      novel.storyStructure.outlineEnforcement.chapterOutlineMap.length > 0;

    // 🔥 如果故事结构尚未生成，提示用户先生成故事结构
    if (!storyStructureExists) {
      setDialog({
        open: true,
        title: '请先生成故事结构',
        content: '批量生成章节前，请先点击"生成故事结构"按钮完成大纲创作。\n\n故事结构包含章节大纲、剧情线、角色画像等核心设定，是批量生成章节的基础。\n\n请返回创作助手界面，按步骤完成：\n1. 设定小说主题和类型\n2. 完善故事大纲\n3. 生成故事结构',
        confirmText: '我知道了',
        onConfirm: () => {
          setDialog({ open: false });
        },
      });
      return;
    }

    // 🔥 检查是否所有章节都已生成完成
    const totalChaptersInStructure = novel.storyStructure.outlineEnforcement.chapterOutlineMap.length;
    const chaptersWithContentIndices = chapters
      .filter(ch => ch.content && ch.content.trim().length > 0)
      .map(ch => ch.index);

    // 如果已有内容的章节数量 >= 故事结构中的章节数量，说明所有章节都已生成
    if (chaptersWithContentIndices.length >= totalChaptersInStructure) {
      setDialog({
        open: true,
        title: '所有章节已生成完成',
        content: `恭喜！根据故事结构，本小说共设定${totalChaptersInStructure}章，目前所有章节都已生成完成。

━━━━━━━━━━━━━━━━━━━━

生成统计：
• 总章节数：${totalChaptersInStructure}章
• 已生成：${chaptersWithContentIndices.length}章
• 完成度：100%

━━━━━━━━━━━━━━━━━━━━

接下来您可以：
• 检查并修改章节内容
• 导出小说文稿
• 创建新的小说项目`,
        confirmText: '知道了',
        onConfirm: () => {
          setDialog({ open: false });
        },
      });
      return;
    }

    // 🔥 计算起始章节：找到第一个没有内容的章节
    let nextStartChapter = 1;
    
    // 如果有章节，找第一个空章节
    if (chapters.length > 0) {
      // 按索引排序
      const sortedChapters = [...chapters].sort((a, b) => a.index - b.index);
      
      // 找到第一个没有内容的章节
      const firstEmptyChapter = sortedChapters.find(ch => 
        !ch.content || ch.content.trim().length === 0
      );
      
      if (firstEmptyChapter) {
        nextStartChapter = firstEmptyChapter.index;
        console.log(`📍 找到第一个空章节: 第${nextStartChapter}章`);
      } else {
        // 所有章节都有内容，从最后一章+1开始
        const maxChapterIndex = Math.max(...sortedChapters.map(ch => ch.index));
        nextStartChapter = maxChapterIndex + 1;
        console.log(`📍 所有章节都有内容，从第${nextStartChapter}章开始`);
      }
    }
    
    const defaultEndChapter = nextStartChapter; // 默认生成1章
    const defaultChapterCount = Math.max(1, defaultEndChapter - nextStartChapter + 1);
    
    // 动态计算所需余额：章节数量 × 2200字（预估值，实际扣费以生成内容为准）
    const estimatedWordCount = defaultChapterCount * 3000;
    if (userBalance < estimatedWordCount) {
      setDialog({
        open: true,
        title: '余额不足，无法批量生成',
        content: `预估生成 ${defaultChapterCount} 章需要约 ${estimatedWordCount} 字，当前余额 ${userBalance} 字，差额约 ${estimatedWordCount - userBalance} 字。\n\n请先充值后再继续批量生成。`,
        confirmText: '去充值',
        cancelText: '取消',
        onConfirm: () => {
          setDialog({ open: false });
          navigate('/recharge');
        },
        onCancel: () => {
          setDialog({ open: false });
        }
      });
      return;
    }
    
    console.log("检测到有内容的章节:", chaptersWithContentIndices);
    
    // 更新chaptersGenerated状态，确保包含所有已有内容的章节
    if (chaptersWithContentIndices.length > 0) {
      // 合并现有的chaptersGenerated和新检测到的章节索引
      const updatedGeneratedChapters = [...new Set([...chaptersGenerated, ...chaptersWithContentIndices])];
      
      // 如果有新的章节被检测到，更新状态
      if (updatedGeneratedChapters.length > chaptersGenerated.length) {
        console.log("更新已生成章节列表:", updatedGeneratedChapters);
        setChaptersGenerated(updatedGeneratedChapters);
        
        // 保存到localStorage
        if (novel && novel.id) {
          localStorage.setItem(`generated_chapters_${novel.id}`, JSON.stringify(updatedGeneratedChapters));
        }
      }
    }
    
    // 🔥 使用前面已经计算好的 nextStartChapter（第一个空章节）
    // 不需要再重新计算，避免跳过被重置的章节
    const nextStartChapterFinal = nextStartChapter;
    
    console.log(`📍 批量生成对话框默认起始章节: ${nextStartChapterFinal}`);
    
    // 计算合理的结束章节（默认生成1章）
    const suggestedEndChapter = nextStartChapterFinal;
    
    // 从localStorage加载上次的批量生成设置
    let savedSettings = {};
    try {
      const savedSettingsStr = localStorage.getItem('batchGenerationSettings');
      if (savedSettingsStr) {
        savedSettings = JSON.parse(savedSettingsStr);
      }
    } catch (error) {
      console.error('加载批量生成设置失败:', error);
    }
    
    // 设置批量生成范围，优先使用计算的值
        setBatchRange(prev => ({
          ...prev,
      ...savedSettings,
      startChapter: nextStartChapterFinal, // 始终使用计算的下一个可用章节
      endChapter: suggestedEndChapter, // 使用计算的建议结束章节
          skipGenerated: true // 强制设置为true，确保总是跳过已生成的章节
        }));
    
    // 打开对话框
    setBatchDialogOpen(true);
    
    console.log(`批量生成对话框已打开，设置起始章节=${nextStartChapterFinal}，结束章节=${suggestedEndChapter}`);
  }, [chaptersGenerated, chapterGenerationStatus, chapters, novel, setChaptersGenerated, userBalance, showMessage, batchGenerating, isGenerating]);

  // 打开导出对话框
  const handleOpenExportDialog = useCallback(() => {
    // 检查是否有章节内容（hasContent 标记的瘦身材节即使正文未懒加载也可导出，导出时会自动拉取）
    const validChapters = chapters.filter(ch => (ch.content && ch.content.trim()) || ch.hasContent);
    
    if (validChapters.length === 0) {
      showMessage('暂无可导出的章节内容，请先生成章节', 'warning');
      return;
    }

    // 打开导出对话框
    setExportDialogOpen(true);
  }, [chapters, showMessage]);

  // 确认导出Word文档
  const handleConfirmExport = useCallback(async () => {
    // 🔥 防止重复点击：导出进行中直接忽略
    if (exporting) return;

    try {
      setExporting(true);
      setExportProgress({ current: 0, total: 0 });
      // 🔥 保持对话框打开，用于持续展示加载进度，直到文档生成完毕才关闭

      // 🔥 懒加载补齐：瘦身后 chapters 中只有浏览过的章节带正文，
      // 其余章节仅有 hasContent 标记。导出前先批量拉取缺失正文，确保导出全部章节
      // 🔥 并发限流（一次最多4个请求）+ 失败重试，防止数百请求同时打爆服务器/数据库
      //    （旧版 Promise.all 全量并发曾导致：服务器卡死 + 大量请求失败被静默丢弃 → 600章只导出150章）
      const needFetch = chapters.filter(ch => ch.hasContent && !(ch.content && ch.content.trim()));
      let fullChapters = chapters;
      if (needFetch.length > 0) {
        setExportProgress({ current: 0, total: needFetch.length });
        const CONCURRENCY = 4;   // 并发上限：匹配服务器连接池承受能力
        const MAX_RETRY = 3;     // 单章失败重试次数
        const contentMap = new Map();
        const failedIndexes = [];
        let doneCount = 0;
        const queue = needFetch.map(ch => ch.index);
        const worker = async () => {
          while (queue.length > 0) {
            const idx = queue.shift();
            let d = null;
            for (let attempt = 1; attempt <= MAX_RETRY; attempt++) {
              d = await fetchChapterContent(idx, { skipStateUpdate: true });
              if (d) break;
              if (attempt < MAX_RETRY) await new Promise(r => setTimeout(r, 600 * attempt)); // 退避后重试
            }
            if (d) contentMap.set(idx, d); else failedIndexes.push(idx);
            doneCount += 1;
            setExportProgress(prev => ({ ...prev, current: doneCount }));
          }
        };
        await Promise.all(
          Array.from({ length: Math.min(CONCURRENCY, queue.length) }, () => worker())
        );
        // 🔥 失败兜底：不再静默丢弃失败章节，明确报错并中止，避免导出缺章的文档
        if (failedIndexes.length > 0) {
          const preview = failedIndexes.slice(0, 10).join('、');
          showMessage(`有 ${failedIndexes.length} 章正文拉取失败（第 ${preview}${failedIndexes.length > 10 ? ' 等' : ' 章'}），已中止导出。请稍后重试，若反复失败请联系管理员`, 'error');
          setExporting(false);
          return;
        }
        fullChapters = chapters.map(ch => {
          const d = contentMap.get(ch.index);
          return d ? { ...ch, content: d.content || ch.content || '' } : ch;
        });
        // 🔥 一次性回填正文到 chapters state（旧版逐章 setChapters 造成数百次重渲染导致前端卡顿）
        setChapters(prev => prev.map(ch => {
          const d = contentMap.get(ch.index);
          return d ? { ...ch, content: d.content || ch.content || '', hasContent: (d.content || '').trim().length > 0, contentLength: d.contentLength } : ch;
        }));
      }

      // 检查是否有章节内容
      const validChapters = fullChapters.filter(ch => ch.content && ch.content.trim());

      if (validChapters.length === 0) {
        showMessage('暂无可导出的章节内容，请先生成章节', 'warning');
        setExporting(false);
        return;
      }

      // 🔥 正文加载完成，进入文档生成阶段
      setExportProgress({ current: -1, total: 0 });

      // 调用导出函数
      const result = await exportToWord({
        novelTitle: title || '未命名小说',
        novelOutline: outline || '',
        chapters: validChapters,
        author: novelSettings?.author || (() => { try { const u = JSON.parse(localStorage.getItem('user') || '{}'); return u.username || '作者'; } catch { return '作者'; } })(),
        includeOutline: exportOptions.includeOutline,
        includeStats: exportOptions.includeStats
      });

      // 导出成功：关闭对话框并提示
      setExportDialogOpen(false);
      setExporting(false);
      showMessage(result.message, 'success');

    } catch (error) {
      console.error('导出Word失败:', error);
      showMessage(`导出失败：${error.message || '未知错误'}`, 'error');
      setExporting(false);
    }
  }, [exporting, chapters, title, outline, exportOptions, novelSettings, fetchChapterContent, showMessage]);

    // ============================================================
    // 🔴 当前【AI 自动生成小说总体大纲】调用入口（前端按钮触发）
    // 🔍 搜索关键字：CURRENT_OUTLINE_BUTTON_HANDLER 或 generateNovelOutline
    // 说明：
    // 1. 点击界面上的「生成小说大纲」按钮时，最终会执行此函数
    // 2. 本函数负责校验设置、检查余额，并一次性调用 generateNovelOutlineFull 生成完整大纲
    // 3. 如需调整按钮行为（改为只保存、不生成等），请优先修改此函数逻辑
    // ============================================================  CURRENT_OUTLINE_BUTTON_HANDLER
    // 生成小说总体大纲
   const generateNovelOutline = useCallback(async () => {
     if (generatingOutline) return; // 防止并发

     // 🔥 检查必填设置，如果不完整则提示用户
     const missingSettings = [];
     if (!novelSettings.platform) missingSettings.push('发布平台');
     if (!novelSettings.perspective) missingSettings.push('视角选择');
     if (!novelSettings.length) missingSettings.push('作品篇幅');
     if (!novelSettings.wordCount) missingSettings.push('预计字数');
     if (!novelSettings.chapterCount) missingSettings.push('预计章节数');
     if (!novelSettings.style) missingSettings.push('写作风格');
     if (!novelSettings.genre) missingSettings.push('小说类型');
    // 🔥 关键词可以为空数组（代表"无关键词"），只有undefined或null才算缺失
    if (!novelSettings.keywords) missingSettings.push('关键词标签');
     
     // AI生成大纲模式下需要检查补充要求（必填）
     if (!useCustomOutline && (!novelSettings.additionalNotes || novelSettings.additionalNotes.trim().length === 0)) {
       missingSettings.push('补充要求');
     }
     
     // 自定义大纲模式下还需要检查大纲内容
     if (useCustomOutline && customOutline.trim().length === 0) {
       missingSettings.push('自定义大纲内容');
     }
     
     if (missingSettings.length > 0) {
      setDialog({
        open: true,
        title: '请完善基础设置',
        content: `请先完成以下设置项，然后再${useCustomOutline ? '保存大纲' : '生成大纲'}：

━━━━━━━━━━━━━━━━━━━━

${missingSettings.map((item, index) => `${index + 1}. ${item}`).join('\n')}

━━━━━━━━━━━━━━━━━━━━

提示：完成上述设置后，即可继续操作`,
        confirmText: '知道了',
        onConfirm: () => {
          setDialog({ open: false });
        },
      });
       return;
     }

     // 🔥 如果是自定义大纲模式，只保存大纲，不自动生成故事结构
     if (useCustomOutline) {
       try {
         setGeneratingOutline(true);
         
         // 1. 保存设置到服务器（使用现有的章节数设置）
         await handleSaveSettings(novelSettings);
         
         // 2. 保存自定义大纲到服务器
         const saveResult = await saveNovelToServer({
           ...novel,
           outline: customOutline,
           settings: novelSettings
         });
         
        if (saveResult.success) {
          setOutlineText(customOutline);
          setOutline(customOutline);
          setOutlineGenerated(true);
          
          // 🔥 更新 novel 状态，确保 novelId 等数据完整
          if (saveResult.novel) {
            setNovel(saveResult.novel);
            console.log(`[自定义大纲] novel 状态已更新，ID: ${saveResult.novel._id}, id: ${saveResult.novel.id}`);
            console.log(`[自定义大纲] novel 完整对象:`, saveResult.novel);
          }
          
          const chapterCount = novelSettings.chapterCount || 30;
          showMessage(`自定义大纲已保存成功（${customOutline.length}字，${chapterCount}章）`, 'success');
          
          // 3. 切换到大纲编辑标签页
          setActiveTab(1);
          
          console.log(`[自定义大纲] 已保存，共${chapterCount}章，需手动生成故事结构`);
        } else {
          showMessage('保存自定义大纲失败，请重试', 'error');
        }
       } catch (error) {
         console.error('[自定义大纲] 保存失败:', error);
         showMessage(`保存自定义大纲失败: ${error.message}`, 'error');
       } finally {
         setGeneratingOutline(false);
       }
       return;
     }

     // 大纲计费规则（用户视角）：每日前10次免费，超出后每次1000字
    //    余额<1000 不硬性拦截——免费额度可能未用完，软提醒后放行
    if (userBalance < 1000) {
      const proceedWithLowBalance = await new Promise((resolve) => {
        setDialog({
          open: true,
          title: '余额较低提醒',
          content: `当前余额 ${userBalance} 字。\n\n大纲生成每日前10次免费；若今日免费额度已用完，每次生成将消耗1000字（余额不足会导致生成失败）。\n\n是否继续尝试生成？`,
          confirmText: '继续生成',
          cancelText: '去充值',
          onConfirm: () => { setDialog({ open: false }); resolve(true); },
          onCancel: () => { setDialog({ open: false }); navigate('/recharge'); resolve(false); },
        });
      });
      if (!proceedWithLowBalance) {
        setGeneratingOutline(false);
        return;
      }
    }

    setGeneratingOutline(true);
    try {
      // 移动端警告：生成大纲时不能切换界面或息屏
      if (isMobile) {
        const userConfirmed = await new Promise((resolve) => {
          setDialog({
            open: true,
            title: '生成大纲中，请保持当前页面',
            content: '重要提醒：生成大纲过程中，请保持手机屏幕常亮，不要切换到其他应用或标签页，否则会导致生成中断失败！\n\n建议：\n1. 关闭自动息屏功能\n2. 保持此页面在前台\n3. 耐心等待生成完成',
            confirmText: '我已知晓，继续生成',
            cancelText: '取消',
            onConfirm: () => { setDialog({ open: false }); resolve(true); },
            onCancel: () => { setDialog({ open: false }); resolve(false); },
          });
        });
        if (!userConfirmed) {
          setGeneratingOutline(false);
          return;
        }
      }
      if (novelSettings) {
         try {
           const saveResult = await handleSaveSettings(novelSettings);
           if (saveResult.success) {
             showMessage('设置已保存', 'success');
           }
         } catch (error) {
           console.error('保存设置失败:', error);
           showMessage(`保存设置失败: ${error.message || '未知错误'}`, 'error');
           setGeneratingOutline(false);
           return;
         }
       }
       setActiveTab(1);
       const chapterCountForOutline = resolveTotalChapters(novelSettings);
       const outlinePlanForStatus = getOutlineGenerationPlan(chapterCountForOutline);
       setOutlineGenerationStatus({
         generating: true,
         progress: 0,
         message: `正在生成${chapterCountForOutline}章小说大纲（${outlinePlanForStatus.tierLabel}，目标约${outlinePlanForStatus.targetWords}字）...`
       });
       setOutlineText('');
       setTimeout(() => {
         const el = outlineRef.current;
         if (el) { el.scrollTop = 0; el.dataset.autoScroll = 'true'; }
       }, 100);
       await new Promise(resolve => setTimeout(resolve, 300));

       const result = await generateNovelOutlineFull();
       const { content: finalOutline, finalWordCount } = result;

       if (outlineRef.current && outlineRef.current._scrollHandler) {
         outlineRef.current.removeEventListener('scroll', outlineRef.current._scrollHandler);
         outlineRef.current._scrollHandler = null;
       }

       setOutlineText(finalOutline);
       setOutline(finalOutline);
       setOutlineGenerated(true);
       setOutlineGenerationStatus({ generating: false, progress: 100, message: '大纲生成完成！正在保存...' });
       setGeneratingOutline(false);

       try {
         await fetchAndUpdateUserBalance();
       } catch (e) { console.error('刷新余额失败:', e); }

       if (novel) {
         try {
           const success = await saveOutlineToServer(finalOutline);
           if (success) {
             setOutline(finalOutline);
             setOutlineText(finalOutline);
             setOutlineGenerated(true);
             showMessage('大纲已保存！', 'success');
             setOutlineGenerationStatus({ generating: false, progress: 100, message: '大纲已保存！' });
           } else {
             showMessage('大纲生成完成，但自动保存失败，请手动保存', 'warning');
           }
         } catch (saveError) {
           showMessage('大纲生成完成，但自动保存失败：' + (saveError.message || '未知错误'), 'warning');
         }
       } else {
         showMessage('大纲生成完成，但无法自动保存，请手动保存', 'warning');
       }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('大纲生成被用户中断');
        setGeneratingOutline(false);
        setThinkingContent('');
        return;
      }
      console.error('大纲生成错误:', error);
      setGeneratingOutline(false);
      setOutlineGenerationStatus({ generating: false, progress: 0, message: '生成失败: ' + (error.message || '未知错误') });
      showMessage('大纲生成失败: ' + (error.message || '未知错误'), 'error');
      setThinkingContent('');
    } finally {
      setGeneratingOutline(false);
      setThinkingContent('');
    }
   }, [generatingOutline, novel, novelSettings, generateNovelOutlineFull, setOutline, showMessage, userBalance, setActiveTab, setGeneratingOutline, setOutlineGenerationStatus, setOutlineText, fetchAndUpdateUserBalance, setNovel, setOutlineGenerated, handleSaveSettings, saveNovelToServer, navigate, outlineRef, useCustomOutline, customOutline, setThinkingContent]);

  // 🎨 故事结构生成函数（手动触发或自动触发）
  const triggerStoryStructureGeneration = useCallback(async (manualTrigger = false) => {
    if (!novel || !novel._id) {
      console.error('❌ 无法生成故事结构：小说信息缺失');
      return;
    }
    
    if (!outline || outline.trim() === '') {
      console.error('❌ 无法生成故事结构：大纲内容为空');
      showMessage('请先生成或输入大纲内容', 'warning');
      return;
    }
    
    console.log('\n╔═══════════════════════════════════════════════════════════╗');
    console.log(`║  🎨 [前端] ${manualTrigger ? '手动触发' : '自动触发'}故事结构生成              ║`);
    console.log('╚═══════════════════════════════════════════════════════════╝\n');
    
    setGeneratingStructure(true);
    setStructureError(null);
    setStructureExpanded(true); // 展开故事结构面板
    
    try {
      // 📝 第一步：调用后端API保存大纲并触发故事结构生成
      console.log('📝 [触发生成] 调用后端API保存大纲...');
      console.log('   - 小说ID:', novel._id);
      console.log('   - 大纲长度:', outline.length, '字符');
      
      const response = await api.put(`/novels/${novel._id}/outline`, {
        outline: outline,
        settings: novelSettings // 🔥 传递完整的小说设置，包括关键词
      });
      
      console.log('✅ [触发生成] 后端已收到请求，开始在后台生成故事结构');
      
      // 🔥【WS化】进度由 storyStructureProgress 事件实时推送（完成/失败均在推送里收尾），
      // 这里仅本地标记 generating 以激活 15 秒兜底轮询（WS 断线时对账），不再 2 秒全量拉取小说详情
      setNovel(prev => (prev ? {
        ...prev,
        storyStructure: {
          ...(prev.storyStructure || {}),
          generationStatus: 'generating',
          generationProgress: {
            ...(prev.storyStructure?.generationProgress || {}),
            stage: 0,
            progress: 0,
            message: '已提交生成任务，等待进度推送…',
            updatedAt: new Date()
          }
        }
      } : prev));

    } catch (error) {
      console.error('❌ [触发生成] 调用后端API失败:', error);
      setGeneratingStructure(false);
      setStructureError('触发生成失败');
      console.error('   ⚠️  故事结构触发生成失败（详细错误仅记录在控制台）');
    }
  }, [novel, outline, showMessage, setNovel]);

  // 🔥 新增：补全失败的章节大纲
  const handleRepairChapterOutlines = useCallback(async () => {
    if (!novel || !novel._id) {
      showMessage('小说信息缺失', 'error');
      return;
    }

    try {
      setRepairingOutlines(true);
      showMessage('正在补全章节大纲，请稍候...', 'info');

      const response = await api.post(`/novels/${novel._id}/repair-chapter-outlines`);

      if (response.data.success) {
        const repairedCount = response.data.repairedCount || 0;
        showMessage(`成功补全${repairedCount}章大纲！`, 'success');
        // 重新加载小说数据（明确传入novelId）
        if (novel && novel._id) {
          await fetchNovelData(novel._id);
        }
      } else {
        showMessage('补全失败：' + (response.data.message || '未知错误'), 'error');
      }
    } catch (error) {
      console.error('补全章节大纲失败:', error);
      showMessage('补全失败：' + (error.response?.data?.message || error.message), 'error');
    } finally {
      setRepairingOutlines(false);
    }
  }, [novel, storyStructure, showMessage, fetchNovelData]);

  // 添加确认批量生成
  // 🔥【弹窗精简】后台批量生成执行函数（原二次确认弹窗逻辑精简重构）：
  //    "后台批量生成"按钮直接调用，不再弹出二次确认弹窗。
  //    同时移除原逻辑中任务启动后的4段重复全量刷新（500ms/1000ms/3000ms/30s interval，
  //    与轮询 effect 和 WebSocket 实时推送重复），由 WS 事件 + 低频兜底轮询接管。
  const executeBackgroundBatchGeneration = async () => {
    // 🔥 立即反馈：禁用按钮、显示生成状态，避免等待后端时的误操作
    setIsGenerating(true);
    setBatchGenerating(true);
    const firstChapter = batchRange.startChapter;
    const lastChapter = batchRange.endChapter;
    const totalCount = lastChapter - firstChapter + 1;
    setBatchProgress({ current: 0, total: totalCount, progress: 0 });

    try {
      // 🔥 检查是否有故事结构（强制要求）
      const hasStoryStructure = novel?.storyStructure &&
        novel.storyStructure.outlineEnforcement &&
        novel.storyStructure.outlineEnforcement.chapterOutlineMap &&
        novel.storyStructure.outlineEnforcement.chapterOutlineMap.length > 0;

      if (!hasStoryStructure) {
        setDialog({
          open: true,
          title: '❌ 必须先生成故事结构',
          content: `检测到您还没有生成故事结构，无法批量生成章节内容。\n\n` +
            `【为什么必须生成故事结构？】\n` +
            `✅ 确保章节内容连贯一致，不会出现前后矛盾\n` +
            `✅ AI严格遵循大纲，不会"放飞自我"乱编内容\n` +
            `✅ 剧情线清晰，逻辑自洽，读者体验流畅\n` +
            `✅ 角色行为一致，性格稳定\n` +
            `✅ 关键情节必定出现，故事推进有序\n` +
            `✅ 伏笔有计划地埋设和回收\n\n` +
            `【如何生成故事结构？】\n` +
            `请先在"故事结构"页面点击"生成故事结构"按钮，生成完整的故事结构后再批量生成章节。\n\n` +
            `💰 提示：生成故事结构会消耗一定字数（约5000-15000字，取决于章节数），但能大幅提升章节质量和连贯性！`,
          confirmText: '前往生成故事结构',
          cancelText: '取消',
          onConfirm: () => {
            setDialog({ open: false });
            setActiveTab(2); // 故事结构在第3个标签（索引2）
          },
          onCancel: () => {
            setDialog({ open: false });
          },
        });
        setIsGenerating(false);
        setBatchGenerating(false);
        setBatchProgress({ current: 0, total: 0, progress: 0 });
        return;
      }

      // 准备章节：确保范围内所有章节存在
      let updatedChapters = [...chapters];
      for (let i = firstChapter; i <= lastChapter; i++) {
        updatedChapters = ensureChapterExists(i, updatedChapters);
      }
      setChapters(updatedChapters);

      // 构建小说对象（章节标题规范化，仅发送结构信息）
      const updatedNovel = {
        ...novel,
        chapters: updatedChapters.map(ch => {
          let chapterTitle;
          if (ch.content && ch.content.trim().length > 0) {
            if (ch.title && ch.title.includes('：')) {
              chapterTitle = ch.title;
            } else if (ch.title !== `第${ch.index}章`) {
              chapterTitle = `第${ch.index}章：${ch.title.replace(/^第\d+章[：:]?\s*/, '')}`;
            } else {
              chapterTitle = `第${ch.index}章`;
            }
          } else {
            chapterTitle = `第${ch.index}章`;
          }
          return {
            id: ch.id,
            index: ch.index,
            title: chapterTitle,
            content: ch.content || '',
            outline: ch.outline || '',
            status: 'draft',
            createdAt: ch.createdAt || new Date().toISOString(),
            updatedAt: ch.updatedAt || new Date().toISOString()
          };
        })
      };

      // 保存到本地存储
      saveChaptersToLocalStorage(updatedNovel, updatedChapters);

      // 保存章节结构到服务器
      const saveResult = await saveNovelToServer(updatedNovel);

      // 同步服务器返回的章节ID（确保本地与服务器一致）
      if (saveResult && saveResult.success && saveResult.novel?.chapters?.length) {
        const serverChapters = saveResult.novel.chapters;
        // 服务器返回章节少于本地（保存不完整）→ 强制重保存一次
        if (serverChapters.length < updatedChapters.length && updatedChapters.length > 0) {
          const retryNovel = {
            ...novel,
            id: saveResult.novel.id,
            chapters: updatedChapters.map(ch => ({
              id: ch.id,
              index: ch.index,
              title: ch.title || `第${ch.index}章`,
              content: ch.content || '',
              outline: ch.outline || '',
              status: 'draft'
            }))
          };
          const retrySaveResult = await saveNovelToServer(retryNovel, true, updatedChapters);
          if (retrySaveResult?.success && retrySaveResult.novel?.chapters?.length) {
            updatedChapters = updatedChapters.map(localCh => {
              const serverCh = retrySaveResult.novel.chapters.find(sc => sc.index === localCh.index);
              return serverCh?.id ? { ...localCh, id: serverCh.id } : localCh;
            });
          }
        } else {
          updatedChapters = updatedChapters.map(localCh => {
            const serverCh = serverChapters.find(sc => sc.index === localCh.index);
            return serverCh?.id ? { ...localCh, id: serverCh.id } : localCh;
          });
        }
        setChapters(updatedChapters);
        saveChaptersToLocalStorage(updatedNovel, updatedChapters);
      }

      // 组装待生成章节索引列表（跳过已有内容章节）
      const chapterIds = [];
      for (let i = firstChapter; i <= lastChapter; i++) {
        const chapter = updatedChapters.find(ch => ch.index === i);
        if (batchRange.skipGenerated && chapter?.content && chapter.content.trim().length > 0) {
          continue;
        }
        chapterIds.push(i.toString());
      }

      if (chapterIds.length === 0) {
        showMessage('没有需要生成的章节', 'info');
        setIsGenerating(false);
        setBatchGenerating(false);
        return;
      }

      // 调用后台批量生成API
      const novelIdToUse = novel._id || novel.id;
      if (!novelIdToUse) {
        throw new Error('无法获取小说ID，请确保小说已保存');
      }

      const result = await taskService.createBatchGenerationTask({
        novelId: novelIdToUse,
        chapterIds: chapterIds,
        settings: {
          ...(novelSettings?.aiSettings || {}),
          model: 'deepseek-v4-flash'
        },
        outline: outline || '',
        genre: novelSettings?.genre || '',
        style: novelSettings?.style || '',
        perspective: novelSettings?.perspective || '',
        dialogueRatio: (batchRange.dialogueRatio || 30) / 100
      });

      if (result && result.data && result.data.taskId) {
        // 保存任务ID（触发轮询 effect 与 WebSocket 实时状态 effect）
        setBatchGenerationTaskId(result.data.taskId);
      }

      // 初始化章节状态显示：第一个待生成章节"生成中"，其余"等待中"（已有内容显示"已生成"）
      const firstToGenerate = parseInt(chapterIds[0], 10);
      const initialStatus = [];
      for (let i = firstChapter; i <= lastChapter; i++) {
        const chapter = updatedChapters.find(ch => ch.index === i);
        const hasContent = chapter?.content && chapter.content.trim().length > 0;
        initialStatus.push({
          chapterIndex: i,
          title: chapter?.title || `第${i}章`,
          status: hasContent ? '已生成' : (i === firstToGenerate ? '生成中' : '等待中'),
          progress: hasContent ? 100 : (i === firstToGenerate ? 5 : 0)
        });
      }
      setChapterGenerationStatus(initialStatus);
      setGeneratingChapterId(firstToGenerate);

      // 同步章节对象的生成状态（UI 正确显示）
      setChapters(prevChapters => {
        return prevChapters.map(ch => {
          if (ch.index >= firstChapter && ch.index <= lastChapter) {
            const hasContent = ch.content && ch.content.trim().length > 0;
            const st = hasContent ? '已生成' : (ch.index === firstToGenerate ? '生成中' : '等待中');
            const chapterStatus = hasContent ? 'generated' : (ch.index === firstToGenerate ? 'generating' : 'pending');
            return {
              ...ch,
              status: chapterStatus,
              generationStatus: st,
              generationProgress: hasContent ? 100 : (ch.index === firstToGenerate ? 5 : 0)
            };
          }
          return ch;
        });
      });
    } catch (error) {
      showMessage(`创建批量生成任务失败: ${error.message || '未知错误'}`, 'error');
      // 🔥 发生错误时重置生成状态
      setIsGenerating(false);
      setBatchGenerating(false);
      setChapterGenerationStatus([]);
      setBatchProgress({ current: 0, total: 0, progress: 0 });
    }
  };

  // 辅助函数：更新章节状态 (保留这个，但改名为避免与impl混淆)
  const updateChapterStatusBatch = useCallback((chapterIndex, statusUpdate) => {
    setChapterGenerationStatus(prev => {
      const newStatus = [...prev];
      const index = newStatus.findIndex(s => s.chapterIndex === chapterIndex);
      if (index !== -1) {
        newStatus[index] = {
          ...newStatus[index],
          ...statusUpdate
        };
      }
      return newStatus;
    });
  }, []);
  
  // 辅助函数：确保章节存在
  const ensureChapterExists = useCallback((chapterIndex, chaptersArray) => {
    // 复制当前章节数组，避免直接修改原数组
    const updatedChapters = [...chaptersArray];
    
    // 检查章节是否已存在
    const existingChapter = updatedChapters.find(ch => ch.index === chapterIndex);
    
    if (!existingChapter) {
      // 创建新章节对象 - 确保标题格式为"第X章"
      const newChapter = {
        id: uuidv4(),
        index: chapterIndex,
        title: `第${chapterIndex}章`,
        content: '',
        outline: '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      // 添加到章节数组
      updatedChapters.push(newChapter);
      
      // 重新排序
      updatedChapters.sort((a, b) => a.index - b.index);
      
      // 立即在控制台记录下添加的新章节，方便调试
      console.log(`创建新章节: 第${chapterIndex}章，ID=${newChapter.id}`, newChapter);
      
      // 通知当前生成的章节ID添加到列表中，方便状态追踪
      if (!chaptersGenerated.includes(chapterIndex)) {
        setChaptersGenerated(prev => [...prev, chapterIndex]);
      }
    } else if (!existingChapter.id) {
      // 如果章节存在但没有ID，添加一个ID
      const chapterIndex = updatedChapters.findIndex(ch => ch === existingChapter);
      if (chapterIndex !== -1) {
        updatedChapters[chapterIndex] = {
          ...existingChapter,
          id: uuidv4()
        };
        console.log(`为已存在的章节添加ID: 第${existingChapter.index}章，ID=${updatedChapters[chapterIndex].id}`);
      }
    }
    
    return updatedChapters;
  }, [chaptersGenerated]);

  // 处理确认对话框
  const handleCloseConfirmDialog = useCallback(() => {
    setConfirmDialog({...confirmDialog, open: false});
  }, [confirmDialog]);

  // 添加确认操作和取消操作的处理函数 
  const handleConfirmAction = useCallback(() => {
    if (confirmDialog.onConfirm) {
      confirmDialog.onConfirm();
    }
    handleCloseConfirmDialog();
  }, [confirmDialog, handleCloseConfirmDialog]);

  const handleCancelAction = useCallback(() => {
    handleCloseConfirmDialog();
  }, [handleCloseConfirmDialog]);

  // 监听chapters变化，更新UI
  useEffect(() => {
    // 当当前选中的章节内容发生变化时，重新加载显示
    if (selectedChapter) {
      const chapterObj = chapters.find(ch => ch.index === selectedChapter);
      // 更新selectedChapterObj状态
      setSelectedChapterObj(chapterObj || null);
      
      if (chapterObj) {
        setCurrentChapterContent(chapterObj.content || '');
        setCurrentChapterOutline(chapterObj.outline || '');
      }
    }
  }, [chapters, selectedChapter]);

  // 抽取保存设置的逻辑为单独的函数
  const saveUpdatedSettings = useCallback((updatedSettings) => {
    if (novel && novel.id) {
      const updatedNovel = {
        ...novel,
        settings: updatedSettings
      };
      setNovel(updatedNovel);
      localStorage.setItem(`novel_${novel.id}`, JSON.stringify(updatedNovel));
      
      // 不再在每次字段变更时保存到服务器
      // 仅在本地更新状态
      console.log('设置已在本地更新，将在生成大纲时保存到服务器');
    }
  }, [novel]);
  
  // 添加一个更新本地设置的辅助函数，不依赖saveUpdatedSettings
  const updateLocalSettings = useCallback((updatedValues) => {
    const updated = { ...novelSettings, ...updatedValues };
    setNovelSettings(updated);
    
    // 直接更新本地状态，不调用saveUpdatedSettings
    if (novel && novel.id) {
      const updatedNovel = {
        ...novel,
        settings: updated
      };
      setNovel(updatedNovel);
      localStorage.setItem(`novel_${novel.id}`, JSON.stringify(updatedNovel));
      console.log('设置已在本地更新，将在生成大纲时保存到服务器');
    }
  }, [novel, novelSettings, setNovel, setNovelSettings]);

  // 给小说设置长度选择联动赋值功能
  const handleLengthChange = useCallback((length) => {
    // 根据长度设置字数和章节数的默认值
    let wordCount = '';
    let chapterCount = '';
    
    switch(length) {
      case '中篇':
        wordCount = '20';
        chapterCount = '100';
        break;
      case '长篇':
        wordCount = '50';
        chapterCount = '250';
        break;
      case '超长篇':
        wordCount = '150';
        chapterCount = '600';
        break;
      default:
        wordCount = '';
        chapterCount = '';
    }

    if (isTrialOnlyUser) {
      chapterCount = '10';
    }

    // 使用updateLocalSettings更新设置
    updateLocalSettings({
      length,
      wordCount: String(wordCount),
      chapterCount: String(chapterCount)
    });
  }, [isTrialOnlyUser, updateLocalSettings]);
  
  useEffect(() => {
    if (!trialCheckDone || !isTrialOnlyUser) {
      return;
    }
    const rawChapterCount = novelSettings && novelSettings.chapterCount;
    const parsedChapterCount = parseInt(rawChapterCount, 10);
    if (!Number.isNaN(parsedChapterCount) && parsedChapterCount > 10) {
      updateLocalSettings({ chapterCount: '10' });
    }
  }, [trialCheckDone, isTrialOnlyUser, novelSettings, updateLocalSettings]);
  
  // 给章节数提供合理性建议
  const getChapterCountSuggestion = useCallback((wordCount, chapterCount) => {
    if (!wordCount || !chapterCount) return null;
    const wc = String(wordCount);
    const cc = String(chapterCount);
    let parsedWordCount = 0;
    if (wc.includes('-')) {
      const parts = wc.split('-');
      parsedWordCount = (parseInt(parts[0]) + parseInt(parts[1])) / 2;
    } else {
      parsedWordCount = parseInt(wc);
    }
    let parsedChapterCount = 0;
    if (cc.includes('-')) {
      const parts = cc.split('-');
      parsedChapterCount = parseInt(parts[0]);
    } else {
      parsedChapterCount = parseInt(cc);
    }
    if (isNaN(parsedWordCount) || isNaN(parsedChapterCount)) return null;
    const avgWordsPerChapter = (parsedWordCount * 10000) / parsedChapterCount;
    if (avgWordsPerChapter < 1000) {
      return `当前设置平均每章仅有${Math.round(avgWordsPerChapter)}字，建议减少章节数或增加总字数。`;
    } else if (avgWordsPerChapter > 5000) {
      return `当前设置平均每章有${Math.round(avgWordsPerChapter)}字，章节可能过长，建议增加章节数或减少总字数。`;
    }
    return null;
  }, []);
  
  // 自动保存设置的函数
  const autoSaveSettings = useCallback(async (settingsUpdate) => {
    if (!novel) {
      console.warn('自动保存设置：小说对象未定义');
      return { success: false, error: '小说对象未定义' };
    }
    
    try {
      // 只更新本地状态，不再每次都保存到服务器
      if (settingsUpdate) {
        updateLocalSettings(settingsUpdate);
        return { success: true };
      }
      return { success: true };
    } catch (error) {
      console.error('设置自动保存出错:', error);
      return { success: false, error: error.message };
    }
  }, [novel, updateLocalSettings]);
  
  // 加载设置 - 在组件挂载时加载保存的设置
  useEffect(() => {
    if (novel && novel.settings) {
      // 确保小说设置里的必要字段都是正确的类型
      const loadedSettings = { ...defaultNovelSettings, ...novel.settings };
      
      // 确保keywords是数组类型
      if (!loadedSettings.keywords || !Array.isArray(loadedSettings.keywords)) {
        loadedSettings.keywords = [];
      }
      
      // 确保所有字符串字段都有值
      const stringFields = ['platform', 'perspective', 'length', 'style', 'genre', 'wordCount', 'chapterCount', 'additionalNotes'];
      stringFields.forEach(field => {
        if (!loadedSettings[field]) {
          loadedSettings[field] = '';
        }
      });
      
      // 如果genre不在预定义列表中且不为空，添加到列表中
      if (loadedSettings.genre && !genres.includes(loadedSettings.genre)) {
        console.log(`小说类型"${loadedSettings.genre}"不在预定义列表中，将作为有效值使用`);
    }
      
      // 对于keywords，确保所有值都有效
      if (Array.isArray(loadedSettings.keywords)) {
        loadedSettings.keywords = loadedSettings.keywords.filter(kw => kw && typeof kw === 'string');
      }
      
      setNovelSettings(loadedSettings);
    }
  }, [novel]);

  // 🔥 自动同步 selectedChapterObj：当 chapters 更新时，同步更新 selectedChapterObj
  useEffect(() => {
    if (selectedChapter && chapters.length > 0) {
      const updatedChapterObj = chapters.find(ch => ch.index === selectedChapter);
      if (updatedChapterObj) {
        // 只在章节对象真正发生变化时才更新（避免无限循环）
        setSelectedChapterObj(prev => {
          // 简单比较关键属性，判断是否需要更新
          if (!prev || 
              prev.generating !== updatedChapterObj.generating ||
              prev.generationStatus !== updatedChapterObj.generationStatus ||
              prev.generationProgress !== updatedChapterObj.generationProgress ||
              prev.content !== updatedChapterObj.content ||
              prev.outline !== updatedChapterObj.outline) {
            return updatedChapterObj;
          }
          return prev;
        });
      }
    }
  }, [chapters, selectedChapter]);

  // 周期性刷新用户余额 - 简化为仅在关键时刻刷新
  useEffect(() => {
    // 初始刷新一次
    fetchAndUpdateUserBalance();
    
    // 创建计数器防止频繁更新
    let lastUpdateTime = Date.now();
    
    // 添加页面获得焦点时刷新余额
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // 只有当距离上次更新超过10秒时才更新
        const now = Date.now();
        if (now - lastUpdateTime > 10000) {
          lastUpdateTime = now;
        fetchAndUpdateUserBalance();
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // 清理事件监听器
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchAndUpdateUserBalance]);

  // 🔥【实时状态推送】批量生成 WebSocket 事件监听（替代高频轮询，消除状态同步延迟与空白期）
  //    后端 queueService 在章节开始/完成/失败、整批完成/失败/等待充值时推送事件到 novel-${novelId} 房间
  const wsConnectedRef = useRef(false);
  // 🔥【WS化】重连对账用：由章节轮询 effect 注册自身，WS 重连时立即拉一次章节状态
  const pollChaptersRef = useRef(null);
  // 🔥【WS化】批量生成事件通道的独立健康标志（与体检/结构通道分离，防止误判导致轮询走30秒慢速造成状态空窗）
  const batchWsRef = useRef(false);
  // 🔥【降噪】轮询effect依赖稳定化：batchRange/fetchNovelData 用 ref 透传，
  // 避免其引用变化导致轮询 effect 反复重启（"已启动"刷屏 + 每次重建重复首查）
  const batchRangeRef = useRef(batchRange);
  const fetchNovelDataRef = useRef(fetchNovelData);
  useEffect(() => { batchRangeRef.current = batchRange; }, [batchRange]);
  useEffect(() => { fetchNovelDataRef.current = fetchNovelData; }, [fetchNovelData]);
  // 🔥【看门狗】最后一次收到批量WS事件的时间戳——轮询以此为活动信号：
  // 90秒内有事件=通道健康，轮询零请求；断线或超90秒无事件=假在线嫌疑，轮询接管对账
  const batchWsLastEventRef = useRef(0);
  // 🔥【看门狗】批量任务ID的 ref 透传（章节轮询 effect 依赖不含它，闭包值会过期）
  const batchTaskIdRef = useRef(null);
  useEffect(() => { batchTaskIdRef.current = batchGenerationTaskId; }, [batchGenerationTaskId]);

  useEffect(() => {
    if (!batchGenerationTaskId || !novel?.id) return;

    // WebSocket 服务地址：复用 axios 实例 baseURL（去掉 /api 后缀）
    const apiBase = (api?.defaults?.baseURL) || '';
    const wsUrl = apiBase.replace(/\/api\/?$/, '');
    if (!wsUrl) return;

    let socket;
    try {
      // 🔥【WS化】无限重连+封顶退避：服务重启窗口过后自动恢复推送，绝不放弃
      socket = io(wsUrl, { transports: ['websocket'], reconnection: true, reconnectionAttempts: Infinity, reconnectionDelayMax: 3000 });
    } catch (_) {
      return; // WebSocket 初始化失败 → 退回纯轮询模式
    }
    const novelIdStr = String(novel.id);

    socket.on('connect', () => {
      batchWsRef.current = true;
      console.log(`🔥[WS] 批量事件通道已连接 @${new Date().toLocaleTimeString()}`);
      socket.emit('joinNovel', novelIdStr);
      // 🔥【WS化】重连对账：立即拉一次章节状态，填补断线期间漏掉的 started/completed 事件
      try { pollChaptersRef.current && pollChaptersRef.current(); } catch (_) {}
    });
    socket.on('disconnect', () => {
      batchWsRef.current = false;
      console.log(`🔥[WS] 批量事件通道断开（自动重连中）@${new Date().toLocaleTimeString()}`);
    });

    // 章节开始生成 → 立即显示"生成中"（消除上一章完成后到下一章开始间的状态空白期）
    socket.on('batch:chapter_started', (data) => {
      const idx = Number(data?.chapterIndex);
      if (!Number.isFinite(idx)) return;
      console.log(`🔥[WS推送] 第${idx}章开始生成 @${new Date().toLocaleTimeString()}`);
      setGeneratingChapterId(idx);
      setChapterGenerationStatus(prev => {
        const arr = Array.isArray(prev) ? [...prev] : [];
        const i = arr.findIndex(s => s.chapterIndex === idx);
        const item = {
          chapterIndex: idx,
          title: (i >= 0 && arr[i].title) || `第${idx}章`,
          status: '生成中',
          progress: 5
        };
        if (i >= 0) arr[i] = { ...arr[i], ...item }; else arr.push(item);
        return arr;
      });
      setChapters(prevChapters => prevChapters.map(ch => {
        if (ch.index === idx && (!ch.content || ch.content.trim().length === 0)) {
          return { ...ch, status: 'generating', generationStatus: '生成中', generationProgress: 5 };
        }
        return ch;
      }));
    });

    // 章节完成 → 立即更新"已生成"（不等下一轮轮询）
    socket.on('batch:chapter_completed', (data) => {
      const idx = Number(data?.chapterIndex);
      if (!Number.isFinite(idx)) return;
      console.log(`🔥[WS推送] 第${idx}章完成（正文${data?.content?.length || 0}字）@${new Date().toLocaleTimeString()}`);
      setChapterGenerationStatus(prev => {
        const arr = Array.isArray(prev) ? [...prev] : [];
        const i = arr.findIndex(s => s.chapterIndex === idx);
        const item = {
          chapterIndex: idx,
          title: data?.title || (i >= 0 && arr[i].title) || `第${idx}章`,
          status: '已生成',
          progress: 100
        };
        if (i >= 0) arr[i] = { ...arr[i], ...item }; else arr.push(item);
        return arr;
      });
      setBatchProgress(prev => ({
        current: Number(data?.current) || prev.current,
        total: Number(data?.total) || prev.total,
        progress: prev.total ? Math.round((Number(data?.current) || prev.current) / prev.total * 100) : prev.progress
      }));
      setChapters(prevChapters => prevChapters.map(ch => {
        if (ch.index === idx) {
          // 🔥【WS化】正文随事件推送回填（免轮询拉取）；无正文时由懒加载/慢速轮询兜底
          const pushedContent = typeof data?.content === 'string' ? data.content : '';
          const hasPushedContent = pushedContent.trim().length > 0;
          return {
            ...ch,
            status: 'generated',
            generationStatus: '已生成',
            generationProgress: 100,
            ...(hasPushedContent ? {
              content: pushedContent,
              hasContent: true,
              contentLength: Number(data?.contentLength) || pushedContent.length
            } : {})
          };
        }
        return ch;
      }));
    });

    // 单章失败 → 立即显示"生成失败"（任务继续下一章）
    socket.on('batch:chapter_failed', (data) => {
      const idx = Number(data?.chapterIndex);
      if (!Number.isFinite(idx)) return;
      setChapterGenerationStatus(prev => {
        const arr = Array.isArray(prev) ? [...prev] : [];
        const i = arr.findIndex(s => s.chapterIndex === idx);
        const item = {
          chapterIndex: idx,
          title: (i >= 0 && arr[i].title) || `第${idx}章`,
          status: '生成失败',
          progress: 0,
          error: data?.message || ''
        };
        if (i >= 0) arr[i] = { ...arr[i], ...item }; else arr.push(item);
        return arr;
      });
      setChapters(prevChapters => prevChapters.map(ch =>
        ch.index === idx
          ? { ...ch, status: 'failed', generationStatus: '生成失败', generationProgress: 0 }
          : ch
      ));
    });

    // 整批完成 → 立即停止轮询、复位状态、智能刷新数据
    socket.on('batch:completed', async (data) => {
      setBatchGenerationTaskId(null);
      setIsGenerating(false);
      setBatchGenerating(false);
      setBatchProgress({ current: 0, total: 0, progress: 0 });
      setGeneratingChapterId(null);
      setChapterGenerationStatus([]);
      setChapters(prevChapters => prevChapters.map(ch => ({
        ...ch,
        generationStatus: '',
        generationProgress: 0,
        generating: false,
        status: ch.content && ch.content.trim().length > 0 ? 'completed' : 'draft',
        waitingForGeneration: false,
        inGenerationQueue: false
      })));
      try {
        await fetchNovelData(novel.id, { preserveTab: true, preserveExpanded: true, silent: true });
        fetchAndUpdateUserBalance();
        const completed = Number(data?.completedChapters) || 0;
        const failed = Number(data?.failedChapters) || 0;
        if (failed > 0) {
          showMessage(`批量生成完成，成功生成${completed}章内容，${failed}章失败`, 'warning');
        } else {
          showMessage(`批量生成完成，成功生成${completed}章内容`, 'success');
        }
      } catch (_) {
        showMessage('批量生成完成，但刷新数据失败，请手动刷新页面', 'warning');
      }
    });

    // 整批失败 → 提示并复位
    socket.on('batch:failed', (data) => {
      setBatchGenerationTaskId(null);
      setIsGenerating(false);
      setBatchGenerating(false);
      setBatchProgress({ current: 0, total: 0, progress: 0 });
      setGeneratingChapterId(null);
      setChapterGenerationStatus([]);
      showMessage(`批量生成失败: ${data?.message || '未知错误'}`, 'error');
    });

    // 余额不足暂停 → 提示充值（充值后可在任务管理续跑）
    socket.on('batch:waiting_recharge', (data) => {
      setBatchGenerationTaskId(null);
      setIsGenerating(false);
      setBatchGenerating(false);
      setGeneratingChapterId(null);
      showMessage(data?.message || '余额不足，任务已暂停，充值后可继续生成', 'warning');
    });

    // 🔥【看门狗】任意批量事件到达即刷新活动时间戳——轮询据此判断通道健康（90秒内有事件=零请求）
    socket.onAny(() => { batchWsLastEventRef.current = Date.now(); });

    return () => {
      batchWsRef.current = false;
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchGenerationTaskId, novel?.id]);

  // ═══════════════════════════════════════════════════════════════
  // 🔥【全书体检】全自动审读-修订：状态轮询 + WebSocket 实时事件
  //    后端 novelAuditService 推送 audit:* 事件到 novel-${novelId} 房间
  // ═══════════════════════════════════════════════════════════════

  // 拉取体检状态（首屏恢复 + 轮询兜底）
  const pollAuditStatus = useCallback(async () => {
    if (!novel?.id) return null;
    try {
      const res = await api.get(`/novels/${novel.id}/audit/status`);
      const s = res.data || {};
      if (s.exists) {
        setAuditStatus(s);
        const active = ['pending', 'auditing', 'revising', 'waiting_recharge'].includes(s.status);
        setAuditActive(active);
        if (s.status === 'completed' && !auditReport) {
          // 完成且本地无报告 → 拉取报告
          try {
            const rr = await api.get(`/novels/${novel.id}/audit/report`);
            if (rr.data?.success && rr.data?.task?.report) {
              setAuditReport(rr.data.task);
              setAuditProblems((rr.data.task.problems || []).map(p => ({
                chapterNo: p.chapterNo, severity: p.severity, typeLabel:
                  ({ number_conflict: '数字矛盾', timeline: '时间线错误', char_state: '人物状态矛盾', setting_conflict: '设定冲突', logic_break: '逻辑断裂', foreshadow: '伏笔断层', style: '文风套路', signing_risk: '签约风险', other: '其他' })[p.type] || p.type,
                description: p.description, status: p.status
              })));
            }
          } catch (_) {}
        }
        return s;
      }
      return null;
    } catch (_) { return null; }
  }, [novel?.id, auditReport]);

  // 首屏加载时恢复体检状态（刷新页面后按钮置灰/面板状态不丢）
  useEffect(() => { pollAuditStatus(); }, [pollAuditStatus]);

  // ═══════════════════════════════════════════════════════════════
  // 🔥【体检留痕】历史体检报告面板（持久可查：每次体检记录+改前/改后明细）
  // ═══════════════════════════════════════════════════════════════
  // 打开面板：拉历史任务列表（持久存储，随时可看"到底有没有体检"）
  const openAuditPanel = useCallback(async () => {
    setAuditPanelOpen(true);
    if (!novel?.id) return;
    try {
      const res = await api.get(`/novels/${novel.id}/audit/history`);
      const tasks = res.data?.tasks || [];
      setAuditHistory(tasks);
      // 默认选中最近一次任务
      if (tasks.length > 0) {
        loadAuditDetailRef.current?.(tasks[0].taskId);
      } else {
        setAuditDetail(null);
      }
    } catch (_) { setAuditHistory([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [novel?.id]);

  // 加载单次体检详情（问题清单+逐章修改明细）
  const loadAuditDetail = useCallback(async (taskId) => {
    if (!taskId || !novel?.id) return;
    setAuditDetailLoading(true);
    setAuditFixExpanded({});
    try {
      const res = await api.get(`/novels/${novel.id}/audit/detail/${taskId}`);
      setAuditDetail(res.data?.detail || null);
      setAuditDetailTab('fixes');
    } catch (_) { setAuditDetail(null); }
    setAuditDetailLoading(false);
  }, [novel?.id]);
  const loadAuditDetailRef = useRef(loadAuditDetail);
  loadAuditDetailRef.current = loadAuditDetail;

  // 体检任务状态 → 中文标签/颜色（所有界面状态用中文）
  const auditStatusLabel = (s) => ({
    pending: '排队中', auditing: '审读中', revising: '修订中', waiting_recharge: '待恢复',
    completed: '已完成', failed: '失败', cancelled: '已取消'
  })[s] || '未知';
  const auditStatusColor = (s) => ({
    pending: '#06b6d4', auditing: '#06b6d4', revising: '#a855f7', waiting_recharge: '#f59e0b',
    completed: '#10b981', failed: '#ef4444', cancelled: 'rgba(255,255,255,0.35)'
  })[s] || 'rgba(255,255,255,0.35)';

  // 🔥【全自动体检】常驻监听：不依赖手动点击，自动体检启动后徽标/面板实时跟进
  useEffect(() => {
    if (!novel?.id) return;

    // 轮询兜底（🔥【WS化】正常时30秒慢速对账；事件驱动为主，断线时此轮询保证状态推进）
    const poll = setInterval(pollAuditStatus, 30000);

    const apiBase = (api?.defaults?.baseURL) || '';
    const wsUrl = apiBase.replace(/\/api\/?$/, '');
    if (!wsUrl) return () => clearInterval(poll);

    let socket;
    try {
      // 🔥【WS化】无限重连+封顶退避：服务重启后自动恢复推送，绝不放弃
      socket = io(wsUrl, { transports: ['websocket'], reconnection: true, reconnectionAttempts: Infinity, reconnectionDelayMax: 3000 });
    } catch (_) { return () => clearInterval(poll); }

    socket.on('connect', () => {
      wsConnectedRef.current = true; // 🔥 常驻通道健康标志（非批量场景的轮询节奏判断用）
      console.log(`🔥[WS] 实时通道已连接（页面刷新后看到此行=WebSocket正常）@${new Date().toLocaleTimeString()}`);
      socket.emit('joinNovel', String(novel.id));
      // 🔥【WS化】重连对账：立即拉一次体检状态与章节状态，填补断线期间漏掉的事件
      try { pollAuditStatus(); } catch (_) {}
      try { pollChaptersRef.current && pollChaptersRef.current(); } catch (_) {}
    });
    socket.on('disconnect', () => {
      wsConnectedRef.current = false;
      console.log(`🔥[WS] 实时通道断开（自动重连中）@${new Date().toLocaleTimeString()}`);
    });

    // 🔥【WS化】故事结构生成进度实时推送（原3秒全量轮询降级为15秒兜底）
    socket.on('storyStructureProgress', (data) => {
      try {
        if (!data) return;
        if (data.failed) {
          // 失败：停轮询、保留进度面板展示失败态与重试入口（与原轮询失败分支一致）
          setGeneratingStructure(false);
          return;
        }
        if (data.completed && data.data) {
          // 完成：后端推送了完整 storyStructure，直接落地（与原轮询完成分支一致）
          setStoryStructure(data.data);
          setGeneratingStructure(false);
          setShowProgressPanel(false);
          showMessage('✅ 故事结构生成完成！', 'success');
          fetchAndUpdateUserBalance().catch(() => {});
          return;
        }
        // 进行中：更新进度面板数据
        setNovel(prev => (prev ? {
          ...prev,
          storyStructure: {
            ...prev.storyStructure,
            generationStatus: 'generating',
            generationProgress: {
              ...(prev.storyStructure?.generationProgress || {}),
              stage: data.stage,
              progress: data.progress,
              message: data.message,
              stageName: data.stageName,
              estimatedTotalSeconds: data.estimatedTotalSeconds,
              estimatedRemainingSeconds: data.estimatedRemainingSeconds,
              elapsedTime: data.elapsedTime,
              stageEstimates: data.stageEstimates || null,
              updatedAt: data.timestamp
            }
          }
        } : prev));
      } catch (_) { /* 推送处理失败不影响主流程 */ }
    });

    // 双进度更新（收到事件即说明有进行中的体检）
    socket.on('audit:progress', (data) => {
      setAuditActive(true);
      setAuditStatus(prev => prev ? { ...prev, progress: data?.progress ?? prev.progress, stageText: data?.stageText ?? prev.stageText } : prev);
    });

    // 实时问题流
    socket.on('audit:problem', (data) => {
      setAuditActive(true);
      setAuditProblems(prev => [...prev, {
        chapterNo: data?.chapterNo, severity: data?.severity, typeLabel: data?.typeLabel || '问题',
        description: data?.description || '', status: 'pending'
      }]);
    });

    // 修订开始
    socket.on('audit:rewrite', (data) => {
      setAuditProblems(prev => [...prev, {
        chapterNo: data?.chapterNo, kind: 'rewrite',
        description: `正在修订第${data?.chapterNo}章（${data?.problemCount || 0}个问题）`, status: 'rewriting'
      }]);
    });

    // 修订结果
    socket.on('audit:rewrite_done', (data) => {
      setAuditProblems(prev => prev.map(p =>
        (p.kind === 'rewrite' && p.chapterNo === data?.chapterNo && p.status === 'rewriting')
          ? { ...p, status: data?.ok ? 'fixed' : 'skip_maxround', description: data?.ok ? `第${data?.chapterNo}章修订完成，复验通过` : `第${data?.chapterNo}章两轮未达标，已保留原稿` }
          : p
      ));
      pollAuditStatus();
    });

    // 完成/失败/挂起
    socket.on('audit:completed', () => {
      setAuditActive(false);
      pollAuditStatus();
      showMessage('全书体检完成，已生成报告', 'success');
    });
    socket.on('audit:failed', (data) => {
      setAuditActive(false);
      pollAuditStatus();
      showMessage(data?.message || '体检失败', 'error');
    });
    socket.on('audit:waiting_recharge', (data) => {
      if (data?.suspendReason === 'reverse') {
        showMessage('逆向通道暂时不可用，体检已自动暂停，恢复后将自动继续', 'warning');
      } else {
        showMessage(data?.message || '体检已暂停', 'warning');
      }
    });

    return () => {
        clearInterval(poll);
        wsConnectedRef.current = false;
        socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [novel?.id, pollAuditStatus]);


  // 批量生成任务状态轮询（WebSocket 已连接时降频为兜底模式：15秒一次且不拉全量小说数据）
  useEffect(() => {
    // 如果没有批量生成任务ID或小说ID，则不轮询
    if (!batchGenerationTaskId || !novel?.id) return;
    
    console.log(`🛰️ [兜底轮询] 已启动（WS在线=15秒节奏，仅防漏事件）: ${batchGenerationTaskId}`);

    let isFirstPoll = true; // 标记是否是首次轮询
    let lastStatusSig = ''; // 🔥【降噪】上次打印的状态签名，无变化静默
    let pollInterval = null;
    
    // 轮询函数
    const pollTaskStatus = async () => {
      let task = null; // 声明在函数顶部，确保作用域覆盖整个函数
      // 🔥【降噪】每次轮询从 ref 取最新值（遮蔽外部同名变量），effect 无需因引用变化而重启
      const batchRange = batchRangeRef.current;
      const fetchNovelData = fetchNovelDataRef.current;

      // 🔥【看门狗】批量WS通道健康（在线且90秒内有事件活动）→ 本轮零请求，
      //    进度/章节状态/完成清理全部由 batch:* 推送驱动；仅排下一次活动检查。
      //    断线或超90秒无事件（假在线嫌疑）才落到下方真实请求对账。
      if (batchWsRef.current && (Date.now() - batchWsLastEventRef.current < 90000)) {
        pollInterval = setTimeout(pollTaskStatus, 90000);
        return;
      }

      try {
        // 获取任务状态
        const taskStatus = await taskService.getTaskStatus(batchGenerationTaskId);
        
        if (!taskStatus || !taskStatus.data) {
          console.log('获取任务状态失败，可能任务不存在');
          if (pollInterval) {
            clearTimeout(pollInterval);
          }
          
          // 完全重置所有批量生成相关状态
          setBatchGenerationTaskId(null);
          setIsGenerating(false);
          setBatchGenerating(false);
          setBatchProgress({current: 0, total: 0, progress: 0});
          setGeneratingChapterId(null);
          setChapterGenerationStatus([]);
          
          // 同时重置所有章节的生成状态
          setChapters(prevChapters => {
            return prevChapters.map(ch => {
              // 检查章节是否有内容
              const hasContent = ch.content && ch.content.trim().length > 0;
              
              return {
                ...ch,
                // 如果有内容，则标记为已生成，否则清除生成状态
                generationStatus: hasContent ? '已生成' : '',
                generationProgress: hasContent ? 100 : 0
              };
            });
          });
          
          console.log('任务状态获取失败，已重置所有批量生成相关状态');
          return;
        }
        
        task = taskStatus.data;
        // 🔥【降噪】进度/状态无变化时静默——WS 推送才是状态驱动主角，轮询日志只在变化时出现
        const statusSig = `${task.status}:${task.progress}`;
        if (statusSig !== lastStatusSig) {
          lastStatusSig = statusSig;
          console.log(`📊 [兜底] 任务状态: ${task.status}, 进度: ${task.progress}%`);
        }
        
        // 更新批量生成进度
        setBatchProgress({
          current: task.result?.completedChapters || 0,
          total: task.result?.totalChapters || 0,
          progress: task.progress || 0
        });
        
        // 🎯 优化：在任务进行中时，也获取最新的小说数据，确保UI显示正确
        // 使用智能刷新，保持用户当前位置
        // 🔥【服务器减压】WebSocket 已连接时由实时事件驱动章节状态更新，
        //    轮询跳过全量小说数据拉取（此前每1-3秒双重全量拉取是服务器压力主源），
        //    仅在 WS 断连的降级场景下保留原全量同步逻辑
        if (task.status === 'processing' && !wsConnectedRef.current) {
          try {
            // 使用fetchNovelData进行静默刷新，保持标签页和展开状态
            await fetchNovelData(novel.id, {
              preserveTab: true,        // 保持当前标签页
              preserveExpanded: true,   // 保持章节展开状态
              silent: true              // 静默刷新
            });
            
            // 获取最新的小说数据（用于下面的章节状态更新和提前检测完成）
            const response = await api.get(`/novels/${novel.id}`);
            const novelData = response.data?.data || response.data;
            
            // 🔥 快速检测：只检查最后一章是否有内容，有内容就说明全部完成
            if (novelData && novelData.chapters && batchRange && batchRange.endChapter) {
              const lastChapterIndex = batchRange.endChapter;
              const lastChapter = novelData.chapters.find(ch => ch.index === lastChapterIndex);
              const lastChapterHasContent = lastChapter && lastChapter.content && lastChapter.content.trim().length > 0;
              
              console.log(`🔍 [快速检测] 最后一章(第${lastChapterIndex}章)有内容: ${lastChapterHasContent}, 内容长度: ${lastChapter?.content?.length || 0}`);
              
              if (lastChapterHasContent) {
                console.log('🎉 [快速检测] 最后一章已有内容，批量生成完成，立即停止生成状态！');
                
                // 立即停止轮询
                if (pollInterval) {
                  clearTimeout(pollInterval);
                  pollInterval = null;
                }
                
                // 计算完成的章节数
                const totalChapters = batchRange.endChapter - batchRange.startChapter + 1;
                
                // 🔧 修复：先清除所有章节的生成状态（在状态重置之前）
                setChapters(prevChapters => {
                  return prevChapters.map(ch => ({
                    ...ch,
                    generationStatus: '',
                    generationProgress: 0,
                    generating: false,
                    status: ch.content && ch.content.trim().length > 0 ? 'completed' : 'draft',
                    waitingForGeneration: false,
                    inGenerationQueue: false
                  }));
                });
                
                // 然后重置所有批量生成相关状态
                setBatchGenerationTaskId(null);
                setIsGenerating(false);
                setBatchGenerating(false);
                setBatchProgress({current: 0, total: 0, progress: 0});
                setGeneratingChapterId(null);
                setChapterGenerationStatus([]);
                
                // 🔧 修复：使用 setTimeout 确保状态更新完成后再刷新数据
                setTimeout(async () => {
                  // 最后一次刷新数据
                  await fetchNovelData(novel.id, {
                    preserveTab: true,
                    preserveExpanded: true,
                    silent: true,
                    clearGenerationStatus: true  // 强制清除生成状态
                  });
                  
                  // 显示成功消息
                  showMessage(`批量生成完成，成功生成${totalChapters}章内容`, 'success');
                }, 100);
                
                // 停止执行后续轮询
                return;
              }
            }
            
            if (novelData && novelData.chapters) {
              // 更新章节列表，保留所有现有章节，同时更新服务器返回的章节
              setChapters(prevChapters => {
                // 创建合并后的章节数组
                const mergedChapters = [...prevChapters];
                let hasUpdates = false;
                
                                  // 更新或添加服务器返回的章节
                  novelData.chapters.forEach(serverCh => {
                    const localIndex = mergedChapters.findIndex(ch => 
                      (ch.id && serverCh.id && ch.id === serverCh.id) || 
                      ch.index === serverCh.index
                    );
                    
                    if (localIndex >= 0) {
                      // 保留原始生成状态
                      const originalStatus = mergedChapters[localIndex].generationStatus;
                      const originalProgress = mergedChapters[localIndex].generationProgress;
                      
                      // 检查是否需要更新内容
                      const needContentUpdate = 
                        serverCh.content && 
                        (!mergedChapters[localIndex].content || 
                         mergedChapters[localIndex].content.trim().length === 0);
                      
                      // 检查服务器章节是否有失败状态
                      const serverHasFailedStatus = 
                        serverCh.status === 'failed' || 
                        serverCh.generationStatus === '生成失败';
                      
                      // 如果服务器章节有内容或标记为失败，则更新状态
                      if (needContentUpdate || serverHasFailedStatus) {
                        // 更新章节内容和状态
                        mergedChapters[localIndex] = {
                          ...mergedChapters[localIndex],
                          ...serverCh,
                          // 保留ID以确保一致性
                          id: mergedChapters[localIndex].id || serverCh.id,
                          // 更新状态
                          generationStatus: serverHasFailedStatus ? '生成失败' : 
                                          (serverCh.content && serverCh.content.trim().length > 0) ? '已生成' : 
                                          originalStatus,
                          generationProgress: serverHasFailedStatus ? 0 : 
                                            (serverCh.content && serverCh.content.trim().length > 0) ? 100 : 
                                            originalProgress
                        };
                        hasUpdates = true;
                      }
                    } else {
                      // 如果是新章节，添加到数组
                      const serverHasFailedStatus = 
                        serverCh.status === 'failed' || 
                        serverCh.generationStatus === '生成失败';
                      
                      mergedChapters.push({
                        ...serverCh,
                        generationStatus: serverHasFailedStatus ? '生成失败' : 
                                         (serverCh.content && serverCh.content.trim().length > 0) ? '已生成' : 
                                         '等待生成',
                        generationProgress: serverHasFailedStatus ? 0 : 
                                           (serverCh.content && serverCh.content.trim().length > 0) ? 100 : 
                                           0
                      });
                      hasUpdates = true;
                    }
                });
                
                // 确保所有批量生成范围内的章节都存在
                if (batchRange && batchRange.startChapter && batchRange.endChapter) {
                  for (let i = batchRange.startChapter; i <= batchRange.endChapter; i++) {
                    // 检查章节是否存在
                    const chapterExists = mergedChapters.some(ch => ch.index === i);
                    
                    if (!chapterExists) {
                      console.log(`添加批量生成范围内缺失的章节: 第${i}章`);
                      mergedChapters.push({
                        id: uuidv4(),
                        index: i,
                        title: `第${i}章`,
                        content: '',
                        outline: '',
                        status: 'generating',
                        createdAt: new Date().toISOString(),
                        updatedAt: new Date().toISOString(),
                        generationStatus: '等待生成',
                        generationProgress: 0
                      });
                      hasUpdates = true;
                    }
                  }
                }
                
                // 排序并返回
                if (hasUpdates) {
                  return mergedChapters.sort((a, b) => a.index - b.index);
                }
                return mergedChapters;
              });
            }
          } catch (error) {
            console.error('轮询期间获取小说数据失败:', error);
          }
        }
        
                  // 如果任务完成或失败，停止轮询
        if (task.status === 'completed' || task.status === 'failed') {
          console.log(`批量生成任务${task.status === 'completed' ? '完成' : '失败'}`);
          if (pollInterval) {
            clearTimeout(pollInterval);
            pollInterval = null; // 清空轮询引用
          }
          
          // 完全重置所有批量生成相关状态
          setBatchGenerationTaskId(null);
          setIsGenerating(false);
          setBatchGenerating(false);
          setBatchProgress({current: 0, total: 0, progress: 0});
          setGeneratingChapterId(null);
          
          // 清空章节生成状态列表，确保不会保留旧的生成状态
          setChapterGenerationStatus([]);
          
          // 修改：彻底清除所有章节的生成状态和标记
          setChapters(prevChapters => {
            return prevChapters.map(ch => ({
              ...ch,
              generationStatus: '', // 清空生成状态
              generationProgress: 0,
              generating: false,
              // 确保批量生成完成后显示正确的状态
              status: ch.content && ch.content.trim().length > 0 ? 'completed' : 'draft',
              // 添加额外的清理属性，确保所有可能影响状态显示的标志都被清除
              waitingForGeneration: false,
              inGenerationQueue: false
            }));
          });
          
          console.log('已重置所有批量生成相关状态');
          
          // 🎯 优化：刷新小说数据，保持用户当前位置和状态
          if (task.status === 'completed') {
            console.log('批量生成任务完成，使用智能刷新（保持标签页和展开状态）');
            try {
              // 使用fetchNovelData并启用状态保持选项
              await fetchNovelData(novel.id, {
                preserveTab: true,        // 保持当前标签页
                preserveExpanded: true,   // 保持章节展开状态
                silent: true              // 静默刷新，不显示loading
              });
              
              // 显示成功消息
              const completedCount = task.result?.completedChapters || 0;
              const failedCount = task.result?.failedChapters || 0;
              
              if (failedCount > 0) {
                showMessage(`批量生成完成，成功生成${completedCount}章内容，${failedCount}章失败`, 'warning');
              } else {
                showMessage(`批量生成完成，成功生成${completedCount}章内容`, 'success');
              }
            } catch (error) {
              console.error('刷新小说数据失败:', error);
              showMessage('批量生成完成，但刷新数据失败，请手动刷新页面', 'warning');
            }
          } else {
            // 显示失败消息
            showMessage(`批量生成失败: ${task.result?.message || '未知错误'}`, 'error');
          }
          
          // ⚠️ 任务完成，停止轮询，不再继续执行
          return;
        }
      } catch (error) {
        console.error('轮询任务状态失败:', error);
      }
      
      // 🔥 优化：使用智能轮询间隔，根据任务进度动态调整
      // 🔥【看门狗】批量WS在线时走到这里=通道断线或90秒无事件（假在线），对账后回到90秒看门狗节奏；
      //    真断线（batchWsRef=false）才使用 1-3 秒智能间隔接管
      let nextPollDelay;
      if (batchWsRef.current) {
        nextPollDelay = 90000; // 在线但刚对账：90秒后再检查活动
      } else if (task && task.progress > 80) {
        nextPollDelay = 1000; // 1秒，即将完成
      } else if (task && task.progress > 50) {
        nextPollDelay = 2000; // 2秒，接近完成
      } else {
        nextPollDelay = 3000; // 3秒，正常轮询
      }
      
      if (isFirstPoll) {
        isFirstPoll = false;
        // 🔥【降噪】轮询节奏不再刷屏——状态变化由上方 statusSig 变更时打印
      }

      pollCount++;
      pollInterval = setTimeout(pollTaskStatus, nextPollDelay);
    };
    
    let pollCount = 0; // 轮询计数器
    
    // 🔥 立即执行首次轮询（立即获取状态）
    pollInterval = setTimeout(pollTaskStatus, 0);
    
    // 清理函数
    return () => {
      if (pollInterval) {
        clearTimeout(pollInterval);
      }
    };
  }, [batchGenerationTaskId, novel?.id]);

  // 🔥 重写章节已改为同步模式，不再需要轮询

  // 初始化已生成章节列表
  useEffect(() => {
    if (novel && novel.id) {
      try {
        // 从localStorage中加载已生成章节的记录
        const generatedChaptersStr = localStorage.getItem(`generated_chapters_${novel.id}`);
        if (generatedChaptersStr) {
          const generatedChapters = JSON.parse(generatedChaptersStr);
          if (Array.isArray(generatedChapters)) {
            // 🔥 过滤掉非数字项，防止旧版本存储了对象导致的 [object Object] 问题
            const validChapters = generatedChapters.filter(ch => typeof ch === 'number' || typeof ch === 'string');
            console.log(`从本地存储加载已生成章节记录: ${validChapters.length}个`);
            setChaptersGenerated(validChapters);
          }
        }
      } catch (error) {
        console.error('加载已生成章节记录失败:', error);
      }
    }
  }, [novel]);

  // 🔥 检查故事结构生成状态，如果在生成中则自动重连WebSocket
  // 🔥 使用 ref 记录是否已经检查过故事结构状态，避免重复提示
  const hasCheckedStructureStatus = useRef(false);
  
  useEffect(() => {
    if (!novel || !(novel._id || novel.id)) return;
    const nid = novel._id || novel.id;

    // 🔥 只在首次加载时检查一次，避免重复提示
    if (hasCheckedStructureStatus.current) return;
    
    const generationStatus = novel.storyStructure?.generationStatus;
    console.log('[故事结构] 首次检查生成状态:', generationStatus);
    
    if (generationStatus === 'generating' || generationStatus === 'retrying') {
      console.log('[故事结构] 检测到正在生成中，自动显示进度面板并重连WebSocket');
      setShowProgressPanel(true);
      setStructureExpanded(true); // 展开故事结构面板
      showMessage('检测到故事结构正在生成中，已自动重连进度显示', 'info');
      hasCheckedStructureStatus.current = true; // 标记已检查
    } else if (generationStatus === 'completed' && !novel.storyStructure?.isGenerated) {
      // 生成已完成但还没刷新，提示用户
      console.log('[故事结构] 生成已完成，提示用户查看');
      showMessage('故事结构已生成完成！', 'success');
      hasCheckedStructureStatus.current = true; // 标记已检查
    } else if (generationStatus === 'failed') {
      console.log('[故事结构] 生成失败');
      console.error('   ⚠️  故事结构生成失败（详细错误仅记录在控制台）');
      hasCheckedStructureStatus.current = true; // 标记已检查
    } else {
      // 没有正在生成的任务，也标记为已检查
      hasCheckedStructureStatus.current = true;
    }
  }, [novel, showMessage]);

  // 🔥 轮询故事结构生成进度
  useEffect(() => {
    if (!novel || !(novel._id || novel.id)) return;
    const nid = novel._id || novel.id;
    
    const generationStatus = novel.storyStructure?.generationStatus;
    
    // 只在生成中时启动轮询
    if (generationStatus !== 'generating') {
      return;
    }
    
    console.log('[故事结构轮询] 启动进度轮询');

    // 创建轮询定时器 - 🔥【WS化】进度已由 storyStructureProgress 事件实时推送，轮询降级为15秒兜底
    const pollTimer = setInterval(async () => {
      try {
        // 查询最新进度（静默，正常时由 WS 推送）

        // 获取小说最新数据
        const response = await api.get(`/novels/${nid}`);
        const novelData = response.data?.data || response.data;

        if (novelData && novelData.storyStructure) {
          const latestStatus = novelData.storyStructure.generationStatus;
          const latestProgress = novelData.storyStructure.generationProgress;

          // 更新小说数据
          setNovel(prevNovel => ({
            ...prevNovel,
            storyStructure: {
              ...prevNovel.storyStructure,
              ...novelData.storyStructure
            }
          }));
          
          // 更新故事结构状态
          if (novelData.storyStructure.isGenerated || latestStatus === 'completed') {
            console.log('[故事结构轮询] 生成已完成！');
            setStoryStructure(novelData.storyStructure);
            setGeneratingStructure(false);
            setShowProgressPanel(false);
            showMessage('✅ 故事结构生成完成！', 'success');
            
            // 🔥 立即刷新用户余额，确保扣费后的余额同步到前端
            await fetchAndUpdateUserBalance();
            console.log('✅ 故事结构生成完成后已刷新用户余额');
            
            // 保存到本地存储
            await saveNovelToServer(prevNovel => ({
              ...prevNovel,
              storyStructure: novelData.storyStructure
            }));
          } else if (latestStatus === 'failed') {
            console.log('[故事结构轮询] 生成失败，但保持进度面板显示以便用户重试');
            setGeneratingStructure(false);
            // 不隐藏进度面板，让用户看到失败状态和重试按钮
            // setShowProgressPanel(false); // ❌ 注释掉这行
            console.error('   ⚠️  故事结构生成失败（详细错误仅记录在控制台供调试）');
          } else if (latestProgress) {
            // 更新进度显示
            console.log(`[故事结构轮询] 更新进度: 阶段${latestProgress.stage} - ${latestProgress.progress}% - ${latestProgress.message}`);
          }
        }
      } catch (error) {
        console.error('[故事结构轮询] 查询进度失败:', error);
      }
    }, 15000); // 🔥【WS化】15秒兜底（进度实时性由 storyStructureProgress 推送保证）
    
    // 清理定时器
    return () => {
      console.log('[故事结构轮询] 停止进度轮询');
      clearInterval(pollTimer);
    };
  }, [novel, api, setNovel, setStoryStructure, setShowProgressPanel, setGeneratingStructure, showMessage, saveNovelToServer]);
  
  // 定时轮询章节生成状态
  useEffect(() => {
    if (!novel || !(novel._id || novel.id)) return;
    const nid = novel._id || novel.id;
    
    // 启动章节状态轮询（静默）
    
    // 自适应轮询：生成中每3秒，空闲时每20秒
    let cancelled = false;
    let timer;
    const pollChapters = async () => {
      if (cancelled) return;

      // 🔥【看门狗】批量进行中且批量WS通道健康（在线且90秒内有事件）→ 本轮零请求：
      //    章节状态/进度/正文全部由 batch:* 推送驱动，仅排下一次活动检查。
      //    断线、无批量任务（单章生成等）或超90秒无事件时照常轮询。
      if (batchTaskIdRef.current && batchWsRef.current &&
          (Date.now() - batchWsLastEventRef.current < 90000)) {
        timer = setTimeout(pollChapters, 90000);
        return;
      }

      let hasGeneratingChapters = false; // 默认无生成任务
      
      try {
        // 🔥【WS化配套】轮询改用轻量章节状态接口（响应仅几KB），不再全量拉取小说详情（重水合）
        const response = await api.get(`/novels/${nid}/chapters/status`);
        const novelData = response.data?.data || response.data;

        // 从服务器数据判断是否有生成中的章节
        hasGeneratingChapters = novelData?.chapters?.some(ch =>
          ch.status === 'generating' || ch.status === 'pending'
        ) || false;

        if (novelData && novelData.chapters) {
          const serverChapters = novelData.chapters;
          
          // 更新章节状态
          setChapters(prevChapters => {
            // 创建新的章节数组
            const updatedChapters = [...prevChapters];
            let hasUpdates = false;

            // 检查每个章节的状态
            serverChapters.forEach(serverCh => {
              const localIndex = updatedChapters.findIndex(ch => 
                (ch.id && serverCh.id && ch.id === serverCh.id) || 
                ch.index === serverCh.index
              );
              
              if (localIndex >= 0) {
                // 如果章节已有内容，则更新状态为已生成
                // 🔥 瘦身适配：getNovel 详情接口已剥离 content（null），只保留 hasContent/contentLength，
                //    必须用这两字段判断"已生成"，否则已完成的章节会永久卡在"生成中"
                const serverHasContent = Boolean(serverCh.content && serverCh.content.trim().length > 0) ||
                                         serverCh.hasContent === true ||
                                         (typeof serverCh.contentLength === 'number' && serverCh.contentLength > 0);
                if (serverHasContent) {
                  // 检查本地章节是否需要更新
                  const localChapter = updatedChapters[localIndex];
                  const needsUpdate = !localChapter.content || 
                                      localChapter.content.trim().length === 0 || 
                                      localChapter.status === 'generating';
                  
                  if (needsUpdate) {
                    // 更新章节内容和状态
                    updatedChapters[localIndex] = {
                      ...updatedChapters[localIndex],
                      ...serverCh,
                      // 🔥 瘦身适配：服务器 content 已剥离为 null，保留本地已有正文（正文点击时懒加载）
                      content: serverCh.content || updatedChapters[localIndex].content || '',
                      status: 'generated',
                      generationStatus: '已生成',
                      generationProgress: 100
                    };
                    
                    hasUpdates = true;
                    
                    // 同时更新生成状态列表
                    setChapterGenerationStatus(prev => {
                      const newStatus = [...prev];
                      const statusIndex = newStatus.findIndex(s => s.chapterIndex === serverCh.index);
                      if (statusIndex >= 0) {
                        newStatus[statusIndex] = {
                          ...newStatus[statusIndex],
                          status: '已生成',
                          progress: 100
                        };
                      } else {
                        newStatus.push({
                          chapterIndex: serverCh.index,
                          title: serverCh.title || `第${serverCh.index}章`,
                          status: '已生成',
                          progress: 100
                        });
                      }
                      return newStatus;
                    });
                    
                    // 🔥 关键修复：如果当前生成的章节完成了，清除 generatingChapterId
                    setGeneratingChapterId(prev => (prev === serverCh.index ? null : prev));
                  }
                } else if (serverCh.status === 'generating' || serverCh.status === 'pending') {
                  // 章节正在生成中或等待中
                  const isGenerating = serverCh.status === 'generating';

                  // 更新章节状态
                  updatedChapters[localIndex] = {
                    ...updatedChapters[localIndex],
                    ...serverCh,
                    status: serverCh.status, // 保持原状态(generating或pending)
                    generationStatus: isGenerating ? '生成中' : '等待中',
                    generationProgress: isGenerating ? 50 : 0
                  };
                  
                  // 强制触发UI更新
                  if (isGenerating) {
                    // 直接在这里更新当前章节状态，确保它被正确设置
                    updatedChapters[localIndex].status = 'generating';
                    updatedChapters[localIndex].generationStatus = '生成中';
                    
                    // 设置当前正在生成的章节ID
                    setGeneratingChapterId(serverCh.index);
                  }

                  // 标记有更新，确保返回新的数组
                  hasUpdates = true;
                  
                  // 同时更新生成状态列表
                  setChapterGenerationStatus(prev => {
                    const newStatus = [...prev];
                    const statusIndex = newStatus.findIndex(s => s.chapterIndex === serverCh.index);
                    const isGenerating = serverCh.status === 'generating';
                    const statusText = isGenerating ? '生成中' : '等待中';
                    const progress = isGenerating ? 50 : 0;
                    
                    if (statusIndex >= 0) {
                      newStatus[statusIndex] = {
                        ...newStatus[statusIndex],
                        status: statusText,
                        progress: progress
                      };
                    } else {
                      newStatus.push({
                        chapterIndex: serverCh.index,
                        title: serverCh.title || `第${serverCh.index}章`,
                        status: statusText,
                        progress: progress
                      });
                    }
                    return newStatus;
                  });
                }
              }
            });
            
            // 强制更新章节列表，即使没有变化也返回新数组，确保UI重新渲染
            // 最后一次检查，确保生成中的章节状态正确
            const finalChapters = [...updatedChapters];
            
            // 检查是否有任何章节标记为"生成中"
            const generatingChapter = finalChapters.find(ch => ch.status === 'generating');
            
            // 如果没有找到生成中的章节，但服务器日志表明第19章应该是生成中，则手动修复
            if (!generatingChapter && serverChapters.some(ch => ch.index === 19 && ch.status === 'generating')) {
              const idx = finalChapters.findIndex(ch => ch.index === 19);
              if (idx >= 0) {
                console.log('手动修复章节19的状态为生成中');
                finalChapters[idx].status = 'generating';
                finalChapters[idx].generationStatus = '生成中';
              }
            }
            
            return finalChapters;
          });
          
          // 🔥 关键优化：检查是否所有生成任务都已完成，如果是则清空状态
          // 🔥 修复：基于服务器返回的实际章节状态来判断
          const serverHasGenerating = serverChapters.some(ch =>
            ch.status === 'generating' || ch.status === 'pending'
          );
          // 🔥 瘦身适配：content 已剥离为 null，用 hasContent/contentLength 判断完成
          const _chHasContent = (ch) => Boolean(ch.content && ch.content.trim().length > 0) ||
            ch.hasContent === true ||
            (typeof ch.contentLength === 'number' && ch.contentLength > 0);
          const serverAllCompleted = serverChapters.length > 0 &&
            serverChapters.every(_chHasContent);
          const chapterStatusCount = chapterGenerationStatus ? chapterGenerationStatus.length : 0;

          if (!serverHasGenerating && (serverAllCompleted || chapterStatusCount === 0)) {
            console.log(`✅ [状态清理] 所有章节生成完成，清空所有生成状态`);
            // 清空所有生成状态
            setGeneratingChapterId(null);
            setBatchGenerating(false);
            setChapterGenerationStatus([]);
          } else {
            // 服务器仍有生成中的章节，更新 chapterGenerationStatus 以反映真实状态
            setChapterGenerationStatus(prev => {
              if (!prev || prev.length === 0) return prev;
              const updated = prev.map(s => {
                const serverCh = serverChapters.find(sc => sc.index === s.chapterIndex);
                if (serverCh) {
                  // 🔥 瘦身适配：content 已剥离，优先 hasContent/contentLength
                  const chHasContent = Boolean(serverCh.content && serverCh.content.trim().length > 0) ||
                    serverCh.hasContent === true ||
                    (typeof serverCh.contentLength === 'number' && serverCh.contentLength > 0);
                  if (chHasContent) {
                    return { ...s, status: '已生成', progress: 100 };
                  }
                  if (serverCh.status === 'generating') {
                    return { ...s, status: '生成中', progress: 50 };
                  }
                  if (serverCh.status === 'pending') {
                    return { ...s, status: '等待中', progress: 0 };
                  }
                }
                return s;
              });
              return updated;
            });
          }
        }
      } catch (error) {
        console.error('轮询任务状态失败:', error);
      } finally {
        if (!cancelled) {
          // 🔥【看门狗】走到这里=WS断线或90秒无事件（假在线嫌疑），已对账一次；
          //    WS在线→回90秒看门狗节奏；断线→3秒主通道（生成中）/20秒（空闲）
          // 🔥 批量生成中只看批量事件通道（batchWsRef），防止体检通道在线掩盖批量通道断线
          const wsUp = batchTaskIdRef.current ? (batchWsRef.current === true) : (wsConnectedRef.current === true);
          const nextInterval = hasGeneratingChapters ? (wsUp ? 90000 : 3000) : (wsUp ? 90000 : 20000);
          timer = setTimeout(pollChapters, nextInterval);
        }
      }
    };

    pollChapters();
    // 🔥【WS化】注册到 ref，供 WS 重连对账调用
    pollChaptersRef.current = pollChapters;

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      pollChaptersRef.current = null;
    };
  }, [novel?.id, novel?._id]); // 只依赖小说ID，避免频繁重启轮询

  // 保存大纲到服务器（只保存，不自动生成故事结构）
  const saveOutlineToServer = useCallback(async (outlineText) => {
    try {
      if (!novel) {
        console.error('无法保存大纲：小说对象未定义');
        return false;
      }
      const novelId = novel._id || novel.id;
      if (!novelId) {
        console.error('无法保存大纲：小说ID未定义');
        return false;
      }
      // 🔥 仅保存大纲：使用通用更新接口，避免触发后台故事结构生成
      const response = await api.put(`/novels/${novelId}`, { outline: outlineText });
      // 重新拉取小说数据，确保本地和服务器同步
      const updatedNovel = response.data && response.data.data ? response.data.data : response.data;
      
      // 🔥 确保保留小说ID，防止丢失
      if (updatedNovel) {
        // 如果返回的数据有ID，使用返回的数据；否则合并到现有novel对象
        const finalNovel = {
          ...novel, // 保留原有数据
          ...updatedNovel, // 更新返回的数据
          _id: updatedNovel._id || updatedNovel.id || novel._id || novel.id, // 确保ID存在
          id: updatedNovel.id || updatedNovel._id || novel.id || novel._id // 确保两种ID格式都存在
        };
        setNovel(finalNovel);
      } else {
        // 如果返回数据为空，只更新大纲字段
        setNovel(prev => ({
          ...prev,
          outline: outlineText
        }));
      }
      
      setOutline(outlineText);
      setOutlineText(outlineText);
      
      // ❌ 不自动触发生成，需手动点击"生成故事结构"按钮
      
      return true;
    } catch (error) {
      console.error('保存大纲到服务器失败:', error);
      return false;
    }
  }, [novel]);

  // 🔥 大纲编辑处理函数
  const handleOutlineEditStart = useCallback(() => {
    // 检查是否已生成故事结构
    if (storyStructure && storyStructure.isGenerated) {
      showMessage('已生成故事结构，无法修改大纲', 'warning');
      return;
    }

    if (!outlineText || outlineText.trim().length === 0) {
      showMessage('大纲内容为空，无法编辑', 'warning');
      return;
    }

    setOutlineEdit({
      isEditing: true,
      editedContent: outlineText,
      originalContent: outlineText,
      saving: false,
      hasUnsavedChanges: false
    });
  }, [outlineText, storyStructure, showMessage]);

  const handleOutlineEditCancel = useCallback(() => {
    setOutlineEdit(prev => ({
      ...prev,
      isEditing: false,
      editedContent: prev.originalContent,
      hasUnsavedChanges: false
    }));
  }, []);

  const handleOutlineSave = useCallback(async () => {
    setOutlineEdit(prev => ({ ...prev, saving: true }));
    
    const success = await saveOutlineToServer(outlineEdit.editedContent);
    
    if (success) {
      setOutlineEdit(prev => ({
        ...prev,
        isEditing: false,
        saving: false,
        originalContent: prev.editedContent,
        hasUnsavedChanges: false
      }));
      showMessage('大纲已保存', 'success');
    } else {
      setOutlineEdit(prev => ({ ...prev, saving: false }));
      showMessage('保存大纲失败', 'error');
    }
  }, [outlineEdit.editedContent, saveOutlineToServer, showMessage]);

  const handleOutlineChange = useCallback((e) => {
    const newContent = e.target.value;
    setOutlineEdit(prev => ({
      ...prev,
      editedContent: newContent,
      hasUnsavedChanges: newContent !== prev.originalContent
    }));
  }, []);

  // 🔥 显示生成故事结构确认对话框
  const showGenerateStoryStructureConfirm = useCallback(() => {
    // 检查基础设置
    if (!novel) {
      showMessage('请先打开一个小说', 'error');
      return;
    }
    
    // 兼容：从路由参数回退novelId
    const novelId = (novel._id || novel.id || (typeof window !== 'undefined' ? (window.location.pathname.split('/').pop() || '').trim() : ''));
    if (!novelId) {
      showMessage('无法获取小说ID', 'error');
      return;
    }
    
    // 若大纲有未保存更改，先保存再校验
    if (outlineEdit?.hasUnsavedChanges && outlineEdit?.editedContent) {
      try {
        // 同步本地大纲
        setOutline(outlineEdit.editedContent);
        setOutlineText(outlineEdit.editedContent);
        // 保存至服务器（不自动触发生成）
        saveOutlineToServer(outlineEdit.editedContent);
      } catch (e) {
        console.warn('保存未提交的大纲失败，将继续使用当前大纲:', e?.message);
      }
    }
    
    if (!(outlineEdit?.editedContent || outline)?.trim() || (outlineEdit?.editedContent || outline).trim().length < 50) {
      showMessage('请先填写大纲（至少50字）', 'warning');
      return;
    }
    
    // 获取章节数
    const chapterCount = novelSettings?.chapterCount || novel?.settings?.chapterCount || 30;
    
    // 💰 检查余额（每章预留250字，因为生成完成后才扣费，需要确保有足够余额）
    const requiredBalance = chapterCount * 250;
    if (userBalance < requiredBalance) {
      // 显示余额不足对话框，引导用户去充值
      setDialog({
        open: true,
        title: '余额不足提醒',
        content: `生成故事结构需要预留${requiredBalance.toLocaleString()}字（每章250字）。

当前余额：${userBalance.toLocaleString()}字
还需充值：${(requiredBalance - userBalance).toLocaleString()}字

请充值后继续使用。`,
        confirmText: '去充值',
        cancelText: '取消',
        onConfirm: () => {
          setDialog({ open: false });
          // 跳转到充值页面
          navigate('/recharge');
        },
        onCancel: () => {
          setDialog({ open: false });
        }
      });
      return;
    }
    
    // 显示确认对话框
    setStoryStructConfirmData({ chapterCount, requiredBalance, userBalance });
    setStoryStructConfirmOpen(true);
  }, [novel, outline, userBalance, novelSettings, showMessage, setDialog, navigate]);
  
  // ============================================================
  // 🔥 番茄平台发布信息：重新生成 / 编辑保存
  // ============================================================
  const handleRegenerateFanqieInfo = useCallback(async () => {
    if (!/番茄/.test(novelSettings.platform || '')) {
      showMessage('请先将发布平台设为「番茄小说网」再生成番茄发布信息', 'error');
      return;
    }
    const novelId = novel?._id || novel?.id;
    if (!novelId) { showMessage('无法获取小说ID', 'error'); return; }
    setFanqieGenerating(true);
    try {
      const resp = await api.post(`/novels/${novelId}/generate-fanqie-info`);
      const info = resp.data?.data || resp.data;
      if (info && info.bookName) {
        setFanqieInfo(info);
        showMessage('番茄发布信息已重新生成', 'success');
      } else {
        showMessage(resp.data?.message || '生成失败，请稍后重试', 'error');
      }
    } catch (e) {
      showMessage(e?.response?.data?.message || `生成失败: ${e?.message || '未知错误'}`, 'error');
    } finally {
      setFanqieGenerating(false);
    }
  }, [novel, novelSettings, showMessage]);

  const handleSaveFanqieInfo = useCallback(async () => {
    const novelId = novel?._id || novel?.id;
    if (!novelId || !fanqieEditDraft) return;
    const draft = fanqieEditDraft;
    if (!draft.bookName?.trim()) { showMessage('书名不能为空', 'error'); return; }
    if (draft.abstract?.length < 50 || draft.abstract?.length > 500) {
      showMessage(`简介需50-500字（当前${draft.abstract?.length || 0}字）`, 'error'); return;
    }
    try {
      const resp = await api.put(`/novels/${novelId}/fanqie-info`, draft);
      const info = resp.data?.data || resp.data;
      if (info && info.bookName) {
        setFanqieInfo(info);
        setFanqieEditing(false);
        setFanqieEditDraft(null);
        showMessage('番茄发布信息已保存', 'success');
      } else {
        showMessage(resp.data?.message || '保存失败', 'error');
      }
    } catch (e) {
      showMessage(e?.response?.data?.message || `保存失败: ${e?.message || '未知错误'}`, 'error');
    }
  }, [novel, fanqieEditDraft, showMessage]);

  // 🔥 补生成封面提示词（存量小说 fanqieInfo 缺 coverPrompts 时单独补，不动其他字段）
  const handleGenerateCoverPrompts = useCallback(async () => {
    const novelId = novel?._id || novel?.id;
    if (!novelId) { showMessage('无法获取小说ID', 'error'); return; }
    setCoverPromptGenerating(true);
    try {
      const resp = await api.post(`/novels/${novelId}/generate-cover-prompts`);
      const info = resp.data?.data || resp.data;
      if (info && Array.isArray(info.coverPrompts) && info.coverPrompts.length === 3) {
        setFanqieInfo(info);
        showMessage('封面提示词已生成', 'success');
      } else {
        showMessage(resp.data?.message || '生成失败，请稍后重试', 'error');
      }
    } catch (e) {
      showMessage(e?.response?.data?.message || `生成失败: ${e?.message || '未知错误'}`, 'error');
    } finally {
      setCoverPromptGenerating(false);
    }
  }, [novel, showMessage]);

  // 🔥 fanqieInfo 变化时同步当前显示的封面提示词索引 + 重置折叠状态
  useEffect(() => {
    setCoverPromptIdx(Number(fanqieInfo?.coverPromptSelected) || 0);
    setCoverPromptExpanded(false);
  }, [fanqieInfo]);

  // 🔥 复制当前选中的封面提示词到剪贴板（多端复用：手机/网页/电脑端都能粘贴使用）
  const handleCopyCoverPrompt = useCallback(async () => {
    const info = fanqieEditing ? fanqieEditDraft : fanqieInfo;
    const prompt = info?.coverPrompts?.[coverPromptIdx];
    if (!prompt) { showMessage('暂无封面提示词', 'error'); return; }
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(prompt);
      } else {
        // 兜底：用 textarea 选中复制（兼容旧浏览器/非安全上下文）
        const ta = document.createElement('textarea');
        ta.value = prompt; ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.select();
        document.execCommand('copy'); document.body.removeChild(ta);
      }
      showMessage(`已复制「${info.coverPromptStyles?.[coverPromptIdx] || '封面'}」提示词`, 'success');
    } catch (e) {
      showMessage('复制失败，请手动选择文本复制', 'error');
    }
  }, [fanqieInfo, fanqieEditDraft, fanqieEditing, coverPromptIdx, showMessage]);

  // 🔥 切换封面提示词风格（编辑模式下还会同步 draft 的 coverPromptSelected 以便保存）
  const handleSwitchCoverPrompt = useCallback((idx) => {
    setCoverPromptIdx(idx);
    setCoverPromptExpanded(false);
    if (fanqieEditing && fanqieEditDraft) {
      setFanqieEditDraft({ ...fanqieEditDraft, coverPromptSelected: idx });
    }
  }, [fanqieEditing, fanqieEditDraft]);

  // 🔥 实际执行生成故事结构的函数
  const handleGenerateStoryStructureImpl = useCallback(async () => {
    try {
      console.log('============ 开始生成故事结构 ============');
      
      // 兼容：从路由或路径回退novelId
      const novelId = (novel._id || novel.id || (typeof window !== 'undefined' ? (window.location.pathname.split('/').pop() || '').trim() : ''));
      console.log('小说ID:', novelId);
      if (!novelId) {
        showMessage('无法获取小说ID', 'error');
        return;
      }
      
      // 若有未保存大纲，优先保存最新编辑内容
      if (outlineEdit?.hasUnsavedChanges && outlineEdit?.editedContent) {
        try {
          setOutline(outlineEdit.editedContent);
          setOutlineText(outlineEdit.editedContent);
          await saveOutlineToServer(outlineEdit.editedContent);
        } catch (e) {
          console.warn('保存未提交的大纲失败，将尝试继续:', e?.message);
        }
      }
      
      // 🔥 关键修复：在生成之前，从服务器重新获取最新的小说数据，确保使用最新的大纲
      console.log('📥 正在从服务器获取最新的大纲数据...');
      let latestOutline = outline;
      try {
        const novelResponse = await api.get(`/novels/${novelId}`);
        const latestNovel = novelResponse.data?.data || novelResponse.data;
        if (latestNovel && latestNovel.outline) {
          latestOutline = latestNovel.outline;
          console.log('✅ 已获取最新大纲，长度:', latestOutline?.length || 0);
          // 同步更新本地状态
          setNovel(latestNovel);
          setOutline(latestOutline);
          setOutlineText(latestOutline);
        } else {
          console.log('⚠️ 服务器返回的小说数据中没有大纲，使用本地大纲');
        }
      } catch (fetchError) {
        console.warn('⚠️ 获取最新小说数据失败，使用本地大纲:', fetchError.message);
        // 如果获取失败，使用本地大纲（优先使用 novel.outline，其次使用 state 中的 outline）
        latestOutline = novel.outline || outline;
      }
      
      console.log('📝 使用的大纲长度:', latestOutline?.length || 0);
      
      if (!latestOutline || latestOutline.trim().length === 0) {
        showMessage('大纲内容为空，无法生成故事结构', 'error');
        return;
      }
      
      // 显示进度面板
      setShowProgressPanel(true);
      setStructureExpanded(true); // 展开故事结构面板
      setGeneratingStructure(true); // 设置生成中状态
      
      // 立即将状态置为generating，确保轮询立即启动（避免等待后端第一次返回）
      setNovel(prev => ({
        ...prev,
        storyStructure: {
          ...(prev.storyStructure || {}),
          generationStatus: 'generating',
          generationProgress: {
            stage: 0,
            progress: 0,
            message: '正在启动生成...',
            estimatedTotalSeconds: null,
            estimatedRemainingSeconds: null,
            stageEstimates: null,
            elapsedTime: 0,
            updatedAt: new Date().toISOString()
          }
        }
      }));

      // 调用后端API触发生成（使用最新的大纲）
      const apiUrl = `/novels/${novelId}/outline`;
      console.log('调用API:', apiUrl);
      
      const response = await api.put(apiUrl, {
        outline: latestOutline, // 🔥 使用从服务器获取的最新大纲
        settings: novelSettings // 🔥 传递完整的小说设置，包括关键词
      });
      
      console.log('API调用成功，响应:', response.status);
      showMessage('故事结构生成已开始，请查看进度面板', 'info');
      
    } catch (error) {
      console.error('============ 触发生成失败 ============');
      console.error('错误信息:', error.message);
      console.error('响应数据:', error.response?.data);
      showMessage('触发生成失败：' + (error.response?.data?.message || error.message), 'error');
      setGeneratingStructure(false);
      setShowProgressPanel(false);
    }
  }, [novel, outline, novelSettings, showMessage, setGeneratingStructure]);
  
  // 🔥 保持兼容性：原函数名指向确认对话框函数
  const handleGenerateStoryStructure = showGenerateStoryStructureConfirm;
  
  // 🔥 新增：重新生成故事结构（清除所有数据后从头开始）
  const handleRegenerateStoryStructure = useCallback(async () => {
    try {
      console.log('============ 开始重新生成故事结构 ============');
      
      const novelId = novel._id || novel.id;
      
      // 显示确认对话框
      const confirmed = window.confirm(
        '⚠️ 重新生成确认\n\n' +
        '此操作将：\n' +
        '✓ 清除所有已生成的故事结构数据\n' +
        '✓ 从头开始重新生成完整的故事结构\n' +
        '✓ 确保故事结构的完整性和一致性\n\n' +
        '是否继续？'
      );
      
      if (!confirmed) {
        console.log('用户取消了重新生成');
        return;
      }
      
      showMessage('正在清除故事结构数据...', 'info');
      
      // 调用清除API
      const clearResponse = await api.delete(`/novels/${novelId}/story-structure`);
      
      if (!clearResponse.data.success) {
        showMessage('清除故事结构失败，请重试', 'error');
        return;
      }
      
      console.log('✅ 故事结构已清除，开始重新生成');
      showMessage('故事结构已清除，正在重新生成...', 'success');
      
      // 更新novel状态
      setNovel(prev => ({
        ...prev,
        storyStructure: {
          isGenerated: false,
          generationStatus: 'not_started',
          generationProgress: null
        }
      }));
      
      // 延迟一秒后调用生成函数
      setTimeout(() => {
        handleGenerateStoryStructureImpl();
      }, 1000);
      
    } catch (error) {
      console.error('❌ 重新生成故事结构失败:', error);
      showMessage(`重新生成失败: ${error.message}`, 'error');
      setGeneratingStructure(false);
    }
  }, [novel, showMessage, handleGenerateStoryStructureImpl]);

  // 创建防抖的保存函数
  const debouncedSave = useCallback(
    debounce(async (content) => {
      if (noAutoSave || isChapterSwitching || !novel || !selectedChapter) {
        return;
      }

      try {
        const updatedChapters = chapters.map(ch =>
          ch.index === selectedChapter
            ? { ...ch, content, updatedAt: new Date().toISOString() }
            : ch
        );

        // 保存到本地存储
        saveChaptersToLocalStorage(novel, updatedChapters);

        // 更新服务器
        const updatedNovel = {
          ...novel,
          chapters: updatedChapters,
          updatedAt: new Date().toISOString()
        };
        
        await saveNovelToServer(updatedNovel, false);
        setLastSavedContent(content);
      } catch (error) {
        console.error('自动保存失败:', error);
      }
    }, 5000),
    [novel, selectedChapter, chapters, saveChaptersToLocalStorage, saveNovelToServer, noAutoSave, isChapterSwitching]
  );

  // 监听内容变化，触发自动保存
  useEffect(() => {
    if (currentChapterContent !== lastSavedContent) {
      debouncedSave(currentChapterContent);
    }
  }, [currentChapterContent, lastSavedContent, debouncedSave]);

  // 清理自动保存定时器
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, []);

  // ⭐⭐⭐ 关键修复：当 novel.storyStructure 更新时，同步到 storyStructure state
  useEffect(() => {
    if (novel && novel.storyStructure) {
      // 同步 storyStructure 到 state（静默）
      setStoryStructure(novel.storyStructure);
    }
  }, [novel?.storyStructure, novel?.relationshipTracking]);

  // 修改渲染逻辑，添加加载和错误处理
  if (loading || isCreatingNovel) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          backgroundColor: '#0a1929'
        }}
      >
        <CircularProgress color="primary" size={60} />
        <Typography variant="h6" sx={{ mt: 3, color: 'white' }}>
          {isCreatingNovel ? '正在创建新小说...' : '正在加载小说编辑器...'}
        </Typography>
      </Box>
    );
  }

  if (loadError) {
    const isAuthError = loadError.includes('认证') || loadError.includes('登录');
    
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          backgroundColor: '#0a1929',
          p: 3
        }}
      >
        {isAuthError ? (
          <WarningIcon color="warning" sx={{ fontSize: 60, mb: 2 }} />
        ) : (
          <ErrorOutlineIcon color="error" sx={{ fontSize: 60, mb: 2 }} />
        )}
        
        <Typography variant="h5" sx={{ mb: 2, color: 'white', textAlign: 'center' }}>
          {isAuthError ? '登录已过期' : '加载小说编辑器时出错'}
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 4, color: 'white', textAlign: 'center', maxWidth: 600 }}>
          {loadError}
        </Typography>
        
        <Stack direction="row" spacing={2}>
          {isAuthError ? (
        <Button 
          variant="contained" 
          color="primary" 
              onClick={() => navigate('/login')}
              startIcon={<ArrowForwardIcon />}
            >
              去登录
            </Button>
          ) : (
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => {
                setLoadError(null);
                if (novelId) {
                  loadNovel(novelId);
                }
              }}
              startIcon={<RefreshIcon />}
            >
              重试加载
            </Button>
          )}
          
          <Button 
            variant="outlined" 
            color="secondary" 
            onClick={() => {
              setLoadError(null);
              setIsCreatingNovel(true);
            }}
            startIcon={<AddIcon />}
          >
            创建新小说
          </Button>
          
          <Button 
            variant="text" 
            color="inherit" 
          onClick={() => navigate('/dashboard')}
            startIcon={<ArrowBackIcon />}
            sx={{ color: 'white' }}
        >
          返回仪表板
        </Button>
        </Stack>
      </Box>
    );
  }

  if (!novel) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          backgroundColor: '#0a1929'
        }}
      >
        <WarningIcon color="warning" sx={{ fontSize: 60, mb: 2 }} />
        <Typography variant="h5" sx={{ mb: 2, color: 'white', textAlign: 'center' }}>
          未找到小说数据
          </Typography>
        <Typography variant="body1" sx={{ mb: 4, color: 'white', textAlign: 'center' }}>
          无法加载ID为 {novelId} 的小说，可能已被删除或不存在
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleReturnToDashboard}
        >
          返回仪表板
        </Button>
        </Box>
    );
  }

  return (
    <Box className="theme-container editor-container">
      {/* 强制移动端settings-panel无padding */}
      <style>{`
        @media (max-width: 600px) {
          .settings-panel {
            padding: 0 !important;
            margin: 0 !important;
          }
          .settings-panel .settings-section {
            padding: 8px 10px 6px !important;
            margin: 0 !important;
            border-radius: 0 !important;
            width: 100% !important;
          }
          .settings-panel .MuiGrid-container {
            margin: 0 !important;
          }
          .settings-panel .MuiGrid-item {
            padding: 2px 4px !important;
          }
          .settings-panel .notes-textfield textarea,
          .settings-panel .notes-textfield .MuiOutlinedInput-input {
            height: auto !important;
            min-height: 80px !important;
            max-height: 300px !important;
            overflow-y: auto !important;
            resize: none !important;
            field-sizing: content !important;
          }
          /* 移动端底部留白，防止生成按钮被遮挡 */
          .settings-panel {
            padding-bottom: 80px !important;
          }
        }
        /* 按钮呼吸灯动画 */
        @keyframes buttonGlow {
          0%, 100% {
            box-shadow: 0 0 5px currentColor, 0 0 10px currentColor;
            opacity: 1;
          }
          50% {
            box-shadow: 0 0 15px currentColor, 0 0 25px currentColor;
            opacity: 0.85;
          }
        }
      `}</style>
      {/* 动态粒子背景 */}
      <canvas ref={canvasRef} className="novel-canvas-bg" />
      {/* 主内容滚动区域 */}
      <Box className="novel-main-content">
      {/* 顶部导航栏 */}
      <Box className="novel-topbar novel-topbar-compact">
          {/* 返回按钮 */}
            <IconButton
              onClick={handleReturnToDashboard}
            className="novel-topbar-btn novel-topbar-back"
              sx={{ 
              width: 36, height: 36, flexShrink: 0,
              color: 'rgba(255,255,255,0.7)', background: 'rgba(59,130,246,0.1)',
              border: '1px solid rgba(59,130,246,0.2)', borderRadius: '8px',
              '&:hover': { background: 'rgba(59,130,246,0.22)', borderColor: 'rgba(59,130,246,0.4)' }
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 18 }} />
            </IconButton>

          {/* 标题 */}
          <Box className="novel-topbar-title">
            <TextField 
              variant="standard"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={() => saveTitle(title)}
              inputProps={{ style: { textAlign: 'center' } }}
              sx={{ 
                '& .MuiInput-underline:before': { borderBottomColor: 'rgba(59,130,246,0.3)' },
                '& .MuiInput-underline:hover:before': { borderBottomColor: 'rgba(59,130,246,0.5)' },
                '& .MuiInput-underline:after': { borderBottomColor: '#3b82f6' },
              }}
              placeholder="小说标题"
            />
          </Box>
          
          {/* 右侧操作区 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            {/* 余额徽章 */}
            <Box className="novel-balance-badge">
              <MonetizationOnIcon />
              <span>{userBalance?.toLocaleString() || 0}</span>
              </Box>
            {/* 保存按钮 */}
            <Button
              variant="contained"
                onClick={handleSaveAndExit}
              startIcon={<SaveIcon />}
              className="novel-topbar-btn"
              sx={{
                background: 'linear-gradient(135deg, #3b82f6, #06b6d4)',
                borderRadius: '8px',
                fontWeight: 700,
                px: 2,
                py: 0.75,
                boxShadow: '0 2px 10px rgba(59,130,246,0.3)',
                '&:hover': { background: 'linear-gradient(135deg, #2563eb, #0891b2)', boxShadow: '0 4px 14px rgba(59,130,246,0.4)' }
              }}
            >
              保存
            </Button>
          </Box>
        </Box>

        {/* 生成状态条 */}
        {generatingChapterId && (
          <Box className="novel-generating-bar">
            <CircularProgress size={14} sx={{ color: '#3b82f6' }} />
            <Typography className="novel-generating-text">
              正在生成第{generatingChapterId}章
            </Typography>
          </Box>
        )}

        {/* 大纲生成状态 */}
        {outlineGenerationStatus && outlineGenerationStatus.generating && renderOutlineGenerationStatus()}
        
        {/* 标签页导航 - 移动端使用独立组件固定在底部 */}
        {isMobile && (
          <MobileBottomNav
            activeTab={activeTab}
            onTabChange={setActiveTab}
            scrollPositions={scrollPositions}
            setScrollPositions={setScrollPositions}
          />
        )}
        
        {/* 桌面端导航栏 - 保持原位置 */}
        {!isMobile && (
          <Box className="novel-tabs-wrapper">
            <Tabs 
              className="novel-tabs"
              value={activeTab} 
              onChange={(event, newValue) => {
                const panelEl = document.getElementById(`tabpanel-${activeTab}`);
                if (panelEl) {
                  const scrollBox = panelEl.querySelector('[style*="overflow-y: auto"]') || panelEl.querySelector('[class*="MuiBox-root"]');
                  if (scrollBox) {
                    setScrollPositions(prev => ({ ...prev, [activeTab]: scrollBox.scrollTop }));
                  }
                }
                setActiveTab(newValue);
              }} 
              variant="fullWidth"
            >
              <Tab className="novel-tab" icon={<SettingsIcon sx={{ fontSize: 18 }} />} iconPosition="top" label="设置" />
              <Tab className="novel-tab" icon={<ListAltIcon sx={{ fontSize: 18 }} />} iconPosition="top" label="大纲" />
              <Tab className="novel-tab" icon={<AccountTreeIcon sx={{ fontSize: 18 }} />} iconPosition="top" label="结构" />
              <Tab className="novel-tab" icon={<MenuBookIcon sx={{ fontSize: 18 }} />} iconPosition="top" label="章节" />
              <Tab className="novel-tab" icon={<HubIcon sx={{ fontSize: 18 }} />} iconPosition="top" label="关系" />
              <Tab className="novel-tab" icon={<ShowChartIcon sx={{ fontSize: 18 }} />} iconPosition="top" label="情感" />
            </Tabs>
          </Box>
        )}
          
        {/* 主内容区域 - 移动端底部留空给固定导航栏 */}
        <Box
          className="novel-content-area"
          sx={{
            '@media (max-width: 600px)': {
              paddingBottom: 'calc(70px + env(safe-area-inset-bottom, 0px)) !important',
            }
          }}
        >
          <TabPanel value={activeTab} index={0}>
            <Box className="outline-tab0-wrap">
              <Box className="outline-panel tab-0">
              {/* 顶部英雄头部 */}
              <Box className="outline-hero-header">
                <Box className="outline-hero-bg" />
                <Box className="hero-particles-outline" />
                <Box className="outline-hero-content">
                  <Box className="outline-hero-icon">
                    <AutoAwesomeIcon sx={{ fontSize: 24, color: '#fff' }} />
                  </Box>
                  <Box className="outline-hero-text">
                    <Typography className="outline-hero-title">开始创作</Typography>
                    <Typography className="outline-hero-subtitle">配置您的专属小说参数</Typography>
                  </Box>
                  <Box className="outline-hero-badge">
                    <Box className="outline-hero-badge-dot" />
                    <Typography className="outline-hero-badge-text">AI智能创作</Typography>
                  </Box>
                </Box>
              </Box>

              {/* 体验卡用户提示 - 大纲专业性说明 */}
              {trialCheckDone && isTrialOnlyUser && (
                <Box sx={{
                  mx: 2,
                  mt: 2,
                  p: 2,
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.08) 0%, rgba(139, 92, 246, 0.04) 100%)',
                  border: '1px solid rgba(168, 85, 247, 0.2)',
                  boxShadow: '0 0 16px rgba(168, 85, 247, 0.06)',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Box sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '7px',
                      background: 'rgba(168, 85, 247, 0.12)',
                      border: '1px solid rgba(168, 85, 247, 0.25)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      mt: 0.25,
                    }}>
                      <AutoAwesomeIcon sx={{ fontSize: 14, color: '#a855f7' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontWeight: 700, color: '#a855f7', fontSize: '0.82rem', mb: 0.5 }}>
                        关于大纲生成的专业说明
                      </Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.65 }}>
                        大纲质量与小说总章数正相关。10章体验大纲仅展示基础框架，长篇小说（100章+）会生成更完整的故事结构——包含<strong style={{ color: '#a855f7' }}>多层级情节铺垫、人物成长弧线、伏笔与呼应</strong>等高级技巧。我们专注长篇精品，正式用户平均创作<strong style={{ color: '#4caf50' }}>150-300章</strong>，生成的大纲更饱满、剧情更紧凑、人物更立体。
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              {/* 合并的参数容器 */}
              <Box className="settings-merged-container">
                <Grid container spacing={2} alignItems="flex-start">
                  {/* 发布平台 */}
                  <Grid item xs={6} sm={4}>
                    <Box className="settings-field">
                      <Typography className="settings-field-label">发布平台</Typography>
                      <FormControl fullWidth size="small">
                  <Select
                    value={novelSettings.platform || ''}
                    onChange={(e) => {
                      const updated = { ...novelSettings, platform: e.target.value };
                      setNovelSettings(updated);
                      saveUpdatedSettings(updated);
                    }}
                          className="settings-select"
                          displayEmpty
                    >
                          <MenuItem value="" disabled><Box sx={{ color: 'rgba(255,255,255,0.55)' }}>选择发布平台</Box></MenuItem>
                      {platforms.map(platform => (
                        <MenuItem key={platform} value={platform}>{platform}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                    </Box>
              </Grid>
              
                  {/* 视角选择 */}
                  <Grid item xs={6} sm={4}>
                    <Box className="settings-field">
                      <Typography className="settings-field-label">叙事视角</Typography>
                      <FormControl fullWidth size="small">
                  <Select
                    value={novelSettings.perspective || ''}
                    onChange={(e) => {
                      const updated = { ...novelSettings, perspective: e.target.value };
                      setNovelSettings(updated);
                      saveUpdatedSettings(updated);
                    }}
                          className="settings-select"
                          displayEmpty
                    >
                          <MenuItem value="" disabled><Box sx={{ color: 'rgba(255,255,255,0.55)' }}>选择叙事视角</Box></MenuItem>
                          {perspectives.map(p => (
                            <MenuItem key={p} value={p}>{p}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                    </Box>
              </Grid>
              
                  {/* 频道选择 */}
                  <Grid item xs={6} sm={4}>
                    <Box className="settings-field">
                      <Typography className="settings-field-label">目标频道</Typography>
                      <FormControl fullWidth size="small">
                  <Select
                    value={novelSettings.channel || ''}
                    onChange={(e) => {
                      const updated = { ...novelSettings, channel: e.target.value };
                      setNovelSettings(updated);
                      saveUpdatedSettings(updated);
                    }}
                          className="settings-select"
                          displayEmpty
                  >
                          <MenuItem value="" disabled><Box sx={{ color: 'rgba(255,255,255,0.55)' }}>选择目标频道</Box></MenuItem>
                    <MenuItem value="男频">男频</MenuItem>
                    <MenuItem value="女频">女频</MenuItem>
                  </Select>
                </FormControl>
                    </Box>
              </Grid>

                  {/* 作品篇幅 */}
                  <Grid item xs={6} sm={4}>
                    <Box className="settings-field">
                      <Typography className="settings-field-label">作品篇幅</Typography>
                      <FormControl fullWidth size="small">
                  <Select
                    value={novelSettings.length || ''}
                    onChange={(e) => handleLengthChange(e.target.value)}
                          className="settings-select"
                          displayEmpty
                    >
                          <MenuItem value="" disabled><Box sx={{ color: 'rgba(255,255,255,0.55)' }}>选择篇幅</Box></MenuItem>
                          {lengths.map(l => (
                            <MenuItem key={l} value={l}>{l}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                    </Box>
              </Grid>

                  {/* 写作风格 */}
                  <Grid item xs={6} sm={4}>
                    <Box className="settings-field">
                      <Typography className="settings-field-label">写作风格</Typography>
                      <FormControl fullWidth size="small">
                        <Select
                          value={novelSettings.style || ''}
                          onChange={(e) => updateLocalSettings({ style: e.target.value })}
                          className="settings-select"
                          displayEmpty
                        >
                          <MenuItem value="" disabled><Box sx={{ color: 'rgba(255,255,255,0.55)' }}>选择写作风格</Box></MenuItem>
                          {styles.map(s => (
                            <MenuItem
                              key={s}
                              value={s}
                              sx={s === '过AI检测风格' ? { color: '#4caf50', fontWeight: 'bold' } : {}}
                            >
                              {s}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Grid>

                  {/* 小说类型 */}
                  <Grid item xs={6} sm={4}>
                    <Box className="settings-field">
                      <Typography className="settings-field-label">小说类型</Typography>
                      <FormControl fullWidth size="small">
                        <Select
                          value={novelSettings.genre || ''}
                          onChange={(e) => updateLocalSettings({ genre: e.target.value })}
                          className="settings-select"
                          displayEmpty
                        >
                          <MenuItem value="" disabled><Box sx={{ color: 'rgba(255,255,255,0.55)' }}>选择小说类型</Box></MenuItem>
                          {genres.map(g => (
                            <MenuItem key={g} value={g}>{g}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Grid>

                  {/* 预计总字数 */}
                  <Grid item xs={6} sm={4}>
                    <Box className="settings-field">
                      <Typography className="settings-field-label">预计总字数</Typography>
                <TextField
                  fullWidth
                        size="small"
                  value={novelSettings.wordCount || ''}
                    onChange={(e) => updateLocalSettings({ wordCount: e.target.value })}
                        placeholder="例如：100"
                        className="settings-input"
                  InputProps={{
                    endAdornment: (
                            <InputAdornment position="end" sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.8rem', pointerEvents: 'none' }}>
                        万字
                      </InputAdornment>
                    ),
                          sx: { textAlign: 'center', '& input': { textAlign: 'center', pr: '40px' } }
                        }}
                      />
                    </Box>
              </Grid>
              
                  {/* 预计章节数 */}
                  <Grid item xs={6} sm={4}>
                    <Box className="settings-field">
                      <Typography className="settings-field-label">
                        预计章节数
                        {trialCheckDone && isTrialOnlyUser && (
                          <Box component="span" className="settings-field-hint trial-hint">
                            体验卡上限10章
                          </Box>
                        )}
                      </Typography>
                <TextField
                  fullWidth
                        size="small"
                  type="number"
                  value={novelSettings.chapterCount || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                          if (value === '') { updateLocalSettings({ chapterCount: '' }); return; }
                    const numValue = parseInt(value);
                    if (!isNaN(numValue)) {
                      const maxAllowed = isTrialOnlyUser ? 10 : 600;
                      const clampedValue = Math.max(1, Math.min(maxAllowed, numValue));
                      updateLocalSettings({ chapterCount: String(clampedValue) });
                    }
                  }}
                        inputProps={{ min: 1, max: isTrialOnlyUser ? 10 : 600 }}
                        className="settings-input"
                  InputProps={{
                    endAdornment: (
                            <InputAdornment position="end" sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.8rem', pointerEvents: 'none' }}>
                        章
                      </InputAdornment>
                    ),
                          sx: { textAlign: 'center', '& input': { textAlign: 'center', pr: '40px' } }
                        }}
                      />
                    </Box>
              </Grid>
                
                  {/* 关键词标签 */}
                  <Grid item xs={6} sm={4}>
                    <Box className="settings-field">
                      <Typography className="settings-field-label">
                        关键词标签
                        <Typography component="span" className="settings-field-count">
                          {(() => {
                            const kw = Array.isArray(novelSettings.keywords) ? novelSettings.keywords : [];
                            return kw.length > 0 ? `${kw.length}/5` : '';
                          })()}
                        </Typography>
                      </Typography>
                      <FormControl fullWidth size="small">
                  <Select
                    multiple
                    value={Array.isArray(novelSettings.keywords) ? novelSettings.keywords : []}
                    open={keywordsMenuOpen}
                    onOpen={() => {
                      setKeywordsMenuOpen(true);
                      const currentKeywords = Array.isArray(novelSettings.keywords) ? [...novelSettings.keywords] : [];
                      setTempKeywords(currentKeywords.length === 0 ? ['无关键词'] : currentKeywords);
                    }}
                          onClose={() => {}}
                          className="settings-select settings-select-chip"
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.length === 0 ? (
                                <Chip label="无关键词" size="small" className="chip-none" />
                              ) : (
                                selected.map((v) => (
                                  <Chip key={v} label={v} size="small" className="chip-selected" />
                            ))
                          )}
                        </Box>
                      )}
                    MenuProps={{
                      PaperProps: {
                              style: { maxHeight: '60vh', width: '90vw', maxWidth: '460px' },
                              sx: { display: 'flex', flexDirection: 'column' }
                            }
                          }}
                        >
                    <Box sx={{ 
                      p: 1.5,
                            bgcolor: tempKeywords.includes('无关键词')
                              ? 'rgba(6, 182, 212, 0.1)'
                              : (tempKeywords.length >= 5 ? 'rgba(251, 191, 36, 0.1)' : 'rgba(59, 130, 246, 0.1)'),
                            borderBottom: '1px solid rgba(255,255,255,0.06)',
                            position: 'sticky', top: 0, zIndex: 2
                    }}>
                      <Typography variant="body2" sx={{ 
                        textAlign: 'center',
                        color: tempKeywords.includes('无关键词') 
                                ? '#06b6d4'
                                : (tempKeywords.length >= 5 ? '#fbbf24' : '#3b82f6'),
                              fontWeight: 600, fontSize: '0.8rem'
                      }}>
                        {tempKeywords.includes('无关键词') 
                                ? '已选择"无关键词"'
                          : tempKeywords.length === 0
                                ? '请选择关键词（最多5个）'
                                : `已选 ${tempKeywords.length}/5 个`}
                      </Typography>
                    </Box>
                          <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5 }}>
                            {keywordCategories.map((cat) => {
                              const hasNoKw = tempKeywords.includes('无关键词');
                        return (
                                <Box key={cat.name} sx={{ mb: 1.5 }}>
                                  <Typography sx={{
                                    color: cat.color, fontWeight: 700, fontSize: '0.75rem',
                                    pb: 0.5, mb: 0.5,
                                    borderBottom: `1px solid ${cat.color}40`,
                                    display: 'flex', alignItems: 'center', gap: 0.5
                                  }}>
                                    {cat.name}
                                    <Typography component="span" variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', fontWeight: 400, ml: 0.5 }}>
                                      ({cat.keywords.length})
                              </Typography>
                            </Typography>
                                  {cat.keywords.map(kw => {
                                    const isNoKw = kw === '无关键词';
                                    const isDisabled = !isNoKw && hasNoKw;
                                    const isMaxed = !tempKeywords.includes(kw) && tempKeywords.length >= 5 && !hasNoKw;
                        return (
                        <MenuItem 
                                        key={kw}
                                        value={kw}
                                        disabled={isDisabled || isMaxed}
                          onClick={(e) => {
                            e.preventDefault();
                                          if (isNoKw) {
                                            setTempKeywords(tempKeywords.includes('无关键词') ? [] : ['无关键词']);
                              return;
                            }
                                          let newKw = [...tempKeywords];
                                          if (hasNoKw) newKw = newKw.filter(k => k !== '无关键词');
                                          const idx = newKw.indexOf(kw);
                                          if (idx === -1) {
                                            if (newKw.length >= 5) return;
                                            newKw.push(kw);
                            } else {
                                            newKw.splice(idx, 1);
                            }
                                          setTempKeywords(newKw);
                          }}
                          sx={{
                                          borderRadius: '6px', mb: 0.25,
                                          ...(cat.isSpecial && {
                                            borderTop: '1px solid rgba(255,255,255,0.06)',
                                            mt: 0.5
                                          }),
                                          '&.Mui-selected': { bgcolor: `${cat.color}18 !important` },
                                          '&.Mui-selected:hover': { bgcolor: `${cat.color}25 !important` }
                          }}
                        >
                          <Checkbox 
                                          checked={tempKeywords.indexOf(kw) > -1}
                                          disabled={isDisabled || isMaxed}
                                          sx={{ color: cat.color, '&.Mui-checked': { color: cat.color }, p: 0.25, mr: 0.5 }}
                                        />
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                          <Typography sx={{ fontWeight: 500, fontSize: '0.85rem' }}>{kw}</Typography>
                                          {!cat.isSpecial && (
                                            <Box sx={{
                                              px: 0.6, py: 0.1, borderRadius: '3px', fontSize: '0.6rem', fontWeight: 700,
                                              bgcolor: `${cat.color}18`, color: cat.color
                                            }}>
                                              {cat.name}
                                            </Box>
                                        )}
                                      </Box>
                      </MenuItem>
                      );
                    })}
                    </Box>
                        );
                      })}
                    </Box>
                    <Box sx={{ 
                            display: 'flex', gap: 1.5, p: 1.5,
                            bgcolor: 'rgba(10,14,24,0.98)',
                            position: 'sticky', bottom: 0, zIndex: 2,
                            borderTop: '1px solid rgba(255,255,255,0.06)'
                          }}>
                            <Button variant="outlined" size="small" fullWidth
                        onClick={() => {
                          setKeywordsMenuOpen(false);
                          setTempKeywords(Array.isArray(novelSettings.keywords) ? [...novelSettings.keywords] : []);
                        }}
                              sx={{ color: 'rgba(255,255,255,0.5)', borderColor: 'rgba(255,255,255,0.12)' }}
                      >
                        取消
                      </Button>
                            <Button variant="contained" size="small" fullWidth
                        onClick={() => {
                                const finalKw = tempKeywords.includes('无关键词') ? [] : tempKeywords;
                                updateLocalSettings({ keywords: finalKw });
                          setKeywordsMenuOpen(false);
                        }}
                              className="nexus-btn-primary"
                      >
                        确定
                      </Button>
                    </Box>
                  </Select>
                </FormControl>
                    </Box>
              </Grid>
              
                  {/* 合理性提示 */}
                  {getChapterCountSuggestion(novelSettings.wordCount, novelSettings.chapterCount) && (
              <Grid item xs={12}>
                      <Box className="settings-suggestion">
                        <Box className="suggestion-icon">
                          <InfoIcon sx={{ fontSize: 16 }} />
                        </Box>
                        <Typography className="suggestion-text">
                          {getChapterCountSuggestion(novelSettings.wordCount, novelSettings.chapterCount)}
                </Typography>
                      </Box>
                    </Grid>
                  )}

                  {/* 体验卡上限提示 */}
                  {trialCheckDone && isTrialOnlyUser && (
                    <Grid item xs={12}>
                      <Box className="settings-trial-warn">
                        <Box className="trial-warn-icon">
                          <WarningAmberIcon sx={{ fontSize: 16 }} />
                        </Box>
                        <Typography className="trial-warn-text">
                          体验卡账户章节上限10章，完成正式充值后可解锁更多
                  </Typography>
                      </Box>
                    </Grid>
                  )}

                  {/* AI 设置区 */}
                  <Grid item xs={12}>
                    <Box sx={{
                      p: 2,
                      bgcolor: 'rgba(0, 212, 255, 0.04)',
                      border: '1px solid rgba(0, 212, 255, 0.15)',
                      borderRadius: '10px',
                    }}>
                      <Typography sx={{
                        fontSize: '0.72rem',
                        color: 'rgba(0, 212, 255, 0.7)',
                        fontWeight: 600,
                        mb: 1.5,
                        letterSpacing: '0.05em',
                      }}>
                        AI 生成设置
                      </Typography>

                      {/* 思考模式开关 */}
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        py: 0.5,
                      }}>
                        <Box>
                          <Typography className="settings-field-label" sx={{ mb: 0 }}>
                            开启推理模式
                          </Typography>
                          <Typography sx={{
                            fontSize: '0.65rem',
                            color: 'rgba(255,255,255,0.35)',
                            mt: 0.25,
                            lineHeight: 1.4,
                          }}>
                            AI 推理过程实时显示，输出时自动隐藏推理内容
                          </Typography>
                        </Box>
                        <Switch
                          size="small"
                          checked={novelSettings.enableThinking || false}
                          onChange={(e) => updateLocalSettings({ enableThinking: e.target.checked })}
                          sx={{
                            '& .MuiSwitch-switchBase': {
                              '&.Mui-checked': {
                                color: '#00d4ff',
                                '& + .MuiSwitch-track': {
                                  bgcolor: 'rgba(0, 212, 255, 0.5)',
                                  opacity: 1,
                                },
                              },
                            },
                          }}
                        />
                      </Box>
                    </Box>
                  </Grid>
                </Grid>

              </Box>

              {/* 大纲设置区 */}
              <Box className="settings-section accent">

                {/* 大纲生成方式选择 */}
                <Box className="outline-mode-cards">
                  {/* AI生成大纲 */}
                  <Box
                    className={`outline-mode-card ${!useCustomOutline ? 'active' : ''}`}
                      onClick={() => setUseCustomOutline(false)}
                  >
                    <Box className="outline-mode-radio">
                      <Radio checked={!useCustomOutline} sx={{ p: 0, color: '#3b82f6', '&.Mui-checked': { color: '#3b82f6' } }} onClick={e => e.stopPropagation()} />
                      </Box>
                    <Box className="outline-mode-icon ai-icon">
                      <AutoAwesomeIcon sx={{ fontSize: 22 }} />
                    </Box>
                    <Box className="outline-mode-info">
                      <Typography className="outline-mode-title">AI生成大纲</Typography>
                      <Typography className="outline-mode-desc">根据设置自动生成专业小说大纲</Typography>
                    </Box>
                      {!useCustomOutline && (
                      <Box className="outline-mode-tags">
                        <Box className="mode-tag">智能</Box>
                        <Box className="mode-tag">高效</Box>
                        </Box>
                      )}
                    </Box>

                  {/* 自定义大纲 */}
                    <Box
                    className={`outline-mode-card ${useCustomOutline ? 'active purple' : ''}`}
                      onClick={() => setUseCustomOutline(true)}
                  >
                    <Box className="outline-mode-radio">
                      <Radio checked={useCustomOutline} sx={{ p: 0, color: '#3b82f6', '&.Mui-checked': { color: '#3b82f6' } }} onClick={e => e.stopPropagation()} />
                      </Box>
                    <Box className="outline-mode-icon custom-icon">
                      <EditIcon sx={{ fontSize: 22 }} />
                    </Box>
                    <Box className="outline-mode-info">
                      <Typography className="outline-mode-title">自定义大纲</Typography>
                      <Typography className="outline-mode-desc">手动编写，AI严格遵循</Typography>
                    </Box>
                      {useCustomOutline && (
                      <Box className="outline-mode-tags">
                        <Box className="mode-tag purple">自由</Box>
                        <Box className="mode-tag purple">创意</Box>
                        </Box>
                      )}
                    </Box>
                  </Box>
              
                {/* AI生成 - 补充要求 */}
              {!useCustomOutline && (
                  <Box className="notes-panel">
                    <Box className="notes-header">
                      <Box className="notes-header-left">
                        <EditIcon className="notes-icon" sx={{ fontSize: 16 }} />
                        <Typography className="notes-title">补充要求</Typography>
                        <Box className="notes-required">必填</Box>
                      </Box>
                      <Box className={`notes-counter ${(novelSettings.additionalNotes || '').length >= 1800 ? (novelSettings.additionalNotes || '').length >= 2000 ? 'warn' : 'caution' : ''}`}>
                        <Typography className="notes-counter-text">
                          {(novelSettings.additionalNotes || '').length >= 2000 && '上限 '}
                          {(novelSettings.additionalNotes || '').length}/2000
                        </Typography>
                      </Box>
                    </Box>
                  <TextField
                    fullWidth
                    multiline
                      rows={7}
                    value={novelSettings.additionalNotes || ''}
                    onChange={(e) => {
                        const newVal = e.target.value;
                        if (newVal.length <= 2000) {
                          const updated = { ...novelSettings, additionalNotes: newVal };
                      setNovelSettings(updated);
                      saveUpdatedSettings(updated);
                        }
                    }}
                      error={(novelSettings.additionalNotes || '').length >= 2000}
                      placeholder={`请输入您的补充要求或特殊说明（必填）...

━━━━━━━━━━━━━━━━━━━━━━━━
建议填写以下内容

【剧情要求】希望增加反转情节、强调感情线、突出爽点等

【人物设定】希望某个角色有特殊背景、增加某个类型的配角等

【世界观补充】希望增加某个特殊设定、强调某个规则等

【风格偏好】希望文风更轻松/严肃、希望对话更多/更少等`}
                      className="notes-textfield"
                      helperText={
                        (novelSettings.additionalNotes || '').length >= 2000 
                          ? '已达到字数上限（2000字）' 
                          : (novelSettings.additionalNotes || '').length === 0
                          ? '补充要求为必填项'
                          : ''
                      }
                  />
                  </Box>
              )}
              
                {/* 自定义大纲 */}
              {useCustomOutline && (
                  <Box className="custom-outline-panel">
                    <Box className="notes-header">
                      <Box className="notes-header-left">
                        <CreateIcon className="notes-icon purple" sx={{ fontSize: 16 }} />
                        <Typography className="notes-title purple">自定义大纲</Typography>
                      </Box>
                      <Box className={`notes-counter ${customOutline.length >= 45000 ? customOutline.length >= 50000 ? 'warn' : 'caution' : ''}`}>
                        <Typography className="notes-counter-text">
                          {customOutline.length >= 50000 ? '上限 ' : `剩余 ${50000 - customOutline.length} 字`}
                          {customOutline.length}/50000
                          </Typography>
                        </Box>
                      </Box>
                    <TextField
                      fullWidth
                      multiline
                      rows={10}
                      value={customOutline}
                      onChange={(e) => {
                        if (e.target.value.length <= 50000) setCustomOutline(e.target.value);
                      }}
                      placeholder="请在此输入您的小说大纲，系统将基于您的创意生成章节内容..."
                      className="notes-textfield purple"
                    />
                      {/* 进度条 */}
                    <Box className="custom-outline-progress">
                      <Box className="progress-bar-bg">
                        <Box
                          className={`progress-bar-fill ${customOutline.length >= 45000 ? (customOutline.length >= 50000 ? 'warn' : 'caution') : ''}`}
                          style={{ width: `${Math.min((customOutline.length / 50000) * 100, 100)}%` }}
                        />
                        </Box>
                      <Box className="progress-hints">
                        <Typography className="progress-hint-text">
                          {customOutline.length < 2000 ? '开始输入您的创意...'
                            : customOutline.length < 5000 ? '大纲逐渐丰富'
                            : customOutline.length < 40000 ? '内容充实，继续完善'
                            : customOutline.length < 50000 ? '接近字数上限' : '已达到字数上限'}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                )}

                  {/* 提示信息 */}
                <Box className="settings-tips">
                  <Box className="settings-tip">
                    <InfoIcon className="settings-tip-icon" sx={{ fontSize: 16 }} />
                    <Typography className="settings-tip-text">
                      {useCustomOutline
                        ? '自定义大纲可保存后直接生成章节内容，建议控制在3000-5000字'
                        : '补充要求将作为最高优先级，AI会在大纲中充分体现您的需求'}
                        </Typography>
                    </Box>
                </Box>
                
                {/* 生成按钮 - 放在大纲设置容器内 */}
                {/* 扣费提示 */}
                {!useCustomOutline && (
                  <Box className="settings-tips" sx={{ mt: 1 }}>
                    <Box className="settings-tip" sx={{
                      background: 'rgba(59, 130, 246, 0.08)',
                      border: '1px solid rgba(59, 130, 246, 0.2)',
                      borderRadius: '8px',
                      padding: '8px 12px'
                    }}>
                      <InfoIcon className="settings-tip-icon" sx={{ fontSize: 16, color: '#3b82f6' }} />
                      <Typography className="settings-tip-text" sx={{ color: '#93c5fd', fontSize: '0.78rem' }}>
                        大纲生成每日前10次免费，超出后每次消耗1000字
                      </Typography>
                    </Box>
                  </Box>
                )}
                <Box className="settings-action">
                <GeneratingButton
                  variant="contained"
                  fullWidth
                  onClick={generateNovelOutline}
                  isGenerating={generatingOutline}
                    text={useCustomOutline ? '保存自定义大纲' : '生成大纲'}
                  generatingText="正在处理..."
                  disabled={generatingOutline}
                    className="nexus-btn-primary settings-generate-btn"
                  />
                </Box>
              </Box>
            </Box>
            </Box>
          </TabPanel>
          
          {/* 大纲编辑 */}
          <TabPanel value={activeTab} index={1} sx={{ display: 'flex', flexDirection: 'column', overflowY: 'hidden', pb: '44px' }}>
            <Box className="outline-panel tab-1">
            {/* 英雄头部 */}
            <Box className="outline-hero-header">
              <Box className="outline-hero-bg" />
              <Box className="hero-particles-outline" />
              <Box className="outline-hero-content">
                <Box className="outline-hero-icon">
                  <ListAltIcon sx={{ fontSize: 22, color: '#00d4ff' }} />
                </Box>
                <Box className="outline-hero-text">
                  <Typography className="outline-hero-title">小说大纲</Typography>
                  <Typography className="outline-hero-subtitle">构建完整故事骨架</Typography>
                </Box>
                <Box className="outline-hero-badge">
                  <Box className="outline-hero-badge-dot" />
                  <Typography className="outline-hero-badge-text">AI智能</Typography>
                </Box>
              </Box>
              </Box>
              
            {/* 工具栏 */}
            <Box className="outline-toolbar">
              <Box className="outline-toolbar-left">
                {showWordCount && (
                  <Box className="outline-word-count-wrap">
                    <Box sx={{ display: 'inline-block', width: 'auto', height: { xs: '24px', sm: '32px' } }}>
                    <WordCountBadge
                      wordCount={wordCount}
                      visible={true}
                      position="header"
                      isGenerating={generatingOutline}
                    />
                  </Box>
              </Box>
              )}
              </Box>

              {/* 推理模式提示条 */}
              {novelSettings.enableThinking && generatingOutline && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.5,
                  py: 0.75,
                  background: 'linear-gradient(135deg, rgba(255,183,77,0.15) 0%, rgba(255,138,101,0.1) 100%)',
                  border: '1px solid rgba(255,183,77,0.3)',
                  borderRadius: '8px',
                  flex: 1,
                  maxWidth: { xs: '180px', sm: '240px' },
                }}>
                  <Box sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: '#ffb74d',
                    animation: 'thinkingPulse 1.5s ease-in-out infinite',
                    flexShrink: 0,
                    '@keyframes thinkingPulse': {
                      '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                      '50%': { opacity: 0.5, transform: 'scale(0.7)' },
                    },
                  }} />
                  <Typography sx={{ fontSize: '0.68rem', color: '#ffb74d', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    推理中...
                  </Typography>
                </Box>
              )}

              <Box className="outline-btn-area">
                {/* 手动修改大纲按钮 */}
                {(() => {
                  const hasStoryStructure = storyStructure && storyStructure.isGenerated;
                  const canEdit = !hasStoryStructure && outlineText && outlineText.trim().length > 0 && !generatingOutline;
                  
                  return (
                    <Tooltip 
                      title={hasStoryStructure ? "已生成故事结构，无法修改大纲" : (!outlineText || outlineText.trim().length === 0 ? "请先生成大纲" : "手动修改大纲")}
                      arrow
                      placement="top"
                    >
                      <span>
                        <Button
                          className="outline-btn-edit"
                          startIcon={<EditIcon sx={{ fontSize: '1rem !important' }} />}
                          onClick={handleOutlineEditStart}
                          disabled={!canEdit}
                          fullWidth={isMobile}
                        >
                          修改大纲
                        </Button>
                      </span>
                    </Tooltip>
                  );
                })()}
                
                {/* 重新生成大纲按钮 */}
                {(() => {
                  const hasGeneratedChapters = novel && novel.chapters && novel.chapters.some(ch => ch.content && ch.content.trim().length > 0);
                  const isButtonDisabled = loading || generatingOutline || hasGeneratedChapters;
                  
                  return (
                    <Tooltip 
                      title={hasGeneratedChapters ? "已有生成的章节内容，无法重新生成大纲以避免不一致" : ""}
                      arrow
                      placement="top"
                    >
                      <span>
                        <GeneratingButton
                          className="outline-btn-regenerate"
                          onClick={generateNovelOutline}
                          isGenerating={generatingOutline}
                          text="重新生成"
                          generatingText="生成中..."
                          disabled={isButtonDisabled}
                        />
                      </span>
                    </Tooltip>
                  );
                })()}
              </Box>
            </Box>
            
            {/* 提示条 */}
            {!outlineEdit.isEditing && outlineText && outlineText.trim().length > 0 && (
              <Box className={`outline-alert ${storyStructure?.isGenerated ? 'warning' : ''}`}>
                <EditIcon className="outline-alert-icon" sx={{ fontSize: 18 }} />
                <Box>
                  <Typography className={`outline-alert-title`}>
                    {storyStructure?.isGenerated ? '重要提示' : '温馨提示'}
                  </Typography>
                  <Typography className="outline-alert-body">
                  {storyStructure?.isGenerated ? (
                    <>
                        已生成故事结构，无法修改大纲。建议在生成故事结构前仔细检查大纲内容。
                    </>
                  ) : (
                    <>
                        如需修改角色名称或大纲信息，请点击「修改大纲」按钮。生成故事结构后将无法修改大纲，请在生成前仔细检查。
                    </>
                  )}
                </Typography>
                </Box>
              </Box>
            )}

            {/* 推理内容展示区 */}
            {novelSettings.enableThinking && thinkingContent && (
              <Box sx={{
                mx: { xs: 1, sm: 2 },
                mb: 1.5,
                p: 2,
                background: 'linear-gradient(135deg, rgba(255,183,77,0.08) 0%, rgba(255,138,101,0.05) 100%)',
                border: '1px solid rgba(255,183,77,0.25)',
                borderRadius: '10px',
                position: 'relative',
                overflow: 'hidden',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Box sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: '#ffb74d',
                    animation: 'thinkingPulse 1.5s ease-in-out infinite',
                    '@keyframes thinkingPulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.4 },
                    },
                  }} />
                  <Typography sx={{ fontSize: '0.72rem', color: '#ffb74d', fontWeight: 600 }}>
                    推理过程
                  </Typography>
                </Box>
                <Box sx={{
                  maxHeight: { xs: 120, sm: 180 },
                  overflowY: 'auto',
                  fontSize: '0.72rem',
                  color: 'rgba(255,220,150,0.8)',
                  lineHeight: 1.7,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontFamily: '"Courier New", monospace',
                  '&::-webkit-scrollbar': { width: '4px' },
                  '&::-webkit-scrollbar-track': { background: 'rgba(255,183,77,0.05)' },
                  '&::-webkit-scrollbar-thumb': { background: 'rgba(255,183,77,0.3)', borderRadius: '2px' },
                }}>
                  {thinkingContent}
                </Box>
              </Box>
            )}

            {/* 大纲内容区 */}
            <Box className="outline-section">
              {/* 编辑模式 */}
              {outlineEdit.isEditing ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* 编辑提示条 */}
                  <Box className="outline-alert editing">
                    <EditIcon className="outline-alert-icon" sx={{ fontSize: 18 }} />
                    <Box>
                      <Typography className="outline-alert-title">编辑模式</Typography>
                      <Typography className="outline-alert-body">
                        您正在编辑大纲内容，修改后请点击保存按钮保存。更改角色名时，请使用下方的查找替换功能确保全部修改一致。
                  {outlineEdit.hasUnsavedChanges && (
                          <Box component="span" sx={{ display: 'block', mt: 0.5, color: '#f59e0b !important' }}>
                            有未保存的修改
                          </Box>
                        )}
                      </Typography>
                    </Box>
                  </Box>

                  {/* 保存按钮条 - 移动端放在提示条下方 */}
                  <Box className="outline-save-bar outline-save-bar-top">
                    <Button
                      className="outline-btn-cancel"
                      onClick={handleOutlineEditCancel}
                      disabled={outlineEdit.saving}
                      startIcon={<CancelIcon />}
                    >
                      取消
                    </Button>
                    <Button
                      className="outline-btn-save"
                      onClick={handleOutlineSave}
                      disabled={outlineEdit.saving || generatingOutline}
                      startIcon={outlineEdit.saving ? <CircularProgress size={16} /> : <SaveIcon />}
                    >
                        {outlineEdit.saving ? '保存中...' : '保存大纲'}
                    </Button>
                  </Box>

                  {/* 查找替换工具条 */}
                  <Box className="outline-find-replace">
                  <TextField
                    size="small"
                    label="查找"
                    value={findReplace.findText}
                    onChange={(e) => setFindReplace(prev => ({ ...prev, findText: e.target.value, lastIndex: 0 }))}
                      className="outline-find-input"
                  />
                  <TextField
                    size="small"
                    label="替换为"
                    value={findReplace.replaceText}
                    onChange={(e) => setFindReplace(prev => ({ ...prev, replaceText: e.target.value }))}
                      className="outline-find-input"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={findReplace.caseSensitive}
                        onChange={(e) => setFindReplace(prev => ({ ...prev, caseSensitive: e.target.checked, lastIndex: 0 }))}
                      />
                    }
                      label={<Box className="outline-case-label">区分大小写</Box>}
                  />
                    <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto', width: { xs: '100%', sm: 'auto' } }}>
                      <Button size="small" className="outline-find-btn" onClick={handleFindNext}>
                      下一个
                    </Button>
                      <Button size="small" className="outline-find-btn replace" onClick={handleReplaceOne}>
                      替换
                    </Button>
                      <Button size="small" className="outline-find-btn replace-all" onClick={handleReplaceAll}>
                        全部
                    </Button>
                  </Box>
                </Box>

                  {/* 大纲编辑框 */}
            <TextField
              label="小说大纲"
              variant="outlined"
              fullWidth
              multiline
                    minRows={15}
                    maxRows={30}
                  value={outlineEdit.editedContent}
              onChange={handleOutlineChange}
                  disabled={outlineEdit.saving || generatingOutline}
                  inputRef={outlineEditRef}
                    className="outline-textfield"
                  />
              </Box>
            ) : (
                /* 只读模式 */
              <TextField
                label="小说大纲"
                variant="outlined"
                fullWidth
                multiline
                rows={25}
                value={outlineText}
                onChange={(e) => {
                  setOutlineText(e.target.value);
                }}
              inputRef={outlineRef}
              InputProps={{
                  readOnly: generatingOutline || outlineEdit.isEditing,
                  }}
                  className="outline-textfield outline-readonly"
                disabled={generatingOutline || outlineEdit.isEditing}
            />
            )}
            </Box>

            {/* 显示大纲生成状态 */}
            {renderOutlineGenerationStatus()}
            {/* 移动端底部留白，防止被导航栏遮挡 */}
            <Box className="mobile-nav-bottom-spacer" />
            </Box>
          </TabPanel>
          
          {/* 故事结构 */}
          <TabPanel value={activeTab} index={2}>
            <Box className="outline-panel tab-2">
              {/* 英雄头部 */}
              <Box className="outline-hero-header">
                <Box className="outline-hero-bg" />
                <Box className="hero-particles-outline" />
                <Box className="outline-hero-content">
                  <Box className="outline-hero-icon">
                    <AccountTreeIcon sx={{ fontSize: 24, color: '#fff' }} />
                          </Box>
                  <Box className="outline-hero-text">
                    <Typography className="outline-hero-title">故事结构</Typography>
                    <Typography className="outline-hero-subtitle">规划故事三幕架构</Typography>
                          </Box>
                  <Box className="outline-hero-badge">
                    <Box className="outline-hero-badge-dot" />
                    <Typography className="outline-hero-badge-text">AI规划</Typography>
                          </Box>
                </Box>
              </Box>

              {/* 内容区 */}
              <Box className="structure-content-wrapper">

                {/* 故事结构生成进度面板 */}
                {showProgressPanel && (
                  <Box sx={{ mb: 2 }}>
                    <StoryStructureProgressPanel
                      novelId={novel._id || novel.id}
                      onComplete={() => {
                        setShowProgressPanel(false);
                        // 🔥 生成完成：拉取番茄发布信息（阶段1完成后自动生成）
                        const nid = novel._id || novel.id;
                        if (nid) {
                          api.get(`/novels/${nid}`).then((r) => {
                            const n = r.data?.data || r.data;
                            if (n?.fanqieInfo) setFanqieInfo(n.fanqieInfo);
                          }).catch(() => {});
                        }
                      }}
                      onClose={() => {
                        setShowProgressPanel(false);
                      }}
                      totalChapters={novelSettings.chapterCount || 30}
                      initialProgress={novel.storyStructure?.generationProgress || null}
                    />
                  </Box>
                )}

                {/* 🔥 番茄平台发布信息卡片（仅发布平台=番茄 时显示；阶段1完成后自动生成，发布助手一键建书用） */}
                {!showProgressPanel && /番茄/.test(novelSettings.platform || '') && (fanqieInfo || fanqieGenerating) && (
                  <Box className="fanqie-info-card" sx={{
                    mb: 2, p: { xs: '14px', sm: '16px' },
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.06) 0%, rgba(16, 185, 129, 0.03) 100%)',
                    border: '1px solid rgba(34, 197, 94, 0.2)',
                  }}>
                    {/* 标题行 */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#22c55e', flexShrink: 0 }} />
                      <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.88rem' }, fontWeight: 700, color: '#4ade80', letterSpacing: '0.02em' }}>
                        番茄发布信息
                      </Typography>
                      <Typography sx={{ fontSize: '0.68rem', color: 'rgba(74, 222, 128, 0.4)', fontWeight: 500 }}>
                        供 KM发布助手 一键创建番茄书本
                      </Typography>
                      <Box sx={{ flex: 1 }} />
                      {!fanqieEditing && (
                        <Box sx={{ display: 'flex', gap: 0.75, ml: { xs: 'auto', sm: 0 }, flexShrink: 0 }}>
                          <Button
                            size="small"
                            disabled={fanqieGenerating}
                            onClick={handleRegenerateFanqieInfo}
                            sx={{ minWidth: 0, px: 1.25, py: 0.25, fontSize: '0.72rem', color: '#4ade80', borderColor: 'rgba(34,197,94,0.35)', '&:hover': { borderColor: 'rgba(34,197,94,0.6)', bgcolor: 'rgba(34,197,94,0.08)' } }}
                            variant="outlined"
                          >
                            {fanqieGenerating ? '生成中…' : '重新生成'}
                          </Button>
                          {fanqieInfo && (
                            <Button
                              size="small"
                              onClick={() => { setFanqieEditDraft({ ...fanqieInfo }); setFanqieEditing(true); }}
                              sx={{ minWidth: 0, px: 1.25, py: 0.25, fontSize: '0.72rem', color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.2)', '&:hover': { borderColor: 'rgba(255,255,255,0.4)', bgcolor: 'rgba(255,255,255,0.05)' } }}
                              variant="outlined"
                            >
                              编辑
                            </Button>
                          )}
                        </Box>
                      )}
                    </Box>

                    {fanqieGenerating && !fanqieInfo ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
                        <CircularProgress size={18} sx={{ color: '#4ade80' }} />
                        <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.55)' }}>
                          AI 正在根据故事核心框架生成番茄发布信息…
                        </Typography>
                      </Box>
                    ) : fanqieInfo && !fanqieEditing ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {/* 书名 + 频道 + 分类 */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography sx={{ fontSize: { xs: '0.95rem', sm: '1.05rem' }, fontWeight: 700, color: '#fff' }}>
                            《{fanqieInfo.bookName}》
                          </Typography>
                          <Chip size="small" label={fanqieInfo.gender === 2 ? '女频' : '男频'} sx={{ height: 20, fontSize: '0.68rem', bgcolor: 'rgba(34,197,94,0.12)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.3)' }} />
                          {fanqieInfo.categoryName && (
                            <Chip size="small" label={fanqieInfo.categoryName} sx={{ height: 20, fontSize: '0.68rem', bgcolor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.75)', border: '1px solid rgba(255,255,255,0.12)' }} />
                          )}
                          {fanqieInfo.roles?.length > 0 && (
                            <Chip size="small" label={`主角：${fanqieInfo.roles.join('、')}`} sx={{ height: 20, fontSize: '0.68rem', bgcolor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.75)', border: '1px solid rgba(255,255,255,0.12)' }} />
                          )}
                        </Box>
                        {/* 标签 */}
                        {fanqieInfo.tagNames?.length > 0 && (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {fanqieInfo.tagNames.map((tn, i) => (
                              <Chip key={i} size="small" label={tn} sx={{ height: 20, fontSize: '0.68rem', bgcolor: 'rgba(34,197,94,0.05)', color: 'rgba(74,222,128,0.85)', border: '1px solid rgba(34,197,94,0.18)' }} />
                            ))}
                          </Box>
                        )}
                        {/* 简介 */}
                        <Typography sx={{ fontSize: { xs: '0.76rem', sm: '0.8rem' }, color: 'rgba(255,255,255,0.6)', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                          {fanqieInfo.abstract}
                        </Typography>
                        {/* 🔥 封面提示词：三套风格 Chip 切换 + 折叠文本 + 复制（紧凑布局省空间） */}
                        {fanqieInfo.coverPrompts?.length === 3 && (
                          <Box sx={{ mt: 0.5, p: 0.75, borderRadius: '8px', bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
                              <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.55)', fontWeight: 600, mr: 0.25 }}>封面提示词</Typography>
                              {fanqieInfo.coverPromptStyles?.map((style, i) => (
                                <Chip
                                  key={i}
                                  label={style}
                                  size="small"
                                  onClick={() => handleSwitchCoverPrompt(i)}
                                  sx={{
                                    height: 18, fontSize: '0.62rem', cursor: 'pointer',
                                    bgcolor: coverPromptIdx === i ? 'rgba(34,197,94,0.18)' : 'transparent',
                                    color: coverPromptIdx === i ? '#4ade80' : 'rgba(255,255,255,0.55)',
                                    border: `1px solid ${coverPromptIdx === i ? 'rgba(34,197,94,0.4)' : 'rgba(255,255,255,0.15)'}`,
                                    '&:hover': { bgcolor: coverPromptIdx === i ? 'rgba(34,197,94,0.25)' : 'rgba(255,255,255,0.08)' }
                                  }}
                                />
                              ))}
                              <Box sx={{ flex: 1, minWidth: 8 }} />
                              <Button
                                size="small"
                                onClick={handleCopyCoverPrompt}
                                variant="outlined"
                                sx={{ minWidth: 0, px: 1, py: 0, height: 18, fontSize: '0.62rem', color: '#4ade80', borderColor: 'rgba(34,197,94,0.35)', '&:hover': { borderColor: 'rgba(34,197,94,0.6)', bgcolor: 'rgba(34,197,94,0.08)' } }}
                              >
                                复制
                              </Button>
                            </Box>
                            <Typography
                              onClick={() => setCoverPromptExpanded(!coverPromptExpanded)}
                              sx={{
                                fontSize: '0.7rem', color: 'rgba(255,255,255,0.62)', lineHeight: 1.55,
                                cursor: 'pointer',
                                display: '-webkit-box',
                                WebkitLineClamp: coverPromptExpanded ? 'none' : 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                '&:hover': { color: 'rgba(255,255,255,0.78)' }
                              }}
                            >
                              {fanqieInfo.coverPrompts[coverPromptIdx]}
                              <Box component="span" sx={{ ml: 0.5, color: '#4ade80', fontSize: '0.6rem', fontWeight: 600 }}>
                                {coverPromptExpanded ? '〔收起〕' : '〔展开〕'}
                              </Box>
                            </Typography>
                          </Box>
                        )}
                        {/* 🔥 封面提示词缺失：补生成按钮（存量小说 fanqieInfo 早于该功能生成，coverPrompts 为空） */}
                        {(!fanqieInfo.coverPrompts || fanqieInfo.coverPrompts.length !== 3) && (
                          <Box sx={{ mt: 0.5, p: 0.75, borderRadius: '8px', bgcolor: 'rgba(255,255,255,0.03)', border: '1px dashed rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', gap: 1 }}>
                            {coverPromptGenerating ? (
                              <>
                                <CircularProgress size={14} sx={{ color: '#4ade80' }} />
                                <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.55)' }}>
                                  AI 正在生成 3 套封面提示词（主角特写/场景渲染/意境象征）…
                                </Typography>
                              </>
                            ) : (
                              <>
                                <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.45)', flex: 1 }}>
                                  尚未生成封面提示词（AI 绘画用，3 套风格可选）
                                </Typography>
                                <Button
                                  size="small"
                                  onClick={handleGenerateCoverPrompts}
                                  variant="outlined"
                                  sx={{ minWidth: 0, px: 1.25, py: 0.25, fontSize: '0.72rem', color: '#4ade80', borderColor: 'rgba(34,197,94,0.35)', '&:hover': { borderColor: 'rgba(34,197,94,0.6)', bgcolor: 'rgba(34,197,94,0.08)' } }}
                                >
                                  生成封面提示词
                                </Button>
                              </>
                            )}
                          </Box>
                        )}
                      </Box>
                    ) : fanqieEditing && fanqieEditDraft ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', gap: 1.5, flexDirection: { xs: 'column', sm: 'row' } }}>
                          <TextField
                            label="书名（≤15字）"
                            value={fanqieEditDraft.bookName || ''}
                            onChange={(e) => setFanqieEditDraft({ ...fanqieEditDraft, bookName: e.target.value.slice(0, 15) })}
                            size="small"
                            fullWidth
                            sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.04)' } }}
                          />
                          <TextField
                            label="主角名（多个用、分隔）"
                            value={(fanqieEditDraft.roles || []).join('、')}
                            onChange={(e) => setFanqieEditDraft({ ...fanqieEditDraft, roles: e.target.value.split(/[、,，\s]+/).filter(Boolean).slice(0, 2) })}
                            size="small"
                            fullWidth
                            sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.04)' } }}
                          />
                        </Box>
                        <TextField
                          label={`作品简介（50-500字，当前${(fanqieEditDraft.abstract || '').length}字）`}
                          value={fanqieEditDraft.abstract || ''}
                          onChange={(e) => setFanqieEditDraft({ ...fanqieEditDraft, abstract: e.target.value.slice(0, 500) })}
                          multiline
                          minRows={3}
                          maxRows={8}
                          fullWidth
                          sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.04)' } }}
                        />
                        {/* 🔥 封面提示词（编辑模式）：切换风格 + 折叠文本 + 复制；切换会同步 draft.coverPromptSelected 以便保存 */}
                        {fanqieEditDraft.coverPrompts?.length === 3 && (
                          <Box sx={{ p: 0.75, borderRadius: '8px', bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
                              <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.55)', fontWeight: 600, mr: 0.25 }}>封面提示词</Typography>
                              {fanqieEditDraft.coverPromptStyles?.map((style, i) => (
                                <Chip
                                  key={i}
                                  label={style}
                                  size="small"
                                  onClick={() => handleSwitchCoverPrompt(i)}
                                  sx={{
                                    height: 18, fontSize: '0.62rem', cursor: 'pointer',
                                    bgcolor: coverPromptIdx === i ? 'rgba(34,197,94,0.18)' : 'transparent',
                                    color: coverPromptIdx === i ? '#4ade80' : 'rgba(255,255,255,0.55)',
                                    border: `1px solid ${coverPromptIdx === i ? 'rgba(34,197,94,0.4)' : 'rgba(255,255,255,0.15)'}`,
                                    '&:hover': { bgcolor: coverPromptIdx === i ? 'rgba(34,197,94,0.25)' : 'rgba(255,255,255,0.08)' }
                                  }}
                                />
                              ))}
                              <Box sx={{ flex: 1, minWidth: 8 }} />
                              <Button
                                size="small"
                                onClick={handleCopyCoverPrompt}
                                variant="outlined"
                                sx={{ minWidth: 0, px: 1, py: 0, height: 18, fontSize: '0.62rem', color: '#4ade80', borderColor: 'rgba(34,197,94,0.35)', '&:hover': { borderColor: 'rgba(34,197,94,0.6)', bgcolor: 'rgba(34,197,94,0.08)' } }}
                              >
                                复制
                              </Button>
                            </Box>
                            <Typography
                              onClick={() => setCoverPromptExpanded(!coverPromptExpanded)}
                              sx={{
                                fontSize: '0.7rem', color: 'rgba(255,255,255,0.62)', lineHeight: 1.55,
                                cursor: 'pointer',
                                display: '-webkit-box',
                                WebkitLineClamp: coverPromptExpanded ? 'none' : 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                '&:hover': { color: 'rgba(255,255,255,0.78)' }
                              }}
                            >
                              {fanqieEditDraft.coverPrompts[coverPromptIdx]}
                              <Box component="span" sx={{ ml: 0.5, color: '#4ade80', fontSize: '0.6rem', fontWeight: 600 }}>
                                {coverPromptExpanded ? '〔收起〕' : '〔展开〕'}
                              </Box>
                            </Typography>
                            <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.4)', mt: 0.5 }}>
                              选中风格将随保存生效（多端共用，可粘贴到任意 AI 绘画工具）
                            </Typography>
                          </Box>
                        )}
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Button size="small" variant="outlined" onClick={() => { setFanqieEditing(false); setFanqieEditDraft(null); }} sx={{ color: 'rgba(255,255,255,0.6)', borderColor: 'rgba(255,255,255,0.2)' }}>取消</Button>
                          <Button size="small" variant="contained" onClick={handleSaveFanqieInfo} sx={{ bgcolor: 'rgba(34,197,94,0.85)', '&:hover': { bgcolor: 'rgba(34,197,94,1)' } }}>保存</Button>
                        </Box>
                      </Box>
                    ) : null}
                  </Box>
                )}

                {/* 未生成状态（进度面板显示时隐藏） */}
                {!showProgressPanel && !storyStructure?.isGenerated ? (
                  <Box sx={{ textAlign: 'center', py: 5 }}>
                    <InfoIcon sx={{ fontSize: 48, color: 'rgba(255,255,255,0.2)', mb: 2 }} />
                    <Typography sx={{ color: 'rgba(255,255,255,0.45)', mb: 2 }}>
                      点击下方按钮生成故事结构
                          </Typography>
                    <Button className="assist-chapter-ai-glow-btn" size="large" startIcon={<AutoFixHighIcon />} onClick={handleGenerateStoryStructure} disabled={!outline || outline.trim().length < 50} sx={{ px: { xs: 3, sm: 4 }, minHeight: { xs: 44, sm: 42 } }}>
                      生成故事结构
                    </Button>
                    {(!outline || outline.trim().length < 50) && (
                      <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>请先填写大纲（至少50字）</Typography>
                    )}
                                </Box>
                ) : !showProgressPanel && (
                  <>
                    {/* 三栏布局：桌面md+均分 | 移动端单栏 */}
                    <Box className="structure-three-col">

                      {/* ====== 左栏：核心信息（阶段/线索/角色/结局）====== */}
                      <Box className="structure-core-col">
                        {/* 故事阶段 */}
                        {storyStructure.phases?.length > 0 && (
                          <Box className="structure-section-card" data-type="phase">
                            <Accordion 
                              expanded={expandedSections['phase'] !== false}
                              onChange={() => handleSectionToggle('phase')}
                              disableGutters sx={{ '&:before': { display: 'none' }, bgcolor: 'transparent', boxShadow: 'none', borderRadius: '0 !important' }}>
                              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '1rem !important', color: '#ff6b35 !important' }} />} sx={{ minHeight: '36px !important', '& .MuiAccordionSummary-content': { my: '8px !important' } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ff6b35', flexShrink: 0 }} />
                                  <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.88rem' }, fontWeight: 700, color: '#ff6b35', letterSpacing: '0.02em' }}>故事阶段</Typography>
                                  <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255,107,53,0.45)', fontWeight: 500 }}>{storyStructure.phases.length}个</Typography>
                              </Box>
                              </AccordionSummary>
                              <AccordionDetails sx={{ p: '0 0 10px !important' }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                  {storyStructure.phases.map((phase, idx) => (
                                    <Box key={idx} sx={{ 
                                      bgcolor: 'rgba(255,107,53,0.04)', 
                                      borderRadius: '8px', 
                                      border: '1px solid rgba(255,107,53,0.15)',
                                      p: { xs: '10px', sm: '12px' },
                                      transition: 'all 0.2s',
                                      '&:hover': { borderColor: 'rgba(255,107,53,0.3)', bgcolor: 'rgba(255,107,53,0.08)' }
                                    }}>
                                      {/* 标题行 */}
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <Box sx={{ width: 20, height: 20, borderRadius: '5px', bgcolor: 'rgba(255,107,53,0.15)', border: '1px solid rgba(255,107,53,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                          <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#ff8c5a' }}>{idx + 1}</Typography>
                                        </Box>
                                        <Typography sx={{ fontSize: { xs: '0.88rem', sm: '0.92rem' }, fontWeight: 700, color: '#ff6b35', flex: 1 }}>{phase.name}</Typography>
                                      </Box>
                                      
                                      {/* 分隔线 */}
                                      <Box sx={{ height: '1px', bgcolor: 'rgba(255,107,53,0.2)', mb: 1 }} />
                                      
                                      {/* 章节范围 */}
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75, textAlign: 'left' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, px: 0.8, py: 0.35, bgcolor: 'rgba(74,222,128,0.12)', borderRadius: '5px', border: '1px solid rgba(74,222,128,0.3)' }}>
                                          <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#4ade80', flexShrink: 0 }} />
                                          <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: '#4ade80' }}>章节</Typography>
                                        </Box>
                                        <Typography sx={{ fontSize: { xs: '0.72rem', sm: '0.75rem' }, color: 'rgba(255,255,255,0.8)', lineHeight: 1.5 }}>{phase.chapterRange[0]}-{phase.chapterRange[1]}章</Typography>
                                      </Box>
                                      
                                      {/* 阶段目标 */}
                                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 0.75, textAlign: 'left' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, px: 0.8, py: 0.35, bgcolor: 'rgba(255,107,53,0.12)', borderRadius: '5px', border: '1px solid rgba(255,107,53,0.3)', flexShrink: 0 }}>
                                          <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#ff6b35', flexShrink: 0 }} />
                                          <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: '#ff8c5a' }}>目标</Typography>
                                        </Box>
                                        <Typography sx={{ fontSize: { xs: '0.72rem', sm: '0.75rem' }, color: 'rgba(255,255,255,0.8)', lineHeight: 1.5, flex: 1 }}>{phase.goal}</Typography>
                                      </Box>
                                      
                                      {/* 必完任务 */}
                                      {phase.mustComplete?.length > 0 && (
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, textAlign: 'left' }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, px: 0.8, py: 0.35, bgcolor: 'rgba(239,68,68,0.12)', borderRadius: '5px', border: '1px solid rgba(239,68,68,0.3)', flexShrink: 0 }}>
                                            <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#ef4444', flexShrink: 0 }} />
                                            <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: '#ef4444' }}>必完</Typography>
                                          </Box>
                                          <Typography sx={{ fontSize: { xs: '0.72rem', sm: '0.75rem' }, color: 'rgba(255,200,150,0.85)', lineHeight: 1.5, flex: 1 }}>
                                            {phase.mustComplete.map(t => t.task).join('、')}
                                          </Typography>
                                        </Box>
                                      )}
                                    </Box>
                                  ))}
                                </Box>
                              </AccordionDetails>
                            </Accordion>
                          </Box>
                        )}

                        {/* 剧情线索 */}
                        {storyStructure.plotThreads?.length > 0 && (
                          <Box className="structure-section-card" data-type="outline">
                            <Accordion 
                              expanded={expandedSections['plotThreads'] !== false}
                              onChange={() => handleSectionToggle('plotThreads')}
                              disableGutters sx={{ '&:before': { display: 'none' }, bgcolor: 'transparent', boxShadow: 'none', borderRadius: '0 !important' }}>
                              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '1rem !important', color: '#ec4899 !important' }} />} sx={{ minHeight: '36px !important', '& .MuiAccordionSummary-content': { my: '8px !important' } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ec4899', flexShrink: 0 }} />
                                  <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.88rem' }, fontWeight: 700, color: '#ec4899', letterSpacing: '0.02em' }}>剧情线索</Typography>
                                  <Typography sx={{ fontSize: '0.68rem', color: 'rgba(236,64,122,0.45)', fontWeight: 500 }}>{storyStructure.plotThreads.length}条</Typography>
                              </Box>
                              </AccordionSummary>
                              <AccordionDetails sx={{ p: '0 0 10px !important' }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                  {storyStructure.plotThreads.map((thread, idx) => (
                                    <Box key={idx} sx={{ 
                                      bgcolor: 'rgba(236,64,122,0.04)', 
                                      borderRadius: '8px', 
                                      border: '1px solid rgba(236,64,122,0.12)',
                                      p: { xs: '10px', sm: '12px' },
                                      transition: 'all 0.2s',
                                      '&:hover': { borderColor: 'rgba(236,64,122,0.25)', bgcolor: 'rgba(236,64,122,0.06)' }
                                    }}>
                                      {/* 标题行 */}
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <Box sx={{ width: 20, height: 20, borderRadius: '5px', bgcolor: 'rgba(236,64,122,0.15)', border: '1px solid rgba(236,64,122,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                          <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#f472b6' }}>{idx + 1}</Typography>
                                        </Box>
                                        <Typography sx={{ fontSize: { xs: '0.85rem', sm: '0.88rem' }, fontWeight: 700, color: '#ec4899', flex: 1 }}>{thread.name}</Typography>
                                        {thread.relatedCharacters?.length > 0 && (
                                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                            {thread.relatedCharacters.slice(0, 2).map((char, ci) => (
                                              <Box key={ci} sx={{ px: 0.75, py: 0.15, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.1)' }}>
                                                <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.5)' }}>{char}</Typography>
                                              </Box>
                                            ))}
                                            {thread.relatedCharacters.length > 2 && (
                                              <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.35)' }}>+{thread.relatedCharacters.length - 2}</Typography>
                                            )}
                                          </Box>
                                        )}
                                      </Box>
                                      
                                      {/* 分隔线 */}
                                      <Box sx={{ height: '1px', bgcolor: 'rgba(236,64,122,0.2)', mb: 1 }} />
                                      
                                      {/* 描述 */}
                                      <Box sx={{ mb: 1, pl: 0.5 }}>
                                        <Typography sx={{ fontSize: { xs: '0.7rem', sm: '0.72rem' }, color: 'rgba(255,255,255,0.75)', lineHeight: 1.7 }}>{thread.description}</Typography>
                                      </Box>
                                      
                                      {/* 分隔线 */}
                                      <Box sx={{ height: '1px', bgcolor: 'rgba(236,64,122,0.12)', mb: 1 }} />
                                      
                                      {/* 章节节点 */}
                                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', pl: 0.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, bgcolor: 'rgba(74,222,128,0.1)', borderRadius: '6px', border: '1px solid rgba(74,222,128,0.25)' }}>
                                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#4ade80', flexShrink: 0 }} />
                                          <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#4ade80' }}>起</Typography>
                                          <Typography sx={{ fontSize: { xs: '0.65rem', sm: '0.68rem' }, color: 'rgba(255,255,255,0.8)' }}>第{thread.startChapter}章</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, bgcolor: 'rgba(239,68,68,0.1)', borderRadius: '6px', border: '1px solid rgba(239,68,68,0.25)' }}>
                                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#ef4444', flexShrink: 0 }} />
                                          <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#ef4444' }}>高</Typography>
                                          <Typography sx={{ fontSize: { xs: '0.65rem', sm: '0.68rem' }, color: 'rgba(255,255,255,0.8)' }}>第{thread.peakChapter}章</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, bgcolor: 'rgba(167,139,250,0.1)', borderRadius: '6px', border: '1px solid rgba(167,139,250,0.25)' }}>
                                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#a78bfa', flexShrink: 0 }} />
                                          <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#a78bfa' }}>解</Typography>
                                          <Typography sx={{ fontSize: { xs: '0.65rem', sm: '0.68rem' }, color: 'rgba(255,255,255,0.8)' }}>第{thread.resolveChapter}章</Typography>
                                        </Box>
                                      </Box>
                                    </Box>
                                  ))}
                                </Box>
                              </AccordionDetails>
                            </Accordion>
                          </Box>
                        )}

                        {/* 角色画像 */}
                        {storyStructure.characterProfiles?.length > 0 && (
                          <Box className="structure-section-card" data-type="character">
                            <Accordion 
                              expanded={expandedSections['characterProfiles'] !== false}
                              onChange={() => handleSectionToggle('characterProfiles')}
                              disableGutters sx={{ '&:before': { display: 'none' }, bgcolor: 'transparent', boxShadow: 'none', borderRadius: '0 !important' }}>
                              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '1rem !important', color: '#ffd700 !important' }} />} sx={{ minHeight: '36px !important', '& .MuiAccordionSummary-content': { my: '8px !important' } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ffd700', flexShrink: 0 }} />
                                  <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.88rem' }, fontWeight: 700, color: '#ffd700', letterSpacing: '0.02em' }}>角色画像</Typography>
                                  <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255,215,0,0.45)', fontWeight: 500 }}>{storyStructure.characterProfiles.length}人</Typography>
                                </Box>
                        </AccordionSummary>
                              <AccordionDetails sx={{ p: '0 0 10px !important' }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                  {storyStructure.characterProfiles.map((profile, idx) => {
                                    // 角色类型颜色
                                    const roleColors = {
                                      protagonist: { main: '#ffd700', light: '#fef08a', border: 'rgba(255,215,0,0.25)', hover: 'rgba(255,215,0,0.4)' },
                                      antagonist: { main: '#ef4444', light: '#fca5a5', border: 'rgba(239,68,68,0.25)', hover: 'rgba(239,68,68,0.4)' },
                                      supporting: { main: '#a78bfa', light: '#c4b5fd', border: 'rgba(167,139,250,0.25)', hover: 'rgba(167,139,250,0.4)' }
                                    };
                                    const roleKey = profile.role || 'supporting';
                                    const roleColor = roleColors[roleKey] || roleColors.supporting;
                                    return (
                                    <Box key={idx} sx={{ 
                                      bgcolor: `${roleColor.main}08`, 
                                      borderRadius: '8px', 
                                      border: `1px solid ${roleColor.border}`,
                                      p: { xs: '10px', sm: '12px' },
                                      transition: 'all 0.2s',
                                      '&:hover': { borderColor: roleColor.hover, bgcolor: `${roleColor.main}12` }
                                    }}>
                                      {/* 标题行 */}
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <Box sx={{ width: 28, height: 28, borderRadius: '6px', bgcolor: `${roleColor.main}20`, border: `1px solid ${roleColor.main}50`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: roleColor.main }}>{profile.character?.charAt(0) || '?'}</Typography>
                                        </Box>
                                        <Typography sx={{ fontSize: { xs: '0.88rem', sm: '0.92rem' }, fontWeight: 700, color: roleColor.main, flex: 1, textAlign: 'left' }}>{profile.character}</Typography>
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                          {profile.gender && (
                                            <Box sx={{ px: 0.75, py: 0.2, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.1)' }}>
                                              <Typography sx={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.6)', fontWeight: 500 }}>{getGenderDisplay(profile.gender)}</Typography>
                                            </Box>
                                          )}
                                          {profile.age && (
                                            <Box sx={{ px: 0.75, py: 0.2, bgcolor: `${roleColor.main}15`, borderRadius: '4px', border: `1px solid ${roleColor.main}40` }}>
                                              <Typography sx={{ fontSize: '0.62rem', color: roleColor.main, fontWeight: 600 }}>{profile.age}岁</Typography>
                                            </Box>
                                          )}
                                          <Box sx={{ px: 0.75, py: 0.2, bgcolor: roleColor.main, borderRadius: '4px' }}>
                                            <Typography sx={{ fontSize: '0.62rem', color: '#000', fontWeight: 700 }}>{getRoleDisplay(profile.role)}</Typography>
                                          </Box>
                                        </Box>
                                      </Box>
                                      
                                      {/* 分隔线 */}
                                      <Box sx={{ height: '1px', bgcolor: `${roleColor.main}25`, mb: 1 }} />
                                      
                                      {/* 性格 */}
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75, textAlign: 'left' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, px: 0.8, py: 0.35, bgcolor: 'rgba(167,139,250,0.12)', borderRadius: '5px', border: '1px solid rgba(167,139,250,0.3)' }}>
                                          <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#a78bfa', flexShrink: 0 }} />
                                          <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: '#a78bfa' }}>性格</Typography>
                                        </Box>
                                        <Typography sx={{ fontSize: { xs: '0.72rem', sm: '0.75rem' }, color: 'rgba(255,255,255,0.8)', lineHeight: 1.6, flex: 1 }}>
                                          {Array.isArray(profile.personality?.traits) ? profile.personality.traits.join('、') : (profile.personality?.traits || '—')}
                                        </Typography>
                                      </Box>
                                      
                                      {/* 动机 */}
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75, textAlign: 'left' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, px: 0.8, py: 0.35, bgcolor: 'rgba(96,165,250,0.12)', borderRadius: '5px', border: '1px solid rgba(96,165,250,0.3)' }}>
                                          <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#60a5fa', flexShrink: 0 }} />
                                          <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: '#60a5fa' }}>动机</Typography>
                                        </Box>
                                        <Typography sx={{ fontSize: { xs: '0.72rem', sm: '0.75rem' }, color: 'rgba(255,255,255,0.8)', lineHeight: 1.6, flex: 1 }}>{profile.personality?.motivation || '—'}</Typography>
                                      </Box>
                                      
                                      {profile.backgroundGrowth && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75, textAlign: 'left' }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, px: 0.8, py: 0.35, bgcolor: 'rgba(251,191,36,0.12)', borderRadius: '5px', border: '1px solid rgba(251,191,36,0.3)' }}>
                                            <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#fbbf24', flexShrink: 0 }} />
                                            <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: '#fbbf24' }}>背景</Typography>
                                          </Box>
                                          <Typography sx={{ fontSize: { xs: '0.72rem', sm: '0.75rem' }, color: 'rgba(255,200,150,0.85)', lineHeight: 1.6, flex: 1 }} title={profile.backgroundGrowth}>
                                            {profile.backgroundGrowth.substring(0, 80)}{profile.backgroundGrowth.length > 80 ? '...' : ''}
                                          </Typography>
                                        </Box>
                                      )}
                                      
                                      {profile.goldenFinger && profile.goldenFinger !== '无' && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, textAlign: 'left' }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, px: 0.8, py: 0.35, bgcolor: `${roleColor.main}15`, borderRadius: '5px', border: `1px solid ${roleColor.main}40` }}>
                                            <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: roleColor.main, flexShrink: 0 }} />
                                            <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: roleColor.main }}>金手指</Typography>
                                          </Box>
                                          <Typography sx={{ fontSize: { xs: '0.72rem', sm: '0.75rem' }, color: roleColor.light, lineHeight: 1.6, flex: 1 }}>{profile.goldenFinger}</Typography>
                                        </Box>
                                      )}
                                    </Box>
                                    );
                                  })}
                                </Box>
                              </AccordionDetails>
                            </Accordion>
                          </Box>
                        )}

                        {/* 结局设计 */}
                        {storyStructure.endingPlan && (
                          <Box className="structure-section-card" data-type="ending">
                            <Accordion 
                              expanded={expandedSections['endingPlan'] !== false}
                              onChange={() => handleSectionToggle('endingPlan')}
                              disableGutters sx={{ '&:before': { display: 'none' }, bgcolor: 'transparent', boxShadow: 'none', borderRadius: '0 !important' }}>
                              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '1rem !important', color: '#ef4444 !important' }} />} sx={{ minHeight: '36px !important', '& .MuiAccordionSummary-content': { my: '8px !important' } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ef4444', flexShrink: 0 }} />
                                  <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.88rem' }, fontWeight: 700, color: '#ef4444', letterSpacing: '0.02em' }}>结局设计</Typography>
                                </Box>
                        </AccordionSummary>
                              <AccordionDetails sx={{ p: '0 0 10px !important' }}>
                                <Box sx={{ 
                                  bgcolor: 'rgba(239,68,68,0.04)', 
                                  borderRadius: '8px', 
                                  border: '1px solid rgba(239,68,68,0.15)',
                                  p: { xs: '12px', sm: '14px' }
                                }}>
                                  {/* 结局类型 */}
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Box sx={{ px: 1.25, py: 0.35, bgcolor: 'rgba(239,68,68,0.2)', borderRadius: '6px', border: '1px solid rgba(239,68,68,0.35)' }}>
                                        <Typography sx={{ fontSize: { xs: '0.72rem', sm: '0.78rem' }, fontWeight: 700, color: '#ef4444' }}>{storyStructure.endingPlan.type || '结局类型'}</Typography>
                                      </Box>
                                    </Box>
                                    <IconButton size="small" onClick={() => { setEndingEdit({ open: true, type: storyStructure.endingPlan.type || '', description: storyStructure.endingPlan.description || '', emotionalTone: storyStructure.endingPlan.emotionalTone || '', saving: false }); }} sx={{ p: 0.25, opacity: 0.5, '&:hover': { opacity: 1 } }}>
                                      <EditIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                                  </Box>
                                  
                                  {/* 分隔线 */}
                                  <Box sx={{ height: '1px', bgcolor: 'rgba(239,68,68,0.15)', mb: 1 }} />
                                  
                                  {/* 描述 */}
                                  <Typography sx={{ fontSize: { xs: '0.72rem', sm: '0.75rem' }, color: 'rgba(255,255,255,0.65)', lineHeight: 1.7, mb: storyStructure.endingPlan.emotionalTone ? 1 : 0 }}>{storyStructure.endingPlan.description || '暂无描述'}</Typography>
                                  
                                  {storyStructure.endingPlan.emotionalTone && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75, pt: 0.75, borderTop: '1px solid rgba(239,68,68,0.1)' }}>
                                      <Box sx={{ px: 0.75, py: 0.2, bgcolor: 'rgba(248,113,113,0.15)', borderRadius: '4px', border: '1px solid rgba(248,113,113,0.25)', flexShrink: 0 }}>
                                        <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#f87171' }}>情感</Typography>
                                      </Box>
                                      <Typography sx={{ fontSize: { xs: '0.7rem', sm: '0.72rem' }, color: 'rgba(248,113,113,0.75)' }}>{storyStructure.endingPlan.emotionalTone}</Typography>
                                    </Box>
                                  )}
                                </Box>
                              </AccordionDetails>
                            </Accordion>
                          </Box>
                        )}

                        {/* 修复缺失章节 */}
                              {(() => {
                          let needsRepair = storyStructure.needsRepair || false;
                          let failedChapters = storyStructure.failedChapters || [];
                          if (!needsRepair && storyStructure.outlineEnforcement?.chapterOutlineMap) {
                            const detected = [];
                            storyStructure.outlineEnforcement.chapterOutlineMap.forEach(ch => {
                              const c = ch.outlineContent || ch.plot || '';
                              if (c && (c.includes('AI生成失败') || c.includes('需手动完善') || c.length < 50)) detected.push(ch.chapterNumber);
                            });
                            if (detected.length > 0) { needsRepair = true; failedChapters = detected; }
                          }
                          if (!needsRepair || failedChapters.length === 0) return null;
                                  return (
                            <Box className="structure-section-card warning">
                              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#fbbf24', mb: 0.5 }}>{failedChapters.length}个章节大纲生成失败</Typography>
                              <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', mb: 0.75 }}>第{failedChapters.slice(0, 5).join('、')}{failedChapters.length > 5 ? '...' : ''}章</Typography>
                              <Button variant="contained" color="warning" size="small" onClick={handleRepairChapterOutlines} disabled={repairingOutlines} startIcon={repairingOutlines ? <CircularProgress size={12} color="inherit" /> : null} sx={{ fontSize: '0.72rem', py: 0.5 }}>
                                {repairingOutlines ? '补全中...' : '补全章节大纲'}
                              </Button>
                                    </Box>
                                  );
                              })()}
                            </Box>

                      {/* ====== 中栏：动态故事状态（紧凑折叠）====== */}
                      <Box className="structure-dynamic-col" sx={{ order: { xs: 2, md: 1 } }}>
                        {/* 人物关系时间线 */}
                        {storyStructure.relationshipTimeline && storyStructure.relationshipTimeline.length > 0 && (
                          <Box className="structure-section-card" data-type="relationship">
                            <Accordion 
                              expanded={expandedSections['relationshipTimeline'] !== false}
                              onChange={() => handleSectionToggle('relationshipTimeline')}
                              disableGutters sx={{ '&:before': { display: 'none' }, bgcolor: 'transparent', boxShadow: 'none', borderRadius: '0 !important' }}>
                              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '1rem !important', color: '#f472b6 !important' }} />} sx={{ minHeight: '36px !important', '& .MuiAccordionSummary-content': { my: '8px !important' } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#f472b6', flexShrink: 0 }} />
                                  <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.88rem' }, fontWeight: 700, color: '#f472b6', letterSpacing: '0.02em' }}>人物关系</Typography>
                                  <Typography sx={{ fontSize: '0.68rem', color: 'rgba(244,114,182,0.45)', fontWeight: 500 }}>{storyStructure.relationshipTimeline.length}组</Typography>
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails sx={{ p: '0 0 10px !important' }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                  {storyStructure.relationshipTimeline.map((rel, idx, arr) => (
                                    <Box key={idx} sx={{ 
                                      bgcolor: 'rgba(244,114,182,0.04)', 
                                      borderRadius: '8px', 
                                      border: '1px solid rgba(244,114,182,0.12)',
                                      p: { xs: '10px', sm: '12px' },
                                      transition: 'all 0.2s',
                                      '&:hover': { borderColor: 'rgba(244,114,182,0.25)', bgcolor: 'rgba(244,114,182,0.06)' }
                                    }}>
                                      {/* 关系行 */}
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#f472b6', flexShrink: 0, boxShadow: '0 0 6px rgba(244,114,182,0.5)' }} />
                                        <Typography sx={{ fontSize: { xs: '0.82rem', sm: '0.85rem' }, fontWeight: 700, color: '#f472b6' }}>{rel.from}</Typography>
                                        <Box sx={{ px: 0.75, py: 0.15, bgcolor: 'rgba(244,114,182,0.1)', borderRadius: '4px', border: '1px solid rgba(244,114,182,0.2)' }}>
                                          <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.4)' }}>⟷</Typography>
                                        </Box>
                                        <Typography sx={{ fontSize: { xs: '0.82rem', sm: '0.85rem' }, fontWeight: 700, color: '#f472b6' }}>{rel.to}</Typography>
                                        <Box sx={{ px: 0.75, py: 0.2, borderRadius: '4px', bgcolor: 'rgba(244,114,182,0.15)', border: '1px solid rgba(244,114,182,0.3)', ml: 'auto', flexShrink: 0 }}>
                                          <Typography sx={{ fontSize: { xs: '0.6rem', sm: '0.62rem' }, color: '#f472b6', fontWeight: 600 }}>{rel.currentStatus || '初始'}</Typography>
                                        </Box>
                                      </Box>
                                      
                                      {/* 分隔线 */}
                                      <Box sx={{ height: '1px', bgcolor: 'rgba(244,114,182,0.1)', mt: 0.75, mb: 0.5 }} />
                                      
                                      {/* 最新状态 */}
                                      {rel.timeline?.length > 0 && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                          <Box sx={{ px: 0.6, py: 0.1, bgcolor: 'rgba(96,165,250,0.15)', borderRadius: '4px', border: '1px solid rgba(96,165,250,0.2)', flexShrink: 0 }}>
                                            <Typography sx={{ fontSize: '0.58rem', fontWeight: 600, color: '#60a5fa' }}>最新</Typography>
                                          </Box>
                                          <Typography sx={{ fontSize: { xs: '0.65rem', sm: '0.68rem' }, color: 'rgba(255,255,255,0.5)' }}>
                                            <Box component="span" sx={{ color: '#60a5fa', fontWeight: 600 }}>第{rel.timeline[rel.timeline.length - 1].chapter}章</Box>
                                            <Box component="span" sx={{ color: 'rgba(255,255,255,0.3)', mx: 0.5 }}>→</Box>
                                            {rel.timeline[rel.timeline.length - 1].status}
                                          </Typography>
                                        </Box>
                                      )}
                                    </Box>
                                  ))}
                                </Box>
                              </AccordionDetails>
                            </Accordion>
                                </Box>
                        )}

                        {/* 伏笔追踪 */}
                        {storyStructure.foreshadowsTracking && storyStructure.foreshadowsTracking.length > 0 && (
                          <Box className="structure-section-card" data-type="foreshadow">
                            <Accordion 
                              expanded={expandedSections['foreshadowsTracking'] !== false}
                              onChange={() => handleSectionToggle('foreshadowsTracking')}
                              disableGutters sx={{ '&:before': { display: 'none' }, bgcolor: 'transparent', boxShadow: 'none', borderRadius: '0 !important' }}>
                              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '1rem !important', color: '#a78bfa !important' }} />} sx={{ minHeight: '36px !important', '& .MuiAccordionSummary-content': { my: '8px !important' } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#a78bfa', flexShrink: 0 }} />
                                  <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.88rem' }, fontWeight: 700, color: '#a78bfa', letterSpacing: '0.02em' }}>伏笔追踪</Typography>
                                  <Typography sx={{ fontSize: '0.68rem', color: 'rgba(167,139,250,0.45)', fontWeight: 500 }}>{storyStructure.foreshadowsTracking.length}条</Typography>
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails sx={{ p: '0 0 10px !important' }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                  {storyStructure.foreshadowsTracking.map((f, idx) => {
                                    const expanded = !!expandedForeshadows[idx];
                                    const importanceColor = f.importance === 'high' ? '#ef4444' : f.importance === 'low' ? '#6b7280' : '#f59e0b';
                                    const statusColor = f.currentStatus === 'revealed' ? '#4ade80' : '#a78bfa';
                                    const hints = f.hints || [];
                                    const content = f.content || f.description || '(无描述)';

                                    return (
                                      <Box key={idx} sx={{ 
                                        bgcolor: 'rgba(167,139,250,0.04)', 
                                        borderRadius: '8px', 
                                        border: '1px solid rgba(167,139,250,0.12)',
                                        p: { xs: '12px', sm: '14px' },
                                        transition: 'all 0.2s',
                                        '&:hover': { borderColor: 'rgba(167,139,250,0.25)', bgcolor: 'rgba(167,139,250,0.06)' }
                                      }}>
                                        {/* 状态行 */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: statusColor, flexShrink: 0, boxShadow: `0 0 6px ${statusColor}66` }} />
                                          <Typography sx={{ fontSize: { xs: '0.85rem', sm: '0.88rem' }, fontWeight: 700, color: 'white', flex: 1 }}>
                                            {content}
                                          </Typography>
                                        </Box>
                                        
                                        {/* 分隔线 */}
                                        <Box sx={{ height: '1px', bgcolor: 'rgba(167,139,250,0.15)', mb: 1 }} />
                                        
                                        {/* 状态标签 */}
                                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 0.75 }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.4, bgcolor: 'rgba(167,139,250,0.1)', borderRadius: '4px', border: '1px solid rgba(167,139,250,0.25)' }}>
                                            <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#a78bfa' }}>埋</Typography>
                                            <Typography sx={{ fontSize: { xs: '0.7rem', sm: '0.72rem' }, color: 'rgba(255,255,255,0.8)' }}>第{String(f.introducedInChapter || f.introducedAt || '?')}章</Typography>
                                          </Box>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.4, bgcolor: statusColor === '#4ade80' ? 'rgba(74,222,128,0.1)' : 'rgba(255,152,0,0.1)', borderRadius: '4px', border: statusColor === '#4ade80' ? '1px solid rgba(74,222,128,0.25)' : '1px solid rgba(255,152,0,0.25)' }}>
                                            <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: statusColor }}>{statusColor === '#4ade80' ? '已揭' : '待揭'}</Typography>
                                            <Typography sx={{ fontSize: { xs: '0.7rem', sm: '0.72rem' }, color: 'rgba(255,255,255,0.8)' }}>{f.revealChapter ? `第${f.revealChapter}章` : ''}</Typography>
                                          </Box>
                                          {f.importance && f.importance !== 'medium' && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', px: 0.75, py: 0.4, borderRadius: '4px', bgcolor: `${importanceColor}18`, border: `1px solid ${importanceColor}35` }}>
                                              <Typography sx={{ fontSize: '0.6rem', color: importanceColor, fontWeight: 700 }}>
                                                {f.importance === 'high' ? '重要' : '次要'}
                                              </Typography>
                                            </Box>
                                          )}
                                        </Box>
                                        
                                        {/* 可展开内容 */}
                                        {hints.length > 0 && (
                                          <Box 
                                            onClick={() => setExpandedForeshadows(prev => ({ ...prev, [idx]: !prev[idx] }))}
                                            sx={{ 
                                              display: 'flex', 
                                              alignItems: 'center', 
                                              justifyContent: 'center',
                                              gap: 0.5, 
                                              py: 0.5,
                                              cursor: 'pointer',
                                              borderRadius: '4px',
                                              bgcolor: 'rgba(167,139,250,0.05)',
                                              border: '1px solid rgba(167,139,250,0.1)',
                                              transition: 'all 0.2s',
                                              '&:hover': { bgcolor: 'rgba(167,139,250,0.1)' }
                                            }}
                                          >
                                            <Typography sx={{ fontSize: '0.68rem', color: 'rgba(167,139,250,0.7)' }}>
                                              {expanded ? '收起线索' : `查看${hints.length}条线索`}
                                            </Typography>
                                            <ExpandMoreIcon sx={{ fontSize: '0.75rem !important', color: 'rgba(167,139,250,0.5)', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                                          </Box>
                                        )}
                                        
                                        {/* 展开的线索 */}
                                        {expanded && hints.length > 0 && (
                                          <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(167,139,250,0.1)' }}>
                                            {hints.map((h, hi) => {
                                              const hType = h.type || h.action;
                                              const hColor = hType === 'bury' ? '#a78bfa' : hType === 'reveal_full' || hType === 'reveal' ? '#4ade80' : '#60a5fa';
                                              const hLabel = hType === 'bury' ? '埋设' : hType === 'reveal_full' || hType === 'reveal' ? '揭晓' : hType === 'reveal_partial' ? '部分' : hType === 'hint' || hType === 'build_up' ? '铺垫' : '其他';
                                              return (
                                                <Box key={hi} sx={{ display: 'flex', gap: 0.75, mb: 0.75, pl: 1, borderLeft: `2px solid ${hColor}` }}>
                                                  <Typography sx={{ fontSize: '0.65rem', color: hColor, fontWeight: 600, flexShrink: 0 }}>
                                                    {h.chapter || h.chapterNumber || '?'}章
                                                  </Typography>
                                                  <Box sx={{ flex: 1 }}>
                                                    <Typography sx={{ fontSize: '0.65rem', color: hColor, fontWeight: 600, mb: 0.25 }}>{hLabel}</Typography>
                                                    <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.5 }}>
                                                      {h.desc || h.description || h.content || '(无描述)'}
                                                    </Typography>
                                                  </Box>
                                                </Box>
                                              );
                                            })}
                                          </Box>
                                        )}
                                      </Box>
                                    );
                                  })}
                                </Box>
                              </AccordionDetails>
                            </Accordion>
                          </Box>
                        )}

                        {/* 无动态数据时 */}
                        {(!storyStructure.chapterStateMap || Object.keys(storyStructure.chapterStateMap).length === 0) &&
                         (!storyStructure.relationshipTimeline || storyStructure.relationshipTimeline.length === 0) &&
                         (!storyStructure.foreshadowsTracking || storyStructure.foreshadowsTracking.length === 0) && (
                          <Box className="structure-section-card" sx={{ textAlign: 'center', py: 2 }}>
                            <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)' }}>生成章节后自动记录</Typography>
                          </Box>
                        )}
                      </Box>

                      {/* ====== 右栏：章节细纲（全部章节，可折叠）====== */}
                      <Box className="structure-outline-col" sx={{ order: { xs: 1, md: 2 } }}>
                        {(storyStructure.outlineEnforcement?.chapterOutlineMap?.length > 0) && (
                          <Box className="structure-section-card" data-type="outline">
                            <Accordion 
                              expanded={expandedSections['chapterOutline'] !== false}
                              onChange={() => handleSectionToggle('chapterOutline')}
                              disableGutters sx={{ '&:before': { display: 'none' }, bgcolor: 'transparent', boxShadow: 'none', borderRadius: '0 !important' }}>
                              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '1rem !important', color: '#ff6b35 !important' }} />} sx={{ minHeight: '36px !important', '& .MuiAccordionSummary-content': { my: '8px !important' } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ff6b35', flexShrink: 0 }} />
                                  <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.88rem' }, fontWeight: 700, color: '#ff6b35', letterSpacing: '0.02em' }}>章节细纲</Typography>
                                  <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255,107,53,0.45)', fontWeight: 500 }}>{storyStructure.outlineEnforcement?.chapterOutlineMap?.length || 0}章</Typography>
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails sx={{ p: '0 0 4px !important' }}>
                                {/* 直达章节 */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                  <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,107,53,0.5)', flexShrink: 0 }}>直达:</Typography>
                                  <TextField
                                    size="small"
                                    placeholder="章节号"
                                    type="number"
                                    inputProps={{ min: 1, style: { fontSize: '0.7rem', padding: '2px 6px' } }}
                                    sx={{ '& .MuiOutlinedInput-root': { height: '24px' }, '& .MuiInputLabel-root': { fontSize: '0.65rem' } }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        const num = parseInt(e.target.value);
                                        if (num > 0) {
                                          const GROUP_SIZE = 50;
                                          const groupIndex = Math.floor((num - 1) / GROUP_SIZE);
                                          setExpandedOutlineGroup(groupIndex);
                                          setExpandedOutlineChapter(num);
                                          setTimeout(() => {
                                            const el = document.querySelector(`[data-outline-chapter="${num}"]`);
                                            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                          }, 100);
                                        }
                                        e.target.value = '';
                                      }
                                    }}
                                  />
                                </Box>

                                {/* 章节列表 - 50章一组，每组可折叠，内层每次只展开一章 */}
                                <Box className="structure-outline-list" sx={{ mt: 0 }}>
                                  {(() => {
                                    const allChapters = storyStructure.outlineEnforcement?.chapterOutlineMap || [];
                                    const GROUP_SIZE = 50;
                                    const groups = [];
                                    for (let i = 0; i < allChapters.length; i += GROUP_SIZE) {
                                      groups.push(allChapters.slice(i, i + GROUP_SIZE));
                                    }
                                    return groups.map((group, gi) => {
                                      const groupStart = group[0].chapterNumber;
                                      const groupEnd = group[group.length - 1].chapterNumber;
                                      const generatedCount = group.filter(c => (c.outlineContent || c.plot || '').trim().length > 0).length;
                                      return (
                                        <Accordion
                                          key={gi}
                                          disableGutters
                                          expanded={expandedOutlineGroup === gi}
                                          TransitionProps={{ unmountOnExit: true }}
                                          onChange={() => {
                                            if (expandedOutlineGroup === gi) {
                                              setExpandedOutlineGroup(null);
                                            } else {
                                              setExpandedOutlineGroup(gi);
                                              setExpandedOutlineChapter(null);
                                            }
                                          }}
                                          sx={{
                                            '&:before': { display: 'none' },
                                            bgcolor: 'rgba(255,107,53,0.02)',
                                            boxShadow: 'none',
                                            borderRadius: '8px !important',
                                            mx: 0,
                                            my: 0,
                                            mb: 1,
                                            border: `1px solid ${expandedOutlineGroup === gi ? 'rgba(255,107,53,0.5)' : 'rgba(255,107,53,0.2)'}`,
                                            overflow: 'hidden',
                                            transition: 'all 0.25s',
                                            '&:hover': { borderColor: 'rgba(255,107,53,0.4)' }
                                          }}
                                        >
                                          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '0.85rem !important', color: '#ff6b35 !important', transition: 'transform 0.25s', transform: expandedOutlineGroup === gi ? 'rotate(180deg)' : 'rotate(0deg)' }} />}                                           sx={{ 
                                            minHeight: '38px !important', 
                                            '& .MuiAccordionSummary-content': { my: '8px !important' },
                                            bgcolor: 'transparent',
                                            transition: 'background 0.25s',
                                            px: 1.5,
                                            borderBottom: expandedOutlineGroup === gi ? '1px solid rgba(255,107,53,0.2)' : 'none'
                                          }}>
                                            <Box sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: '#ff6b35', flexShrink: 0, mr: 1, boxShadow: '0 0 8px rgba(255,107,53,0.5)' }} />
                                            <Typography sx={{ fontSize: { xs: '0.82rem', sm: '0.88rem' }, fontWeight: 700, color: '#ff8c5a', whiteSpace: 'nowrap', flexShrink: 0 }}>第{groupStart}-{groupEnd}章</Typography>
                                            <Box sx={{ px: 1, py: 0.25, bgcolor: '#ff6b35', borderRadius: '6px', ml: 1, boxShadow: '0 2px 4px rgba(255,107,53,0.3)' }}>
                                              <Typography sx={{ fontSize: '0.68rem', color: 'white', fontWeight: 700 }}>{generatedCount}/{group.length}</Typography>
                                            </Box>
                                          </AccordionSummary>
                                          <AccordionDetails sx={{ p: '0 !important' }}>
                                            {group.map((chapter, idx) => {
                                              let line_content = chapter.outlineContent || chapter.plot || '';
                                              // 如果第一行是"第X章"格式（如 #第1章 标题 或 第1章 标题），去掉第一行
                                              const firstLine = line_content.split('\n')[0] || '';
                                              const isChapterTitleLine = /^#?\s*第[一二三四五六七八九十百千万零\d]+章[：:\s]/.test(firstLine);
                                              if (isChapterTitleLine) {
                                                line_content = line_content.split('\n').slice(1).join('\n');
                                              }
                                              const hasContent = chapter.hasOutline || (line_content && line_content.trim().length > 0);
                                              const isEditing = chapterOutlineEdit.chapterNumber === chapter.chapterNumber;
                                              const isExpanded = expandedOutlineChapter === chapter.chapterNumber;
                                              // 根据章节号分配不同颜色
                                              const chapterColors = [
                                                { main: '#4ade80', light: '#86efac' },  // 绿色
                                                { main: '#60a5fa', light: '#93c5fd' },  // 蓝色
                                                { main: '#f472b6', light: '#f9a8d4' },  // 粉色
                                                { main: '#a78bfa', light: '#c4b5fd' },  // 紫色
                                                { main: '#fbbf24', light: '#fcd34d' },  // 黄色
                                                { main: '#f87171', light: '#fca5a5' },  // 红色
                                              ];
                                              const colorIdx = (chapter.chapterNumber - 1) % chapterColors.length;
                                              const colors = chapterColors[colorIdx];
                                              return (
                                                <Accordion key={chapter.chapterNumber} disableGutters expanded={isExpanded} TransitionProps={{ unmountOnExit: true }} onChange={() => { if (isExpanded) { setExpandedOutlineChapter(null); } else { setExpandedOutlineGroup(gi); setExpandedOutlineChapter(chapter.chapterNumber); if (chapter.hasOutline && !chapter.outlineContent) { loadChapterOutline(chapter.chapterNumber); } } }} data-outline-chapter={chapter.chapterNumber} sx={{

                                                  '&:before': { display: 'none' }, 
                                                  bgcolor: 'transparent', 
                                                  boxShadow: 'none', 
                                                  borderRadius: '0 !important', 
                                                  mx: 0, 
                                                  my: 0, 
                                                  borderTop: idx > 0 ? `1px solid ${colors.main}25` : `1px solid ${colors.main}25`,
                                                  borderLeft: `3px solid ${isExpanded ? colors.main : 'transparent'}`,
                                                  transition: 'all 0.2s',
                                                  '&:hover': { borderLeft: `3px solid ${colors.main}80` }
                                                }}>
                                                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '0.7rem !important', color: isExpanded ? colors.main : 'rgba(255,255,255,0.25) !important', transition: 'all 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }} />} sx={{ 
                                                    minHeight: '32px !important', 
                                                    '& .MuiAccordionSummary-content': { my: '5px !important', alignItems: 'center', gap: 0.5 },
                                                    bgcolor: 'transparent',
                                                    transition: 'background 0.2s',
                                                    px: 1.5
                                                  }}>
                                                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: isExpanded ? colors.main : 'rgba(255,255,255,0.2)', flexShrink: 0, transition: 'all 0.2s' }} />
                                                    <Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.78rem' }, fontWeight: 600, color: isExpanded ? colors.main : 'rgba(255,255,255,0.5)', whiteSpace: 'nowrap', flexShrink: 0, textAlign: 'left' }}>
                                                      第{chapter.chapterNumber}章
                                                    </Typography>
                                                    {chapter.title && (
                                                      <Typography sx={{ fontSize: { xs: '0.72rem', sm: '0.75rem' }, color: isExpanded ? colors.light : 'rgba(255,255,255,0.35)', textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, ml: 0.5 }}>
                                                        ：{chapter.title}
                                                      </Typography>
                                                    )}
                                                    {!hasContent && (
                                                      <Box sx={{ px: 0.75, py: 0.15, borderRadius: '4px', ml: 0.5, border: `1px solid ${colors.main}40` }}>
                                                        <Typography sx={{ fontSize: '0.6rem', color: colors.main, fontWeight: 600 }}>暂无</Typography>
                                                      </Box>
                                                    )}
                                                  </AccordionSummary>
                                                  <AccordionDetails sx={{ p: '0 !important' }}>
                                                    {isEditing ? (
                                                      <Box sx={{ p: 1.5, pl: 2.5, borderTop: `1px solid ${colors.main}25` }}>
                                                        <TextField
                                                          fullWidth
                                                          multiline
                                                          minRows={2}
                                                          value={chapterOutlineEdit.editedContent}
                                                          onChange={(e) => { if (e.target.value.length <= 1200) { setChapterOutlineEdit({ ...chapterOutlineEdit, editedContent: e.target.value }); } }}
                                                          helperText={<Box component="span" sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}><span>{chapterOutlineEdit.editedContent.length}/1200字</span>{chapterOutlineEdit.editedContent.length >= 1200 && <span style={{ color: '#ff6b6b' }}>已达上限</span>}</Box>}
                                                          error={chapterOutlineEdit.editedContent.length >= 1200}
                                                          disabled={chapterOutlineEdit.saving}
                                                          sx={{ '& .MuiInputBase-root': { fontSize: '0.75rem', bgcolor: 'rgba(5,8,20,0.2)', borderRadius: '6px' }, '& .MuiFormHelperText-root': { fontSize: '0.65rem', color: 'rgba(255,255,255,0.4)' } }}
                                                        />
                                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                                                          <Button size="small" variant="outlined" onClick={() => { setChapterOutlineEdit({ open: false, chapterNumber: null, originalContent: '', editedContent: '', saving: false }); }} disabled={chapterOutlineEdit.saving} sx={{ fontSize: '0.7rem', py: 0.25, px: 1.5, color: 'rgba(255,255,255,0.5)', borderColor: 'rgba(255,255,255,0.2)', '&:hover': { borderColor: 'rgba(255,255,255,0.4)' } }}>取消</Button>
                                                          <Button size="small" variant="contained" onClick={async () => { setChapterOutlineEdit({ ...chapterOutlineEdit, saving: true }); const result = await saveChapterOutline(chapterOutlineEdit.chapterNumber, chapterOutlineEdit.editedContent); if (result.success) { setChapterOutlineEdit({ open: false, chapterNumber: null, originalContent: '', editedContent: '', saving: false }); setExpandedOutlineChapter(null); } else { setChapterOutlineEdit({ ...chapterOutlineEdit, saving: false }); } }} disabled={chapterOutlineEdit.saving || chapterOutlineEdit.editedContent.trim().length === 0} startIcon={chapterOutlineEdit.saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon sx={{ fontSize: '0.85rem !important' }} />} sx={{ fontSize: '0.7rem', py: 0.25, px: 1.5, minWidth: 64, bgcolor: colors.main, '&:hover': { bgcolor: colors.light } }}>
                                                            {chapterOutlineEdit.saving ? '保存中' : '保存'}
                                                          </Button>
                                                        </Box>
                                                      </Box>
                                                    ) : hasContent ? (
                                                      <Box sx={{ p: 1.25, pl: 2.5, borderTop: `1px solid ${colors.main}25` }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1, pb: 0.75, borderBottom: `1px solid ${colors.main}20` }}>
                                                          <Box sx={{ px: 1, py: 0.25, bgcolor: colors.main, borderRadius: '5px' }}>
                                                            <Typography sx={{ fontSize: '0.62rem', color: 'white', fontWeight: 700 }}>大纲</Typography>
                                                          </Box>
                                                        </Box>
                                                        {loadingOutlineChapter === chapter.chapterNumber ? (
                                                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.25, py: 3 }}>
                                                            <CircularProgress size={20} thickness={4} sx={{ color: colors.main, animationDuration: '0.9s' }} />
                                                            <Typography sx={{ fontSize: '0.68rem', color: `${colors.main}cc`, fontWeight: 600 }}>正在加载章纲...</Typography>
                                                          </Box>
                                                        ) : (
                                                          <>
                                                        {line_content.split('\n').filter(l => l.trim()).map((line, li) => (
                                                          <Box key={li} sx={{ display: 'flex', mb: 0.75, pl: 1.5, borderLeft: `2px solid ${colors.main}50` }}>
                                                            <Typography sx={{ fontSize: { xs: '0.7rem', sm: '0.72rem' }, color: 'rgba(255,255,255,0.7)', lineHeight: 1.7, textAlign: 'left', flex: 1 }}>
                                                              {line}
                                                            </Typography>
                                                          </Box>
                                                        ))}
                                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, pt: 0.75, borderTop: `1px solid ${colors.main}20` }}>
                                                          <Button size="small" variant="outlined" startIcon={<EditIcon sx={{ fontSize: '0.85rem !important' }} />} onClick={() => { setChapterOutlineEdit({ open: false, chapterNumber: chapter.chapterNumber, originalContent: line_content, editedContent: line_content, saving: false }); }} sx={{ fontSize: '0.72rem', py: 0.25, px: 1.5, color: colors.main, borderColor: `${colors.main}60`, '&:hover': { borderColor: colors.main, bgcolor: `${colors.main}10` } }}>
                                                            编辑
                                                          </Button>
                                                        </Box>
                                                          </>
                                                        )}
                                                      </Box>
                                                    ) : (
                                                      <Box sx={{ p: 1.25, pl: 2.5, borderTop: `1px solid ${colors.main}20` }}>
                                                        <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)', fontStyle: 'italic', textAlign: 'left' }}>暂无大纲内容</Typography>
                                                      </Box>
                                                    )}
                                                  </AccordionDetails>
                                                </Accordion>
                                              );
                                            })}
                                          </AccordionDetails>
                                        </Accordion>
                                      );
                                    });
                                  })()}
                                </Box>
                              </AccordionDetails>
                            </Accordion>
                          </Box>
                        )}
                      </Box>
                    </Box>

                    {/* 保存提示 */}
                    <Alert severity="success" sx={{ mt: 2, background: 'rgba(0,176,155,0.12)', color: 'white', border: '1px solid rgba(0,176,155,0.18)', borderRadius: 2, '& .MuiAlert-icon': { color: 'white' } }}>
                      <AlertTitle sx={{ fontWeight: 700, fontSize: '0.82rem', color: 'white' }}>故事结构已保存</AlertTitle>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.75rem' }}>
                        批量生成章节时，将自动使用以上结构来指导AI创作。
                        </Typography>
                    </Alert>
                  </>
                        )}
                      </Box>
                  </Box>
          </TabPanel>

          {/* 章节内容 - PC左右分栏 / 移动端保留原有Accordion折叠布局 */}
          <TabPanel value={activeTab} index={3}>
            {/* PC 端：左侧章节列表 + 右侧编辑器 */}
            {!isMobile ? (
              <Box className="novel-chapter-split">

                {/* ── 左侧：章节列表面板 ── */}
              <Box className="novel-chapter-list-panel">
                {/* 列表头部 */}
                <Box className="novel-chapter-list-header" sx={{
                  display: 'flex', alignItems: 'center', gap: 1,
                  px: 2, py: 1.5,
                  borderBottom: '1px solid rgba(6, 182, 212, 0.12)',
                  background: 'rgba(6, 182, 212, 0.04)',
                  position: 'sticky', top: 0, zIndex: 3, backdropFilter: 'blur(8px)',
                  flexWrap: 'wrap',
                }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#06b6d4', flexShrink: 0, boxShadow: '0 0 8px rgba(6, 182, 212, 0.6)', animation: 'novelGreenPulse 2s ease-in-out infinite' }} />
                  <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#06b6d4', flexShrink: 0 }}>章节列表</Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.35)', fontWeight: 600, flexShrink: 0 }}>{chapters.length}章</Typography>
                  <Box
                    component="input"
                    type="number"
                    placeholder="直达"
                    value={jumpChapterInput}
                    onChange={(e) => setJumpChapterInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const num = parseInt(jumpChapterInput, 10);
                        if (!isNaN(num) && num > 0 && num <= chapters.length) {
                          handleChapterSelect(num);
                          setCurrentChapterContent(chapters.find(ch => ch.index === num)?.content || '');
                          // 展开目标章节所在的分组，折叠其他分组
                          const groupStart = Math.floor((num - 1) / 50) * 50 + 1;
                          const groupEnd = Math.min(groupStart + 49, chapters.length);
                          const targetGroupKey = `${groupStart}-${groupEnd}`;
                          const newCollapsed = {};
                          for (let i = 0; i < chapters.length; i += 50) {
                            const gs = i + 1;
                            const ge = Math.min(i + 50, chapters.length);
                            const gk = `${gs}-${ge}`;
                            newCollapsed[gk] = gk !== targetGroupKey;
                          }
                          setCollapsedGroups(newCollapsed);
                        }
                        setJumpChapterInput('');
                        setTimeout(() => {
                          const rowEl = chapterRowRefs.current[num];
                          if (rowEl) rowEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }, 30);
                      }
                    }}
                    className="chapter-jump-input"
                    sx={{
                      width: 56,
                      ml: 'auto',
                      px: 0.75,
                      py: 0.25,
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      color: '#06b6d4',
                      background: 'rgba(6, 182, 212, 0.08)',
                      border: '1px solid rgba(6, 182, 212, 0.25)',
                      borderRadius: '6px',
                      outline: 'none',
                      textAlign: 'center',
                      '&::placeholder': { color: 'rgba(6, 182, 212, 0.35)', fontWeight: 400 },
                      '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': { '-webkit-appearance': 'none' },
                      '&:focus': { borderColor: '#06b6d4', boxShadow: '0 0 0 2px rgba(6, 182, 212, 0.15)' },
                    }}
                  />
                </Box>

                {/* 批量生成+导出入口 */}
                <Box className="novel-chapter-btn-row" sx={{ p: '8px 14px', borderBottom: '1px solid rgba(6, 182, 212, 0.08)', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {/* 按钮行：批量生成 + 导出，始终单行 */}
                  <Box className="batch-export-btn-row" sx={{ display: 'flex', gap: '6px' }}>
                    <GeneratingButton
                      className="assist-chapter-ai-glow-btn"
                      variant="contained"
                      onClick={handleOpenBatchDialog}
                      isGenerating={batchGenerating}
                      text="批量生成"
                      generatingText="生成中..."
                      sx={{ borderRadius: '8px', background: 'transparent !important', fontSize: '0.72rem', fontWeight: 700, minHeight: 30, flex: 1, '&:hover': { background: 'transparent !important', transform: 'translateY(-1px)' }, transition: 'all 0.2s ease', animation: 'assist-chapter-ai-breath 2.75s ease-in-out infinite' }}
                    />
                    <Button className="assist-chapter-ai-glow-btn" startIcon={<FileDownloadIcon />} onClick={handleOpenExportDialog} sx={{ minHeight: 30, fontSize: '0.72rem', flex: 1, animation: 'assist-chapter-ai-breath 2.75s ease-in-out infinite' }}>导出</Button>
                  </Box>
                  {/* 🔥【体检留痕】全书体检报告入口（持久可查：历史体检记录+逐条改前/改后明细） */}
                  <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <Button
                      startIcon={<FactCheckOutlinedIcon sx={{ fontSize: '0.9rem !important' }} />}
                      onClick={openAuditPanel}
                      sx={{
                        minHeight: 30, fontSize: '0.72rem', fontWeight: 700, flex: 1,
                        color: '#06b6d4', borderColor: 'rgba(6, 182, 212, 0.3)',
                        background: 'rgba(6, 182, 212, 0.06)',
                        borderRadius: '8px', px: 1,
                        '&:hover': { background: 'rgba(6, 182, 212, 0.14)', borderColor: 'rgba(6, 182, 212, 0.5)' }
                      }}
                      variant="outlined"
                    >
                      体检报告
                    </Button>
                    {auditStatus && auditStatus.exists !== false && (
                      <Tooltip title={auditStatus.stageText || ''} arrow>
                        <Box sx={{
                          display: 'flex', alignItems: 'center', gap: 0.5,
                          px: 1, py: 0.5, borderRadius: '6px', flexShrink: 0,
                          background: 'rgba(255,255,255,0.03)',
                          border: '1px solid rgba(255,255,255,0.06)'
                        }}>
                          {auditActive && (
                            <Box sx={{
                              width: 6, height: 6, borderRadius: '50%',
                              bgcolor: auditStatusColor(auditStatus.status),
                              animation: 'novelGreenPulse 2s ease-in-out infinite'
                            }} />
                          )}
                          <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: auditStatusColor(auditStatus.status) }}>
                            {auditStatusLabel(auditStatus.status)}
                          </Typography>
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                  {alertMessage.open && (
                    <Box>
                      <Alert severity={alertMessage.type === 'info' ? 'info' : alertMessage.type === 'success' ? 'success' : alertMessage.type === 'error' ? 'error' : 'warning'} sx={{ borderRadius: '8px', py: 0.5 }} onClose={() => setAlertMessage(prev => ({...prev, open: false}))}>{alertMessage.message}</Alert>
                    </Box>
                  )}
                </Box>

                {/* 章节列表 */}
                {chapters.length === 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 4, px: 2 }}>
                    <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.8rem', textAlign: 'center' }}>暂无章节</Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.72rem', textAlign: 'center' }}>点击上方按钮生成章节</Typography>
                  </Box>
                ) : (
                  <Box className="novel-chapter-list-body" ref={el => { chapterRowRefs.current._listEl = el; }}>
                    {(() => {
                      const totalChapters = chapters.length;
                      const groups = [];
                      for (let i = 0; i < totalChapters; i += 50) {
                        const groupStart = i + 1;
                        const groupEnd = Math.min(i + 50, totalChapters);
                        const groupKey = `${groupStart}-${groupEnd}`;
                        const groupChapters = chapters.slice(i, i + 50);
                        const isCollapsed = collapsedGroups === null ? true : !!collapsedGroups[groupKey];
                        // 🔥【瘦身兼容】瘦身后 content=null，必须用 hasContent 标记判断
                        const hasContentInGroup = groupChapters.filter(ch => ch.hasContent || (ch.content && ch.content.trim().length > 0)).length;
                        groups.push(
                          <Box key={groupKey} className="novel-chapter-group">
                            <Box className={`novel-chapter-group-header ${isCollapsed ? 'collapsed' : ''}`}
                              onClick={() => {
                                if (collapsedGroups === null) {
                                  // 首次点击：初始化状态，其余全折叠，当前点击的展开
                                  const initCollapsed = {};
                                  for (let i = 0; i < chapters.length; i += 50) {
                                    const gs = i + 1;
                                    const ge = Math.min(i + 50, chapters.length);
                                    const gk = `${gs}-${ge}`;
                                    initCollapsed[gk] = gk !== groupKey;
                                  }
                                  setCollapsedGroups(initCollapsed);
                                } else if (isCollapsed) {
                                  // 已初始化 + 当前折叠 → 展开当前组，折叠其他组
                                  const newCollapsed = {};
                                  for (let i = 0; i < chapters.length; i += 50) {
                                    const gs = i + 1;
                                    const ge = Math.min(i + 50, chapters.length);
                                    const gk = `${gs}-${ge}`;
                                    newCollapsed[gk] = gk !== groupKey;
                                  }
                                  setCollapsedGroups(newCollapsed);
                                } else {
                                  // 已展开 → 折叠当前组
                                  setCollapsedGroups(prev => ({ ...prev, [groupKey]: true }));
                                }
                              }}>
                              <Typography className="novel-chapter-group-title">
                                {isCollapsed ? '▶' : '▼'} 第{groupStart}-{groupEnd}章
                              </Typography>
                              <Typography className="novel-chapter-group-count">{hasContentInGroup}/{groupChapters.length}章</Typography>
                            </Box>
                            {!isCollapsed && groupChapters.map((chapter, localIdx) => {
                              const isSelected = selectedChapter === chapter.index;
                              // 🔥【瘦身兼容】瘦身后 content=null，优先用 hasContent 标记判断
                              const hasContent = !!(chapter.hasContent || (chapter.content && chapter.content.trim().length > 0));
                              const isCurrentlyGenerating = generatingChapterId === chapter.index;
                              const chapterId = (chapter._id || chapter.id || '').toString();
                              const isOptimizing = chapter.isOptimizing || (chapterId ? optimizingChapters.has(chapterId) : false);
                              const isRewriting = chapter.isRewriting || (chapterId ? rewritingChapters.has(chapterId) : false);
                              const isOptimized = chapter.isOptimized;
                              const isRewritten = chapter.isRewritten;
                              const isGenerating = chapter.generating || isCurrentlyGenerating;
                              const isInBatchQueue = ((chapter.generationStatus && ['等待生成', '等待中', '生成中'].includes(chapter.generationStatus)) && (!chapter.content || chapter.content.trim().length === 0));
                              const formattedTitle = (() => {
                                if (chapter.title && chapter.title.trim()) {
                                  const cleanTitle = chapter.title.replace(/^第\d+章[：:]\s*/, '');
                                  if (cleanTitle) {
                                    return `第${chapter.index}章：${cleanTitle}`;
                                  }
                                }
                                return `第${chapter.index}章`;
                              })();
                              const wordCount = chapter.contentLength || (chapter.content ? chapter.content.length : 0);
                              const globalIdx = i + localIdx;

                              return (
                                <Box key={chapter.index} ref={el => { chapterRowRefs.current[chapter.index] = el; }} className={`novel-chapter-row ${isSelected ? 'selected' : ''} ${hasContent ? 'has-content' : ''}`} sx={{ animationDelay: `${Math.min(globalIdx * 0.03, 0.4)}s` }}
                                  onClick={() => {
                                    // 🔥 已选中且已有内容则跳过；已选中但内容为空（懒加载未完成/失败）则重新触发加载
                                    if (isSelected && chapter.content) return;
                                    handleChapterSelect(chapter.index);
                                    setTimeout(() => {
                                      const rowEl = chapterRowRefs.current[chapter.index];
                                      if (rowEl) rowEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                    }, 30);
                                  }}>
                                  <Box className="novel-chapter-row-indicator" />
                                  <Typography className="novel-chapter-row-title">
                                    {isCurrentlyGenerating && <CircularProgress size={10} sx={{ color: '#06b6d4', mr: 0.5, verticalAlign: 'middle' }} />}
                                    {formattedTitle}
                                  </Typography>
                                  {isOptimizing && <Box className="novel-chapter-row-badge generating">优化中</Box>}
                                  {isRewriting && <Box className="novel-chapter-row-badge generating">重写中</Box>}
                                  {!isOptimizing && !isRewriting && (isCurrentlyGenerating || isInBatchQueue) && <Box className="novel-chapter-row-badge generating">{isCurrentlyGenerating ? '生成中' : '等待'}</Box>}
                                  {isOptimized && !isOptimizing && <Box className="novel-chapter-row-badge optimized">优</Box>}
                                  {isRewritten && !isRewriting && <Box className="novel-chapter-row-badge rewritten">重</Box>}
                                  {!isOptimizing && !isRewriting && !isGenerating && wordCount > 0 && <Box className="novel-chapter-row-badge">{wordCount}</Box>}
                                </Box>
                              );
                            })}
                          </Box>
                        );
                      }
                      return groups;
                    })()}
                  </Box>
                )}
              </Box>

              {/* ── 右侧：章节编辑器面板 ── */}
              <Box className="novel-chapter-editor-panel">
                {selectedChapter === null || !chapters.find(ch => ch.index === selectedChapter) ? (
                  <Box className="novel-editor-empty-state">
                    <Box className="novel-editor-empty-icon"><MenuBookIcon sx={{ fontSize: '1.8rem', color: 'rgba(6, 182, 212, 0.3)' }} /></Box>
                    <Typography className="novel-editor-empty-text">从左侧列表选择章节<br />开始编辑内容</Typography>
                  </Box>
                ) : (() => {
                  const chapter = chapters.find(ch => ch.index === selectedChapter);
                  const chapterId = (chapter._id || chapter.id || '').toString();
                  const isCurrentlyGenerating = generatingChapterId === chapter.index;
                  const isOptimizing = chapter?.isOptimizing || (chapterId ? optimizingChapters.has(chapterId) : false);
                  const isRewriting = chapter?.isRewriting || (chapterId ? rewritingChapters.has(chapterId) : false);
                  const isGenerating = chapter?.generating || isCurrentlyGenerating;
                  // 🔥【瘦身兼容】瘦身后 content=null，优先用 hasContent 标记判断
                  const hasContent = !!(chapter?.hasContent || (chapter?.content && chapter.content.trim().length > 0));
                  const formattedTitle = (() => {
                    if (chapter?.title && chapter.title.trim()) {
                      const cleanTitle = chapter.title.replace(/^第\d+章[：:]\s*/, '');
                      if (cleanTitle) {
                        return `第${chapter.index}章：${cleanTitle}`;
                      }
                    }
                    return `第${chapter.index}章`;
                  })();

                  const safeCopyToClipboard = (text) => {
                    try {
                      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
                        navigator.clipboard.writeText(text).catch(() => {
                          const ta = document.createElement('textarea');
                          ta.value = text; ta.style.cssText = 'position:fixed;opacity:0;top:0;left:0';
                          document.body.appendChild(ta); ta.focus(); ta.select();
                          document.execCommand('copy'); document.body.removeChild(ta);
                        });
                      } else {
                        const ta = document.createElement('textarea');
                        ta.value = text; ta.style.cssText = 'position:fixed;opacity:0;top:0;left:0';
                        document.body.appendChild(ta); ta.focus(); ta.select();
                        document.execCommand('copy'); document.body.removeChild(ta);
                      }
                    } catch (err) { /* silent */ }
                  };

                  return (
                    <>
                      <Box className="novel-editor-chapter-header">
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#06b6d4', flexShrink: 0, boxShadow: '0 0 8px rgba(6, 182, 212, 0.6)', animation: 'novelGreenPulse 2s ease-in-out infinite' }} />
                        <Typography className="novel-editor-chapter-title">{formattedTitle}</Typography>
                        {isCurrentlyGenerating && <CircularProgress size={14} sx={{ color: '#06b6d4', flexShrink: 0 }} />}
                      </Box>

                      <Box className="novel-editor-actions">
                        <Button className="assist-chapter-ai-glow-btn" startIcon={<AutoFixHighIcon />} onClick={() => { if (chapter?.content && chapter?.content?.trim()?.length >= 100) { handleOptimizeChapter(chapter.index); } else { showMessage('章节内容需要至少100字才能优化', 'warning'); } }} disabled={!chapter?.content || chapter?.content?.trim()?.length < 100 || isOptimizing || isRewriting || isGenerating}>{isOptimizing ? '优化中' : '优化'}</Button>
                        <Button className="assist-chapter-ai-glow-btn" startIcon={<AutoAwesomeIcon />} onClick={() => handleRewriteChapter(chapter.index)} disabled={!chapter?.content || chapter?.content?.trim()?.length < 100 || isRewriting || isOptimizing || isGenerating}>{isRewriting ? '重写中' : '重写'}</Button>
                        <Button className="assist-chapter-ai-glow-btn" startIcon={<ContentCopyIcon />} onClick={() => { safeCopyToClipboard(formattedTitle.replace(/[《》]/g, '').trim()); showMessage('章节标题已复制', 'success'); }}>复制标题</Button>
                        <Button className="assist-chapter-ai-glow-btn" startIcon={<ContentCopyIcon />} onClick={() => { if (chapter?.content) { safeCopyToClipboard(chapter.content); showMessage('章节内容已复制', 'success'); } else { showMessage('没有可复制的内容', 'warning'); } }} disabled={!hasContent}>复制内容</Button>
                        <Button className="assist-chapter-ai-glow-btn" startIcon={<EditIcon />} onClick={() => handleTitleEditStart(chapter)} disabled={!chapter || isGenerating || isOptimizing || isRewriting}>改标题</Button>
                        <Button className="assist-chapter-ai-glow-btn" startIcon={<EditIcon />} onClick={() => handleContentEditStart(chapter)} disabled={!hasContent || isGenerating || isOptimizing || isRewriting}>改内容</Button>
                      </Box>

                      {chapter?.outline && chapter.outline.trim().length > 0 && (
                        <Box sx={{ mx: 2, mt: 2, p: '10px 14px', background: 'rgba(139, 92, 246, 0.06)', border: '1px solid rgba(139, 92, 246, 0.15)', borderRadius: '8px', animation: 'novelFadeIn 0.4s ease both' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                            <HubIcon sx={{ fontSize: '0.75rem', color: '#a78bfa' }} />
                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#a78bfa' }}>章节大纲</Typography>
                          </Box>
                          <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.7 }}>{chapter.outline}</Typography>
                        </Box>
                      )}

                      <Box className="novel-editor-content-area">
                        <Box className="novel-content-status-bar">
                          <Box className={`novel-content-status-dot ${hasContent ? 'active' : 'inactive'}`} />
                          <Typography className="novel-content-status-text">章节内容</Typography>
                          <Typography className="novel-content-count-badge">{hasContent ? `${chapter.contentLength || (chapter.content ? chapter.content.length : 0)} 字` : '暂无内容'}</Typography>
                        </Box>
                        {isLoadingChapterContent ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 5, borderRadius: '10px', background: 'rgba(5, 10, 20, 0.6)', border: '1px solid rgba(6, 182, 212, 0.15)' }}>
                            <CircularProgress size={26} thickness={4} sx={{ color: '#06b6d4', animationDuration: '0.9s' }} />
                            <Typography sx={{ fontSize: '0.78rem', color: 'rgba(6, 182, 212, 0.85)', fontWeight: 600, letterSpacing: '0.04em' }}>正在加载章节正文...</Typography>
                          </Box>
                        ) : (
                        <TextField fullWidth multiline minRows={11} variant="outlined" placeholder="在此编辑章节内容..." value={currentChapterContent || ''}
                          onChange={(e) => { setCurrentChapterContent(e.target.value); debouncedSaveChapterContent(e.target.value); }}
                          inputRef={contentTextareaRef}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', backgroundColor: 'rgba(5, 10, 20, 0.95)', border: '1px solid rgba(6, 182, 212, 0.2)', '& fieldset': { border: 'none' }, '&:hover': { borderColor: 'rgba(6, 182, 212, 0.4)' }, '&.Mui-focused': { borderColor: '#06b6d4', boxShadow: '0 0 0 3px rgba(6, 182, 212, 0.12), 0 0 20px rgba(6, 182, 212, 0.08)' } }, '& textarea': { fontSize: { xs: '0.82rem', sm: '0.92rem' }, lineHeight: 1.85, color: 'rgba(255,255,255,0.85)', p: { xs: '10px 12px', sm: '12px 14px' }, '&::placeholder': { color: 'rgba(255,255,255,0.25)', opacity: 1 } } }}
                          disabled={isGenerating || isOptimizing}
                        />
                        )}
                      </Box>
                    </>
                  );
                })()}
              </Box>
            </Box>
            ) : (
              /* 移动端：保留原有 Accordion 折叠式布局 */
            <Box className="chapter-panel">
              {/* 英雄头部 */}
              <Box className="chapter-hero-header">
                <Box className="chapter-hero-bg" />
                <Box className="hero-particles-chapter" />
                <Box className="chapter-hero-content">
                  <Box className="chapter-hero-icon">
                    <MenuBookIcon sx={{ fontSize: 22, color: '#06b6d4' }} />
                  </Box>
                  <Box className="chapter-hero-text">
                    <Typography className="chapter-hero-title">章节内容</Typography>
                    <Typography className="chapter-hero-subtitle">分章节创作正文</Typography>
                  </Box>
                  <Box className="chapter-hero-badge">
                    <Box className="chapter-hero-badge-dot" />
                    <Typography className="chapter-hero-badge-text">连载中</Typography>
                  </Box>
                </Box>
              </Box>

              {/* 章节操作区域 */}
                <Box className="novel-chapter-list-panel settings-section accent" sx={{ mx: { xs: 0, sm: 2 }, mt: { xs: 0, sm: 2 }, mb: { xs: 0, sm: 2 }, borderRadius: { xs: 0, sm: '12px' }, animation: 'sectionReveal 0.45s ease both', background: { xs: 'rgba(6,182,212,0.04)', sm: 'transparent' }, border: { xs: '1px solid rgba(6,182,212,0.15)', sm: 'none' } }}>
                  <Box className="batch-export-btn-row" sx={{ display: 'flex', flexDirection: 'column', gap: '6px', p: { xs: '8px 16px', sm: '10px 12px' } }}>
                    {/* 🔥【移动端优化】三按钮等宽单行，紧凑不换行 */}
                    <Box className="btn-triple-row" sx={{ display: 'flex', gap: '6px', alignItems: 'stretch' }}>
                      <GeneratingButton className="assist-chapter-ai-glow-btn" variant="contained" onClick={handleOpenBatchDialog} isGenerating={batchGenerating} text="批量生成" generatingText="生成中..."
                        sx={{ borderRadius: '8px', background: 'transparent !important', flex: 1, fontSize: '0.7rem', fontWeight: 700, minHeight: 30, minWidth: 0, px: 0.5, whiteSpace: 'nowrap', '&:hover': { background: 'transparent !important', transform: 'translateY(-1px)' }, transition: 'all 0.2s ease', animation: 'assist-chapter-ai-breath 2.75s ease-in-out infinite' }}
                        />
                      <Button className="assist-chapter-ai-glow-btn" startIcon={<FileDownloadIcon sx={{ fontSize: '0.8rem !important' }} />} onClick={handleOpenExportDialog} sx={{ flex: 1, fontSize: '0.7rem', minHeight: 30, minWidth: 0, px: 0.5, whiteSpace: 'nowrap' }}>导出</Button>
                      <Button
                        startIcon={<FactCheckOutlinedIcon sx={{ fontSize: '0.8rem !important' }} />}
                        onClick={openAuditPanel}
                        sx={{
                          minHeight: 30, minWidth: 0, px: 0.5, flex: 1, fontSize: '0.7rem', fontWeight: 700, whiteSpace: 'nowrap',
                          color: '#06b6d4', borderColor: 'rgba(6, 182, 212, 0.3)',
                          background: 'rgba(6, 182, 212, 0.06)',
                          borderRadius: '8px',
                          '&:hover': { background: 'rgba(6, 182, 212, 0.14)', borderColor: 'rgba(6, 182, 212, 0.5)' }
                        }}
                        variant="outlined"
                      >
                        体检
                      </Button>
                    </Box>
                    {/* 🔥【体检状态条】仅体检进行中显示（呼吸灯+阶段进度），完成后自动收起不占空间 */}
                    {auditActive && auditStatus && auditStatus.exists !== false && (
                      <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 1,
                        px: 1.25, py: 0.75, borderRadius: '8px',
                        background: 'rgba(6,182,212,0.07)', border: '1px solid rgba(6,182,212,0.22)',
                        animation: 'novelFadeIn 0.3s ease both'
                      }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: auditStatusColor(auditStatus.status), animation: 'novelGreenPulse 2s ease-in-out infinite', flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: auditStatusColor(auditStatus.status), flexShrink: 0 }}>
                          {auditStatusLabel(auditStatus.status)}
                        </Typography>
                        <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.45)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {auditStatus.stageText || ''}
                        </Typography>
                      </Box>
                    )}
                    {/* 🔥【生成进度留位】批量/单章生成状态内嵌条（移动端布局流内，非浮层，不遮挡任何按钮） */}
                    {renderGenerationStatus()}
                  </Box>
                            </Box>

                {/* 章节列表区域 */}
                <Box sx={{ mx: { xs: 0, sm: 2 }, mt: { xs: 0, sm: 3 }, mb: { xs: 0, sm: 3 }, '@media (max-width: 600px)': { mx: 0, mt: 0 }, height: { xs: 'auto', sm: 'auto' } }}>
                {chapters.length === 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, p: { xs: 3, sm: 5 }, background: 'rgba(6, 182, 212, 0.03)', border: '1px solid rgba(6, 182, 212, 0.08)', borderRadius: { xs: 0, sm: '12px' }, animation: 'sectionReveal 0.45s ease both' }}>
                      <Box sx={{ width: 64, height: 64, borderRadius: '16px', background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.12) 0%, rgba(5, 150, 105, 0.08) 100%)', border: '1px solid rgba(6, 182, 212, 0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 30px rgba(6, 182, 212, 0.15), inset 0 0 20px rgba(6, 182, 212, 0.05)', animation: 'chapterIconPulse 2.5s ease-in-out infinite' }}>
                        <AutorenewIcon sx={{ fontSize: '1.8rem', color: '#06b6d4' }} />
                      </Box>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography sx={{ fontWeight: 700, color: 'rgba(255,255,255,0.75)', fontSize: { xs: '0.95rem', sm: '1.05rem' }, mb: 0.5 }}>开始创作您的故事</Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: { xs: '0.75rem', sm: '0.85rem' }, lineHeight: 1.6, maxWidth: 280, mx: 'auto' }}>点击下方按钮，使用AI智能生成章节内容</Typography>
                      </Box>
                      <GeneratingButton variant="contained" onClick={handleOpenBatchDialog} isGenerating={batchGenerating} text="批量生成" generatingText="正在生成..."
                        sx={{ borderRadius: '10px', background: 'transparent !important', px: 4, py: 1, fontSize: '0.9rem', fontWeight: 700, minHeight: 42, '&:hover': { background: 'transparent !important', transform: 'translateY(-2px)' }, transition: 'all 0.3s ease', animation: 'assist-chapter-ai-breath 2.75s ease-in-out infinite' }}
                      />
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '2px', sm: 1.5 }, animation: 'sectionReveal 0.45s ease both' }}>
                      {alertMessage.open && (
                        <Alert severity={alertMessage.type === 'info' ? 'info' : alertMessage.type === 'success' ? 'success' : alertMessage.type === 'error' ? 'error' : 'warning'} sx={{ borderRadius: { xs: 0, sm: '10px' } }} onClose={() => setAlertMessage(prev => ({...prev, open: false}))}>{alertMessage.message}</Alert>
                      )}

                      {(() => {
                        const CHAPTERS_PER_GROUP = 25;
                        const chapterGroups = [];
                        for (let i = 0; i < chapters.length; i += CHAPTERS_PER_GROUP) {
                          chapterGroups.push(chapters.slice(i, i + CHAPTERS_PER_GROUP));
                        }
                        return chapterGroups.map((group, groupIndex) => {
                          const startChapter = group[0].index;
                          const endChapter = group[group.length - 1].index;
                          return (
                            <Accordion
                              key={groupIndex}
                              disableGutters
                              expanded={expandedChapterGroup === groupIndex}
                              onChange={handleChapterGroupChange(groupIndex)}
                              TransitionProps={{ unmountOnExit: true }}
                              sx={{ '&:before': { display: 'none' }, boxShadow: 'none', background: 'transparent', mx: 0, my: 0.5, borderRadius: { xs: 0, sm: '10px' }, border: '1px solid rgba(6, 182, 212, 0.1)', overflow: 'hidden', '&:hover': { borderColor: 'rgba(6, 182, 212, 0.25)' } }}
                            >
                              <AccordionSummary
                                expandIcon={<ExpandMoreIcon sx={{ color: '#06b6d4', fontSize: { xs: '1.1rem', sm: '1.3rem' } }} />}
                                sx={{ minHeight: { xs: 36, sm: 44 }, px: { xs: 1, sm: 2 }, py: { xs: 0.5, sm: 0.5 }, background: expandedChapterGroup === groupIndex ? 'rgba(6, 182, 212, 0.1)' : 'rgba(6, 182, 212, 0.06)', borderBottom: expandedChapterGroup === groupIndex ? '1px solid rgba(6, 182, 212, 0.2)' : '1px solid rgba(6, 182, 212, 0.12)', borderRadius: { xs: 0, sm: '10px 10px 0 0' }, '&:hover': { background: 'rgba(6, 182, 212, 0.1)' }, '& .MuiAccordionSummary-content': { my: 0 } }}
                              >
                                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#06b6d4', flexShrink: 0, boxShadow: '0 0 8px rgba(6, 182, 212, 0.6)' }} />
                                <Typography sx={{ fontWeight: 700, color: '#06b6d4', fontSize: { xs: '0.8rem', sm: '0.9rem' }, ml: 1 }}>第 {startChapter} - {endChapter} 章 ({group.length}章)</Typography>
                              </AccordionSummary>
                              <AccordionDetails sx={{ p: 0 }}>
                                {group.map((chapter) => {
                        const isSelected = selectedChapter === chapter.index;
                        const chapterStatus = chapterGenerationStatus.find(s => s.chapterIndex === chapter.index);
                        const isCurrentlyGenerating = generatingChapterId === chapter.index;
                                const isInBatchQueue = ((!!chapterStatus && ['等待生成', '等待中', '生成中'].includes(chapterStatus.status)) || (chapter.generationStatus && ['等待生成', '等待中', '生成中'].includes(chapter.generationStatus))) && (!chapter.content || chapter.content.trim().length === 0);
                        const isGenerating = chapter.generating || isCurrentlyGenerating;
                        const chapterId = (chapter._id || chapter.id || '').toString();
                        const isOptimizing = chapter.isOptimizing || (chapterId ? optimizingChapters.has(chapterId) : false);
                        const isRewriting = chapter.isRewriting || (chapterId ? rewritingChapters.has(chapterId) : false);
                        const isRewritten = chapter.isRewritten;
                        const hasContent = chapter.hasContent || (chapter.content && chapter.content.trim().length > 0);
                        const hasOutline = chapter.hasOutline || (chapter.outline && chapter.outline.trim().length > 0);
                        const isOptimized = chapter.isOptimized;
                        const generationProgress = chapterStatus?.progress || chapter.generationProgress || 0;
                        const formattedTitle = (() => {
                          if (chapter.title && chapter.title.trim()) {
                            const cleanTitle = chapter.title.replace(/^第\d+章[：:]\s*/, '');
                            if (cleanTitle) {
                              return `第${chapter.index}章：${cleanTitle}`;
                            }
                          }
                          return `第${chapter.index}章`;
                        })();
                        
                                const safeCopyMobile = (text) => {
                          try {
                            if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
                                      navigator.clipboard.writeText(text).catch(() => { const ta = document.createElement('textarea'); ta.value = text; ta.style.cssText = 'position:fixed;opacity:0;top:0;left:0'; document.body.appendChild(ta); ta.focus(); ta.select(); document.execCommand('copy'); document.body.removeChild(ta); });
                                    } else { const ta = document.createElement('textarea'); ta.value = text; ta.style.cssText = 'position:fixed;opacity:0;top:0;left:0'; document.body.appendChild(ta); ta.focus(); ta.select(); document.execCommand('copy'); document.body.removeChild(ta); }
                                  } catch (err) { /* silent */ }
                        };
                        
                        return (
                                  <Accordion key={chapter.index} expanded={isSelected}
                            onChange={(e, expanded) => {
                                      if (isSelected) { if (chapter.isOptimized) setChapters(prev => prev.map(ch => ch.index === chapter.index ? {...ch, isOptimized: true} : ch)); handleChapterSelect(null); }
                                      else { if (chapter.isOptimized) setChapters(prev => prev.map(ch => ch.index === chapter.index ? {...ch, isOptimized: true} : ch)); handleChapterSelect(chapter.index); }
                                    }}
                                    sx={{ m: 0, borderRadius: { xs: 0, sm: isSelected ? '10px 10px 0 0' : '10px' }, '&:before': { display: 'none' }, boxShadow: isSelected ? '0 0 0 1px rgba(6, 182, 212, 0.4), 0 4px 16px rgba(6, 182, 212, 0.15)' : '0 1px 4px rgba(0,0,0,0.3)', border: isSelected ? { xs: 'none', sm: '1px solid rgba(6, 182, 212, 0.4)' } : { xs: 'none', sm: '1px solid rgba(6, 182, 212, 0.1)' }, background: isSelected ? 'rgba(6, 182, 212, 0.06)' : 'rgba(6, 182, 212, 0.02)', '&:hover': { background: 'rgba(6, 182, 212, 0.05)', borderColor: 'rgba(6, 182, 212, 0.25)' }, transition: 'all 0.25s ease', '@media (max-width: 600px)': { borderRadius: 0 } }}
                                  >
                                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#06b6d4', fontSize: { xs: '1.2rem', sm: '1.5rem' }, transition: 'all 0.2s ease' }} />}
                                      sx={{ minHeight: { xs: 36, sm: 52 }, px: { xs: '8px', sm: 2 }, py: { xs: '2px', sm: 0 }, background: isSelected ? 'linear-gradient(to right, rgba(6, 182, 212, 0.12) 0%, rgba(5, 150, 105, 0.05) 100%)' : 'transparent', '&:hover': { background: 'rgba(6, 182, 212, 0.06)' }, justifyContent: 'flex-start', '& .MuiAccordionSummary-content': { m: { xs: '2px 0', sm: '10px 0' }, justifyContent: 'flex-start !important', width: '100%' }, transition: 'all 0.25s ease', '@media (max-width: 600px)': { px: '8px', py: '2px' } }}
                                    >
                                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1, flexWrap: 'wrap' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                                          {isCurrentlyGenerating && <CircularProgress size={16} thickness={5} sx={{ color: '#06b6d4', flexShrink: 0 }} />}
                                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: hasContent ? '#06b6d4' : '#6b7280', flexShrink: 0, boxShadow: hasContent ? '0 0 6px rgba(6, 182, 212, 0.6)' : 'none' }} />
                                          <Typography sx={{ fontWeight: isSelected ? 700 : 600, color: isSelected ? '#06b6d4' : 'rgba(255,255,255,0.7)', fontSize: { xs: '0.8rem', sm: '0.95rem' }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>{formattedTitle}</Typography>
                                  </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                                          {isGenerating && (
                                            <Box sx={{ width: { xs: 48, sm: 60 }, flexShrink: 0 }}>
                                              <LinearProgress variant="determinate" value={generationProgress} sx={{ height: 3, borderRadius: 2, bgcolor: 'rgba(16,185,129,0.15)', '& .MuiLinearProgress-bar': { bgcolor: '#06b6d4', borderRadius: 2 } }} />
                                    </Box>
                                  )}
                                          {(isCurrentlyGenerating || (isInBatchQueue && !chapter.content)) && (
                                            <Chip size="small" label={isCurrentlyGenerating ? '生成中' : '等待中'} color={isCurrentlyGenerating ? "success" : "warning"} variant="outlined"
                                              icon={isCurrentlyGenerating ? <CircularProgress size={10} color="inherit" /> : <HourglassEmptyIcon sx={{ fontSize: '12px !important' }} />}
                                              sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                                            />
                                          )}
                                          {isOptimized && !isOptimizing && (
                                            <Box sx={{ minWidth: 16, width: 16, height: 16, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(76, 175, 80, 0.15)', color: '#66bb6a', border: '1px solid rgba(76, 175, 80, 0.25)', fontSize: '0.6rem', fontWeight: 700, flexShrink: 0 }}>
                                              优
                                            </Box>
                                          )}
                                          {isRewriting && <Chip size="small" label="重写中" color="info" variant="outlined" icon={<CircularProgress size={10} color="inherit" />} sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }} />}
                                          {isRewritten && !isRewriting && (
                                            <Box sx={{ minWidth: 16, width: 16, height: 16, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255, 152, 0, 0.15)', color: '#ffa726', border: '1px solid rgba(255, 152, 0, 0.25)', fontSize: '0.6rem', fontWeight: 700, flexShrink: 0 }}>
                                              重
                                            </Box>
                                          )}
                                          <Chip size="small" label={hasContent ? `${chapter.contentLength || (chapter.content ? chapter.content.length : 0)}字` : '0字'}
                                            sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700, backgroundColor: hasContent ? 'rgba(6, 182, 212, 0.2)' : 'rgba(107, 114, 128, 0.2)', color: hasContent ? '#06b6d4' : '#9ca3af', border: 'none' }}
                                          />
                                          {hasOutline && <Chip size="small" label="纲" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, backgroundColor: 'rgba(139, 92, 246, 0.2)', color: '#a78bfa' }} />}
                                </Box>
                              </Box>
                            </AccordionSummary>

                                    {isSelected && (
                                      <AccordionDetails sx={{ p: 0, m: 0, background: 'rgba(5, 10, 20, 0.95)', borderTop: '1px solid rgba(6, 182, 212, 0.15)', '@media (max-width: 600px)': { p: 0, m: 0 } }}>
                                        <Box sx={{ display: 'flex', flexWrap: { xs: 'wrap', sm: 'nowrap' }, gap: { xs: '3px', sm: '6px' }, p: { xs: '4px 6px', sm: '8px 12px' }, borderBottom: '1px solid rgba(6, 182, 212, 0.1)', justifyContent: { xs: 'space-between', sm: 'space-between' }, alignItems: 'center', width: '100%' }}>
                                          <Button className="assist-chapter-ai-glow-btn" startIcon={<AutoFixHighIcon />} onClick={() => { if (chapter?.content && chapter?.content?.trim()?.length >= 100) { handleOptimizeChapter(chapter.index); } }} disabled={!chapter?.content || chapter?.content?.trim()?.length < 100 || isOptimizing || isRewriting}>{isOptimizing ? '优化中' : '优化'}</Button>
                                          <Button className="assist-chapter-ai-glow-btn" startIcon={<AutoAwesomeIcon />} onClick={() => handleRewriteChapter(chapter.index)} disabled={!chapter?.content || chapter?.content?.trim()?.length < 100 || isRewriting || isOptimizing}>{isRewriting ? '重写中' : '重写'}</Button>
                                          <Button className="assist-chapter-ai-glow-btn" startIcon={<ContentCopyIcon />} onClick={() => { safeCopyMobile(formattedTitle.replace(/[《》]/g, '').trim()); showMessage('章节标题已复制', 'success'); }}>复制标题</Button>
                                          <Button className="assist-chapter-ai-glow-btn" startIcon={<ContentCopyIcon />} onClick={() => { if (chapter.content) { safeCopyMobile(chapter.content); showMessage('章节内容已复制', 'success'); } else { showMessage('没有可复制的内容', 'warning'); } }} disabled={!hasContent}>复制内容</Button>
                                          <Button className="assist-chapter-ai-glow-btn" startIcon={<EditIcon />} onClick={() => handleTitleEditStart(chapter)} disabled={!chapter || isGenerating || isOptimizing || isRewriting}>改标题</Button>
                                          <Button className="assist-chapter-ai-glow-btn" startIcon={<EditIcon />} onClick={() => handleContentEditStart(chapter)} disabled={!hasContent || isGenerating || isOptimizing || isRewriting}>改内容</Button>
                                      </Box>
                                        <Box sx={{ p: { xs: '8px 10px', sm: '12px 16px' } }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 1, sm: 1.5 }, px: { xs: 1, sm: 1.5 }, py: { xs: '4px', sm: '6px' }, background: 'rgba(6, 182, 212, 0.06)', borderRadius: '8px', border: '1px solid rgba(6, 182, 212, 0.12)' }}>
                                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: hasContent ? '#06b6d4' : '#6b7280', boxShadow: hasContent ? '0 0 8px rgba(6, 182, 212, 0.6)' : 'none', animation: hasContent ? 'chapterBadgePulse 1.5s ease-in-out infinite' : 'none' }} />
                                            <Typography sx={{ fontWeight: 600, color: '#06b6d4', fontSize: { xs: '0.78rem', sm: '0.88rem' } }}>章节内容</Typography>
                                            <Box sx={{ flex: 1 }} />
                                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: hasContent ? '#06b6d4' : '#6b7280', backgroundColor: 'rgba(6, 182, 212, 0.08)', px: 1, py: '2px', borderRadius: '6px', border: '1px solid rgba(6, 182, 212, 0.2)' }}>{hasContent ? `${chapter.contentLength || (chapter.content ? chapter.content.length : 0)} 字` : '暂无内容'}</Typography>
                                      </Box>
                                          {isLoadingChapterContent ? (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 4, borderRadius: '10px', background: 'rgba(5, 10, 20, 0.6)', border: '1px solid rgba(6, 182, 212, 0.15)' }}>
                                              <CircularProgress size={24} thickness={4} sx={{ color: '#06b6d4', animationDuration: '0.9s' }} />
                                              <Typography sx={{ fontSize: '0.75rem', color: 'rgba(6, 182, 212, 0.85)', fontWeight: 600 }}>正在加载章节正文...</Typography>
                                            </Box>
                                          ) : (
                                          <TextField fullWidth multiline minRows={6} maxRows={40} variant="outlined" placeholder="在此编辑章节内容..." value={currentChapterContent || ''}
                                            onChange={(e) => { setCurrentChapterContent(e.target.value); debouncedSaveChapterContent(e.target.value); }}
                                            inputRef={contentTextareaRef}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', backgroundColor: 'rgba(5, 10, 20, 0.95)', border: '1px solid rgba(6, 182, 212, 0.2)', '& fieldset': { border: 'none' }, '&:hover': { borderColor: 'rgba(6, 182, 212, 0.4)' }, '&.Mui-focused': { borderColor: '#06b6d4', boxShadow: '0 0 0 3px rgba(6, 182, 212, 0.12), 0 0 20px rgba(6, 182, 212, 0.08)' } }, '& textarea': { fontSize: { xs: '0.82rem', sm: '0.95rem' }, lineHeight: 1.8, color: 'rgba(255,255,255,0.85)', p: { xs: '10px 12px', sm: '12px 14px' }, '&::placeholder': { color: 'rgba(255,255,255,0.3)', opacity: 1 } } }}
                                        disabled={isGenerating || isOptimizing}
                                      />
                                          )}
                                </Box>
                            </AccordionDetails>
                            )}
                          </Accordion>
                        );
                      })}
                            </AccordionDetails>
                          </Accordion>
                          );
                        });
                      })()}
                    </Box>
                )}
              </Box>
            </Box>
            )}
            {/* 🔥【已改造】生成状态条已改为布局内嵌（挂载在章节操作按钮区下方），不再使用浮层挂载 */}
          </TabPanel>

          {/* 🔥🔥🔥 新增Tab：关系规划查看器 */}
          <TabPanel value={activeTab} index={4} sx={{ display: 'flex', flexDirection: 'column', overflowY: 'hidden', pb: '44px' }}>
            <Box className="relationship-panel">
              {/* 英雄头部 */}
              <Box className="relationship-hero-header">
                <Box className="relationship-hero-bg" />
                <Box className="hero-particles-relationship" />
                <Box className="relationship-hero-content">
                  <Box className="relationship-hero-icon">
                    <HubIcon sx={{ fontSize: 22, color: '#f59e0b' }} />
                  </Box>
                  <Box className="relationship-hero-text">
                    <Typography className="relationship-hero-title">关系规划</Typography>
                    <Typography className="relationship-hero-subtitle">构建人物关系网络</Typography>
                  </Box>
                  <Box className="relationship-hero-badge">
                    <Box className="relationship-hero-badge-dot" />
                    <Typography className="relationship-hero-badge-text">AI分析</Typography>
                  </Box>
                </Box>
              </Box>
              {/* 内容区容器 */}
              <Box className="outline-section">
                <Box sx={{ p: {xs: 1, sm: 2} }}>
                  <RelationshipPlanViewer novelId={novel?._id} />
                </Box>
              </Box>
              {/* 移动端底部留白，防止被导航栏遮挡 */}
              <Box className="mobile-nav-bottom-spacer" />
            </Box>
          </TabPanel>

          {/* 情感弧线查看器 */}
          <TabPanel value={activeTab} index={5} sx={{ display: 'flex', flexDirection: 'column', overflowY: 'hidden', pb: '44px' }}>
            <Box className="emotion-panel">
              {/* 英雄头部 */}
              <Box className="emotion-hero-header">
                <Box className="emotion-hero-bg" />
                <Box className="hero-particles-emotion" />
                <Box className="emotion-hero-content">
                  <Box className="emotion-hero-icon">
                    <ShowChartIcon sx={{ fontSize: 22, color: '#ec4899' }} />
                  </Box>
                  <Box className="emotion-hero-text">
                    <Typography className="emotion-hero-title">情感弧线</Typography>
                    <Typography className="emotion-hero-subtitle">跟踪情感变化轨迹</Typography>
                  </Box>
                  <Box className="emotion-hero-badge">
                    <Box className="emotion-hero-badge-dot" />
                    <Typography className="emotion-hero-badge-text">可视化</Typography>
                  </Box>
                </Box>
              </Box>
              {/* 内容区容器 */}
              <Box className="outline-section">
                <Box sx={{ p: {xs: 1, sm: 2} }}>
                  <EmotionalArcViewer novelId={novel?._id} />
                </Box>
              </Box>
              {/* 移动端底部留白，防止被导航栏遮挡 */}
              <Box className="mobile-nav-bottom-spacer" />
            </Box>
          </TabPanel>
        </Box>
        
        {/* 对话框组件 */}
        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          PaperProps={{
            sx: {
              background: 'linear-gradient(135deg, rgba(8, 14, 28, 0.98) 0%, rgba(10, 18, 35, 0.98) 100%)',
              backdropFilter: 'blur(24px)',
              border: '1px solid rgba(6, 182, 212, 0.2)',
              borderRadius: '14px !important',
              boxShadow: '0 12px 40px rgba(0,0,0,0.6), 0 0 30px rgba(16,185,129,0.08)',
              overflow: 'hidden'
            }
          }}
        >
          <Box sx={{
            p: { xs: 2, sm: 2.5 },
            background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.12) 0%, rgba(5, 150, 105, 0.06) 100%)',
            borderBottom: '1px solid rgba(6, 182, 212, 0.15)',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}>
            <Box sx={{
              width: 36,
              height: 36,
              borderRadius: '10px',
              background: 'rgba(6, 182, 212, 0.12)',
              border: '1px solid rgba(6, 182, 212, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <MenuBookIcon sx={{ fontSize: 18, color: '#06b6d4' }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: '#06b6d4', fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
            {dialogType === 'add' ? '添加新章节' : '修改章节'}
            </Typography>
          </Box>
          <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Box className="settings-field" sx={{ mb: 2 }}>
              <Typography className="settings-field-label">章节序号</Typography>
            <TextField
              autoFocus
              margin="dense"
              type="number"
              fullWidth
              value={newChapter.index}
              onChange={(e) => setNewChapter({ ...newChapter, index: e.target.value })}
                className="settings-input"
                inputProps={{ style: { textAlign: 'center' } }}
            />
            </Box>
            <Box className="settings-field">
              <Typography className="settings-field-label">章节标题</Typography>
            <TextField
              margin="dense"
              type="text"
              fullWidth
              value={newChapter.title}
              onChange={(e) => setNewChapter({ ...newChapter, title: e.target.value })}
                className="settings-input"
            />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, borderTop: '1px solid rgba(16,185,129,0.1)', gap: 1 }}>
            <Button
              onClick={() => setOpenDialog(false)}
              sx={{ color: 'rgba(255,255,255,0.5)', borderColor: 'rgba(255,255,255,0.12)' }}
              variant="outlined"
            >
              取消
            </Button>
            <Button
              onClick={handleSaveChapter}
              variant="contained"
              className="nexus-btn-primary"
            >
              保存
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* 🔥【体检留痕】全书体检报告面板（历史任务+逐条改前/改后明细，持久可查） */}
        <Dialog
          open={auditPanelOpen}
          onClose={() => setAuditPanelOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              background: 'linear-gradient(135deg, rgba(8, 14, 28, 0.98) 0%, rgba(10, 18, 35, 0.98) 100%)',
              backdropFilter: 'blur(24px)',
              border: '1px solid rgba(6, 182, 212, 0.2)',
              borderRadius: '14px !important',
              boxShadow: '0 12px 40px rgba(0,0,0,0.6), 0 0 30px rgba(16,185,129,0.08)',
              overflow: 'hidden'
            }
          }}
        >
          {/* 头部 */}
          <Box sx={{
            p: { xs: 1.5, sm: 2 },
            background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.12) 0%, rgba(5, 150, 105, 0.06) 100%)',
            borderBottom: '1px solid rgba(6, 182, 212, 0.15)',
            display: 'flex', alignItems: 'center', gap: 1.5
          }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: '10px',
              background: 'rgba(6, 182, 212, 0.12)', border: '1px solid rgba(6, 182, 212, 0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <FactCheckOutlinedIcon sx={{ fontSize: 18, color: '#06b6d4' }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, color: '#06b6d4', fontSize: { xs: '0.95rem', sm: '1.05rem' } }}>
                全书体检报告
              </Typography>
              {/* 说明文字移动端隐藏，避免头部折行 */}
              <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.4)', display: { xs: 'none', sm: 'block' } }}>
                每次体检自动留档 · 逐条记录改了哪里、改了什么
              </Typography>
            </Box>
            {auditActive && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.5, borderRadius: 999, background: 'rgba(6,182,212,0.08)', border: '1px solid rgba(6,182,212,0.25)' }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#06b6d4', animation: 'novelGreenPulse 2s ease-in-out infinite' }} />
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#06b6d4' }}>体检进行中</Typography>
              </Box>
            )}
            <IconButton size="small" onClick={() => setAuditPanelOpen(false)} sx={{ color: 'rgba(255,255,255,0.5)' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* 🔥 移动端上下堆叠（记录横向滚动选条+全宽详情），桌面端保持左右分栏 */}
          {/* 移动端高度自适应弹窗（minHeight:0 允许收缩，详情区内部滚动），确保按钮不被底部导航栏遮挡 */}
          <DialogContent className="audit-dialog-content" sx={{ p: 0, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, height: { xs: 'auto', sm: '540px' }, minHeight: 0, overflow: 'hidden' }}>
            {/* 历史任务列表：桌面=左侧竖栏 / 移动端=顶部横向滚动选条 */}
            <Box sx={{
              width: { xs: '100%', sm: 240 }, flexShrink: 0,
              borderRight: { sm: '1px solid rgba(6, 182, 212, 0.1)' },
              borderBottom: { xs: '1px solid rgba(6, 182, 212, 0.1)', sm: 'none' },
              overflowY: { xs: 'hidden', sm: 'auto' },
              overflowX: { xs: 'auto', sm: 'hidden' },
              display: { xs: 'flex', sm: 'block' },
              alignItems: { xs: 'stretch', sm: 'initial' },
              py: { xs: 1, sm: 1 }, px: { xs: 1, sm: 1 }
            }}>
              {auditHistory.length === 0 ? (
                <Box sx={{ p: 1.5, textAlign: 'center', width: '100%' }}>
                  <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)', lineHeight: 1.8 }}>
                    暂无体检记录
                  </Typography>
                  <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.25)', lineHeight: 1.8, mt: 1 }}>
                    批量生成完成后系统会自动体检，完成后此处留档
                  </Typography>
                </Box>
              ) : auditHistory.map((t, idx) => {
                const selected = auditDetail && String(auditDetail.taskId) === String(t.taskId);
                return (
                  <Box
                    key={String(t.taskId)}
                    onClick={() => loadAuditDetail(t.taskId)}
                    sx={{
                      p: 1,
                      mb: { xs: 0, sm: 0.75 },
                      mr: { xs: 0.75, sm: 0 },
                      flexShrink: 0,
                      minWidth: { xs: 168, sm: 'auto' },
                      maxWidth: { sm: 'none' },
                      borderRadius: '8px', cursor: 'pointer',
                      border: selected ? '1px solid rgba(6, 182, 212, 0.5)' : '1px solid rgba(255,255,255,0.06)',
                      background: selected ? 'rgba(6, 182, 212, 0.1)' : 'rgba(255,255,255,0.02)',
                      transition: 'all 0.2s ease',
                      '&:hover': { background: 'rgba(6, 182, 212, 0.08)' }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: selected ? '#06b6d4' : 'rgba(255,255,255,0.75)', whiteSpace: 'nowrap' }}>
                        {t.auto ? '自动体检' : '手动体检'}{idx === 0 ? ' · 最新' : ''}
                      </Typography>
                      <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: auditStatusColor(t.status), whiteSpace: 'nowrap' }}>
                        {auditStatusLabel(t.status)}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.4)', mt: 0.25, whiteSpace: 'nowrap' }}>
                      {t.rangeStart ? `第${t.rangeStart}-${t.rangeEnd}章` : '全书'} · {new Date(t.createdAt).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                    {t.stats && (t.stats.problemsHard > 0 || t.stats.fixed > 0) && (
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: '0.62rem', color: '#f59e0b' }}>硬伤{t.stats.problemsHard}</Typography>
                        <Typography sx={{ fontSize: '0.62rem', color: '#10b981' }}>修复{t.stats.fixed}</Typography>
                        {t.stats.skipped > 0 && <Typography sx={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.4)' }}>保留{t.stats.skipped}</Typography>}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>

            {/* 右侧：详情区 */}
            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: { xs: 1, sm: 2 } }}>
              {auditDetailLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1.5 }}>
                  <CircularProgress size={20} sx={{ color: '#06b6d4' }} />
                  <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.5)' }}>加载体检详情中...</Typography>
                </Box>
              ) : !auditDetail ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.35)' }}>
                    {auditHistory.length === 0 ? '这本书还没有体检记录' : '选择一条体检记录查看详情'}
                  </Typography>
                </Box>
              ) : (
                <>
                  {/* 任务概览 */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
                    {[
                      { label: '状态', value: auditStatusLabel(auditDetail.status), color: auditStatusColor(auditDetail.status) },
                      { label: '范围', value: auditDetail.rangeStart ? `第${auditDetail.rangeStart}-${auditDetail.rangeEnd}章` : '全书', color: 'rgba(255,255,255,0.8)' },
                      { label: '审读', value: `${auditDetail.chaptersTotal}章`, color: 'rgba(255,255,255,0.8)' },
                      { label: '水位线', value: auditDetail.auditedUpTo ? `第${auditDetail.auditedUpTo}章` : '-', color: 'rgba(255,255,255,0.8)' },
                      { label: '硬伤', value: `${auditDetail.stats?.problemsHard || 0}`, color: '#f59e0b' },
                      { label: '软伤', value: `${auditDetail.stats?.problemsSoft || 0}`, color: '#a855f7' },
                      { label: '已修复', value: `${auditDetail.stats?.fixed || 0}`, color: '#10b981' },
                      { label: '保留原稿', value: `${auditDetail.stats?.skipped || 0}章`, color: 'rgba(255,255,255,0.55)' }
                    ].map(item => (
                      <Box key={item.label} sx={{
                        px: 1.25, py: 0.5, borderRadius: '8px',
                        background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)'
                      }}>
                        <Typography component="span" sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.4)', mr: 0.5 }}>{item.label}</Typography>
                        <Typography component="span" sx={{ fontSize: '0.72rem', fontWeight: 700, color: item.color }}>{item.value}</Typography>
                      </Box>
                    ))}
                  </Box>
                  {auditDetail.error && (
                    <Typography sx={{ fontSize: '0.7rem', color: '#ef4444', mb: 1 }}>{auditDetail.error}</Typography>
                  )}

                  {/* Tab切换：修改明细 / 问题清单 */}
                  <Box sx={{ display: 'flex', gap: 0.75, mb: 1.5 }}>
                    {[
                      { key: 'fixes', label: `修改明细 (${(auditDetail.fixes || []).length})` },
                      { key: 'problems', label: `问题清单 (${(auditDetail.problems || []).length})` }
                    ].map(tab => (
                      <Box
                        key={tab.key}
                        onClick={() => setAuditDetailTab(tab.key)}
                        sx={{
                          px: 1.5, py: 0.5, borderRadius: '8px', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 700,
                          color: auditDetailTab === tab.key ? '#06b6d4' : 'rgba(255,255,255,0.45)',
                          background: auditDetailTab === tab.key ? 'rgba(6, 182, 212, 0.12)' : 'rgba(255,255,255,0.03)',
                          border: auditDetailTab === tab.key ? '1px solid rgba(6, 182, 212, 0.4)' : '1px solid rgba(255,255,255,0.06)',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {tab.label}
                      </Box>
                    ))}
                  </Box>

                  {/* 修改明细（改前/改后对照） */}
                  {auditDetailTab === 'fixes' && (
                    (auditDetail.fixes || []).length === 0 ? (
                      <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.35)', py: 2, textAlign: 'center' }}>
                        本次体检没有修改任何章节（未发现需要修订的硬伤）
                      </Typography>
                    ) : [...(auditDetail.fixes || [])].sort((a, b) => a.chapterNo - b.chapterNo).map((fix, fi) => {
                      const expanded = !!auditFixExpanded[`${fix.chapterNo}_${fi}`];
                      return (
                        <Box key={`${fix.chapterNo}_${fi}`} sx={{ mb: 1, borderRadius: '10px', border: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)', overflow: 'hidden' }}>
                          {/* 章节卡头 */}
                          <Box
                            onClick={() => setAuditFixExpanded(prev => ({ ...prev, [`${fix.chapterNo}_${fi}`]: !expanded }))}
                            sx={{ p: 1.25, display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', flexWrap: 'wrap', '&:hover': { background: 'rgba(6,182,212,0.05)' } }}
                          >
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'rgba(255,255,255,0.85)' }}>
                              第{fix.chapterNo}章{fix.chapterTitle ? `《${fix.chapterTitle}》` : ''}
                            </Typography>
                            {fix.result === 'fixed' ? (
                              <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#10b981' }}>已修复</Typography>
                            ) : (
                              <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#f59e0b' }}>保留原稿</Typography>
                            )}
                            <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.4)' }}>
                              修订{fix.rounds}轮 · {fix.wordCountBefore}字→{fix.wordCountAfter}字
                              {fix.changedCount > 0 ? ` · ${fix.changedCount}处改动` : ''}
                            </Typography>
                            <Typography sx={{ fontSize: '0.65rem', color: 'rgba(6,182,212,0.7)', ml: 'auto' }}>{expanded ? '收起' : '展开'}</Typography>
                          </Box>
                          {expanded && (
                            <Box sx={{ px: 1.25, pb: 1.25 }}>
                              {/* 本章修复的问题 */}
                              {(fix.problems || []).length > 0 && (
                                <Box sx={{ mb: 1 }}>
                                  {fix.problems.map((pr, pi) => (
                                    <Typography key={pi} sx={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.7 }}>
                                      · {pr}
                                    </Typography>
                                  ))}
                                </Box>
                              )}
                              {fix.note && (
                                <Typography sx={{ fontSize: '0.68rem', color: '#f59e0b', mb: 1 }}>{fix.note}</Typography>
                              )}
                              {/* 改前/改后对照 */}
                              {(fix.changes || []).length === 0 ? (
                                <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.35)' }}>无文本改动</Typography>
                              ) : fix.changes.map((c, ci) => (
                                <Box key={ci} sx={{ mb: 0.75 }}>
                                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.75 }}>
                                    <Box sx={{ p: 0.75, borderRadius: '6px', background: 'rgba(239, 68, 68, 0.07)', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
                                      <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#ef4444', mb: 0.25 }}>改前</Typography>
                                      <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.6, wordBreak: 'break-all' }}>
                                        {c.before || '（删除）'}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ p: 0.75, borderRadius: '6px', background: 'rgba(16, 185, 129, 0.07)', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
                                      <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#10b981', mb: 0.25 }}>改后</Typography>
                                      <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.6, wordBreak: 'break-all' }}>
                                        {c.after || '（删除）'}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Box>
                              ))}
                              {fix.changedCount > (fix.changes || []).length && (
                                <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.35)' }}>
                                  另有{fix.changedCount - (fix.changes || []).length}处改动未展示
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                      );
                    })
                  )}

                  {/* 问题清单 */}
                  {auditDetailTab === 'problems' && (
                    (auditDetail.problems || []).length === 0 ? (
                      <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.35)', py: 2, textAlign: 'center' }}>
                        本次体检未发现问题
                      </Typography>
                    ) : (auditDetail.problems || []).map((p, pi) => {
                      const statusLabel = ({ pending: '待处理', rewriting: '修订中', fixed: '已修复', skip_maxround: '保留原稿', record_only: '仅记录' })[p.status] || p.status;
                      const statusColor = ({ pending: '#f59e0b', rewriting: '#06b6d4', fixed: '#10b981', skip_maxround: '#f59e0b', record_only: 'rgba(255,255,255,0.4)' })[p.status] || 'rgba(255,255,255,0.4)';
                      return (
                        <Box key={pi} sx={{ mb: 0.75, p: 1.25, borderRadius: '10px', border: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: p.severity === 'hard' ? '#f59e0b' : '#a855f7' }}>
                              {p.severity === 'hard' ? '硬伤' : '软伤'}
                            </Typography>
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'rgba(255,255,255,0.75)' }}>{p.typeLabel || p.type}</Typography>
                            <Typography sx={{ fontSize: '0.7rem', color: 'rgba(6,182,212,0.8)' }}>第{p.chapterNo}章</Typography>
                            <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: statusColor, ml: 'auto' }}>{statusLabel}</Typography>
                          </Box>
                          <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.7, mb: 0.5 }}>{p.description}</Typography>
                          {p.evidence && (
                            <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.4)', lineHeight: 1.7, pl: 1, borderLeft: '2px solid rgba(6,182,212,0.3)' }}>
                              原文证据：{p.evidence}
                            </Typography>
                          )}
                        </Box>
                      );
                    })
                  )}
                </>
              )}
            </Box>
          </DialogContent>
        </Dialog>

        {/* 通用确认对话框 */}
        <Dialog
          open={confirmDialog.open}
          onClose={handleCloseConfirmDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              background: 'linear-gradient(135deg, rgba(8, 14, 28, 0.98) 0%, rgba(10, 18, 35, 0.98) 100%)',
              backdropFilter: 'blur(24px)',
              border: '1px solid rgba(6, 182, 212, 0.2)',
              borderRadius: '14px !important',
              boxShadow: '0 12px 40px rgba(0,0,0,0.6), 0 0 30px rgba(16,185,129,0.08)',
              overflow: 'hidden',
              '@media (max-width: 600px)': {
                marginBottom: '70px !important',
                marginTop: 'auto !important',
                maxHeight: 'calc(100vh - 120px)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }
            }
          }}
        >
          <Box sx={{
            p: { xs: 1.5, sm: 1.5 },
            background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.12) 0%, rgba(5, 150, 105, 0.06) 100%)',
            borderBottom: '1px solid rgba(6, 182, 212, 0.15)',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexShrink: 0,
          }}>
            <Box sx={{
              width: 32,
              height: 32,
              borderRadius: '8px',
              background: 'rgba(6, 182, 212, 0.12)',
              border: '1px solid rgba(6, 182, 212, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <MenuBookIcon sx={{ fontSize: 18, color: '#06b6d4' }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: '#06b6d4', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
              {confirmDialog.title || '确认操作'}
            </Typography>
          </Box>
          <DialogContent sx={{
            p: { xs: 2, sm: 2.5 },
            '@media (max-width: 600px)': {
              flex: 1,
              overflow: 'auto',
              minHeight: 0,
            }
          }}>
            {confirmDialog.content || (
              <Typography sx={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.75)' }}>
                {confirmDialog.message}
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{
            p: { xs: 1.5, sm: 2 },
            borderTop: '1px solid rgba(16,185,129,0.1)',
            gap: 1
          }}>
            <Button
              onClick={() => { setConfirmDialog({...confirmDialog, open: false}); }}
              variant="outlined"
              sx={{
                borderColor: 'rgba(255,255,255,0.12)',
                color: 'rgba(255,255,255,0.5)',
                borderRadius: '10px',
                fontWeight: 600,
                '&:hover': { borderColor: 'rgba(255,255,255,0.25)', backgroundColor: 'rgba(255,255,255,0.05)' }
              }}
            >
              {confirmDialog.cancelText || '取消'}
            </Button>
            <Button 
              onClick={async () => {
                setConfirmDialog({...confirmDialog, open: false});
                if (confirmDialog.action) { await confirmDialog.action(); }
                else if (confirmDialog.onConfirm) { confirmDialog.onConfirm(); }
              }} 
              variant="contained"
              color="primary" 
              autoFocus
              sx={{
                borderRadius: '10px',
                background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.9) 0%, rgba(5, 150, 105, 0.9) 100%)',
                boxShadow: '0 0 16px rgba(6, 182, 212, 0.25)',
                fontWeight: 700,
                '&:hover': {
                  background: 'linear-gradient(135deg, rgba(6, 182, 212, 1) 0%, rgba(5, 150, 105, 1) 100%)',
                  boxShadow: '0 0 24px rgba(6, 182, 212, 0.4)'
                }
              }}
            >
              {confirmDialog.confirmText || '确定'}
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* 批量生成对话框 */}
        <Dialog 
          open={batchDialogOpen} 
          onClose={() => setBatchDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              background: 'linear-gradient(135deg, rgba(8, 14, 28, 0.98) 0%, rgba(10, 18, 35, 0.98) 100%)',
              backdropFilter: 'blur(24px)',
              border: '1px solid rgba(6, 182, 212, 0.2)',
              borderRadius: '10px !important',
              boxShadow: '0 12px 40px rgba(0,0,0,0.6), 0 0 30px rgba(16,185,129,0.08)',
              overflow: 'hidden',
              width: { xs: 'calc(100% - 16px)', sm: '100%' },
              maxWidth: { xs: '100%', sm: 'sm' },
              mx: { xs: 1, sm: 'auto' },
              my: { xs: 4, sm: 'auto' },
              '@media (max-width: 600px)': {
                marginBottom: '70px !important',
                marginTop: 'auto !important',
                maxHeight: 'calc(100vh - 120px)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }
            }
          }}
        >
          <Box sx={{
            p: { xs: 1.25, sm: 1.5 },
            background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.12) 0%, rgba(5, 150, 105, 0.06) 100%)',
            borderBottom: '1px solid rgba(6, 182, 212, 0.15)',
                display: 'flex', 
                alignItems: 'center',
            gap: 1
          }}>
            <Box sx={{
              width: 28,
              height: 28,
                    borderRadius: '8px',
              background: 'rgba(6, 182, 212, 0.12)',
              border: '1px solid rgba(6, 182, 212, 0.3)',
                      display: 'flex', 
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <AutorenewIcon sx={{ fontSize: 15, color: '#06b6d4' }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: '#06b6d4', fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
              批量生成章节
            </Typography>
          </Box>
          <DialogContent sx={{
            p: { xs: 2, sm: 2.5 },
            pt: { xs: 0, sm: 1 },
            '@media (max-width: 600px)': {
              flex: 1,
              overflow: 'auto',
              minHeight: 0,
            }
          }}>
            {/* 体验卡用户醒目提示 - 放在内容区内部可滚动 */}
            {trialCheckDone && isTrialOnlyUser && (
              <Box sx={{
                mb: 2,
                p: 2,
                borderRadius: '10px',
                background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.12) 0%, rgba(255, 87, 34, 0.08) 100%)',
                border: '1px solid rgba(255, 152, 0, 0.4)',
                boxShadow: '0 0 20px rgba(255, 152, 0, 0.1)',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Box sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '8px',
                    background: 'rgba(255, 152, 0, 0.2)',
                    border: '1px solid rgba(255, 152, 0, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <WarningAmberIcon sx={{ fontSize: 18, color: '#ff9800' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 700, color: '#ff9800', fontSize: '0.88rem', mb: 0.5 }}>
                      温馨提示：体验卡章节上限为10章
                    </Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.75)', lineHeight: 1.6, mb: 1 }}>
                      本功能仅供您体验我们的AI小说创作服务。我们专注长篇小说，正式用户创作均<strong style={{ color: '#4caf50' }}>100章起步</strong>。<strong style={{ color: '#ff9800' }}>体验卡的10章内容无法用于番茄发文</strong>，详情请点击查看番茄发文规则。
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setTomatoRulesDialogOpen(true)}
                      startIcon={<InfoOutlinedIcon sx={{ fontSize: '16px !important' }} />}
                      sx={{
                        fontSize: '0.72rem',
                        py: 0.5,
                        px: 1.5,
                        borderColor: 'rgba(255, 152, 0, 0.5)',
                        color: '#ff9800',
                        borderRadius: '6px',
                        '&:hover': {
                          borderColor: '#ff9800',
                          backgroundColor: 'rgba(255, 152, 0, 0.08)',
                        },
                      }}
                    >
                      查看番茄发文规则
                    </Button>
                  </Box>
                </Box>
              </Box>
            )}
            {/* 生成范围 + 对话比例 + 说明全部平铺，不再有外层 Paper 容器 */}
            <Box sx={{ mb: 2 }}>
              {/* 生成范围标题 */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 0.75, flexWrap: 'wrap' }}>
                <FormatListNumberedIcon sx={{ fontSize: '1rem', color: '#06b6d4' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#06b6d4', fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
                    生成范围
                  </Typography>
                {novel?.storyStructure?.isGenerated && novel?.storyStructure?.totalChapters && (
                  <Typography variant="caption" sx={{ color: 'rgba(16,185,129,0.6)', fontSize: '0.75rem' }}>
                    （共{novel.storyStructure.totalChapters}章）
                  </Typography>
                )}
              </Box>
              {/* 起始和结束章节 — 移动端纵向排列，PC端横向 */}
              <Box className="batch-range-box" sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 2 }, alignItems: 'stretch' }}>
                        <TextField
                          label="起始章节"
                          type="text"
                          inputProps={{ 
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                            ...(novel?.storyStructure?.isGenerated && novel?.storyStructure?.totalChapters 
                              ? { max: novel.storyStructure.totalChapters } 
                              : {})
                          }}
                          value={batchRange.startChapter != null ? String(batchRange.startChapter) : ''}
                      onChange={(e) => {
                            const inputValue = e.target.value;
                            const numericOnly = inputValue.replace(/[^0-9]/g, '');
                            if (numericOnly === '') {
                              setBatchRange({ ...batchRange, startChapter: '' });
                              return;
                            }
                            const numValue = Number(numericOnly);
                            if (!isNaN(numValue) && numValue > 0) {
                              let value = Math.floor(numValue);
                              const maxChapters = novel?.storyStructure?.outlineEnforcement?.chapterOutlineMap?.length || 
                                                 novel?.storyStructure?.totalChapters || 999999;
                              if (value > maxChapters) {
                                value = maxChapters;
                                showMessage(`起始章节不能超过第${maxChapters}章`, "warning");
                              }
                              setBatchRange({ ...batchRange, startChapter: value });
                            }
                      }}
                      onBlur={(e) => {
                            const inputValue = e.target.value;
                            if (inputValue === '' || inputValue === '0') {
                              setBatchRange({ ...batchRange, startChapter: 1 });
                              return;
                            }
                            let value = Math.floor(Number(inputValue));
                            if (isNaN(value) || value <= 0) {
                              setBatchRange({ ...batchRange, startChapter: 1 });
                              showMessage("请输入大于0的整数", "warning");
                              return;
                            }
                            if (novel?.storyStructure?.outlineEnforcement?.chapterOutlineMap) {
                              const maxChaptersFromStructure = novel.storyStructure.outlineEnforcement.chapterOutlineMap.length;
                              if (value > maxChaptersFromStructure) {
                                value = maxChaptersFromStructure;
                                showMessage(`故事结构中共有${maxChaptersFromStructure}章大纲，已自动调整`, "warning");
                              }
                            } else if (novel?.storyStructure?.isGenerated && novel?.storyStructure?.totalChapters) {
                              const maxChaptersFromStructure = novel.storyStructure.totalChapters;
                              if (value > maxChaptersFromStructure) {
                                value = maxChaptersFromStructure;
                                showMessage(`故事结构已设定总章节数为${maxChaptersFromStructure}章，已自动调整`, "warning");
                              }
                            }
                            const chaptersWithContent = chapters.filter(ch => 
                              ch.content && ch.content.trim().length > 0
                            ).map(ch => ch.index);
                            const allGeneratedChapters = [...new Set([...chaptersGenerated, ...chaptersWithContent])];
                            const maxGeneratedChapter = allGeneratedChapters.length > 0 ? 
                              Math.max(...allGeneratedChapters) : 0;
                            const generatingChapterIndices = Array.isArray(chapterGenerationStatus) 
                              ? chapterGenerationStatus
                                .filter(ch => ch.status === '生成中' || ch.status === 'generating')
                                .map(ch => ch.chapterIndex)
                              : [];
                            const maxGeneratingChapter = generatingChapterIndices.length > 0 ?
                              Math.max(...generatingChapterIndices) : 0;
                            if (maxGeneratedChapter === 0 && maxGeneratingChapter === 0 && chapters.length === 0 && value !== 1) {
                              value = 1;
                              showMessage("首次生成必须从第1章开始", "warning");
                            } 
                            if (generatingChapterIndices.includes(value)) {
                              while (generatingChapterIndices.includes(value)) {
                                value++;
                              }
                              showMessage(`第${value-1}章正在生成中，已自动选择第${value}章`, "warning");
                            }
                            let newEndChapter = batchRange.endChapter;
                            if (batchRange.endChapter < value) {
                              newEndChapter = value;
                            }
                            setBatchRange({ 
                              ...batchRange, 
                              startChapter: value,
                              endChapter: newEndChapter
                            });
                      }}
                      fullWidth
                      size="small"
                      sx={{ mb: 0 }}
                          InputProps={{
                        sx: {
                          '& .MuiInputBase-root': {
                            width: '100%',
                            minWidth: 0
                          },
                          '& .MuiInputBase-input': {
                            minWidth: '80px',
                            textAlign: 'center'
                          },
                          '& .MuiInputAdornment-root': {
                            flexShrink: 1
                          }
                        },
                            startAdornment: <InputAdornment position="start">第</InputAdornment>,
                            endAdornment: (
                          <InputAdornment position="end" sx={{ p: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
                              <Typography variant="body2" sx={{ mr: 0.25, flexShrink: 0 }}>章</Typography>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      const currentVal = parseInt(batchRange.startChapter) || 1;
                                      if (currentVal > 1) {
                                        const newValue = currentVal - 1;
                                        setBatchRange({ 
                                          ...batchRange, 
                                          startChapter: newValue,
                                          endChapter: Math.max(newValue, batchRange.endChapter)
                                        });
                                      }
                                    }}
                                    sx={{ 
                                      p: 0.5,
                                      color: 'primary.main',
                                      '&:hover': { backgroundColor: 'primary.light' }
                                    }}
                                  >
                                    <RemoveIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      const currentVal = parseInt(batchRange.startChapter) || 0;
                                      const maxChapters = novel?.storyStructure?.outlineEnforcement?.chapterOutlineMap?.length || 
                                                         novel?.storyStructure?.totalChapters || 999;
                                      if (currentVal < maxChapters) {
                                        const newValue = currentVal + 1;
                                        setBatchRange({ 
                                          ...batchRange, 
                                          startChapter: newValue,
                                          endChapter: Math.max(newValue, batchRange.endChapter)
                                        });
                                      }
                                    }}
                                    sx={{ 
                                      p: 0.5,
                                      color: 'primary.main',
                                      '&:hover': { backgroundColor: 'primary.light' }
                                    }}
                                  >
                                    <AddIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </InputAdornment>
                            ),
                          }}
                        />
                        <TextField
                          label="结束章节"
                          type="text"
                          inputProps={{ 
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                            ...(novel?.storyStructure?.isGenerated && novel?.storyStructure?.totalChapters 
                              ? { max: novel.storyStructure.totalChapters } 
                              : {})
                          }}
                          value={batchRange.endChapter != null ? String(batchRange.endChapter) : ''}
                      onChange={(e) => {
                            const inputValue = e.target.value;
                            const numericOnly = inputValue.replace(/[^0-9]/g, '');
                            if (numericOnly === '') {
                              setBatchRange({ ...batchRange, endChapter: '' });
                              return;
                            }
                            const numValue = Number(numericOnly);
                            if (!isNaN(numValue) && numValue > 0) {
                              let value = Math.floor(numValue);
                              const maxChapters = novel?.storyStructure?.outlineEnforcement?.chapterOutlineMap?.length || 
                                                 novel?.storyStructure?.totalChapters || 999999;
                              if (value > maxChapters) {
                                value = maxChapters;
                                showMessage(`结束章节不能超过第${maxChapters}章`, "warning");
                              }
                              setBatchRange({ ...batchRange, endChapter: value });
                            }
                      }}
                      onBlur={(e) => {
                            const inputValue = e.target.value;
                            if (inputValue === '' || inputValue === '0') {
                              const defaultValue = batchRange.startChapter || 1;
                              setBatchRange({ ...batchRange, endChapter: defaultValue });
                              return;
                            }
                            let value = Math.floor(Number(inputValue));
                            if (isNaN(value) || value <= 0) {
                              const defaultValue = batchRange.startChapter || 1;
                              setBatchRange({ ...batchRange, endChapter: defaultValue });
                              showMessage("请输入大于0的整数", "warning");
                              return;
                            }
                            if (novel?.storyStructure?.outlineEnforcement?.chapterOutlineMap) {
                              const maxChaptersFromStructure = novel.storyStructure.outlineEnforcement.chapterOutlineMap.length;
                              if (value > maxChaptersFromStructure) {
                                value = maxChaptersFromStructure;
                                showMessage(`故事结构中共有${maxChaptersFromStructure}章大纲，已自动调整`, "warning");
                              }
                            } else if (novel?.storyStructure?.isGenerated && novel?.storyStructure?.totalChapters) {
                              const maxChaptersFromStructure = novel.storyStructure.totalChapters;
                              if (value > maxChaptersFromStructure) {
                                value = maxChaptersFromStructure;
                                showMessage(`故事结构已设定总章节数为${maxChaptersFromStructure}章，已自动调整`, "warning");
                              }
                            }
                            const chaptersWithContent = chapters.filter(ch => 
                              ch.content && ch.content.trim().length > 0
                            ).map(ch => ch.index);
                            const allGeneratedChapters = [...new Set([...chaptersGenerated, ...chaptersWithContent])];
                            const generatingChapterIndices = Array.isArray(chapterGenerationStatus) 
                              ? chapterGenerationStatus
                                .filter(ch => ch.status === '生成中' || ch.status === 'generating')
                                .map(ch => ch.chapterIndex)
                              : [];
                            if (batchRange.startChapter && value < batchRange.startChapter) {
                              value = batchRange.startChapter;
                              showMessage("结束章节不能小于起始章节", "warning");
                            }
                            if (novel?.storyStructure?.outlineEnforcement?.chapterOutlineMap) {
                              const totalChaptersInStructure = novel.storyStructure.outlineEnforcement.chapterOutlineMap.length;
                              if (value > totalChaptersInStructure) {
                                value = totalChaptersInStructure;
                                showMessage(`故事结构中共有${totalChaptersInStructure}章大纲，已自动调整`, "warning");
                              }
                            }
                            if (generatingChapterIndices.some(idx => idx >= batchRange.startChapter && idx <= value)) {
                              const conflictingChapters = generatingChapterIndices.filter(idx => idx >= batchRange.startChapter && idx <= value);
                              if (conflictingChapters.length > 0) {
                                const minConflict = Math.min(...conflictingChapters);
                                if (minConflict > batchRange.startChapter) {
                                  value = minConflict - 1;
                                  showMessage(`检测到第${minConflict}章正在生成中，已自动调整结束章节为第${value}章`, "warning");
                                } else {
                                  let nextStart = Math.max(...conflictingChapters) + 1;
                                  showMessage(`所选范围内有章节正在生成中，请从第${nextStart}章开始选择`, "warning");
                                  value = nextStart;
                                }
                              }
                            }
                        setBatchRange({ ...batchRange, endChapter: value });
                      }}
                      fullWidth
                      size="small"
                      sx={{ mb: 0 }}
                          InputProps={{
                        sx: {
                          '& .MuiInputBase-root': {
                            width: '100%',
                            minWidth: 0
                          },
                          '& .MuiInputBase-input': {
                            minWidth: '80px',
                            textAlign: 'center'
                          },
                          '& .MuiInputAdornment-root': {
                            flexShrink: 1
                          }
                        },
                            startAdornment: <InputAdornment position="start">第</InputAdornment>,
                            endAdornment: (
                          <InputAdornment position="end" sx={{ p: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
                              <Typography variant="body2" sx={{ mr: 0.25, flexShrink: 0 }}>章</Typography>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      const currentVal = parseInt(batchRange.endChapter) || 1;
                                      const minVal = parseInt(batchRange.startChapter) || 1;
                                      if (currentVal > minVal) {
                                        setBatchRange({ 
                                          ...batchRange, 
                                          endChapter: currentVal - 1
                                        });
                                      }
                                    }}
                                    sx={{ 
                                      p: 0.5,
                                      color: 'primary.main',
                                      '&:hover': { backgroundColor: 'primary.light' }
                                    }}
                                  >
                                    <RemoveIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      const currentVal = parseInt(batchRange.endChapter) || 0;
                                      const maxChapters = novel?.storyStructure?.outlineEnforcement?.chapterOutlineMap?.length || 
                                                         novel?.storyStructure?.totalChapters || 999;
                                      if (currentVal < maxChapters) {
                                        setBatchRange({ 
                                          ...batchRange, 
                                          endChapter: currentVal + 1
                                        });
                                      }
                                    }}
                                    sx={{ 
                                      p: 0.5,
                                      color: 'primary.main',
                                      '&:hover': { backgroundColor: 'primary.light' }
                                    }}
                                  >
                                    <AddIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </InputAdornment>
                            ),
                          }}
                        />
              </Box>
                  </Box>
                  
                  {/* 对话比例滑动条 */}
            <Box sx={{ mb: 2, display: 'none' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2196f3', fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>
                  💬 对话比例
                    </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(33,150,243,0.6)', fontSize: '0.7rem' }}>
                  (推荐25-35%)
                </Typography>
              </Box>
                      <Slider
                        value={batchRange.dialogueRatio || 30}
                        onChange={(e, value) => {
                          setBatchRange({ ...batchRange, dialogueRatio: value });
                        }}
                        min={15}
                        max={45}
                        step={5}
                        marks={[
                          { value: 15, label: '15%' },
                          { value: 25, label: '25%' },
                          { value: 35, label: '35%' },
                          { value: 45, label: '45%' }
                        ]}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(value) => `${value}%`}
                        sx={{
                  color: '#2196f3',
                          '& .MuiSlider-markLabel': {
                    fontSize: '0.65rem'
                          }
                        }}
                      />
                  </Box>
                  
            {/* 生成说明 */}
                  <Alert 
                    severity="info" 
                    sx={{ 
                mb: 2,
                py: 1,
                px: 1.25,
                '& .MuiAlert-message': { width: '100%' },
                '& .MuiAlertTitle-root': { fontSize: '0.8rem', fontWeight: 600 }
              }}
            >
              <Typography variant="caption" sx={{ fontSize: '0.78rem', color: 'text.secondary', display: 'block', mb: 0.5 }}>
                • 自动跳过已生成章节 &nbsp;• 自动保存
                  </Typography>
                    
              <Box sx={{ py: 0.5, px: 0.75, bgcolor: 'rgba(25, 118, 210, 0.07)', borderRadius: 1, mb: 0.5 }}>
                      {(() => {
                        const chaptersWithContent = chapters.filter(ch => 
                          ch.content && ch.content.trim().length > 0
                        ).map(ch => ch.index);
                        const allGeneratedChapters = [...new Set([...chaptersGenerated, ...chaptersWithContent])].sort((a, b) => a - b);
                        return allGeneratedChapters.length > 0 ? (
                    <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#1976d2', fontWeight: 500 }}>
                      已生成第 {formatChapterRanges(allGeneratedChapters)} 章
                            </Typography>
                        ) : null;
                      })()}
                      
                      {(() => {
                        const chaptersWithContent = chapters.filter(ch => 
                          ch.content && ch.content.trim().length > 0
                        ).map(ch => ch.index);
                        const allGeneratedChapters = [...new Set([...chaptersGenerated, ...chaptersWithContent])];
                        const maxGeneratedChapter = allGeneratedChapters.length > 0 ? Math.max(...allGeneratedChapters) : 0;
                        const generatingIndices = chapterGenerationStatus.map(ch => ch.chapterIndex);
                        const maxGeneratingChapter = generatingIndices.length > 0 ? Math.max(...generatingIndices) : 0;
                        const nextAvailableChapter = Math.max(maxGeneratedChapter, maxGeneratingChapter) + 1;
                        return (
                    <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary', display: 'block' }}>
                      下一章从第 <span style={{ color: '#1976d2', fontWeight: 600 }}>{nextAvailableChapter}</span> 章开始
                          </Typography>
                        );
                      })()}
                    </Box>
                    
              <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary', display: 'block' }}>
                      {(() => {
                        const chapterCount = (parseInt(batchRange.endChapter) - parseInt(batchRange.startChapter) + 1 || 0);
                        const requiredBalance = chapterCount * 3000;
                        const isBalanceSufficient = userBalance >= requiredBalance;
                        return (
                          <>
                      预计约 {chapterCount * 2} 分钟 &nbsp;|&nbsp;
                      <span style={{ color: isBalanceSufficient ? '#4caf50' : '#f44336', fontWeight: 600 }}>
                        消耗 {requiredBalance} 字（余额 {userBalance}）
                            </span>
                          </>
                        );
                      })()}
              </Typography>
            </Alert>
            {/* 体验卡用户醒目提示 - 放在内容区内部可滚动 */}
            {trialCheckDone && isTrialOnlyUser && (
              <Box sx={{
                mb: 2,
                p: 2,
                borderRadius: '10px',
                background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.12) 0%, rgba(255, 87, 34, 0.08) 100%)',
                border: '1px solid rgba(255, 152, 0, 0.4)',
                boxShadow: '0 0 20px rgba(255, 152, 0, 0.1)',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Box sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '8px',
                    background: 'rgba(255, 152, 0, 0.2)',
                    border: '1px solid rgba(255, 152, 0, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <WarningAmberIcon sx={{ fontSize: 18, color: '#ff9800' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 700, color: '#ff9800', fontSize: '0.88rem', mb: 0.5 }}>
                      温馨提示：体验卡章节上限为10章
                    </Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.75)', lineHeight: 1.6, mb: 1 }}>
                      本功能仅供您体验我们的AI小说创作服务。我们专注长篇小说，正式用户创作均<strong style={{ color: '#4caf50' }}>100章起步</strong>。<strong style={{ color: '#ff9800' }}>体验卡的10章内容无法用于番茄发文</strong>，详情请点击查看番茄发文规则。
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setTomatoRulesDialogOpen(true)}
                      startIcon={<InfoOutlinedIcon sx={{ fontSize: '16px !important' }} />}
                      sx={{
                        fontSize: '0.72rem',
                        py: 0.5,
                        px: 1.5,
                        borderColor: 'rgba(255, 152, 0, 0.5)',
                        color: '#ff9800',
                        borderRadius: '6px',
                        '&:hover': {
                          borderColor: '#ff9800',
                          backgroundColor: 'rgba(255, 152, 0, 0.08)',
                        },
                      }}
                    >
                      查看番茄发文规则
                    </Button>
                  </Box>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions
            sx={{
              p: {xs: 1.5, sm: 2},
              pt: 1,
              pb: {xs: 2, sm: 2},
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              gap: {xs: 1.5, sm: 2}
            }}
          >
            <Button 
              onClick={() => setBatchDialogOpen(false)} 
              variant="contained"
              startIcon={<CancelIcon />}
              size="small"
              sx={{ 
                minWidth: { xs: '100px', sm: '120px' },
                flex: 1,
                borderRadius: '6px',
                backgroundColor: 'rgba(255,255,255,0.08)',
                color: 'rgba(255,255,255,0.75)',
                border: '1px solid rgba(255,255,255,0.2)',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.12)',
                  borderColor: 'rgba(255,255,255,0.3)',
                  boxShadow: 'none',
                }
              }}
            >
              取消
              </Button>
              <Button
                onClick={() => {
                  setBatchDialogOpen(false);
                  executeBackgroundBatchGeneration();
                }}
                color="primary"
                variant="contained"
              startIcon={<CloudUploadIcon />}
              size="small"
              disabled={(() => {
                const chapterCount = (parseInt(batchRange.endChapter) - parseInt(batchRange.startChapter) + 1 || 0);
                const requiredBalance = chapterCount * 2200;
                return userBalance < requiredBalance || isGenerating || batchGenerating;
              })()}
              sx={{ 
                minWidth: '120px',
                backgroundImage: 'linear-gradient(to right, #1976d2, #2196f3)',
                boxShadow: '0 2px 8px rgba(33,150,243,0.3)'
              }}
              >
              后台批量生成
              </Button>
          </DialogActions>
        </Dialog>

        {/* 番茄发文规则详情弹窗 */}
        <Dialog
          open={tomatoRulesDialogOpen}
          onClose={() => setTomatoRulesDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              background: 'linear-gradient(135deg, rgba(8, 14, 28, 0.98) 0%, rgba(10, 18, 35, 0.98) 100%)',
              backdropFilter: 'blur(24px)',
              border: '1px solid rgba(255, 152, 0, 0.3)',
              borderRadius: '14px !important',
              boxShadow: '0 12px 40px rgba(0,0,0,0.6), 0 0 30px rgba(255, 152, 0, 0.08)',
              overflow: 'hidden',
              '@media (max-width: 600px)': {
                marginBottom: '70px !important',
                marginTop: 'auto !important',
                maxHeight: 'calc(100vh - 140px) !important',
              }
            }
          }}
        >
          <Box sx={{
            p: { xs: 2, sm: 2.5 },
            background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.12) 0%, rgba(255, 87, 34, 0.06) 100%)',
            borderBottom: '1px solid rgba(255, 152, 0, 0.15)',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}>
            <Box sx={{
              width: 36,
              height: 36,
              borderRadius: '10px',
              background: 'rgba(255, 152, 0, 0.12)',
              border: '1px solid rgba(255, 152, 0, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <MenuBookIcon sx={{ fontSize: 18, color: '#ff9800' }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: '#ff9800', fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
              番茄小说发文规则
            </Typography>
          </Box>
          <DialogContent sx={{ pt: 2, px: { xs: 2, sm: 3 }, pb: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* 发布门槛 */}
              <Box sx={{
                p: 2,
                borderRadius: '10px',
                background: 'rgba(255, 255, 255, 0.02)',
                border: '1px solid rgba(255, 255, 255, 0.08)'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#4caf50', boxShadow: '0 0 6px rgba(76, 175, 80, 0.6)' }} />
                  <Typography sx={{ fontWeight: 700, color: '#4caf50', fontSize: '0.88rem' }}>
                    发布门槛
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)' }}>最低发布字数</Typography>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#fff' }}>1000字</Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', lineHeight: 1.5 }}>
                    需满1000字才能在番茄小说平台发布作品
                  </Typography>
                </Box>
              </Box>

              {/* 签约门槛 */}
              <Box sx={{
                p: 2,
                borderRadius: '10px',
                background: 'rgba(255, 152, 0, 0.05)',
                border: '1px solid rgba(255, 152, 0, 0.15)'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ff9800', boxShadow: '0 0 6px rgba(255, 152, 0, 0.6)' }} />
                  <Typography sx={{ fontWeight: 700, color: '#ff9800', fontSize: '0.88rem' }}>
                    签约门槛
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)' }}>签约申请字数</Typography>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#fff' }}>2万字</Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', lineHeight: 1.5 }}>
                    作品满2万字后可主动申请签约，或满2万字3天后自动进入签约流程。签约后作品才能获得平台推荐、打赏和稿费
                  </Typography>
                </Box>
              </Box>

              {/* 推荐评估 */}
              <Box sx={{
                p: 2,
                borderRadius: '10px',
                background: 'rgba(255, 255, 255, 0.02)',
                border: '1px solid rgba(255, 255, 255, 0.08)'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#2196f3', boxShadow: '0 0 6px rgba(33, 150, 243, 0.6)' }} />
                  <Typography sx={{ fontWeight: 700, color: '#2196f3', fontSize: '0.88rem' }}>
                    推荐评估（8万字之前是不会有读者能看到的）
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)' }}>评估申请字数</Typography>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#fff' }}>8万字</Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', lineHeight: 1.5 }}>
                    作品达8万字后可主动发起推荐评估申请，评估结果1-3天反馈。8-15万字期间可随时重新发起评估，只有发起评估后才会有读者能看到你的作品
                  </Typography>
                </Box>
              </Box>

              {/* 首秀（验证期） */}
              <Box sx={{
                p: 2,
                borderRadius: '10px',
                background: 'rgba(255, 255, 255, 0.02)',
                border: '1px solid rgba(255, 255, 255, 0.08)'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#9c27b0', boxShadow: '0 0 6px rgba(156, 39, 176, 0.6)' }} />
                  <Typography sx={{ fontWeight: 700, color: '#9c27b0', fontSize: '0.88rem' }}>
                    首秀（验证期）
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)' }}>首秀字数要求</Typography>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#fff' }}>10万字</Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', lineHeight: 1.5 }}>
                    评估通过后进入7天小流量验证期，验证期结束且字数满10万字后进入正式推荐。验证期需稳定日更4000字以上
                  </Typography>
                </Box>
              </Box>

              {/* 总结提示 */}
              <Box sx={{
                p: 2,
                borderRadius: '10px',
                background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.08) 0%, rgba(5, 150, 105, 0.04) 100%)',
                border: '1px solid rgba(6, 182, 212, 0.15)'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <InfoOutlinedIcon sx={{ fontSize: 18, color: '#06b6d4', flexShrink: 0, mt: 0.25 }} />
                  <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.8)', lineHeight: 1.6 }}>
                    <strong style={{ color: '#06b6d4' }}>总结：</strong>番茄小说签约需<strong style={{ color: '#fff' }}>2万字</strong>，正式推荐需<strong style={{ color: '#fff' }}>10万字+</strong>。我们专注长篇小说创作，正式用户均<strong style={{ color: '#4caf50' }}>100章起步</strong>（约20-30万字），完全满足番茄小说的签约和推荐要求，体验卡仅10章内容无法满足任何发文需求。
                  </Typography>
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, borderTop: '1px solid rgba(255, 152, 0, 0.1)', justifyContent: 'center' }}>
            <Button
              onClick={() => setTomatoRulesDialogOpen(false)}
              variant="contained"
              sx={{
                minWidth: { xs: '120px', sm: '140px' },
                borderRadius: '8px',
                background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.2) 0%, rgba(255, 87, 34, 0.1) 100%)',
                color: '#ff9800',
                border: '1px solid rgba(255, 152, 0, 0.3)',
                fontWeight: 600,
                '&:hover': {
                  background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.3) 0%, rgba(255, 87, 34, 0.15) 100%)',
                  borderColor: 'rgba(255, 152, 0, 0.5)',
                }
              }}
            >
              我知道了
            </Button>
          </DialogActions>
        </Dialog>

        {/* 导出确认对话框 */}
        <Dialog
          open={exportDialogOpen}
          onClose={() => { if (!exporting) setExportDialogOpen(false); }}
          disableEscapeKeyDown={exporting}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              background: 'linear-gradient(135deg, rgba(8, 14, 28, 0.98) 0%, rgba(10, 18, 35, 0.98) 100%)',
              backdropFilter: 'blur(24px)',
              border: '1px solid rgba(6, 182, 212, 0.2)',
              borderRadius: '14px !important',
              boxShadow: '0 12px 40px rgba(0,0,0,0.6), 0 0 30px rgba(16,185,129,0.08)',
              overflow: 'hidden',
              // 移动端底部留空间给固定导航栏
              '@media (max-width: 600px)': {
                marginBottom: '70px !important',
                marginTop: 'auto !important',
                maxHeight: 'calc(100vh - 140px) !important',
              }
            }
          }}
        >
          <Box sx={{
            p: { xs: 2, sm: 2.5 },
            background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.12) 0%, rgba(5, 150, 105, 0.06) 100%)',
            borderBottom: '1px solid rgba(6, 182, 212, 0.15)',
              display: 'flex', 
              alignItems: 'center',
            gap: 1.5
          }}>
            <Box sx={{
              width: 36,
              height: 36,
              borderRadius: '10px',
              background: 'rgba(6, 182, 212, 0.12)',
              border: '1px solid rgba(6, 182, 212, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: 'outlineIconPulse 2.5s ease-in-out infinite'
            }}>
              <FileDownloadIcon sx={{ fontSize: 18, color: '#06b6d4' }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: '#06b6d4', fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
            导出Word文档
            </Typography>
          </Box>
          <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Box>
              {/* 统计信息 */}
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: 2,
                  mb: 2.5,
                  borderRadius: '10px',
                  background: 'rgba(6, 182, 212, 0.04)',
                  border: '1px solid rgba(6, 182, 212, 0.15)',
                  animation: 'sectionReveal 0.35s ease both'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#06b6d4', boxShadow: '0 0 6px rgba(16,185,129,0.6)' }} />
                  <Typography sx={{ fontWeight: 700, color: '#06b6d4', fontSize: '0.88rem' }}>
                  导出统计
                </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.5)' }}>小说标题：</Typography>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: 'rgba(255,255,255,0.85)' }}>
                      {title || '未命名小说'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.5)' }}>可导出章节：</Typography>
                    <Chip
                      label={`${chapters.filter(ch => (ch.content && ch.content.trim()) || ch.hasContent).length} 章`}
                      size="small"
                      sx={{ 
                        fontWeight: 700,
                        fontSize: '0.78rem',
                        background: 'rgba(6, 182, 212, 0.15)',
                        color: '#06b6d4',
                        border: '1px solid rgba(16,185,129,0.3)'
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.5)' }}>总字数：</Typography>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#06b6d4' }}>
                      {chapters.filter(ch => (ch.content && ch.content.trim()) || ch.hasContent).reduce((sum, ch) => sum + (ch.content ? ch.content.length : (ch.contentLength || 0)), 0).toLocaleString()} 字
                    </Typography>
                  </Box>

                  {outline && outline.trim() && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.5)' }}>大纲字数：</Typography>
                      <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.45)' }}>{outline.length.toLocaleString()} 字</Typography>
                    </Box>
                  )}
                </Box>
              </Paper>

              {/* 导出选项 */}
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: 2,
                  borderRadius: '10px',
                  background: 'rgba(6, 182, 212, 0.03)',
                  border: '1px solid rgba(6, 182, 212, 0.1)',
                  animation: 'sectionReveal 0.35s ease both 0.1s'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#06b6d4', boxShadow: '0 0 6px rgba(16,185,129,0.6)' }} />
                  <Typography sx={{ fontWeight: 700, color: '#06b6d4', fontSize: '0.88rem' }}>
                  导出选项
                </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={exportOptions.includeOutline}
                        onChange={(e) => setExportOptions({
                          ...exportOptions,
                          includeOutline: e.target.checked
                        })}
                        sx={{
                          color: '#06b6d4',
                          '&.Mui-checked': {
                            color: '#06b6d4',
                          },
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" fontWeight="500">
                          包含小说大纲
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {outline && outline.trim() 
                            ? `将在文档开头包含大纲内容（${outline.length}字）` 
                            : '暂无大纲内容'}
                        </Typography>
                      </Box>
                    }
                    disabled={!outline || !outline.trim()}
                  />
                  
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={exportOptions.includeStats}
                        onChange={(e) => setExportOptions({
                          ...exportOptions,
                          includeStats: e.target.checked
                        })}
                        sx={{
                          color: '#06b6d4',
                          '&.Mui-checked': {
                            color: '#06b6d4',
                          },
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" fontWeight="500">
                          包含统计信息
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          在文档末尾添加章节数、字数等统计信息
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              </Paper>

              {/* 提示信息 */}
              <Alert 
                severity="info" 
                sx={{ 
                  mt: 2,
                  borderRadius: '8px',
                  '& .MuiAlert-icon': {
                    color: '#06b6d4'
                  }
                }}
              >
                <Typography variant="body2">
                  导出的Word文档将包含所有已生成的章节内容，采用标准小说格式排版
                </Typography>
              </Alert>

              {/* 🔥 导出进度：加载正文/生成文档期间持续显示，防止用户误以为无响应 */}
              {exporting && (
                <Box sx={{ mt: 2, p: 2, borderRadius: '10px', background: 'rgba(6, 182, 212, 0.08)', border: '1px solid rgba(6, 182, 212, 0.25)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <CircularProgress size={16} sx={{ color: '#06b6d4' }} />
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#06b6d4' }}>
                      {exportProgress.current >= 0 && exportProgress.total > 0
                        ? `正在加载章节正文（${exportProgress.current}/${exportProgress.total}），请稍候...`
                        : '正在生成Word文档，请稍候...'}
                    </Typography>
                  </Box>
                  {exportProgress.current >= 0 && exportProgress.total > 0 && (
                    <LinearProgress
                      variant="determinate"
                      value={Math.round((exportProgress.current / exportProgress.total) * 100)}
                      sx={{ height: 6, borderRadius: 3, backgroundColor: 'rgba(255,255,255,0.08)', '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #06b6d4, #059669)' } }}
                    />
                  )}
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: { xs: 2, sm: 2.5 }, borderTop: '1px solid rgba(16,185,129,0.1)', gap: 1 }}>
            <Button
              onClick={() => setExportDialogOpen(false)}
              variant="outlined"
              disabled={exporting}
              sx={{
                borderColor: 'rgba(255,255,255,0.12)',
                color: 'rgba(255,255,255,0.5)',
                borderRadius: '10px',
                fontWeight: 600,
                '&:hover': { borderColor: 'rgba(255,255,255,0.25)', backgroundColor: 'rgba(255,255,255,0.05)' }
              }}
            >
              取消
            </Button>
            <Button
              onClick={handleConfirmExport}
              variant="contained"
              startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <FileDownloadIcon />}
              disabled={exporting || chapters.filter(ch => (ch.content && ch.content.trim()) || ch.hasContent).length === 0}
              sx={{ 
                borderRadius: '10px',
                background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.9) 0%, rgba(5, 150, 105, 0.9) 100%)',
                boxShadow: '0 0 16px rgba(6, 182, 212, 0.25), 0 2px 8px rgba(0,0,0,0.2)',
                fontWeight: 700,
                px: 3,
                py: 1,
                '&:hover': {
                  background: 'linear-gradient(135deg, rgba(6, 182, 212, 1) 0%, rgba(5, 150, 105, 1) 100%)',
                  boxShadow: '0 0 24px rgba(6, 182, 212, 0.4), 0 4px 12px rgba(0,0,0,0.25)',
                  transform: 'translateY(-1px)'
                },
                '&.Mui-disabled': {
                  background: 'rgba(16,185,129,0.2)',
                  color: 'rgba(255,255,255,0.3)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              {exporting ? '正在导出...' : '确认导出'}
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* 提示框组件 - 高科技风格 */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          className="NexusToast-root"
        >
          <div
            className={`NexusToast-card NexusToast-${snackbar.severity}`}
            onClick={() => setSnackbar(prev => ({ ...prev, open: false }))}
          >
            <div className="NexusToast-iconWrap">
              {snackbar.severity === 'success' && <CheckCircleIcon sx={{ fontSize: 20 }} />}
              {snackbar.severity === 'error' && <ErrorIcon sx={{ fontSize: 20 }} />}
              {snackbar.severity === 'warning' && <WarningAmberIcon sx={{ fontSize: 20 }} />}
              {snackbar.severity === 'info' && <InfoIcon sx={{ fontSize: 20 }} />}
            </div>
            <div className="NexusToast-body">
              <span className="NexusToast-message">{snackbar.message}</span>
            </div>
            <IconButton
              size="small"
              className="NexusToast-closeBtn"
              onClick={(e) => {
                e.stopPropagation();
                setSnackbar(prev => ({ ...prev, open: false }));
              }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </div>
        </Snackbar>
        
        
        {/* 批量生成确认对话框 - 使用confirmDialogProps */}
        <Dialog
          open={confirmDialogProps.open || false}
          onClose={() => setConfirmDialogProps({ ...confirmDialogProps, open: false })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
            {confirmDialogProps.title || "确认操作"}
          </DialogTitle>
          <DialogContent sx={{ pt: 2, mt: 2 }}>
            {confirmDialogProps.content}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button 
              onClick={() => {
                if (confirmDialogProps.onCancel) {
                  confirmDialogProps.onCancel();
                } else {
                  setConfirmDialogProps({ ...confirmDialogProps, open: false });
                }
              }} 
              color="inherit"
            >
              {confirmDialogProps.cancelText || "取消"}
            </Button>
            <Button 
              onClick={() => {
                if (confirmDialogProps.onConfirm) {
                  confirmDialogProps.onConfirm();
                } else {
                  setConfirmDialogProps({ ...confirmDialogProps, open: false });
                }
              }} 
              variant="contained" 
              color="primary"
              autoFocus
            >
              {confirmDialogProps.confirmText || "确认"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* 🔥 故事结构生成确认对话框 - 高科技指挥中心风格 */}
        <Dialog
          open={storyStructConfirmOpen}
          onClose={() => setStoryStructConfirmOpen(false)}
          maxWidth="sm"
          fullWidth
          className="ssp-story-dialog"
          PaperProps={{
            sx: {
              background: 'linear-gradient(135deg, rgba(8, 14, 28, 0.98) 0%, rgba(10, 18, 35, 0.98) 100%)',
              backdropFilter: 'blur(24px)',
              border: '1px solid rgba(168, 85, 247, 0.25)',
              borderRadius: '10px !important',
              boxShadow: '0 12px 40px rgba(0,0,0,0.6), 0 0 30px rgba(168,85,247,0.08)',
              overflow: 'hidden',
              width: { xs: 'calc(100% - 16px)', sm: '100%' },
              maxWidth: { xs: '100%', sm: 'sm' },
              mx: { xs: 1, sm: 'auto' },
              my: { xs: 4, sm: 'auto' },
              '@media (max-width: 600px)': {
                marginBottom: 'calc(70px + env(safe-area-inset-bottom, 0px)) !important',
                marginTop: 'auto !important',
                maxHeight: 'calc(100vh - 170px)',
                '@supports (height: 100dvh)': {
                  maxHeight: 'calc(100dvh - 130px - env(safe-area-inset-bottom, 0px))'
                },
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }
            }
          }}
        >
          {/* 头部 */}
          <Box sx={{
            p: { xs: 1.25, sm: 1.5 },
            background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.15) 0%, rgba(139, 92, 246, 0.08) 100%)',
            borderBottom: '1px solid rgba(168, 85, 247, 0.2)',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <Box sx={{
              width: 28,
              height: 28,
              borderRadius: '8px',
              background: 'rgba(168, 85, 247, 0.15)',
              border: '1px solid rgba(168, 85, 247, 0.35)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M7 1L1 4V10L7 13L13 10V4L7 1Z" stroke="#a855f7" strokeWidth="1.2" fill="rgba(168,85,247,0.2)" />
                <path d="M7 1V13M1 4L13 10M13 4L1 10" stroke="#a855f7" strokeWidth="0.8" opacity="0.5" />
              </svg>
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, color: '#a855f7', fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
                生成故事结构确认
              </Typography>
            </Box>
          </Box>

          <DialogContent sx={{
            p: { xs: 1.5, sm: 2 },
            pt: { xs: 1, sm: 1.5 },
            '@media (max-width: 600px)': {
              flex: 1,
              overflow: 'auto',
              minHeight: 0,
            }
          }}>
            {/* 体验卡用户醒目提示 - 放在内容区内部可滚动 */}
            {trialCheckDone && isTrialOnlyUser && (
              <Box sx={{
                mb: 1.5,
                p: 2,
                borderRadius: '10px',
                background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.12) 0%, rgba(255, 87, 34, 0.08) 100%)',
                border: '1px solid rgba(255, 152, 0, 0.4)',
                boxShadow: '0 0 20px rgba(255, 152, 0, 0.1)',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Box sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '8px',
                    background: 'rgba(255, 152, 0, 0.2)',
                    border: '1px solid rgba(255, 152, 0, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <WarningAmberIcon sx={{ fontSize: 18, color: '#ff9800' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 700, color: '#ff9800', fontSize: '0.88rem', mb: 0.5 }}>
                      温馨提示：体验卡章节上限为10章
                    </Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.75)', lineHeight: 1.6, mb: 1 }}>
                      本功能仅供您体验我们的AI小说创作服务。我们专注长篇小说，正式用户均<strong style={{ color: '#4caf50' }}>100章起步</strong>。10章试生成只是为了能看到我们软件的完整功能。点击查看番茄发文规则了解发布要求。
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setTomatoRulesDialogOpen(true)}
                      startIcon={<InfoOutlinedIcon sx={{ fontSize: '16px !important' }} />}
                      sx={{
                        fontSize: '0.72rem',
                        py: 0.5,
                        px: 1.5,
                        borderColor: 'rgba(255, 152, 0, 0.5)',
                        color: '#ff9800',
                        borderRadius: '6px',
                        '&:hover': {
                          borderColor: '#ff9800',
                          backgroundColor: 'rgba(255, 152, 0, 0.08)',
                        },
                      }}
                    >
                      查看番茄发文规则
                    </Button>
                  </Box>
                </Box>
              </Box>
            )}
            {/* 生成规模信息卡 - 移动端三行紧凑 */}
            <Box sx={{
              background: 'rgba(168, 85, 247, 0.06)',
              border: '1px solid rgba(168, 85, 247, 0.15)',
              borderRadius: '10px',
              p: { xs: 1, sm: 1.5 },
              mb: 1.5
            }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: { xs: 0.75, sm: 1 }, alignItems: 'center' }}>
                {/* 章节数 */}
                <Box sx={{
                  background: 'rgba(0, 217, 255, 0.07)',
                  border: '1px solid rgba(0, 217, 255, 0.15)',
                  borderRadius: '8px',
                  p: { xs: '8px 10px', sm: 1.5 },
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1
                }}>
                  <Typography sx={{ fontSize: '1.3rem', fontWeight: 800, color: '#00d9ff', lineHeight: 1 }}>
                    {storyStructConfirmData.chapterCount}
                  </Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: 'rgba(0, 217, 255, 0.7)', lineHeight: 1 }}>
                    章
                  </Typography>
                </Box>
                {/* 需要预留 */}
                <Box sx={{
                  background: 'rgba(255, 215, 0, 0.07)',
                  border: '1px solid rgba(255, 215, 0, 0.15)',
                  borderRadius: '8px',
                  p: { xs: '8px 10px', sm: 1.5 },
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1
                }}>
                  <Typography sx={{ fontSize: '1.3rem', fontWeight: 800, color: '#ffd700', lineHeight: 1 }}>
                    {storyStructConfirmData.requiredBalance >= 1000
                      ? `${(storyStructConfirmData.requiredBalance / 1000).toFixed(0)}K`
                      : storyStructConfirmData.requiredBalance}
                  </Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255, 215, 0, 0.7)', lineHeight: 1 }}>
                    字
                  </Typography>
                </Box>
                {/* 当前余额 */}
                <Box sx={{
                  background: storyStructConfirmData.userBalance >= storyStructConfirmData.requiredBalance
                    ? 'rgba(0, 255, 136, 0.07)'
                    : 'rgba(255, 68, 68, 0.07)',
                  border: storyStructConfirmData.userBalance >= storyStructConfirmData.requiredBalance
                    ? '1px solid rgba(0, 255, 136, 0.2)'
                    : '1px solid rgba(255, 68, 68, 0.2)',
                  borderRadius: '8px',
                  p: { xs: '8px 10px', sm: 1.5 },
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1
                }}>
                  <Typography sx={{
                    fontSize: storyStructConfirmData.userBalance >= 100000 ? '1.1rem' : '1.3rem',
                    fontWeight: 800,
                    color: storyStructConfirmData.userBalance >= storyStructConfirmData.requiredBalance ? '#00ff88' : '#ff6b6b',
                    lineHeight: 1
                  }}>
                    {storyStructConfirmData.userBalance >= 1000000
                      ? `${(storyStructConfirmData.userBalance / 10000).toFixed(0)}W`
                      : storyStructConfirmData.userBalance >= 1000
                        ? `${(storyStructConfirmData.userBalance / 1000).toFixed(0)}K`
                        : storyStructConfirmData.userBalance}
                  </Typography>
                  <Typography sx={{
                    fontSize: '0.72rem',
                    color: storyStructConfirmData.userBalance >= storyStructConfirmData.requiredBalance
                      ? 'rgba(0, 255, 136, 0.7)'
                      : 'rgba(255, 68, 68, 0.7)',
                    lineHeight: 1
                  }}>
                    余额
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* 时间评估卡 */}
            <Box sx={{
              background: 'rgba(255, 215, 0, 0.05)',
              border: '1px solid rgba(255, 215, 0, 0.18)',
              borderRadius: '10px',
              p: { xs: 1.25, sm: 1.5 },
              mb: 1.5,
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* 左侧装饰条 */}
              <Box sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: '3px',
                background: 'linear-gradient(180deg, #ffd700, #ff6b35)',
                borderRadius: '3px 0 0 3px'
              }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                  <circle cx="7" cy="7" r="5.5" stroke="#ffd700" strokeWidth="1.2" />
                  <path d="M7 3.5V7L9.5 8.5" stroke="#ffd700" strokeWidth="1.2" strokeLinecap="round" />
                </svg>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#ffd700' }}>
                  预计耗时
                </Typography>
              </Box>
              <Typography sx={{
                fontSize: { xs: '1.2rem', sm: '1.4rem' },
                fontWeight: 900,
                color: '#ffd700',
                textShadow: '0 0 16px rgba(255, 215, 0, 0.4)',
                lineHeight: 1.1,
                mb: 0.5
              }}>
                约 {(() => {
                  const ch = storyStructConfirmData.chapterCount;
                  if (ch <= 0) return '1';
                  // 基于实测数据：固定开销240秒 + 每章分段计算
                  // <50章: 每章50秒 | ≥50章: 每章22秒
                  const baseOverhead = 240;
                  const perChapter = ch < 50 ? 50 : 22;
                  return Math.ceil((baseOverhead + ch * perChapter) / 60);
                })()} 分钟
              </Typography>
              <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255, 255, 255, 0.4)', lineHeight: 1.4 }}>
                包含大纲分析、多轮AI生成、章节细纲生成、一致性校验等多个环节
              </Typography>
            </Box>

            {/* 重要提示卡 */}
            <Box sx={{
              background: 'rgba(255, 107, 53, 0.05)',
              border: '1px solid rgba(255, 107, 53, 0.15)',
              borderRadius: '10px',
              p: { xs: 1.25, sm: 1.5 }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                  <path d="M7 1L1 4V10L7 13L13 10V4L7 1Z" stroke="#ff6b35" strokeWidth="1.2" />
                  <path d="M7 6V8" stroke="#ff6b35" strokeWidth="1.2" strokeLinecap="round" />
                  <circle cx="7" cy="9.5" r="0.7" fill="#ff6b35" />
                </svg>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#ff6b35' }}>
                  重要提示
                </Typography>
              </Box>
              <Box sx={{ pl: 2 }}>
                <Typography component="div" sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', mb: 0.4, lineHeight: 1.5, '&::before': { content: '"•"', color: '#ff6b35', mr: 0.75 } }}>
                  将一次性生成<strong style={{ color: '#fff' }}>所有{storyStructConfirmData.chapterCount}章的细纲</strong>
                </Typography>
                <Typography component="div" sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', mb: 0.4, lineHeight: 1.5, '&::before': { content: '"•"', color: '#ff6b35', mr: 0.75 } }}>
                  可<strong style={{ color: '#fff' }}>关闭页面</strong>，系统后台继续处理
                </Typography>
                <Typography component="div" sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1.5, '&::before': { content: '"•"', color: '#ff6b35', mr: 0.75 } }}>
                  完成时余额自动扣除
                </Typography>
              </Box>
            </Box>
          </DialogContent>

          <DialogActions sx={{
            p: { xs: 1.25, sm: 1.5 },
            pt: 0,
            gap: 1,
            flexDirection: 'row !important',
            display: 'flex !important',
            '& > *': { flex: '1 1 0 !important', minWidth: 0, width: 'auto !important', margin: '0 !important' }
          }}>
            <Button
              onClick={() => setStoryStructConfirmOpen(false)}
              className="assist-chapter-ai-glow-btn"
              sx={{
                py: 1.25,
                borderRadius: '8px',
                color: '#00e5ff !important',
                fontSize: { xs: '0.8rem', sm: '0.88rem' },
                fontWeight: 600,
                textTransform: 'none',
                animation: 'assist-chapter-ai-breath 2.75s ease-in-out infinite'
              }}
            >
              取消
            </Button>
            <Button
              className="assist-chapter-ai-glow-btn"
              onClick={() => {
                setStoryStructConfirmOpen(false);
                handleGenerateStoryStructureImpl();
              }}
              sx={{
                py: 1.25,
                borderRadius: '8px',
                color: '#00e5ff !important',
                fontSize: { xs: '0.8rem', sm: '0.88rem' },
                fontWeight: 700,
                textTransform: 'none',
                animation: 'assist-chapter-ai-breath 2.75s ease-in-out infinite'
              }}
            >
              确认生成
            </Button>
          </DialogActions>
        </Dialog>

        {/* 通用对话框 */}
        <Dialog
          open={dialog.open}
          onClose={() => dialog.onCancel?.()}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{
            sx: {
              width: '90%',
              maxWidth: '520px',
              borderRadius: '16px',
              p: 0,
              background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.05) 0%, rgba(33, 150, 243, 0.05) 100%)',
              border: '1px solid rgba(25, 118, 210, 0.2)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
            }
          }}
        >
          <DialogTitle 
            id="alert-dialog-title" 
            sx={{ 
              pb: 2,
              pt: 3,
              px: 3,
              fontSize: '1.25rem',
              fontWeight: 600,
              borderBottom: '1px solid rgba(25, 118, 210, 0.1)'
            }}
          >
            {dialog.title}
          </DialogTitle>
          <DialogContent sx={{ whiteSpace: 'pre-line', px: 3, py: 3 }}>
            <DialogContentText 
              id="alert-dialog-description"
              sx={{
                fontSize: '0.95rem',
                lineHeight: 1.8,
                color: 'text.primary',
                '& strong': {
                  color: 'primary.main'
                }
              }}
            >
              {dialog.content}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1.5 }}>
            {dialog.cancelText && (
              <Button
                onClick={() => dialog.onCancel?.()}
                variant="outlined"
                sx={{
                  borderRadius: '8px',
                  px: 3,
                  color: 'text.secondary',
                  borderColor: 'rgba(0, 0, 0, 0.12)',
                  '&:hover': {
                    borderColor: 'rgba(0, 0, 0, 0.3)',
                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                {dialog.cancelText}
              </Button>
            )}
            <Button
              onClick={() => dialog.onConfirm?.()}
              variant="contained"
              autoFocus
              sx={{
                borderRadius: '8px',
                px: 3,
                background: 'linear-gradient(135deg, #1976d2 0%, #2196f3 100%)',
                boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
                color: 'white',
                fontWeight: 600,
                '&:hover': {
                  background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)'
                }
              }}
            >
              {dialog.confirmText}
            </Button>
          </DialogActions>
        </Dialog>

        {/* 🔥 章节细纲编辑对话框 */}
        <Dialog
          open={chapterOutlineEdit.open}
          onClose={() => !chapterOutlineEdit.saving && setChapterOutlineEdit({ ...chapterOutlineEdit, open: false })}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon />
            编辑第{chapterOutlineEdit.chapterNumber}章细纲
          </DialogTitle>
          <DialogContent sx={{ pt: 3, mt: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={6}
              value={chapterOutlineEdit.editedContent}
              onChange={(e) => {
                const newValue = e.target.value;
                if (newValue.length <= 1200) {
                  setChapterOutlineEdit({ ...chapterOutlineEdit, editedContent: newValue });
                }
              }}
              label="章节细纲"
              helperText={`${chapterOutlineEdit.editedContent.length}/1200字${chapterOutlineEdit.editedContent.length >= 1200 ? ' - 已达到字数上限' : ''}`}
              error={chapterOutlineEdit.editedContent.length >= 1200}
              disabled={chapterOutlineEdit.saving}
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => setChapterOutlineEdit({ ...chapterOutlineEdit, open: false })}
              disabled={chapterOutlineEdit.saving}
              color="inherit"
            >
              取消
            </Button>
            <Button
              onClick={async () => {
                setChapterOutlineEdit({ ...chapterOutlineEdit, saving: true });
                const result = await saveChapterOutline(
                  chapterOutlineEdit.chapterNumber,
                  chapterOutlineEdit.editedContent
                );
                if (result.success) {
                  setChapterOutlineEdit({ ...chapterOutlineEdit, open: false, saving: false });
                } else {
                  setChapterOutlineEdit({ ...chapterOutlineEdit, saving: false });
                }
              }}
              variant="contained"
              disabled={chapterOutlineEdit.saving || chapterOutlineEdit.editedContent.trim().length === 0}
              startIcon={chapterOutlineEdit.saving ? <CircularProgress size={20} /> : <SaveIcon />}
            >
              {chapterOutlineEdit.saving ? '保存中...' : '保存'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* 🔥 章节标题编辑对话框 */}
        <Dialog
          open={chapterTitleDialog.open}
          onClose={(_, reason) => {
            if (reason === 'backdropClick') return;
            if (!chapterTitleDialog.saving) {
              setChapterTitleDialog({ ...chapterTitleDialog, open: false });
            }
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon />
            编辑第{chapterTitleDialog.chapterNumber}章标题
          </DialogTitle>
          <DialogContent sx={{ pt: 3, mt: 2 }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              只能修改冒号后面的内容，"第{chapterTitleDialog.chapterNumber}章："格式保持不变。
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography 
                variant="body1" 
                sx={{ 
                  fontWeight: 600, 
                  color: 'text.primary',
                  whiteSpace: 'nowrap'
                }}
              >
                {chapterTitleDialog.prefix}
              </Typography>
              <TextField
                fullWidth
                value={chapterTitleDialog.editedTitle}
                onChange={(e) => {
                  setChapterTitleDialog({ 
                    ...chapterTitleDialog, 
                    editedTitle: e.target.value 
                  });
                }}
                placeholder="请输入章节标题"
                disabled={chapterTitleDialog.saving}
                sx={{ flex: 1 }}
              />
            </Box>
            <Typography variant="caption" color="textSecondary">
              完整标题：{chapterTitleDialog.prefix}{chapterTitleDialog.editedTitle}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => setChapterTitleDialog({ ...chapterTitleDialog, open: false })}
              disabled={chapterTitleDialog.saving}
              color="inherit"
            >
              取消
            </Button>
            <Button
              onClick={async () => {
                if (!chapterTitleDialog.editedTitle.trim()) {
                  showMessage('章节标题不能为空', 'warning');
                  return;
                }

                setChapterTitleDialog({ ...chapterTitleDialog, saving: true });
                
                // 组合完整标题
                const fullTitle = chapterTitleDialog.prefix + chapterTitleDialog.editedTitle.trim();
                
                // 保存标题
                const result = await saveChapterTitle(chapterTitleDialog.chapterIndex, fullTitle);
                
                if (result.success) {
                  showMessage('章节标题已保存', 'success');
                  setChapterTitleDialog({ ...chapterTitleDialog, open: false, saving: false });
                } else {
                  showMessage(`保存失败: ${result.error || '未知错误'}`, 'error');
                  setChapterTitleDialog({ ...chapterTitleDialog, saving: false });
                }
              }}
              variant="contained"
              disabled={chapterTitleDialog.saving || !chapterTitleDialog.editedTitle.trim()}
              startIcon={chapterTitleDialog.saving ? <CircularProgress size={20} /> : <SaveIcon />}
            >
              {chapterTitleDialog.saving ? '保存中...' : '保存'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* 🔥 章节内容编辑对话框 */}
        <Dialog
          open={chapterContentDialog.open}
          onClose={(_, reason) => {
            if (reason === 'backdropClick') return;
            if (!chapterContentDialog.saving) {
              setChapterContentDialog({ ...chapterContentDialog, open: false });
            }
          }}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon />
            编辑第{chapterContentDialog.chapterNumber}章内容
          </DialogTitle>
          <DialogContent sx={{ pt: 3, mt: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={15}
              value={chapterContentDialog.editedContent}
              onChange={(e) => {
                setChapterContentDialog({ 
                  ...chapterContentDialog, 
                  editedContent: e.target.value 
                });
              }}
              label="章节内容"
              placeholder="请输入章节内容"
              disabled={chapterContentDialog.saving}
              sx={{ mt: 1 }}
            />
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
              字数：{chapterContentDialog.editedContent.length} 字
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => setChapterContentDialog({ ...chapterContentDialog, open: false })}
              disabled={chapterContentDialog.saving}
              color="inherit"
            >
              取消
            </Button>
            <Button
              onClick={async () => {
                if (!chapterContentDialog.editedContent.trim()) {
                  showMessage('章节内容不能为空', 'warning');
                  return;
                }

                setChapterContentDialog({ ...chapterContentDialog, saving: true });
                
                // 保存内容
                const result = await saveChapterContentToServer(
                  chapterContentDialog.chapterIndex, 
                  chapterContentDialog.editedContent.trim()
                );
                
                if (result.success) {
                  showMessage('章节内容已保存', 'success');
                  setChapterContentDialog({ ...chapterContentDialog, open: false, saving: false });
                  
                  // 如果当前选中的是这个章节，更新显示的内容
                  if (selectedChapter === chapterContentDialog.chapterIndex) {
                    setCurrentChapterContent(chapterContentDialog.editedContent.trim());
                    setLastSavedContent(chapterContentDialog.editedContent.trim());
                  }
                } else {
                  showMessage(`保存失败: ${result.error || '未知错误'}`, 'error');
                  setChapterContentDialog({ ...chapterContentDialog, saving: false });
                }
              }}
              variant="contained"
              disabled={chapterContentDialog.saving || !chapterContentDialog.editedContent.trim()}
              startIcon={chapterContentDialog.saving ? <CircularProgress size={20} /> : <SaveIcon />}
            >
              {chapterContentDialog.saving ? '保存中...' : '保存'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* 🔥 关键事件编辑对话框 */}
        <Dialog
          open={keyEventsEdit.open}
          onClose={() => !keyEventsEdit.saving && setKeyEventsEdit({ ...keyEventsEdit, open: false })}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon />
            编辑第{keyEventsEdit.chapterNumber}章关键事件
          </DialogTitle>
          <DialogContent sx={{ pt: 3, mt: 2 }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              关键事件用于引导AI生成章节内容时的重点情节，每行一个事件，最多5个。
            </Typography>
            {keyEventsEdit.editedEvents.map((event, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
                <TextField
                  fullWidth
                  value={event}
                  onChange={(e) => {
                    const newEvents = [...keyEventsEdit.editedEvents];
                    newEvents[index] = e.target.value;
                    setKeyEventsEdit({ ...keyEventsEdit, editedEvents: newEvents });
                  }}
                  label={`关键事件 ${index + 1}`}
                  disabled={keyEventsEdit.saving}
                  inputProps={{ maxLength: 50 }}
                  helperText={`${event.length}/50字`}
                />
                <IconButton
                  onClick={() => {
                    const newEvents = keyEventsEdit.editedEvents.filter((_, i) => i !== index);
                    setKeyEventsEdit({ ...keyEventsEdit, editedEvents: newEvents });
                  }}
                  disabled={keyEventsEdit.saving || keyEventsEdit.editedEvents.length <= 1}
                  color="error"
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
            {keyEventsEdit.editedEvents.length < 5 && (
              <Button
                startIcon={<AddIcon />}
                onClick={() => {
                  setKeyEventsEdit({
                    ...keyEventsEdit,
                    editedEvents: [...keyEventsEdit.editedEvents, '']
                  });
                }}
                disabled={keyEventsEdit.saving}
                sx={{ mt: 1 }}
              >
                添加关键事件
              </Button>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => setKeyEventsEdit({ ...keyEventsEdit, open: false })}
              disabled={keyEventsEdit.saving}
              color="inherit"
            >
              取消
            </Button>
            <Button
              onClick={async () => {
                // 过滤掉空事件
                const validEvents = keyEventsEdit.editedEvents.filter(e => e.trim().length > 0);
                if (validEvents.length === 0) {
                  showMessage('至少需要一个关键事件', 'warning');
                  return;
                }
                
                setKeyEventsEdit({ ...keyEventsEdit, saving: true });
                const result = await saveKeyEvents(
                  keyEventsEdit.chapterNumber,
                  validEvents
                );
                if (result.success) {
                  setKeyEventsEdit({ ...keyEventsEdit, open: false, saving: false });
                } else {
                  setKeyEventsEdit({ ...keyEventsEdit, saving: false });
                }
              }}
              variant="contained"
              disabled={keyEventsEdit.saving || keyEventsEdit.editedEvents.every(e => e.trim().length === 0)}
              startIcon={keyEventsEdit.saving ? <CircularProgress size={20} /> : <SaveIcon />}
            >
              {keyEventsEdit.saving ? '保存中...' : '保存'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* 🔥 结局设计编辑对话框 */}
        <Dialog
          open={endingEdit.open}
          onClose={() => !endingEdit.saving && setEndingEdit({ ...endingEdit, open: false })}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon />
            编辑结局设计
          </DialogTitle>
          <DialogContent sx={{ pt: 3, mt: 2 }}>
            <TextField
              fullWidth
              value={endingEdit.type}
              onChange={(e) => setEndingEdit({ ...endingEdit, type: e.target.value })}
              label="结局类型"
              disabled={endingEdit.saving}
              sx={{ mb: 2, mt: 1 }}
            />
            <TextField
              fullWidth
              multiline
              rows={6}
              value={endingEdit.description}
              onChange={(e) => {
                const newValue = e.target.value;
                if (newValue.length <= 500) {
                  setEndingEdit({ ...endingEdit, description: newValue });
                }
              }}
              label="结局描述"
              helperText={`${endingEdit.description.length}/500字${endingEdit.description.length >= 500 ? ' - 已达到字数上限' : ''}`}
              error={endingEdit.description.length >= 500}
              disabled={endingEdit.saving}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              value={endingEdit.emotionalTone}
              onChange={(e) => setEndingEdit({ ...endingEdit, emotionalTone: e.target.value })}
              label="情感基调"
              disabled={endingEdit.saving}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => setEndingEdit({ ...endingEdit, open: false })}
              disabled={endingEdit.saving}
              color="inherit"
            >
              取消
            </Button>
            <Button
              onClick={async () => {
                setEndingEdit({ ...endingEdit, saving: true });
                const updatedEndingPlan = {
                  ...novel.storyStructure.endingPlan,
                  type: endingEdit.type,
                  description: endingEdit.description,
                  emotionalTone: endingEdit.emotionalTone
                };
                const result = await saveEndingPlan(updatedEndingPlan);
                if (result.success) {
                  setEndingEdit({ ...endingEdit, open: false, saving: false });
                } else {
                  setEndingEdit({ ...endingEdit, saving: false });
                }
              }}
              variant="contained"
              disabled={endingEdit.saving}
              startIcon={endingEdit.saving ? <CircularProgress size={20} /> : <SaveIcon />}
            >
              {endingEdit.saving ? '保存中...' : '保存'}
            </Button>
          </DialogActions>
        </Dialog>
    </Box>
    </Box>
  );
};

export default NovelEditor; 

